/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint max-classes-per-file: "off" */

import { Colors, IconNames16, Intent, ISelectOption, StageVariantBasicType } from '@pinpointhq/thumbtack';
import { addDays, addYears, subYears } from 'date-fns';
import { Descendant } from 'slate';
import { Attr, BelongsTo, HasMany, HasOne, Model, Scope, SpraypaintBase } from 'spraypaint';
import * as Yup from 'yup';
import { ConfigurationFormFieldValuesSchema } from '../../lib/integration_plugins/generated/internal/ts/configurationFormFieldValuesSchema';
import { FrontendConfigurationFormFieldItemArray } from '../../lib/integration_plugins/generated/internal/ts/frontendConfigurationFormFieldItemArray';
import { InsightWidgetPayload } from '../components/admin/insights/insightsHub/charts/InsightWidgetPayload';
import { IHiringTeam } from '../components/admin/jobs/applications/actions/inviteToInterview/IHiringTeam';
import { ProcessPayload, StagePayload } from '../components/onboarding/payload';
import { TRequiredLocaleKey } from '../components/shared/utilities/locales';
import { COLORS } from './colors';
import { expiredSessionMiddleware } from './expiredSessionMiddleware';
import type {
  IActiveStorageFile,
  ICloudinaryFile,
  ICronofyConferencingProfile,
  ICronofyProfile,
  IDocuSignRecipientProps,
  IDocuSignTabsProps,
  IOutboundEmailEvent,
  IScheduledMessage,
  ITag,
} from './interfaces';
import { ICronofyCalendar, IInsightsPageBreakpointLayout } from './interfaces';
import { ICertnPackage } from './interfaces/certnIntegration';
import { BypassUser } from './models/admin';
import type { INTEGRATION_CATEGORIES, INTEGRATION_TYPES } from './models/shared/IntegrationDescription';
import {
  Answer_answering_type_key,
  Answer_question_type_key,
  DocumentRequest_document_type_key,
  DocumentRequest_status_key,
  DocumentTemplate_document_type_key,
  Offer_result_key,
  Offer_signature_type_key,
  Offer_status_key,
  Question_question_context_key,
  Question_question_type_key,
} from './pinpointEnumKeys';
import { Shape } from './yupTypes';

@Model()
export class ApplicationRecord extends SpraypaintBase {
  public static apiNamespace = '/admin/api/v1';
  public static baseUrl = '';
}

ApplicationRecord.middlewareStack = expiredSessionMiddleware;

export type TagWithContext = { context: string; tag: string };
type TLocaleKey = TRequiredLocaleKey;

@Model()
export class MobilityApplicationRecord extends ApplicationRecord {
  public static jsonapiType = 'mobility_application_records';

  // tslint:disable-next-line: function-name
  public static withMobilityLocale<I extends typeof MobilityApplicationRecord>(
    this: I,
    locale: TLocaleKey,
  ): Scope<I['prototype']> {
    return this.extraParams({ mobility_locale: locale });
  }

  @Attr({ persist: false }) public readonly mobilityLocale?: TLocaleKey;
  @Attr({ persist: false }) public readonly mobilityLocaleValidations?: Record<TLocaleKey, boolean>;
}

@Model()
export class AccessGroup extends ApplicationRecord {
  public static jsonapiType = 'access_groups';
  public static MAX_PER = 1000;

  public static basicValidationSchema = Yup.object().shape({
    description: Yup.string().nullable().required("Can't be blank."),
    name: Yup.string().nullable().required("Can't be blank.").max(255, 'Max length is 255 characters.'),
  });

  // Not a proper attribute we just use this when creating users
  @Attr({ persist: false }) public custom: boolean;
  @Attr() public createAsReferral: boolean;
  @Attr() public description: string;
  @Attr() public name: string;
  @Attr() public wizardEnabled: boolean;
  @Attr() public wizardRequirements: {
    availability_rules: boolean;
    availability_settings: boolean;
    calendar_sync: boolean;
    conferencing_provider: boolean;
    hiringManagerProfile: boolean;
  };
  @Attr() public roleIds: string[];
  @Attr() public updatePermissions: boolean;
  @Attr() public updateVisibilities: boolean;
  @Attr() public updateNotificationPreferences: boolean;
  @Attr() public updateWizards: boolean;
  @Attr() public updatedAt: string;
  @Attr({ persist: false }) public readonly numberOfRoles: number;
  @Attr({ persist: false }) public readonly numberOfUsers: number;
  @Attr({ persist: false }) public readonly source?: string;
  @HasMany() public visibilities: Visibility[];
  @HasMany() public notificationPresets: NotificationPreset;

  get canEdit() {
    // TODO: Move to policy check
    return true;
  }

  get canDestroy() {
    // TODO: Move to policy check
    return true;
  }
}

@Model()
export class AdditionalInformationRequestInvite extends ApplicationRecord {
  public static jsonapiType = 'additional_information_request_invites';

  @Attr() public applicationId: number;
  @Attr() public companyId: number;
  @Attr() public informationType: string;

  @BelongsTo() public application: Application;
  @BelongsTo() public company: Company;
}

@Model()
export class Agency extends ApplicationRecord {
  public static jsonapiType = 'agencies';

  public static MAX_PER = 100;

  @Attr() public name: string;
  @Attr() public countryCodes: string[];
  // Extra attributes
  @Attr({ persist: false }) public readonly userCount: number;
  @Attr({ persist: false }) public readonly jobCount: boolean;
  @Attr({ persist: false }) public readonly applicationCount: boolean;

  @HasMany() public agenciesCompaniesUsers: AgenciesCompaniesUser[];
}

@Model()
export class AgenciesCompaniesUser extends ApplicationRecord {
  public static jsonapiType = 'agencies_companies_users';

  @Attr() public agencyId: string;

  @BelongsTo() public agency: Agency;
}

@Model()
export class Application extends ApplicationRecord {
  public static jsonapiType = 'applications';
  static basicValidations = {
    email: Yup.string().nullable().required("Can't be blank."),
    firstName: Yup.string().nullable().required("Can't be blank."),
    lastName: Yup.string().nullable().required("Can't be blank."),
  };

  public static validationSchema = Yup.object().shape({ ...Application.basicValidations });
  public static validationSchemaWithPhone = Yup.object().shape({
    ...Application.basicValidations,
    phone: Yup.number().nullable().typeError('Must be a number.').required("Can't be blank"),
  });

  public static validationSchemaWithAddress = Yup.object().shape({
    ...Application.basicValidations,
    address1: Yup.string().nullable().required("Can't be blank."),
    country: Yup.string().nullable().required("Can't be blank."),
    postcode: Yup.string().nullable().required("Can't be blank."),
    town: Yup.string().nullable().required("Can't be blank."),
  });

  public static NEW(attrs: Partial<Application>) {
    const defaultAttrs: Partial<Application> = {
      address1: '',
      address2: '',
      country: '',
      coverLetter: null,
      cv: null,
      email: '',
      firstName: '',
      preferredName: '',
      lastName: '',
      linkedinUrl: '',
      locale: 'en',
      phone: '',
      phoneIso2: '',
      postcode: '',
      middleName: '',
      summary: '',
      town: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  get acceptedOffer(): Offer {
    return this.offers.filter((offer) => offer.isAccepted)[0];
  }

  get isRejected() {
    return this.stageType === 'rejected';
  }

  get isCountryUS() {
    return this.country === 'US';
  }

  get resolveCity() {
    return this.isCountryUS ? this.address2 : this.town;
  }

  get resolveAddress2() {
    return this.isCountryUS ? '' : this.address2;
  }

  get resolveState() {
    return this.isCountryUS ? this.town : '';
  }

  get isConcealed() {
    return this.concealed;
  }

  @Attr() public address1: string;
  @Attr() public address2: string;
  @Attr() public addTagsWithContext: TagWithContext[];
  @Attr() public addTagWithContext: TagWithContext;
  @Attr() public avatarSvg: string;
  @Attr({ persist: false }) public readonly candidateId: string;
  @Attr() public channel: string;
  @Attr() public channelSource: string;
  @Attr({ persist: false }) public collapsed: boolean;
  @Attr() public concealed: boolean;
  @Attr() public country: string;
  @Attr() public createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortDate: string;
  @Attr() public coverLetter: string;
  @Attr({ persist: false }) public readonly coverLetterFile: IActiveStorageFile;
  @Attr() public cv: string;
  @Attr({ persist: false }) public readonly cvFile: IActiveStorageFile;
  @Attr() public dateOfBirth: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public email: string;
  @Attr() public firstName: string;
  @Attr() public preferredName: string;
  @Attr({ persist: false }) public readonly fullName: string;
  @Attr() public hasDocuments: boolean;
  @Attr({ persist: false }) public readonly hideInterpretedResume: boolean;
  @Attr({ persist: false }) public readonly internalCandidate: boolean;
  @Attr() public jobId: number | string;
  @Attr() public jobPostingId: number | string;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr({ persist: false }) public readonly language: string;
  @Attr() public lastName: string;
  @Attr() public linkedinUrl: string;
  @Attr() public locale: TLocaleKey;
  @Attr() public middleName: string;
  @Attr() public phone: string;
  @Attr() public phoneIso2: string;
  @Attr() public preferredPronouns: string;
  @Attr() public postcode: string;
  @Attr() public recruiter: boolean;
  @Attr({ persist: false }) public readonly redactedCoverLetterFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly redactedCvFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly referralAmountFormatted?: string;
  @Attr() public referralConfirmedAt?: string;
  @Attr() public referralCurrency?: string;
  @Attr({ persist: false }) public readonly referralState?: 'pending' | 'successful' | 'unsuccessful';
  @Attr() public removeTagWithContext: TagWithContext;
  @Attr() public revealed: boolean;
  @Attr() public sendSmsMessage: string;
  // Used for validating applications but skipping over the conceal methods before creating
  @Attr() public skipConceal: boolean;
  // Used for sending additional information request email to candidate
  @Attr() public sendAdditionalInformationRequest: boolean;
  @Attr({ persist: false }) public readonly showPath: string;
  @Attr({ persist: false }) public readonly signedOfferLetterFile: IActiveStorageFile;
  @Attr() public skipPhoneValidations: boolean;
  @Attr() public smsPreview: string;
  @Attr() public stageId: number | string;
  @Attr() public startOn: string;
  @Attr() public summary: string;
  @Attr() public town: string;
  // Used to indicate that we simply want to validate the applications (not actually create them)
  @Attr({ persist: false }) public uniqueId: number | string;
  @Attr() public updatedAt: string;
  @Attr() public validate: boolean;
  @Attr() public stageLanePosition: number;

  @Attr() public updateConsentGrant: boolean;
  @Attr() public assignToPositionOpeningId: string;
  @Attr() public assignPositionHolderStartOn: string;
  @Attr() public updateLanePosition: number;
  @Attr() public updateCandidatePreferredName: boolean;

  @Attr({ persist: false }) public readonly visible;

  /** Used to determine if we should wait when parsing the resume and if the resume is currently being parsed */
  @Attr() public isParsing: boolean;
  @Attr() public waitForParse: boolean;

  /** localised time and date attributes */
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;

  // Extra attributes
  @Attr({ persist: false }) public readonly averageRating: number;
  @Attr({ persist: false }) public readonly consentType: string;
  @Attr({ persist: false }) public readonly coverLetterText: string;
  @Attr({ persist: false }) public readonly currentUserRating: number;
  @Attr({ persist: false }) public readonly emailConsent: boolean;
  @Attr({ persist: false }) public readonly hasOtherVisibleApplications: boolean;
  @Attr({ persist: false }) public readonly humanizedCountryName: string;
  @Attr({ persist: false }) public readonly pdfCoverLetterFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly pdfCvFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly stageType: string;
  @Attr({ persist: false }) public readonly hasEmploymentGap: {
    isOverMaxEmploymentGap: boolean;
    maxEmploymentGap: string;
  };
  @Attr({ persist: false }) public readonly exportedAt: string;
  @Attr() public nationalIdentifier: string;
  @Attr({ persist: false }) public readonly showNationalIdentifier: boolean;
  @Attr({ persist: false }) public readonly hideEmploymentHistory: boolean;
  @Attr({ persist: false }) public readonly hideEducationHistory: boolean;
  @Attr({ persist: false }) public readonly smsConsent: boolean;
  @Attr({ persist: false }) public readonly submittedBy: string;
  @Attr({ persist: false }) public readonly tags: ITag[];
  @Attr({ persist: false }) public readonly canSendEqualityMonitoringSurvey: boolean;
  @Attr({ persist: false }) public readonly canSendAdditionalInformationRequest: boolean;
  @Attr({ persist: false }) public readonly hrisExportOraclePersonNumber: string;
  @Attr({ persist: false }) public readonly hrisExportCezannePersonCode: string;
  @Attr({ persist: false }) public readonly hrisExportHumaansReference: string;
  @Attr({ persist: false }) public readonly hrisExportItrentEmployeeNumber: string;
  @Attr({ persist: false }) public readonly hrisExportBizimplyEmployeeId: string;
  @Attr({ persist: false }) public readonly hrisExportCompasEmployeeId: string;

  @Attr({ persist: false }) public readonly otherVisibleApplicationsCount: number;
  @Attr({ persist: false }) public readonly sterlingBillCode: string;
  @Attr({ persist: false }) public readonly uuid: string;
  @Attr({ persist: false }) public readonly compasParams: {};

  // Policy checks (also extra attributes)
  @Attr({ persist: false }) public readonly canConceal: boolean;
  @Attr({ persist: false }) public readonly canDelete: boolean;
  @Attr({ persist: false }) public readonly canMoveStage: boolean;
  @Attr({ persist: false }) public readonly canRate: boolean;
  @Attr({ persist: false }) public readonly canReveal: boolean;
  @Attr({ persist: false }) public readonly canSendEmail: boolean;
  @Attr({ persist: false }) public readonly canSendSms: boolean;
  @Attr({ persist: false }) public readonly canSendToOnboarding: boolean;
  @Attr({ persist: false }) public readonly canSendToRippling: boolean;
  @Attr({ persist: false }) public readonly canSendToBobDataMapping: boolean;
  @Attr({ persist: false }) public readonly canSendToBobWorkflow: boolean;
  @Attr({ persist: false }) public readonly canSendToWorkday: boolean;
  @Attr({ persist: false }) public readonly canTag: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canInviteToVideoInterview: boolean;

  // Scorecard attributes calculated on the frontend
  @Attr({ persist: false }) public scorecardPercentageScore: number;
  @Attr({ persist: false }) public scorecardUserWeightedScoreTotal: number;
  @Attr({ persist: false }) public scorecardMaxWeightedScoreTotal: number;
  @Attr({ persist: false }) public meanByScorecardOptionIds: { [key: string]: number };

  // Relationships
  @HasOne() public applicationsAgency: ApplicationsAgency;
  @HasOne() public resume: Resume;
  @HasOne() public source: Source;
  @HasOne() public onboardingApplication: OnboardingApplication;
  @HasOne() public referral: Referral;
  @HasOne() public candidateSurvey: CandidateSurvey;
  @HasOne() public geocodedAddress: GeocodedAddress;
  @BelongsTo() public candidate: Candidate;
  @BelongsTo() public job: Job;
  @BelongsTo() public jobPosting: JobPosting;
  @BelongsTo() public location: Location;
  @BelongsTo() public stage: Stage;
  @BelongsTo() public referralsUser?: ReferralsUser;
  @HasMany() public jobPreferences: JobPreference[];
  @HasMany() public scorecardNotes: ScorecardNote[];
  @HasMany() public scorecardRatings: ScorecardRating[];
  @HasMany() public scorecardAnswers: ScorecardAnswer[];
  @HasMany() public equalityMonitoringTraits: EqualityMonitoringTrait[];
  @HasMany() public videoInterviewCandidates: VideoInterviewCandidate[];
  @HasMany() public answers: Answer[];
  @HasMany() public hrisExports: HrisExport[];
  @HasMany() public associatedAudits: Audit[];
  @HasMany() public audits: Audit[];
  @HasMany() public comments: Comment[];
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public folders: Folder[];
  @HasMany() public ratings: Rating[];
  @HasMany() public backgroundChecks: BackgroundCheck[];
  @HasMany() public scheduledBackgroundChecks: ScheduledBackgroundCheck[];
  @HasMany() public referenceChecks: ReferenceCheck[];
  @HasMany() public documentRequests: DocumentRequest[];
  @HasMany() public offers: Offer[];
  @HasMany() public groupAssessmentInvites: GroupAssessmentInvite[];
  @HasMany() public userScorecards: UserScorecard[];
  @HasMany() public structuredSectionResponses: StructuredSectionResponse[];

  // Interviewable Concern
  @HasMany() public interviews: Interview[];
  @HasMany() public interviewInvites: InterviewInvite[];
  @HasOne() public currentInterview: Interview;
  @HasOne() public currentInterviewInvite: InterviewInvite;

  // Scheduled Messages Concern
  @Attr({ persist: false }) public readonly scheduledMessages: IScheduledMessage[];
}

export namespace Application {
  export namespace Stats {
    export type total_count = number;
    export type properties_bulk_download_parser_resources = { value: string; text: string }[];
    export type count_one_email = number;
    export type by_stage_basic_type_count = { key: string; value: number; label: string }[];
    export type by_stage_name_count = { key: string; value: number; label: string }[];
    export type by_average_time_spent_in_stage_count = { key: string; value: number; label: string }[];
    export type by_tag_category_count_by_tag = { [tagCategory: string]: { [tag: string]: number } };
    export type by_date_created_cumulative_count = { [date: string]: number };
    export type by_highest_stage_achieved_count = { key: string; value: number; label: string }[];
    export type by_job_posting_count = { key: string; value: number; label: string }[];
    export type by_channel_source_count = {
      key: string;
      value: number;
      label: string;
      detail: { label: string; value: number }[];
    }[];
    export type by_source_domain_count = {
      key: string;
      value: number;
      label: string;
      detail: { label: string; value: number }[];
    }[];
    export type average_per_job = number;
    export type average_messages_sent = number;
    export type permissions = {
      can_add_to_talent_pipeline: boolean;
      can_bulk_send_offers: boolean;
      can_invite_to_talent_pipeline: boolean;
      can_manage_offers: boolean;
      can_request_reference_checks: boolean;
      can_send_national_identifier_request: boolean;
      can_send_to_adp_workforce_now: boolean;
      can_send_to_alva_labs: boolean;
      can_send_to_bamboo_hr: boolean;
      can_send_to_bizimply: boolean;
      can_send_to_bob_data_mapping: boolean;
      can_send_to_bob_workflow: boolean;
      can_send_to_breathe: boolean;
      can_send_to_bryq: boolean;
      can_send_to_canditech: boolean;
      can_send_to_cappfinity: boolean;
      can_send_to_care_planner: boolean;
      can_send_to_cascade: boolean;
      can_send_to_certn: boolean;
      can_send_to_cezanne: boolean;
      can_send_to_checkr: boolean;
      can_send_to_cintra: boolean;
      can_send_to_codility: boolean;
      can_send_to_compas: boolean;
      can_send_to_creative_services_inc: boolean;
      can_send_to_criteria: boolean;
      can_send_to_deel: boolean;
      can_send_to_docu_sign: boolean;
      can_send_to_first_advantage: boolean;
      can_send_to_first_advantage_kyp: boolean;
      can_send_to_gusto: boolean;
      can_send_to_hacker_rank: boolean;
      can_send_to_harver: boolean;
      can_send_to_hire_right: boolean;
      can_send_to_humaans: boolean;
      can_send_to_intellihr: boolean;
      can_send_to_itrent: boolean;
      can_send_to_oracle: boolean;
      can_send_to_outmatch: boolean;
      can_send_to_people_hr: boolean;
      can_send_to_predictive_index: boolean;
      can_send_to_ref_now: boolean;
      can_send_to_sage_people: boolean;
      can_send_to_sapling: boolean;
      can_send_to_sftp_csv_transfer: boolean;
      can_send_to_shl: boolean;
      can_send_to_sterling: boolean;
      can_send_to_test_gorilla: boolean;
      can_send_to_trust_id: boolean;
      can_send_to_zinc: boolean;
      can_view_background_checks: boolean;
    };
  }
}

@Model()
export class ApplicationsAgency extends ApplicationRecord {
  public static jsonapiType = 'applications_agencies';

  @Attr() public updatedAt: string;

  @BelongsTo() public application: Application;
  @BelongsTo() public agency: Agency;
}

@Model()
export class ApprovalList extends ApplicationRecord {
  public static jsonapiType = 'approval_lists';

  @Attr() public approvalEmail: string;
  @Attr() public name: string;
  @Attr() public updatedAt: string;

  @HasMany() public approvalListsUsers: ApprovalListsUser[];
}

@Model()
export class ApprovalListsUser extends ApplicationRecord {
  public static jsonapiType = 'approval_lists_users';

  @Attr({ persist: false }) public readonly userId: string;
  @Attr() public updatedAt: string;

  @BelongsTo() public user: User;
  @BelongsTo() public approvalList: ApprovalList;
}

@Model()
export class ApprovalWorkflow extends ApplicationRecord {
  public static jsonapiType = 'approval_workflows';

  get isRejected(): boolean {
    return this.status === 'rejected';
  }

  @Attr({ persist: false }) public readonly approvableId: string;
  @Attr({ persist: false }) public readonly approvableType: string;
  @Attr({ persist: false }) public readonly name: string;
  @Attr() public restartWorkflow: boolean;
  @Attr({ persist: false }) public readonly status: string;
  @Attr() public readonly updatedAtLongWithYearDate: string;
  @HasMany() public approvalWorkflowStages: ApprovalWorkflowStage[];
}

@Model()
export class ApprovalWorkflowStage extends ApplicationRecord {
  public static jsonapiType = 'approval_workflow_stages';

  get hasSufficientSignatories(): boolean {
    return this.numberOfSignatories <= this.approvalWorkflowStagesUsers.length;
  }

  @Attr({ persist: false }) public readonly assignment: string;
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly numberOfSignatories: number;
  @Attr({ persist: false }) public readonly position: string;
  @Attr({ persist: false }) public readonly status: string;

  @Attr() public sendReminders: boolean;

  @HasMany() public approvalWorkflowStagesUsers: ApprovalWorkflowStagesUser[];
}

@Model()
export class ApprovalWorkflowStagesUser extends ApplicationRecord {
  public static jsonapiType = 'approval_workflow_stages_users';

  get isPending(): boolean {
    return this.status === 'pending';
  }

  @Attr() public comment: string;
  @Attr() public status: string;
  @Attr() public respondedViaEmail: boolean;
  @Attr() public nextApprovalUserIds: string[];
  @Attr() public skipNextStageApproval: boolean;
  @Attr() public skipCustomValidation: boolean;
  @Attr({ persist: false }) public readonly delegatorId: string;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly updatedAtShortWithYearTimeWithZone: string;

  @BelongsTo() public user: User;
  @BelongsTo() public delegator: User;
  @BelongsTo() public approvalWorkflowStage: ApprovalWorkflowStage;
}

@Model()
export class JobPreference extends ApplicationRecord {
  public static jsonapiType = 'job_preferences';

  @Attr({ persist: false }) public applicationId: string;
  @Attr({ persist: false }) public jobId: string;
  @Attr({ persist: false }) public position: string;

  @BelongsTo() public application: Application;
  @BelongsTo() public job: Job;
}

@Model()
export class Automation extends ApplicationRecord {
  public static jsonapiType = 'automations';

  public static validationSchema = Yup.object().shape({
    title: Yup.string().nullable().required("Can't be blank."),
  });

  public static NEW(params: Partial<Automation> = {}) {
    return new this({
      enabled: true,
      title: '',
      ...params,
    });
  }

  @Attr({ persist: false }) public collapsed: boolean;
  @Attr() public title: string;
  @Attr() public enabled: boolean;

  @BelongsTo() public company: Company;
  @HasMany() public automationSteps: AutomationStep['instanceClasses'][];
}

@Model()
export class AutomationStepsRequirable extends ApplicationRecord {
  public static jsonapiType = 'automation_steps_requirables';

  @Attr({ persist: false }) public readonly requirableId: string;
  @Attr({ persist: false }) public readonly requirableType: 'Job';

  @BelongsTo() public automationStep: AutomationStep;
  @BelongsTo() public company: Company;
  @BelongsTo() public requirable: Job;
}

@Model()
export class AutomationStep extends ApplicationRecord {
  public static jsonapiType = 'automation_steps';
  public instanceClasses:
    | AutomationStepsAutomationActionsCreateTask
    | AutomationStepsAutomationActionsRejectJobApplication
    | AutomationStepsAutomationActionsSendEmailToJobApplicant
    | AutomationStepsAutomationActionsSendSmsToJobApplicant
    | AutomationStepsAutomationActionsTagJobApplication
    | AutomationStepsAutomationConditionsApplicationStageNameEquals
    | AutomationStepsAutomationConditionsMultipleChoiceQuestionAnswersInclude
    | AutomationStepsAutomationConditionsQuestionAnswerMatches
    | AutomationStepsAutomationConditionsStageTypeEqualsApplied
    | AutomationStepsAutomationTargetsApplication
    | AutomationStepsAutomationTriggersJobApplicationStageChanged
    | AutomationStepsAutomationTriggersNewJobApplicationCreated;

  public static NEW = (
    type: AutomationStep['instanceClasses']['type'],
    { id, ...attributes }: Partial<AutomationStep['instanceClasses']>,
  ) => {
    let newAutomationStep: AutomationStep['instanceClasses'];
    switch (type) {
      case 'AutomationSteps::AutomationActions::CreateTask':
        newAutomationStep = new AutomationStepsAutomationActionsCreateTask({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationActions::RejectJobApplication':
        newAutomationStep = new AutomationStepsAutomationActionsRejectJobApplication({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationActions::SendEmailToJobApplicant':
        newAutomationStep = new AutomationStepsAutomationActionsSendEmailToJobApplicant({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationActions::SendSmsToJobApplicant':
        newAutomationStep = new AutomationStepsAutomationActionsSendSmsToJobApplicant({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationActions::TagJobApplication':
        newAutomationStep = new AutomationStepsAutomationActionsTagJobApplication({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationConditions::ApplicationStageNameEquals':
        newAutomationStep = new AutomationStepsAutomationConditionsApplicationStageNameEquals({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationConditions::MultipleChoiceQuestionAnswersInclude':
        newAutomationStep = new AutomationStepsAutomationConditionsMultipleChoiceQuestionAnswersInclude({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationConditions::QuestionAnswerMatches':
        newAutomationStep = new AutomationStepsAutomationConditionsQuestionAnswerMatches({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationConditions::StageTypeEqualsApplied':
        newAutomationStep = new AutomationStepsAutomationConditionsStageTypeEqualsApplied({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationTargets::Application':
        newAutomationStep = new AutomationStepsAutomationTargetsApplication({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationTriggers::JobApplicationStageChanged':
        newAutomationStep = new AutomationStepsAutomationTriggersJobApplicationStageChanged({
          ...attributes,
        });
        break;
      case 'AutomationSteps::AutomationTriggers::NewJobApplicationCreated':
        newAutomationStep = new AutomationStepsAutomationTriggersNewJobApplicationCreated({
          ...attributes,
        });
        break;
      default:
        throw new Error(`Unknown automation step type: ${type}`);
    }

    // NOTE: need to set this again, because for some reason it is brittle and can end up as undefined.
    // and it an't be undefined, because it screws things up later.
    newAutomationStep.type = type;
    newAutomationStep.temp_id = `${newAutomationStep.type}_${Date.now()}`;
    return newAutomationStep;
  };

  @Attr({ persist: false }) public type: AutomationStep['instanceClasses']['type'];

  @Attr() public position: number;
  @Attr({ persist: false }) public readonly title: string;

  @BelongsTo() public automatable: unknown;
  @BelongsTo() public automation: Automation;
  @BelongsTo() public company: Company;
  @HasMany() public automationStepsRequirables: AutomationStepsRequirable[];

  // @note: (Leo Melo) this method is a stub, child classes should override it
  // eslint-disable-next-line class-methods-use-this
  public validate = (): Record<string, string> => ({});
}

@Model()
export class AutomationStepsAutomationActionsRejectJobApplication extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_actions/reject_job_application';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationActions::RejectJobApplication' as const;
  @Attr() public delayInMinutes: number;
  @Attr() public userId: string;

  @Attr() public setTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public tagsWithContext: TagWithContext[];
  @Attr() public useOfficeHoursOnly: boolean;

  @BelongsTo() public automatable: EmailTemplate;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (this.delayInMinutes < 0) {
      errors.delayInMinutes = 'Must be greater than or equal to 0';
    }
    if (!this.automatable?.isPersisted) {
      errors[`${id}_automatable`] = 'Please select an email template';
    }
    if (!this.userId) {
      errors[`${id}_userId`] = 'Please select a user';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationActionsSendEmailToJobApplicant extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_actions/send_email_to_job_applicant';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationActions::SendEmailToJobApplicant' as const;
  @Attr() public delayInMinutes: number;
  @Attr() public userId: string;

  @Attr() public setTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public tagsWithContext: TagWithContext[];
  @Attr() public useOfficeHoursOnly: boolean;

  @BelongsTo() public automatable: EmailTemplate;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (this.delayInMinutes < 0) {
      errors.delayInMinutes = 'Must be greater than or equal to 0';
    }
    if (!this.automatable?.isPersisted) {
      errors[`${id}_automatable`] = 'Please select an email template';
    }
    if (!this.userId) {
      errors[`${id}_userId`] = 'Please select a user';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationActionsCreateTask extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_actions/create_task';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationActions::CreateTask' as const;
  @Attr() public ownerIds: string[];
  @Attr() public dueOn: string;
  @Attr() public taskTitle: string;
  @Attr() public taskDescription: string;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (!this.taskTitle) {
      errors[`${id}_taskTitle`] = "Title can't be blank";
    }
    if (!this.taskDescription) {
      errors[`${id}_taskDescription`] = "Description can't be blank";
    }
    if (!this.ownerIds) {
      errors[`${id}_ownerIds`] = 'Please select at least one owner';
    }
    if (!this.dueOn) {
      errors[`${id}_dueOn`] = 'Please select a due date';
    }

    return errors;
  };

  public static dynamicDateOptions = [
    { label: 'End of the day', value: '0_day' },
    { label: '1 day later', value: '1_day' },
    { label: '2 days later', value: '2_day' },
    { label: '3 days later', value: '3_day' },
    { label: '4 days later', value: '4_day' },
    { label: '5 days later', value: '5_day' },
    { label: '6 days later', value: '6_day' },
    { label: '1 week later', value: '1_week' },
    { label: '2 weeks later', value: '2_week' },
    { label: '3 weeks later', value: '3_week' },
    { label: '4 weeks later', value: '4_week' },
    { label: '1 month later', value: '1_month' },
    { label: '2 months later', value: '2_month' },
    { label: '3 months later', value: '3_month' },
    { label: '4 months later', value: '4_month' },
    { label: '5 months later', value: '5_month' },
    { label: '6 months later', value: '6_month' },
    { label: '9 months later', value: '9_month' },
    { label: '1 year later', value: '1_year' },
  ];
}

@Model()
export class AutomationStepsAutomationActionsSendSmsToJobApplicant extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_actions/send_sms_to_job_applicant';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationActions::SendSmsToJobApplicant' as const;
  @Attr() public delayInMinutes: number;
  @Attr() public userId: string;

  @Attr() public setTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public tagsWithContext: TagWithContext[];
  @Attr() public useOfficeHoursOnly: boolean;

  @BelongsTo() public automatable: SmsTemplate;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (this.delayInMinutes < 0) {
      errors.delayInMinutes = 'Must be greater than or equal to 0';
    }
    if (!this.automatable?.isPersisted) {
      errors[`${id}_automatable`] = 'Please select an sms template';
    }
    if (!this.userId) {
      errors[`${id}_userId`] = 'Please select a user';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationActionsTagJobApplication extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_actions/tag_job_application';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationActions::TagJobApplication' as const;

  @Attr() public setTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public tagsWithContext: TagWithContext[];

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};
    const hasSetTags = Array.isArray(this.setTagsWithContext) && this.setTagsWithContext.length > 0;
    const hasExistingTags = Array.isArray(this.tagsWithContext) && this.tagsWithContext.length > 0;
    if (!hasSetTags && !hasExistingTags) {
      errors[`${id}_setTagsWithContext`] = 'Please select at least one tag';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationConditionsApplicationStageNameEquals extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_conditions/application_stage_name_equals';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationConditions::ApplicationStageNameEquals' as const;
  @Attr() public stageName: string;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (!this.stageName || this.stageName.length < 1) {
      errors[`${id}_stageName`] = 'Please select a stage.';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationConditionsMultipleChoiceQuestionAnswersInclude extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_conditions/multiple_choice_question_answers_include';

  @Attr({ persist: false }) public type =
    'AutomationSteps::AutomationConditions::MultipleChoiceQuestionAnswersInclude' as const;
  @Attr() public questionOptionIds: string[];
  @BelongsTo() public automatable: Question;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (!this.automatable?.isPersisted) {
      errors[`${id}_automatable`] = 'Please select a question.';
    }
    if (!this.questionOptionIds || this.questionOptionIds.length < 1) {
      errors[`${id}_questionOptionIds`] = 'Please select at least one option.';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationConditionsQuestionAnswerMatches extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_conditions/question_answer_matches';

  public static humanizedBooleanAnswer = {
    '1': 'Yes',
    '0': 'No',
  };

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationConditions::QuestionAnswerMatches' as const;
  @Attr() public answer: string;
  @BelongsTo() public automatable: Question;

  public validate = (): Record<string, string> => {
    const id = this.id || this.temp_id;
    const errors: Partial<Record<keyof this, string>> = {};

    if (!this.automatable?.isPersisted) {
      errors[`${id}_automatable`] = 'Please select a question.';
    }
    if (!this.answer) {
      errors[`${id}_answer`] = 'Please select an answer.';
    }

    return errors;
  };
}

@Model()
export class AutomationStepsAutomationConditionsStageTypeEqualsApplied extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_conditions/stage_type_equals_applied';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationConditions::StageTypeEqualsApplied' as const;
  @Attr() public stageType: string;
}

@Model()
export class AutomationStepsAutomationTargetsApplication extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_targets/application';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationTargets::Application' as const;
}

@Model()
export class AutomationStepsAutomationTriggersJobApplicationStageChanged extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_triggers/job_application_stage_changed';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationTriggers::JobApplicationStageChanged' as const;
}

@Model()
export class AutomationStepsAutomationTriggersNewJobApplicationCreated extends AutomationStep {
  public static jsonapiType = 'automation_steps/automation_triggers/new_job_application_created';

  @Attr({ persist: false }) public type = 'AutomationSteps::AutomationTriggers::NewJobApplicationCreated' as const;
}

export interface IAutomationStepTemplate {
  consumes: string[];
  id: AutomationStep['type'];
  is_available: boolean;
  provides: string[];
  title: string;
}

@Model()
export class CandidateSurvey extends ApplicationRecord {
  public static jsonapiType = 'candidate_surveys';
  public static MAX_PER = 10000;

  @Attr({ persist: false }) public readonly rating: number;
  @Attr({ persist: false }) public readonly comment: string;
  @Attr({ persist: false }) public readonly firstName: string;
  @Attr({ persist: false }) public readonly respondedAt: string;

  @BelongsTo() public application: Application;
  @BelongsTo() public location: Location;
  @HasMany() public answers: Answer[];
}

export namespace CandidateSurvey {
  export namespace Stats {
    export type total_count = number;
    export type responded_count = number;
    export type nps_score = number;
    export type nps_breakdown = {
      promoters: number;
      passives: number;
      detractors: number;
    };
    export type nps_time_series = { date: string; value: number }[];
    export type nps_grouped = { id: string; score: number };
  }
}

@Model()
export class Attachment extends ApplicationRecord {
  public static jsonapiType = 'attachments';
  @Attr() public recordType: FilterCollection['recordType'];
  @Attr() public recordId: string;
  // Used to rename files through the Attachment -> Blob association
  @Attr() public blobName: string;
}

@Model()
export class Answer extends ApplicationRecord {
  public static jsonapiType = 'answers';

  @Attr() public answeringType: Answer_answering_type_key;
  @Attr() public document: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public questionId: string;
  @Attr() public questionType: Answer_question_type_key;
  @Attr() public title: string;
  @Attr() public additionalInformation: string;
  @Attr() public booleanAnswer: boolean | string | '';
  @Attr() public textAnswer: string;
  @Attr() public dateAnswer: string;
  @Attr() public urlAnswer: string;
  @Attr() public dateFromAnswer: string;
  @Attr() public dateToAnswer: string;
  // Down as a string because it can be a decimal which is a hybrid
  @Attr() public numberAnswer: string;
  @Attr() public validate: boolean;
  @Attr() public skipAnswerValidation: boolean;
  @Attr({ persist: false }) public readonly answerableType: string;
  @Attr({ persist: false }) public readonly answerableId: number;
  @Attr() public skipAnswerOptionValidation: boolean;
  @Attr() public documentFile: IActiveStorageFile;
  @Attr() public documentsFiles: IActiveStorageFile[];
  @BelongsTo() public answerable: Application | JobSeeker;
  @BelongsTo() public application: Application;
  // @BelongsTo() public jobSeeker: JobSeeker;
  @BelongsTo() public question: Question;
  @HasMany() public answerOptions: AnswerOption[];
}

@Model()
export class AnswerOption extends ApplicationRecord {
  public static jsonapiType = 'answer_options';
  @Attr({ persist: false }) public questionOptionId: string;
  @Attr() public text: string;
  @Attr() public validate: boolean;
}

@Model()
export class HrisExport extends ApplicationRecord {
  public static jsonapiType = 'hris_exports';

  @Attr() public applicationId: string;
  @Attr() public exporterType: string;
  @Attr() public hrisReference: string;
  @Attr() public hrisSystem: string;

  @HasMany() public audits: Audit[];
}

@Model()
export class Audit extends ApplicationRecord {
  get isCreate(): boolean {
    return this.action === 'create';
  }

  get isUpdate(): boolean {
    return this.action === 'update';
  }

  get isDestroy(): boolean {
    return this.action === 'destroy';
  }

  public static jsonapiType = 'audits';
  @Attr() public action: 'create' | 'update' | 'destroy';
  @Attr() public auditableId: string;
  @Attr() public auditableType: string;
  @Attr() public associatedType: string;
  @Attr() public createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortWithTime: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr() public additionalInformationRequestChanges: { text: string };
  @Attr() public firstNameChanges: { from: string | null; to: string };
  @Attr() public lastNameChanges: { from: string | null; to: string };
  @Attr() public phoneChanges: { from: string | null; to: string };
  @Attr() public revealedChanges: { from: string | null; to: string };
  @Attr() public emailChanges: { from: string | null; to: string };
  @Attr() public channelChanges: { from: string | null; to: string };
  @Attr() public channelSourceChanges: { from: string | null; to: string };
  @Attr() public consentTypeChanges: { text: string };
  @Attr() public ratingScoreChanges: { from: number | null; to: number };
  @Attr() public stageIdChanges: { from: number | null; to: number };
  @Attr() public requisitionStatusChanges: { from: number | null; to: number };
  @Attr() public requisitionArchivedChanges: { text: string };
  @Attr() public jobTitleChanges: { from: string | null; to: string };
  @Attr() public jobStatusChanges: { from: number | null; to: number };
  @Attr() public jobHeadcountMaximumChanges: { from: number | null; to: number };
  @Attr() public jobChanges: { text: string };
  @Attr() public nationalIdentifierChanges: { text: string };
  @Attr() public approvalWorkflowStagesUserChanges: { comment: string; from: string; to: string };
  @Attr() public jobCompensationCurrencyChanges: { from: string | null; to: string };
  @Attr() public jobCompensationFrequencyChanges: { from: number | null; to: number };
  @Attr() public jobCompensationMinimumChanges: { from: number | null; to: number };
  @Attr() public jobCompensationMaximumChanges: { from: number | null; to: number };
  @Attr() public jobDeadlineOnChanges: { from: string | null; to: string };
  @Attr() public jobRemoteChanges: { from: boolean | null; to: boolean };
  @Attr() public jobVisibilityChanges: { from: number | null; to: number };
  @Attr() public jobDiversityMonitoringChanges: { from: boolean | null; to: boolean };
  @Attr() public jobReferralsEnabledChanges: { from: boolean | null; to: boolean };
  @Attr() public jobReferralDetailsChanges: { from: string | null; to: string };
  @Attr() public jobReferralAmountChanges: { from: number | null; to: number };
  @Attr() public jobReferralCurrencyChanges: { from: string | null; to: string };
  @Attr() public jobSignOffWorkflowChanges: { text: string };
  @Attr() public jobWorkplaceTypeChanges: { from: string | null; to: string };
  @Attr() public signOffStageApprovalChanges: { comment?: string; consent: string };
  @Attr() public skippedSignOffStageChanges: { text: string };
  @Attr() public questionsQuestionableChanges: { from: string | null; to: string };
  @Attr() public customAttributeChanges: { from: string | null; to: string };
  @Attr() public hrisExportChanges: { text: string };
  @Attr() public coverLetterChanges: { text: string };
  @Attr() public cvChanges: { text: string };
  @Attr() public interviewChanges: { text: string };
  @Attr() public offerChanges: { text: string };
  @Attr() public offerResultChanges: { from: string | null; to: string };
  @Attr() public offerPaidDaysOffChanges: { from: string | null; to: string };
  @Attr() public offerSalaryAmountChanges: { from: string | null; to: string };
  @Attr() public offerSalaryCurrencyChanges: { from: string | null; to: string };
  @Attr() public offerSalaryDurationChanges: { from: string | null; to: string };
  @Attr() public offerWorkAddressChanges: { from: string | null; to: string };
  @BelongsTo() public user: User;
  @BelongsTo() public auditable: unknown;
}

@Model()
export class AvailabilityRule extends ApplicationRecord {
  public static jsonapiType = 'availability_rules';

  public static validationSchema = Yup.object().shape({
    bufferAfter: Yup.number()
      .typeError('Must be a number.')
      .required("Can't be blank.")
      .min(0, "Can't be negative.")
      .max(120, 'Can be at most 120 minutes.'),
    bufferBefore: Yup.number()
      .typeError('Must be a number.')
      .required("Can't be blank.")
      .min(0, "Can't be negative.")
      .max(120, 'Can be at most 120 minutes.'),
    minimumNoticePeriod: Yup.string().required("Can't be blank."),
    minimumNoticeValue: Yup.number()
      .required("Can't be blank.")
      .typeError('Must be a number.')
      .min(0, "Can't be negative.")
      .max(24, 'Can be at most 24.'),
  });

  @Attr() public available: boolean;
  @Attr() public bufferAfter: number;
  @Attr() public bufferBefore: number;
  @Attr() public confirmed: boolean;
  @Attr({ persist: false }) public readonly cronofySub: string;
  @Attr() public minimumNoticePeriod: string;
  @Attr() public minimumNoticeValue: number;
  @Attr() public startFrequency: number;
  @Attr() public syncedToCronofy: boolean;
}

@Model()
export class BackgroundCheck extends ApplicationRecord {
  public static jsonapiType = 'background_checks';
  @Attr() public readonly documentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly applicationId: number;
  @Attr({ persist: false }) public readonly provider: string;
  @Attr({ persist: false }) public readonly reportUrl: string;
  @Attr({ persist: false }) public readonly requestedBy: string;
  @Attr({ persist: false }) public readonly updatedAt: string;
  @Attr({ persist: false }) public readonly configuration: Record<string, string>;
  /** @extraAttributes */
  @Attr({ persist: false }) trustIdReportUrl: string;
  @Attr({ persist: false }) logo: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTime: string;
  @Attr({ persist: false }) public readonly humanizedProvider: string;

  @BelongsTo() public application: Application;
}

@Model()
export class BulkDownload extends ApplicationRecord {
  public static jsonapiType = 'bulk_downloads';

  @Attr() public applicationIds: string[];
  @Attr() public jobId: string;
  @Attr() public exportType: 'pdf' | 'csv';
  @Attr() public sections: { [key: string]: boolean };
  @Attr() public filenameInterpolationVariable: string;
}

@Model()
export class BulkUpload extends ApplicationRecord {
  public static jsonapiType = 'bulk_uploads';

  public static validationSchema = Yup.object().shape<Shape<BulkUpload>>({
    name: Yup.string().required("Can't be blank."),
    recordType: Yup.string().required("Can't be blank."),
    csvDocument: Yup.mixed().required("Can't be blank."),
  });

  @Attr() public csvDocument: string;
  @Attr() public name: string;
  @Attr() public status: 'uploaded' | 'validated' | 'validating' | 'processing' | 'completed' | 'failed';
  @Attr() public recordType: string;
  @Attr({ persist: false }) public readonly csvDocumentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTime: string;
  @Attr({ persist: false }) public readonly errorMessages: string;
  @Attr({ persist: false }) public readonly userFullName: string;
  @Attr({ persist: false }) public readonly warningMessages: string;
}

@Model()
export class BulkOffer extends ApplicationRecord {
  public static jsonapiType = 'bulk_offers';

  public static signatureTypeOptions = [
    { value: 'wet', label: 'Wet Signature' },
    { value: 'electronic', label: 'E-Signature' },
    { value: 'not_required', label: 'No Signature' },
  ];

  /**
   * Not happy about duplicating this but it can be refactored once we've
   * removed the feature flag for creating offers
   */
  public static salaryValidations = {
    salaryCurrency: Yup.string().when(['salaryAmount'], {
      is: (salaryAmount: unknown) => !!salaryAmount,
      then: Yup.string().required("Can't be blank").nullable(),
      otherwise: Yup.string().nullable(),
    }),
    salaryAmount: Yup.string().when(['salaryCurrency'], {
      is: (salaryCurrency: unknown) => !!salaryCurrency,
      then: Yup.string()
        .required("Can't be blank")
        .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
      otherwise: Yup.string().nullable(),
    }),
  };

  public static validationSchema = Yup.object().shape<Shape<BulkOffer>>(
    {
      ...BulkOffer.salaryValidations,
      expiresAt: Yup.string().required("Can't be blank."),
      jobTitle: Yup.string().required("Can't be blank."),
      offerTemplateId: Yup.string().when(['skipOfferTemplate'], {
        is: (skipOfferTemplate) => !skipOfferTemplate,
        then: Yup.string().required("Can't be blank."),
        otherwise: Yup.string().nullable(),
      }),
      signatureType: Yup.string().required('Must be selected'),
    },
    [
      ['salaryAmount', 'salaryCurrency'],
      ['salaryCurrency', 'salaryCurrency'],
    ],
  );

  public static salaryValidationSchema = Yup.object().shape<Shape<BulkOffer>>({
    expiresAt: Yup.string().required("Can't be blank."),
    jobTitle: Yup.string().required("Can't be blank."),
    offerTemplateId: Yup.string().when(['skipOfferTemplate'], {
      is: (skipOfferTemplate) => !skipOfferTemplate,
      then: Yup.string().required("Can't be blank."),
      otherwise: Yup.string().nullable(),
    }),
    signatureType: Yup.string().required('Must be selected'),
    salaryCurrency: Yup.string().required("Can't be blank").nullable(),
    salaryAmount: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' })
      .nullable(),
    salaryDuration: Yup.string().required("Can't be blank").nullable(),
  });

  public static strictValidationSchema = Yup.object().shape<Shape<BulkOffer>>({
    expiresAt: Yup.string().required("Can't be blank."),
    jobTitle: Yup.string().required("Can't be blank."),
    offerTemplateId: Yup.string().when(['skipOfferTemplate'], {
      is: (skipOfferTemplate) => !skipOfferTemplate,
      then: Yup.string().required("Can't be blank."),
      otherwise: Yup.string().nullable(),
    }),
    signatureType: Yup.string().required('Must be selected'),
    startOn: Yup.string().required("Can't be blank.").nullable(),
    salaryCurrency: Yup.string().required("Can't be blank").nullable(),
    salaryAmount: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' })
      .nullable(),
    salaryDuration: Yup.string().required("Can't be blank").nullable(),
    managerId: Yup.string().required("Can't be blank.").nullable(),
    paidDaysOff: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
    workAddress: Yup.string().required('Must be selected'),
  });

  public static NEW(attrs?: Partial<BulkOffer>) {
    const defaultAttrs: Partial<BulkOffer> = {
      additionalLetterText: '',
      bcc: [],
      cc: [],
      emailBody: '',
      emailSalutation: '',
      emailSignature: '',
      emailSubject: '',
      expiresAt: addDays(new Date(), 7).toISOString(),
      jobTitle: '',
      letterBody: '',
      letterSignature: '',
      letterSubject: '',
      managerId: null,
      offers: [],
      offerDocuments: [],
      offerTemplateId: '',
      paidDaysOff: '',
      positionOpeningId: null,
      salaryAmount: null,
      salaryCurrency: null,
      salaryDuration: null,
      signatureType: 'wet',
      startOn: null,
      workAddress: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr({ persist: false }) public stageId: string;
  @Attr({ persist: false }) public managerId: string;
  @Attr({ persist: false }) public offerTemplateId: string;

  @Attr({ persist: false }) public overwriteTemplateDocx: string;
  @Attr({ persist: false }) public cc: string[];
  @Attr({ persist: false }) public bcc: string[];
  @Attr({ persist: false }) public offerDocuments: string[];
  @Attr({ persist: false }) public jobTitle: string;
  @Attr({ persist: false }) public expiresAt: string;
  @Attr({ persist: false }) public startOn: string;
  @Attr({ persist: false }) public paidDaysOff: string;
  @Attr({ persist: false }) public emailSubject: string;
  @Attr({ persist: false }) public emailSalutation: string;
  @Attr({ persist: false }) public emailBody: string;
  @Attr({ persist: false }) public emailSignature: string;
  @Attr({ persist: false }) public letterSubject: string;
  @Attr({ persist: false }) public letterBody: string;
  @Attr({ persist: false }) public letterSignature: string;
  @Attr({ persist: false }) public positionOpeningId: string;
  @Attr({ persist: false }) public salaryAmount: string;
  @Attr({ persist: false }) public salaryCurrency: string;
  @Attr({ persist: false }) public salaryDuration: string;
  @Attr({ persist: false }) public signatureType: string;
  @Attr({ persist: false }) public skipOfferTemplate: boolean;
  @Attr({ persist: false }) public additionalLetterText: string;
  @Attr({ persist: false }) public workAddress: string;

  @HasMany() public offers: Offer[];
  @HasMany() public offerDocumentObjects: OfferDocument[];
}

@Model()
export class CalendarFeedSubscription extends ApplicationRecord {
  public static jsonapiType = 'calendar_feed_subscriptions';

  @Attr() public regenerateUrl: boolean;
  @Attr({ persist: false }) public readonly feedUrl: string;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly token: string;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr() public updatedAt: string;
}

@Model()
export class ConcealedCandidate extends ApplicationRecord {
  public static jsonapiType = 'concealed_candidates';

  @Attr() public avatarSvg: string;

  @Attr() public names: string[];
  @Attr() public emails: string[];
  @Attr() public phones: string[];

  @HasMany() public applications: Application[];
}

@Model()
export class ConditionalCustomField extends ApplicationRecord {
  public static jsonapiType = 'conditional_custom_fields';

  @Attr({ persist: false }) public companyId: string;
  @Attr({ persist: false }) public parentCustomFieldId: string;
  @Attr({ persist: false }) public childCustomFieldId: string;
  @Attr() public position: number;
  @Attr() public conditions: {
    operator: string;
    values: { id?: string; label: string; value: string; uniqueId?: string }[];
  }[];
  @Attr() public conditionalName: string;
  @Attr() public conditionalDescription: string;
  @Attr() public conditionalFieldType: string;
  @Attr() public conditionalResourceType: string;
  @Attr() public conditionalSensitive: boolean;
  @Attr() public conditionalRequired: boolean;
  @Attr() public conditionalSampleValue: string;
  @Attr() public conditionalIsChild: boolean;
  @Attr() public conditionalOptionGroups: CustomFieldOptionGroup[];

  @Attr({ persist: false }) public uniqueId: number;
  @Attr({ persist: false }) public parentCustomFieldUniqueId: number;

  @BelongsTo() public parentCustomField: CustomField;
  @BelongsTo() public childCustomField: CustomField;
}

@Model()
export class ConditionalQuestion extends ApplicationRecord {
  public static jsonapiType = 'conditional_questions';

  @Attr({ persist: false }) public companyId: string;
  @Attr({ persist: false }) public parentQuestionId: string;
  @Attr({ persist: false }) public childQuestionId: string;
  @Attr() public position: number;
  @Attr() public conditions: { operator: string; value: string }[];
  @Attr() public conditionalTitle: string;
  @Attr({ persist: false }) public readonly conditionalTitleEn: string;
  @Attr() public conditionalAdditionalInformation: string;
  @Attr() public conditionalQuestionType: string;
  @Attr() public conditionalSensitive: boolean;
  @Attr() public conditionalRequired: boolean;
  @Attr() public conditionalNumRequiredAnswerOptions: string | number;
  @Attr() public conditionalMaxNumRequiredAnswerOptions: string | number;
  @Attr() public conditionalOptions: { id: string; text: string; _destroy?: string }[];
  @Attr() public conditionalAnswerRegex: string;

  @Attr({ persist: false }) public uniqueId: number;
  @Attr({ persist: false }) public parentQuestionUniqueId: number;

  @BelongsTo() public parentQuestion: Question;
  @BelongsTo() public childQuestion: Question;
}

@Model()
export class Candidate extends ApplicationRecord {
  public static jsonapiType = 'candidates';

  public static validationSchema = Yup.object().shape({
    firstName: Yup.string().nullable().required("Can't be blank."),
    lastName: Yup.string().nullable().required("Can't be blank."),
    email: Yup.string().nullable().required("Can't be blank."),
  });

  @Attr() public addTagWithContext: TagWithContext;
  @Attr() public addTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public readonly avatarSvg: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortDate: string;
  @Attr() public dateOfBirth: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public email: string;
  @Attr() public firstName: string;
  @Attr() public preferredName: string;
  @Attr() public middleName: string;
  @Attr() public lastName: string;
  @Attr({ persist: false }) public readonly fullName: string;
  @Attr({ persist: false }) public readonly internal: boolean;
  @Attr() public locale: TLocaleKey;
  @Attr() public phone: string;
  @Attr() public phoneIso2: string;
  @Attr() public removeTagWithContext: TagWithContext;
  @Attr() public blocked: boolean;
  @Attr({ persist: false }) public readonly linkedInPersonUrn: string;
  @Attr() public tagsToSave: TagWithContext[];
  @Attr({ persist: false }) public communicationPreferences: {
    application_email: boolean;
    application_sms: boolean;
    general_email: boolean;
    general_sms: boolean;
  };
  @Attr() public address1: string;
  @Attr() public address2: string;
  @Attr() public town: string;
  @Attr() public country: string;
  @Attr() public postcode: string;
  @Attr({ persist: false }) public readonly humanizedCountryName: string;

  @Attr() public mergingCandidateId: string;
  @Attr() public updateCandidatePreferredName: boolean;

  @Attr({ persist: false }) public readonly averageRating: number;
  @Attr({ persist: false }) public readonly applicationCount: number;
  @Attr({ persist: false }) public readonly currentUserRating: number;
  @Attr({ persist: false }) public readonly tags: ITag[];

  @HasMany() public applications: Application[];
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public jobSeekers: JobSeeker[];
  @HasMany() public skillablesSkills: SkillablesSkill[];
  @HasMany() public comments: Comment[];
  @HasMany() public hrisExports: HrisExport[];
}

@Model()
export class OnboardingCandidateStage extends ApplicationRecord {
  public static jsonapiType = 'onboarding_candidate_stages';
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly onboardingApplicationId: string;
  @Attr({ persist: false }) public readonly candidateFullName: string;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly startedAt: string;
  @Attr({ persist: false }) public readonly priorityStatus: string;
  @Attr({ persist: false }) public readonly stageOwnerName: string;
  @Attr({ persist: false }) public readonly target: string;
}

@Model()
export class OnboardingCandidate extends Candidate {
  public static jsonapiType = 'onboarding_candidates';
}

@Model()
export class CandidateAssessment extends ApplicationRecord {
  public static jsonapiType = 'candidate_assessments';
  public instanceClasses:
    | CandidateAssessmentAlvaLabs
    | CandidateAssessmentBryq
    | CandidateAssessmentCanditech
    | CandidateAssessmentCappfinity
    | CandidateAssessmentCodility
    | CandidateAssessmentCriteria
    | CandidateAssessmentHackerRank
    | CandidateAssessmentOutmatch
    | CandidateAssessmentPredictiveIndex
    | CandidateAssessmentTestGorilla
    | CandidateAssessmentShl;

  @Attr({ persist: false }) public readonly applicationId: string;
  @Attr({ persist: false }) public readonly assessmentName: string;
  @Attr({ persist: false }) public readonly score: string;
  @Attr({ persist: false }) public readonly type: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTime: string;

  /** @extraAttributes */
  @Attr({ persist: false }) logo: string;
  @Attr({ persist: false }) public readonly humanizedName: string;

  @BelongsTo() public application: Application;

  public isAlvaLabs(): this is CandidateAssessmentAlvaLabs {
    return this.isType('candidate_assessments_alva_labs');
  }

  public isBryq(): this is CandidateAssessmentBryq {
    return this.isType('candidate_assessments_bryq');
  }

  public isCanditech(): this is CandidateAssessmentCanditech {
    return this.isType('candidate_assessments_canditech');
  }

  public isCappfinity(): this is CandidateAssessmentCappfinity {
    return this.isType('candidate_assessments_cappfinity');
  }

  public isCodility(): this is CandidateAssessmentCodility {
    return this.isType('candidate_assessments_codility');
  }

  public isCriteria(): this is CandidateAssessmentCriteria {
    return this.isType('candidate_assessments_criteria');
  }

  public isHackerRank(): this is CandidateAssessmentHackerRank {
    return this.isType('candidate_assessments_hacker_rank');
  }

  public isOutmatch(): this is CandidateAssessmentOutmatch {
    return this.isType('candidate_assessments_outmatch');
  }

  public isPredictiveIndex(): this is CandidateAssessmentPredictiveIndex {
    return this.isType('candidate_assessments_predictive_index');
  }

  public isTestGorilla(): this is CandidateAssessmentTestGorilla {
    return this.isType('candidate_assessments_test_gorilla');
  }

  public isShl(): this is CandidateAssessmentShl {
    return this.isType('candidate_assessments_shl');
  }
}

@Model()
export class CandidateAssessmentAlvaLabs extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_alva_labs';

  @Attr({ persist: false }) public readonly reportUrl: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly roleFitScore: string;
  @Attr({ persist: false }) public readonly roleFitLabel: string;
}

export type bryqDetailKeys =
  | 'Teamwork'
  | 'Work style'
  | 'Personality'
  | 'Social skills'
  | 'Verbal reasoning'
  | 'Logical reasoning'
  | 'Attention to detail'
  | 'Numerical reasoning'
  | 'Personality detail'
  | 'Cognitive match score'
  | 'Personality match score'
  | 'Verbal reasoning weighted'
  | 'Logical reasoning weighted'
  | 'Attention to detail weighted'
  | 'Numerical reasoning weighted'
  | 'duration';

export type bryqResults = {
  score: number;
  details: Record<bryqDetailKeys, number>;
  duration: string;
};

@Model()
export class CandidateAssessmentBryq extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_bryq';

  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly resultDetails: {
    url: string;
    status: string;
    results: bryqResults;
    duration: string;
    embed_url: string;
  };
}

@Model()
export class CandidateAssessmentCanditech extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_canditech';
  @Attr({ persist: false }) public readonly score: string;
  @Attr({ persist: false }) public readonly resultUrl: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly verdict: string;
  @Attr({ persist: false }) public readonly comments: string;
  @Attr({ persist: false }) public readonly resultDetails: {}[];
}

export type cappfinityDetailKeys = 'Name' | 'Score' | 'ScoreType' | 'SubScores';

@Model()
export class CandidateAssessmentCappfinity extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_cappfinity';

  @Attr({ persist: false }) public readonly overallScoreType: string;
  @Attr({ persist: false }) public readonly notificationType: string;
  @Attr({ persist: false }) public readonly startedDate: string;
  @Attr({ persist: false }) public readonly assessmentReportUrl: string;
  @Attr({ persist: false }) public readonly assessmentScoresBreakdown: Record<cappfinityDetailKeys, string | number>[];
  @Attr({ persist: false }) public readonly resultDetails: {}[];
}

@Model()
export class CandidateAssessmentCodility extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_codility';

  @Attr({ persist: false }) public readonly startDate: string;
  @Attr({ persist: false }) public readonly closeDate: string;
  @Attr({ persist: false }) public readonly assessmentReportUrl: string;
  @Attr({ persist: false }) public readonly result: string;
  @Attr({ persist: false }) public readonly maxResult: string;
  @Attr({ persist: false }) public readonly resultDetails: {}[];
}

@Model()
export class CandidateAssessmentCriteria extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_criteria';

  @Attr({ persist: false }) public readonly reportUrl: string;
  @Attr({ persist: false }) public readonly metAllScoreRanges: string;
  @Attr({ persist: false }) public readonly inviteUrl: string;
  @Attr({ persist: false }) public readonly resultDetails: {}[];
}

@Model()
export class CandidateAssessmentPredictiveIndex extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_predictive_index';

  @Attr({ persist: false }) public readonly assessmentType: 'behavioral_assessment' | 'cognitive_assessment';
  @Attr({ persist: false }) public readonly reportUrl: string;
  @Attr({ persist: false }) public readonly status: string;
}

@Model()
export class CandidateAssessmentShl extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_shl';

  @Attr({ persist: false }) public readonly reportUrls: { name: string; url: string }[];
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly score: string;
}

@Model()
export class CandidateAssessmentOutmatch extends CandidateAssessment {
  public static validationSchema = Yup.object().shape({
    jobCode: Yup.string().nullable().required("Can't be blank."),
  });

  public static jsonapiType = 'candidate_assessments_outmatch';

  @Attr({ persist: false }) public readonly assessmentResults: Record<string, string>;
  @Attr({ persist: false }) public readonly assessmentResultDatetime: string;
  @Attr({ persist: false }) public readonly assessmentResultDatetimeShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly assessmentStartDatetime: string;
  @Attr({ persist: false }) public readonly assessmentStartDatetimeShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly developmentReportUrl: string;
  @Attr({ persist: false }) public readonly selectionReportUrl: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly jobCode: string;
}

export type CandidateAssessmentTestGorillaStatus =
  | 'archived'
  | 'invited_not_trackable'
  | 'completed'
  | 'knocked-out'
  | 'pending';

@Model()
export class CandidateAssessmentTestGorilla extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_test_gorilla';

  @Attr({ persist: false }) public readonly reportUrl?: string;
  @Attr({ persist: false }) public readonly status: CandidateAssessmentTestGorillaStatus;
}

@Model()
export class CandidateExternalForm extends ApplicationRecord {
  public static jsonapiType = 'candidate_external_forms';
  @Attr({ persist: false }) public readonly applicationId: string;
  @Attr({ persist: false }) public readonly jobSeekerId: string;
  @Attr({ persist: false }) public readonly talentPipelineId: string;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly title: string;
  @Attr({ persist: false }) public readonly source: string;
  @Attr({ persist: false }) public readonly response: Record<string, string>;
  @Attr({ persist: false }) public readonly submissionDetails: Record<
    string,
    Record<string, Record<string, Record<string, string>>>
  >;
  @Attr({ persist: false }) public readonly createdAt: string;
}

@Model()
export class CandidateAssessmentHackerRank extends CandidateAssessment {
  public static jsonapiType = 'candidate_assessments_hacker_rank';

  @Attr({ persist: false }) public readonly status: 'invited' | 'completed';
  @Attr({ persist: false }) public readonly maxScore: number;
  @Attr({ persist: false }) public readonly reportUrl: string;
  @Attr({ persist: false }) public readonly pdfUrl: string;
}

@Model()
export class CareersPage extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_pages';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank."),
    slug: Yup.string().nullable().required("Can't be blank."),
  });

  public static NEW(attrs = {}) {
    const newThis = new this();
    const defaultAttrs: Partial<typeof newThis> = {
      homePageForTheme: false,
      name: '',
      slug: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public careersSiteId: string;
  @Attr() public duplicate: boolean;
  @Attr() public homePageForTheme: boolean;
  @Attr() public name: string;
  @Attr() public slug: string;
  @Attr({ persist: false }) public readonly externalLink: string;
  @Attr({ persist: false }) public readonly externalUrl: string;
  @Attr({ persist: false }) public themeId: string;
  @Attr({ persist: false }) public uniqueId: string;

  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly canUpdateSlugAndTitle: boolean;

  @BelongsTo() public careersSite: CareersSite;
  @HasMany() public careersBlocks: CareersBlock[];
  @HasOne() public themesThemeable: ThemesThemeable;
}

@Model()
export class CareersSite extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_sites';

  public static NEW(attrs = {}) {
    const newThis = new this();
    const defaultAttrs: Partial<typeof newThis> = {};
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public active: boolean;
  @Attr() public companyId: number;
  @Attr() public enabled: boolean;
  @Attr() public disabledRedirect: string;
  @Attr() public facebookDomainVerificationContent: string;
  @Attr() public openGraphImage: string;
  @Attr() public customTypography: boolean;
  @Attr() public customConversionPixel: string;
  @Attr() public current: boolean;
  @Attr() public typographyImportSource: string;
  @Attr() public typographyName: string;
  @Attr() public customHomeButtonUrl: string;
  @Attr() public customCssEnabled: boolean;
  @Attr() public customCss: string;
  @Attr() public customInterestText: string;
  @Attr() public customNextStepsText: string;
  @Attr() public robotsMeta: string;
  @Attr() public maxApplicationsWarningText: string;
  @Attr() public linkedinConversionPixel: string;
  @Attr() public indeedConversionTrackingId: string;
  @Attr() public talentConversionTrackingId: string;
  @Attr() public zipRecruiterConversionTrackingId: string;
  @Attr() public duplicate: boolean;
  @Attr() public createDraftSite: boolean;
  /** @extraAttributes */
  @Attr({ persist: false }) isProcessingDraftDuplicate: boolean;
  @HasMany() public careersPages: CareersPage[];
  @HasMany() public careersNavigationMenuItems: CareersNavigationMenuItem[];
}

@Model()
export class CareersCallToActionBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_call_to_action_blocks';

  static blockTitle = 'Call To Action';
  static thumbtackIcon = IconNames16.CURSOR;
  public static titleAligns = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  public static textAligns = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  public static layouts = [
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' },
  ];

  public static buttonPositions = [
    { value: 'under_title', label: 'Under Title' },
    { value: 'under_text', label: 'Under Text' },
  ];

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      title: '',
      text: '',
      buttonText: '',
      buttonLink: '',
      layout: 'horizontal',
      titleAlign: 'left',
      textAlign: 'left',
      buttonPosition: 'under_text',
      backgroundColor: '#EBF0F3',
      titleColor: '',
      textColor: '',
      borderRadius: 16,
      lineSeparator: true,
      separatorColor: '#BBB9B3',
      separatorWidth: 1,
      separatorSpacing: 70,
    };
    return new this(attrs);
  }

  public staticType = 'CareersCallToActionBlock' as const;
  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public title: string;
  @Attr() public text: string;
  @Attr() public buttonText: string;
  @Attr() public buttonLink: string;
  @Attr() public backgroundColor: string;
  @Attr() public titleColor: string;
  @Attr() public textColor: string;
  @Attr() public borderRadius: number;
  @Attr() public layout: 'horizontal' | 'vertical';
  @Attr() public titleAlign: 'left' | 'center' | 'right';
  @Attr() public textAlign: 'left' | 'center' | 'right';
  @Attr() public buttonPosition: 'under_text' | 'under_title';
  @Attr() public lineSeparator: boolean;
  @Attr() public separatorColor: string;
  @Attr() public separatorWidth: number;
  @Attr() public separatorSpacing: number;
}

@Model()
export class CareersCardBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_card_blocks';

  public static imagePositions = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
  ];

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      icon: '',
      image: '',
      imageAltText: '',
      imagePosition: 'left',
      linkAriaLabel: '',
      linkPath: '',
      linkText: '',
      mediaType: '',
      provider: '',
      text: '',
      title: '',
      videoId: '',
    };
    return new this(attrs);
  }

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public icon: string;
  @Attr() public image: string;
  @Attr() public imageAltText: string;
  @Attr() public imagePosition: 'left' | 'right' | 'top' | 'bottom';
  @Attr() public imageWidth: number;
  @Attr() public linkAriaLabel: string;
  @Attr() public linkPath: string;
  @Attr() public linkText: string;
  @Attr() public mediaType: string;
  @Attr() public position: number;
  @Attr() public provider: string;
  @Attr() public text: string;
  @Attr() public title: string;
  @Attr() public videoId: string;
  @Attr() public careersCardsBlockId: string;
}

@Model()
export class CareersCardsBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_cards_blocks';
  public static alignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  public static variantOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'large', label: 'Large' },
  ];

  public static backgroundColorOptions = [
    { value: 'white', label: 'White' },
    { value: 'secondary', label: 'Secondary' },
  ];
  public static columnCountOptions = [
    { value: '1', label: 'One' },
    { value: '2', label: 'Two' },
    { value: '3', label: 'Three' },
    { value: '4', label: 'Four' },
  ];

  static blockTitle = 'Cards';
  static thumbtackIcon = IconNames16.CARDS;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      align: '',
      backgroundColor: '',
      blockName: '',
      careersCardBlocks: [],
      carousel: false,
      carouselNavigationText: '',
      columnCount: '',
      htmlId: '',
      variant: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersCardsBlock' as const;
  public onboardingModel = false;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public align: string;
  @Attr() public backgroundColor: string;
  @Attr() public blockName: string;
  @Attr() public columnCount: string;
  @Attr() public htmlId: string;
  @Attr() public variant: string;
  @Attr() public carousel: boolean;
  @Attr() public carouselNavigationText: string;

  @HasMany() public careersCardBlocks: CareersCardBlock[];
}

@Model()
export class CareersCarouselBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_carousel_blocks';

  public static validationSchema = Yup.object().shape({
    autoplaySpeedMs: Yup.number().typeError('Must be a number.').required("Can't be blank."),
    scrollSpeedMs: Yup.number().typeError('Must be a number.').required("Can't be blank."),
    showAmount: Yup.number().typeError('Must be a number.').required("Can't be blank."),
  });

  static blockTitle = 'Carousel';
  static thumbtackIcon = IconNames16.CAROUSEL;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      autoplay: true,
      autoplaySpeedMs: 1000,
      blockName: '',
      htmlId: '',
      infinite: true,
      scrollAmount: 1,
      scrollSpeedMs: 500,
      showAmount: 1,
      showDots: true,
    };
    return new this(attrs);
  }

  public staticType = 'CareersCarouselBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public autoplay: boolean;
  @Attr() public autoplaySpeedMs: number;
  @Attr() public blockName: string;
  @Attr() public showDots: boolean;
  @Attr() public htmlId: string;
  @Attr() public infinite: boolean;
  @Attr() public scrollAmount: number;
  @Attr() public showAmount: number;
  @Attr() public scrollSpeedMs: number;
  @HasMany() public careersBlocks: CareersBlock[];
}

@Model()
export class CareersGlassdoorBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_glassdoor_blocks';

  static blockTitle = 'Glassdoor';
  static thumbtackIcon = IconNames16.BRAND_GLASSDOOR;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      quotation: '',
      url: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersGlassdoorBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public quotation: string;
  @Attr() public url: string;
}

@Model()
export class CareersLocationAndMapsBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_location_and_maps_blocks';

  public static variantOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'large', label: 'Large' },
  ];

  public static backgroundColorOptions = [
    { value: 'white', label: 'White' },
    { value: 'secondary', label: 'Secondary' },
  ];

  public static columnCountOptions = [
    { value: '1', label: 'One' },
    { value: '2', label: 'Two' },
    { value: '3', label: 'Three' },
    { value: '4', label: 'Four' },
  ];

  static blockTitle = 'Location and Maps';
  static thumbtackIcon: IconNames16 = IconNames16.LOCATION;

  public static NEW(): CareersLocationAndMapsBlock {
    const newThis: CareersLocationAndMapsBlock = new this();
    const attrs: Partial<typeof newThis> = {
      htmlId: '',
      blockName: '',
      backgroundColor: '',
      columnCount: '',
      variant: '',
      careersLocationAndMapBlocks: [],
    };
    return new this(attrs);
  }

  public staticType: 'CareersLocationAndMapsBlock' = 'CareersLocationAndMapsBlock' as const;
  public onboardingModel = false;

  @Attr({ persist: false }) public active: boolean;
  @Attr({ persist: false }) public readonly countryCodeOptions: Array<{ label: string; value: string }> = [];

  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public backgroundColor: string;
  @Attr() public columnCount: string;
  @Attr() public variant: string;

  @HasMany() public careersLocationAndMapBlocks: CareersLocationAndMapBlock[];
}

@Model()
export class CareersLocationAndMapBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_location_and_map_blocks';

  public static mediaPositions = [
    { value: 'top', label: 'Top' },
    { value: 'left', label: 'Left' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'right', label: 'Right' },
  ];

  public static mediaOptions: ISelectOption[] = [
    { value: 'plain', label: 'None' },
    { value: 'image', label: 'Image' },
    { value: 'interactive_map', label: 'Interactive Map' },
  ];

  public static providerOptions: ISelectOption[] = [
    { label: 'Google', value: 'google' },
    { label: 'Apple', value: 'apple' },
    { label: 'OpenStreetMap', value: 'osm' },
  ];

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      careersLocationAndMapsBlockId: '',
      blockName: '',
      htmlId: '',
      borderRadiusPx: 20,
      city: '',
      countryCode: '',
      image: '',
      imageAltText: '',
      latitude: '',
      longitude: '',
      mediaHeightPx: 300,
      mediaPosition: 'bottom',
      mediaType: 'plain',
      position: 0,
      postalCode: '',
      provider: 'google',
      province: '',
      streetAddress: '',
      title: '',
      zoomLevel: 12,
    };

    return new this(attrs);
  }

  @Attr({ persist: false }) public active: boolean;
  @Attr() public careersLocationAndMapsBlockId: string;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;

  @Attr() public borderRadiusPx: number;
  @Attr() public city: string;
  @Attr() public countryCode: string;
  @Attr() public image: string;
  @Attr() public imageAltText: string;
  @Attr() public latitude: string;
  @Attr() public longitude: string;
  @Attr() public mediaHeightPx: number;
  @Attr() public mediaPosition: 'top' | 'left' | 'bottom' | 'right';
  @Attr() public mediaType: string;
  @Attr() public position: number;
  @Attr() public postalCode: string;
  @Attr() public provider: 'google' | 'apple' | 'osm';
  @Attr() public province: string;
  @Attr() public streetAddress: string;
  @Attr() public title: string;
  @Attr() public zoomLevel: number;
}

@Model()
export class CareersNavigationBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_navigation_blocks';

  static blockTitle = 'Navigation';
  static thumbtackIcon = IconNames16.LINE_STACK;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      design: 'hero',
      htmlId: '',
      backgroundColor: '',
      color: '',
      logo: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersNavigationBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public design: 'hero' | 'justified' | 'activation';
  @Attr() public htmlId: string;
  @Attr() public backgroundColor: string;
  @Attr() public color: string;
  @Attr() public logo: string;
}

@Model()
export class CareersNavigationMenuItem extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_navigation_menu_items';

  public static validationSchema = Yup.object().shape({
    label: Yup.string().nullable().required("Can't be blank."),
  });

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      label: '',
      url: '',
    };
    return new this(attrs);
  }

  @Attr() public careersSiteId: string;
  @Attr({ persist: false }) public externalUrl: string;
  @Attr() public label: string;
  @Attr() public url: string;

  @Attr() public position: number;
  @Attr() public active: boolean;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public readonly deletable: boolean;
  @Attr() public skipUrlValidation: boolean;
  @Attr() public hasSubNavigationMenu: boolean;
  @Attr() public subNavigationItems: (string | { value: string; label: string })[];
}

@Model()
export class CareersHeroBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_hero_blocks';

  static blockTitle = 'Hero';
  static thumbtackIcon = IconNames16.HERO;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      align: '',
      backgroundColor: '',
      blockName: '',
      buttonLink: '',
      buttonText: '',
      departmentsColumnVisible: true,
      displayButton: true,
      displayJobFilter: false,
      divisionsColumnVisible: true,
      fontColor: '',
      htmlId: '',
      image: '',
      imageBrightness: 'normal',
      logoType: '',
      mediaType: '',
      locationsColumnVisible: true,
      provider: '',
      searchColumnVisible: false,
      structureCustomGroupOnesColumnVisible: true,
      text: '',
      title: '',
      videoBrightness: '',
      videoId: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersHeroBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public align: string;
  @Attr() public backgroundColor: string;
  @Attr() public blockName: string;
  @Attr() public buttonLink: string;
  @Attr() public buttonText: string;
  @Attr() public departmentsColumnVisible: boolean;
  @Attr() public displayButton: boolean;
  @Attr() public displayJobFilter: boolean;
  @Attr() public divisionsColumnVisible: boolean;
  @Attr() public structureCustomGroupOnesColumnVisible: boolean;
  @Attr() public searchColumnVisible: boolean;
  @Attr() public headingTag: string;
  @Attr() public htmlId: string;
  @Attr() public title: string;
  @Attr() public text: string;
  @Attr() public fontColor: string;
  @Attr() public locationsColumnVisible: boolean;
  @Attr() public logoType: string;
  @Attr() public image: string;
  @Attr() public mediaType: string;
  @Attr() public provider: string;
  @Attr() public videoId: string;
  @Attr() public videoBrightness: string;
  @Attr() public imageBrightness: string;
}

@Model()
export class CareersHtmlBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_html_blocks';

  static blockTitle = 'HTML';
  static thumbtackIcon = IconNames16.CODE;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      content: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersHtmlBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public content: string;
}

@Model()
export class CareersImageGalleryBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_image_gallery_blocks';

  static blockTitle = 'Image Gallery';
  static thumbtackIcon = IconNames16.IMAGE;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      images: [],
    };
    return new this(attrs);
  }

  public staticType = 'CareersImageGalleryBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public images: string[];
  @Attr({ persist: false }) public readonly imagesFiles: ICloudinaryFile[];
}

@Model()
export class CareersJobsBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_jobs_blocks';

  static blockTitle = 'Jobs';
  static thumbtackIcon = IconNames16.JOB;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      departmentIds: [],
      departmentsColumnVisible: true,
      divisionIds: [],
      divisionsColumnVisible: true,
      employmentTypeColumnVisible: true,
      htmlId: '',
      locationIds: [],
      locationsColumnVisible: true,
      onlyExistingJobsFilters: false,
      requisitionIdColumnVisible: false,
      structureCustomGroupOneIds: [],
      structureCustomGroupOnesColumnVisible: true,
      title: '',
      workplaceTypeColumnVisible: false,
      pagination: false,
      internalJobs: false,
    };
    return new this(attrs);
  }

  public staticType = 'CareersJobsBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public title: string;
  @Attr() public locationIds: string[];
  @Attr() public departmentIds: string[];
  @Attr() public divisionIds: string[];
  @Attr() public structureCustomGroupOneIds: string[];
  @Attr() public addressColumnVisible: boolean;
  @Attr() public employmentTypeColumnVisible: boolean;
  @Attr() public locationsColumnVisible: boolean;
  @Attr() public departmentsColumnVisible: boolean;
  @Attr() public divisionsColumnVisible: boolean;
  @Attr() public structureCustomGroupOnesColumnVisible: boolean;
  @Attr() public requisitionIdColumnVisible: boolean;
  @Attr() public workplaceTypeColumnVisible: boolean;
  @Attr() public onlyExistingJobsFilters: boolean;
  @Attr() public pagination: boolean;
  @Attr() public pageSize: number;
  @Attr() public internalJobs: boolean;
}

@Model()
export class CareersJobsFilterBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_jobs_filter_blocks';

  static blockTitle = 'Jobs Filter';
  static thumbtackIcon = IconNames16.JOB;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      title: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersJobsFilterBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public title: string;
}

@Model()
export class CareersBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_blocks';

  @Attr() public parentId: number | string;
  @Attr() public parentType: 'CareersPage' | 'CareersCarouselBlock';
  @Attr() public blockableId: number;
  @Attr() public blockableType:
    | 'CareersCallToActionBlock'
    | 'CareersCardsBlock'
    | 'CareersCarouselBlock'
    | 'CareersGlassdoorBlock'
    | 'CareersHeroBlock'
    | 'CareersHtmlBlock'
    | 'CareersImageGalleryBlock'
    | 'CareersJobsBlock'
    | 'CareersJobsFilterBlock'
    | 'CareersLocationAndMapsBlock'
    | 'CareersNavigationBlock'
    | 'CareersTestimonialBlock'
    | 'CareersTextBlock'
    | 'CareersOnboardingBlock'
    | 'CareersVideoBlock';
  @Attr() public position: number;
  @Attr() public active: boolean;
  @Attr() public canEdit: boolean;
  @Attr() public canDuplicate: boolean;
  @Attr() public canDestroy: boolean;
  @Attr() public nestable: boolean;
  @Attr({ persist: false }) public uniqueId: string;
  // Column options
  @Attr({ persist: false }) public readonly colSmall: string;
  @Attr({ persist: false }) public readonly colMedium: string;
  @Attr({ persist: false }) public readonly colLarge: string;
  @Attr() public columnConfiguration: { sm: string; md: string; lg: string };

  @BelongsTo() public parent: CareersPage | CareersCarouselBlock;
  @BelongsTo() public blockable:
    | CareersCallToActionBlock
    | CareersCardsBlock
    | CareersCarouselBlock
    | CareersGlassdoorBlock
    | CareersHeroBlock
    | CareersHtmlBlock
    | CareersImageGalleryBlock
    | CareersJobsBlock
    | CareersJobsFilterBlock
    | CareersLocationAndMapsBlock
    | CareersNavigationBlock
    | CareersTestimonialBlock
    | CareersTextBlock
    | CareersOnboardingBlock
    | CareersVideoBlock;
}

@Model()
export class CareersTestimonialBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_testimonial_blocks';

  static blockTitle = 'Testimonial';
  static thumbtackIcon = IconNames16.QUOTE;

  public static quoteOptions = [
    { value: 'shown', label: 'Shown' },
    { value: 'hidden', label: 'Hidden' },
  ];

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      image: '',
      name: '',
      quotation: '',
      quotes: 'shown',
      title: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersTestimonialBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public quotes: string;
  @Attr() public quotation: string;
  @Attr() public name: string;
  @Attr() public title: string;
  @Attr() public image: string;
}

@Model()
export class CareersOnboardingBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_onboarding_blocks';
  static blockTitle = 'Onboarding';
  static thumbtackIcon = IconNames16.USER;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      buttonText: '',
      htmlId: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersOnboardingBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public buttonText: string;
}

@Model()
export class CareersTextBlock extends MobilityApplicationRecord {
  public static jsonapiType = 'careers_text_blocks';

  public static validationSchema = Yup.object().shape({
    align: Yup.string().required("Can't be blank."),
    backgroundColor: Yup.string().required("Can't be blank."),
    text: Yup.string().nullable(),
    title: Yup.string().nullable().max(255, 'Max length is 255 characters.'),
    variant: Yup.string().required("Can't be blank."),
  });

  public static alignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];
  public static variantOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'header', label: 'Header' },
  ];
  public static backgroundColorOptions = [
    { value: 'white', label: 'White' },
    { value: 'secondary', label: 'Secondary' },
  ];

  static blockTitle = 'Text';
  static thumbtackIcon = IconNames16.TEXT;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      align: '',
      backgroundColor: '',
      blockName: '',
      contentAlign: '',
      htmlId: '',
      text: '',
      title: '',
      variant: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersTextBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public align: string;
  @Attr() public backgroundColor: string;
  @Attr() public blockName: string;
  @Attr() public contentAlign: string;
  @Attr() public externalLinks: string;
  @Attr() public htmlId: string;
  @Attr() public text: string;
  @Attr() public title: string;
  @Attr() public variant: string;
}

@Model()
export class CareersVideoBlock extends ApplicationRecord {
  public static jsonapiType = 'careers_video_blocks';

  static blockTitle = 'Video';
  static thumbtackIcon = IconNames16.VIDEO;

  public static NEW() {
    const newThis = new this();
    const attrs: Partial<typeof newThis> = {
      blockName: '',
      htmlId: '',
      provider: '',
      videoId: '',
    };
    return new this(attrs);
  }

  public staticType = 'CareersVideoBlock' as const;

  @Attr({ persist: false }) public active: boolean;
  @Attr() public blockName: string;
  @Attr() public htmlId: string;
  @Attr() public videoId: string;
  @Attr() public provider: string;
}

@Model()
export class CloudinaryImage extends ApplicationRecord {
  public static jsonapiType = 'cloudinary_images';

  @Attr() public name: string;
  @Attr() public publicId: string;
}

@Model()
export class Comment extends ApplicationRecord {
  public static jsonapiType = 'comments';
  public static validationSchema = Yup.object().shape({
    body: Yup.string().required("Can't be blank."),
  });

  public static emptyBodyJson: Descendant[] = [
    {
      type: 'paragraph',
      children: [
        {
          text: '',
        },
      ],
    },
  ];

  public static NEW(attrs: Partial<Comment>) {
    const defaultAttrs: Partial<Comment> = {
      body: '',
      bodyJson: this.emptyBodyJson,
      privateComment: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public body: string;
  @Attr() public bodyJson: Descendant[];
  @Attr() public category: string;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr() public commentableId: string | number;
  @Attr() public commentableType: string;
  @Attr() public privateComment: boolean;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly updatedAt: string;
  @Attr({ persist: false }) public createdAtLongOrdinal: string;
  @Attr({ persist: false }) public jobTitle: string;

  @BelongsTo({ persist: false }) public readonly user: User;
  @BelongsTo({ persist: false }) public readonly commentable: Application | JobSeeker | Candidate | Task;

  @HasMany() public readonly mentionedUsers: User[];
}

export namespace Comment {
  export namespace Stats {
    export namespace by {
      export type user = { key: string; value: number; label: string }[];
    }
  }
}

@Model()
export class CompanyTeam extends ApplicationRecord {
  public static jsonapiType = 'company_teams';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank").min(3, 'Must be 3 characters minimum'),
  });

  @Attr() public companyId: string;
  @Attr() public name: string;
  @Attr() public creatorId: string;

  @Attr({ persist: false }) public numberOfMembers: number;

  @BelongsTo() public company: Company;
  @HasMany() public companyTeamsUsers: CompanyTeamsUser[];
}

@Model()
export class CompanyTeamsUser extends ApplicationRecord {
  public static jsonapiType = 'company_teams_users';

  @Attr() public companyTeamId: string;
  @Attr() public userId: string;
  @Attr() public usersNote: string;
  @Attr() public updatedAt: string;

  @BelongsTo() public user: User;
  @BelongsTo() public companyTeam: CompanyTeam;
}

@Model()
export class CompasEmployee extends ApplicationRecord {
  public static jsonapiType = 'compas_employees';

  public static validationSchema = Yup.object().shape({
    first_name: Yup.string().nullable().required("Can't be blank"),
    last_name: Yup.string().nullable().required("Can't be blank"),
    email: Yup.string().nullable().required("Can't be blank"),
    date_of_birth: Yup.string().nullable().required("Can't be blank"),
    nationality: Yup.string().nullable().required("Can't be blank"),
  });
}

@Model()
export class Country extends ApplicationRecord {
  public static jsonapiType = 'countries';

  @Attr() public alpha3: string;
  @Attr() public alpha2: string;
  @Attr() public name: string;
  @Attr() public code: string;
}

@Model()
export class CronofyAccessToken extends ApplicationRecord {
  public static jsonapiType = 'cronofy_access_tokens';
  public static readonly CalendarProviderCloudinaryImagePaths = {
    apple: 'Pinpoint/Logos/apple_icloud',
    exchange: 'Pinpoint/Logos/microsoft_exchange',
    google: 'Pinpoint/Logos/google_logotype',
    live_connect: 'Pinpoint/Logos/microsoft_outlook',
    office365: 'Pinpoint/Logos/microsoft_office_365',
  };
  public static readonly CalendarProviderServiceCloudinaryImagePaths = {
    cronofy: '',
    exchange: 'Pinpoint/Logos/microsoft_exchange',
    google: 'Pinpoint/Logos/google_logotype',
    gsuite: 'Pinpoint/Logos/google_logotype',
    icloud: 'Pinpoint/Logos/apple_icloud',
    office365: 'Pinpoint/Logos/microsoft_office_365',
    outlook_com: 'Pinpoint/Logos/microsoft_outlook',
  };
  public static readonly ConferencingProviderCloudinaryImagePaths = {
    go_to: 'Pinpoint/Logos/gotomeeting',
    google_meet: 'Pinpoint/Logos/google_meet',
    ms_teams: 'Pinpoint/Logos/microsoft_teams',
    zoom: 'Pinpoint/Logos/zoom',
  };
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly providerName: string;
  @Attr({ persist: false }) public readonly profileName: string;
  @Attr({ persist: false }) public readonly readFreeBusyScope: boolean;

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly calendar: ICronofyCalendar | null;
  @Attr({ persist: false }) public readonly conferencingProfiles: ICronofyConferencingProfile[];
  @Attr({ persist: false }) public readonly conferencingServiceAuthUrl: string;
  @Attr({ persist: false }) public readonly profile: ICronofyProfile;
  @Attr({ persist: false }) public readonly profiles: ICronofyProfile[];
}

@Model()
export class CronofyResource extends ApplicationRecord {
  public static jsonapiType = 'cronofy_resources';

  @Attr() public generatedName: string;
  @Attr() public email: string;
  @Attr() public name: string;
  @Attr() public cronofySub: string;
}

@Model()
export class CronofyResourcesInterviewPartable extends ApplicationRecord {
  public static jsonapiType = 'cronofy_resources_interview_partables';

  @BelongsTo() public cronofyResource: CronofyResource;
}

@Model()
export class ConferencingHost extends ApplicationRecord {
  public static jsonapiType = 'conferencing_hosts';

  @Attr({ persist: false }) public readonly conferencingHostableId: string;
  @Attr({ persist: false }) public readonly conferencingHostableType: string;
  @Attr() public provider: string;
  @Attr() public providerUserId: string;
  @Attr({ persist: false }) public readonly joinUrl: string;
}

@Model()
export class CronofyHost extends ApplicationRecord {
  public static jsonapiType = 'cronofy_hosts';

  @Attr() public cronofyAccessTokenId: string;
  @Attr() public providerName: string;
  @Attr() public profileId: string;
  @Attr() public profileName: string;
  @Attr() public profileConnected: boolean;

  @BelongsTo(CronofyAccessToken) public cronofyAccessToken: CronofyAccessToken;
}

@Model()
export class Channel extends ApplicationRecord {
  public static jsonapiType = 'channels';

  @Attr() public value: string;
  @Attr() public label: string;
}

@Model()
export class Currency extends ApplicationRecord {
  public static jsonapiType = 'currencies';

  @Attr() public alpha3: string;
  @Attr() public name: string;
}

@Model()
export class Company extends MobilityApplicationRecord {
  public static jsonapiType = 'companies';
  public static rejectionReasonOptions = [
    { value: 'hidden', label: "Don't prompt" },
    { value: 'optional', label: 'Prompt' },
    { value: 'required', label: 'Require' },
  ];

  @Attr() public allowDuplicateCandidates: boolean;
  @Attr() public concealedNotifications: boolean;
  @Attr() public contactAutoRejected: boolean;
  @Attr({ persist: false }) public readonly demo: boolean;
  @Attr() public name: string;
  @Attr() public customFavicon: string;
  @Attr() public openGraphImage: string;
  @Attr() public logo: string;
  @Attr() public logoLight: string;
  @Attr() public squareImage: string;
  @Attr() public senderName: string;

  @Attr() public newJobSeekerOpenGraphImage: string;
  @Attr() public defaultDiversityMonitoring: 'off_by_default' | 'on_by_default' | 'on_for_all';
  @Attr() public diversityMonitoring: boolean;

  @Attr() public internalJobBoardEnabled: boolean;
  @Attr() public maxApplicationsCount: number;
  @Attr() public maxApplicationsTimePeriod: '1 Day' | '3 Days' | '1 Week' | '2 Weeks' | '1 Month' | '3 Months';
  @Attr() public redactionOption: string;
  @Attr() public rejectionReason: string;
  @Attr() public referralsEnabled: boolean;
  @Attr() public requisitionIdPrefix: string;
  @Attr() public requisitionIdRequirement: 'optional' | 'auto_generated' | 'required_custom';
  @Attr() public restrictSelfSignup: boolean;
  @Attr() public selfSignupEnabled: boolean;
  @Attr() public sendMaxApplicationsEmail: boolean;
  @Attr() public defaultReferralsUserLocale?: TLocaleKey;

  @Attr() public facebookjobs: boolean;
  @Attr() public monster: boolean;
  @Attr() public linkedin: boolean;
  @Attr() public ziprecruiter: boolean;
  @Attr() public talentdotcom: boolean;
  @Attr() public countryCode: string;
  @Attr() public timeZone: string;

  /** company settings */
  @Attr() public companySettingRequireHeadcount: boolean;
  @Attr() public companySettingAllowMydataDeletion: boolean;
  @Attr() public companySettingAllowedEmailSuffixesOnlyForInternalJobs: boolean;
  @Attr() public companySettingCandidateJobPreferences: boolean;
  @Attr() public companySettingRecruitmentAgencies: boolean;
  @Attr() public companySettingShowCompensationByDefault: boolean;
  @Attr() public companySettingHideDiscardedEqTemplatesInReports: boolean;
  @Attr() public companySettingScorecardSummary: boolean;
  @Attr() public companySettingReferralsUserNotificationDefaults: boolean;
  @Attr() public companySettingRadiusSearch: boolean;
  @Attr() public companySettingHideCvDownload: boolean;
  @Attr() public companySettingCanDeleteScorecards: boolean;
  @Attr() public companySettingSendRescheduledInterviewCancellationEvents: boolean;

  /** @extraAttributes */
  @Attr() public channelSourceOptions: ISelectOption[];
  @Attr() public employmentTypes: string[];
  @Attr({ persist: false }) public readonly cronofyEnterpriseConnectActive: boolean;
  @Attr({ persist: false }) public readonly canMoveToAnyJob: boolean;
  @Attr({ persist: false }) public readonly groupAssessmentName: string;
  @Attr({ persist: false }) public readonly groupAssessmentNamePlural: string;
  @Attr({ persist: false }) public readonly hasValidAddress: boolean;
  @Attr({ persist: false }) public readonly locales: TLocaleKey[];
  @Attr({ persist: false }) public readonly maxApplicationsEnabled: boolean;
  @Attr({ persist: false }) public readonly referralsSignUpUrl: string;
  @Attr({ persist: false }) public readonly allStageNames: string[];
  @Attr({ persist: false }) public readonly stageNames: string[];
  @Attr({ persist: false }) public readonly normalisedStageNames: string[];
  @Attr({ persist: false }) public readonly tagOptions: string[];
  @Attr({ persist: false }) public readonly jobCompanyNameOverrideEnabled: boolean;
  @Attr({ persist: false }) public readonly referralEnabledJobsSize: number;
  @Attr({ persist: false }) public readonly hasJotFormIntegration: boolean;
  @Attr({ persist: false }) public readonly jotFormWebhookUrl: string;
  @Attr({ persist: false }) public readonly packageName: string;
  @Attr() public showResumeInterpretation: boolean;
  @Attr() public showResumePinpointSummary: boolean;
  @Attr() public showResumeSkills: boolean;
  @Attr() public showResumeEducation: boolean;
  @Attr() public sendTalentPipelineInvitation: boolean;
  @Attr() public stageActionOptions: {
    name: string;
    shortDescription: string;
    type: string;
    icon: IconNames16;
    integration: boolean;
    extendedType?: string;
    basicType?: string;
  }[];
  @Attr() public onboarding: boolean;
  @Attr({ persist: false }) public readonly structures: {
    key: string;
    keyPlural: string;
    name: string;
    namePlural: string;
    nameAccessor: string;
    columnVisibilityAccessor: string;
  }[];
  @Attr({ persist: false }) public readonly validEmailSuffixes: string[];
  @Attr({ persist: false }) public readonly themeLevel?:
    | 'Location'
    | 'Department'
    | 'Division'
    | 'StructureCustomGroupOne';

  @Attr({ persist: false }) public readonly retentionPolicyLevel?:
    | 'Location'
    | 'Department'
    | 'Division'
    | 'StructureCustomGroupOne';

  @HasMany() public equalityMonitoringCategories: EqualityMonitoringCategory[];
  @HasMany() public equalityMonitoringTemplates: EqualityMonitoringTemplate[];
  @HasMany() public locations: Location[];
  @HasMany() public onboardingPortals: OnboardingPortal[];
  @HasMany() public onboardingProcesses: OnboardingProcess[];
  @HasMany() public onboardingWorkflows: OnboardingWorkflow[];
  @HasMany() public scorecards: Scorecard[];
  @HasMany() public companyTeams: CompanyTeam[];
  @HasMany() public themes: Theme[];
  @HasMany() public users: User[];
  @HasMany() public usersVisibilities: UserVisibility[];
  @HasMany() public vonqCampaigns: VonqCampaign[];
  @HasOne() public alvaLabsIntegration: IntegrationsAlvaLabs;
  @HasOne() public appcastIntegration: IntegrationsAppcast;
  @HasOne() public azureActiveDirectoryIntegration: IntegrationsAzureActiveDirectory;
  @HasOne() public bizimplyIntegration: IntegrationsBizimply;
  @HasOne() public certnIntegration: IntegrationsCertn;
  @HasOne() public criteriaIntegration: IntegrationsCriteria;
  @HasOne() public creativeServicesIncIntegration: IntegrationsCreativeServicesInc;
  @HasOne() public cintraIntegration: IntegrationsCintra;
  @HasOne() public firstAdvantageIntegration: IntegrationsFirstAdvantage;
  @HasOne() public hackerRankIntegration: IntegrationsHackerRank;
  @HasOne() public hireflixIntegration: IntegrationsHireflix;
  @HasOne() public hireRightIntegration: IntegrationsHireRight;
  @HasOne() public itrentIntegration: IntegrationsItrent;
  @HasOne() public oracleIntegration: IntegrationsOracle;
  @HasOne() public docuSignIntegration: IntegrationsDocuSign;
  @HasOne() public internsProgrammeIntegration: IntegrationsInternsProgramme;
  @HasOne() public linkedInIntegration: IntegrationsLinkedIn;
  @HasOne() public onboardingConfiguration: OnboardingConfiguration;
  @HasOne() public outmatchIntegration: IntegrationsHarver;
  @HasOne() public predictiveIndexIntegration: IntegrationsPredictiveIndex;
  @HasOne() public talentPipeline: TalentPipeline;
  @HasOne() public theme: Theme;
  @HasOne() public twilioIntegration: IntegrationsTwilio;
  @HasOne() public testGorillaIntegration: IntegrationsTestGorilla;
}

@Model()
export class CandidateSurveyConfiguration extends MobilityApplicationRecord {
  public static jsonapiType = 'candidate_survey_configurations';

  public static validationSchema = Yup.object().shape({
    delayPeriod: Yup.string().required("Can't be blank."),
    delayValue: Yup.string().required("Can't be blank."),
    emailBody: Yup.string().required("Can't be blank."),
    emailSubject: Yup.string().required("Can't be blank."),
    thankYouMessage: Yup.string().required("Can't be blank."),
  });

  @Attr() public enabled: boolean;
  @Attr() public emailSubject: string;
  @Attr() public emailBody: string;
  @Attr() public delayValue: string;
  @Attr() public delayPeriod: string;
  @Attr() public thankYouMessage: string;
  @Attr() public requireInterviewAttendance: boolean;

  @Attr() public requiredStageGroupId: string;
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @BelongsTo() public stageGroup: StageGroup;
}

@Model()
export class CustomAttribute extends ApplicationRecord {
  public static jsonapiType = 'custom_attributes';

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<CustomAttribute> = {
      multiSelectValue: [],
      textValue: '',
      varcharValue: '',
      usersValue: [],
    };
    return new CustomAttribute({ ...defaultAttrs, ...attrs });
  }

  @Attr() public booleanValue: boolean;
  @Attr({ persist: false }) public readonly customFieldId: string;
  @Attr() public dateValue: string;
  @Attr() public decimalValue: number;
  @Attr() public integerValue: number;
  @Attr() public multiSelectValue: string[];
  // @NOTE: (Leo Melo) This value is one or the other depending on whether it's coming from the server or being assigned from a form (🙃)
  @Attr() public usersValue: ISelectOption[] | string[];
  @Attr() public singleSelectValue: string;
  @Attr({ persist: false }) public readonly selectedOptions: ISelectOption[];
  @Attr() public textValue: string;
  @Attr() public varcharValue: string;
  @Attr() public skipPresenceValidations: boolean;
  @Attr({ persist: false }) public visible: boolean;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly fieldName: string;
  @Attr({ persist: false }) public readonly value: string;
  @Attr({ persist: false }) public readonly sensitive: boolean;

  @HasMany() public audits: Audit[];
  @BelongsTo() public customAttributable: Application | JobSeeker;
  @BelongsTo() public customField: CustomField;

  public getUsersValueIds(): string[] {
    if (Array.isArray(this.usersValue)) {
      return this.usersValue.map<string>((uv) => {
        if (typeof uv === 'string') return uv;

        return uv.value;
      });
    }
    return [];
  }

  public getUsersValueSelectOptions(): ISelectOption[] {
    if (Array.isArray(this.usersValue)) {
      return this.usersValue.map<ISelectOption>((uv) => {
        if (typeof uv === 'string') return { value: uv, label: uv };

        return uv;
      });
    }
    return [];
  }
}

@Model()
export class CustomDomainRedirect extends ApplicationRecord {
  public static jsonapiType = 'custom_domain_redirects';

  @Attr({ persist: false }) public readonly from: string;
  @Attr({ persist: false }) public readonly to: string;
}

export type Condition = {
  operator: 'equals' | 'not_equals';
  values: { value: string; label: string }[];
  property: string;
};

export type ScopeConditionConditions = {
  and?: Condition[];
  or?: Condition[];
};

@Model()
export class CustomField extends MobilityApplicationRecord {
  public static jsonapiType = 'custom_fields';

  public static validationSchema = Yup.object().shape({
    fieldType: Yup.string().required("Can't be blank."),
    name: Yup.string().required("Can't be blank."),
    resourceType: Yup.string().required("Can't be blank."),
  });

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<CustomField> = {
      description: '',
      fieldType: '',
      required: false,
      name: '',
      options: [],
      resourceType: '',
      sampleValue: '',
      sensitive: false,
      isChild: false,
    };
    return new CustomField({ ...defaultAttrs, ...attrs });
  }

  public static resourceTypeMap = {
    application: 'application',
    candidate: 'candidate',
    job: 'job',
    offer: 'offer',
    position: 'position',
    talent_pipeline_candidate: 'job_seeker',
  };

  @Attr() public description: string;
  @Attr({ persist: false }) public readonly discardedAt: string;
  @Attr() public fieldType:
    | 'boolean'
    | 'date'
    | 'decimal'
    | 'integer'
    | 'multi_select'
    | 'single_select'
    | 'text'
    | 'users'
    | 'varchar'
    | '';
  @Attr() public required: boolean;
  @Attr() public name: string;
  @Attr() public options: string[];
  @Attr() public position: number;
  @Attr() public resourceType: string;
  @Attr() public sampleValue: string;
  @Attr() public sensitive: boolean;
  @Attr() public scopeConditions: ScopeConditionConditions;
  @Attr() public isChild: boolean;
  @Attr() public readonly parentCustomFieldId: string;
  @Attr() public visible: boolean;
  @Attr({ persist: false }) public readonly uniqueReference: string;

  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public customFieldOptions: CustomFieldOption[];
  @HasMany() public conditionalCustomFields: ConditionalCustomField[];
  @HasMany() public customFieldOptionGroups: CustomFieldOptionGroup[];
  @HasOne() public scopeConditionAssociation: ScopeCondition;
}

@Model()
export class CustomFieldOption extends MobilityApplicationRecord {
  public static jsonapiType = 'custom_field_options';

  @Attr() public customFieldId: string;
  @Attr() public label: string;
  @Attr() public position: number;
  @Attr() public text: string;
  @Attr() public value: string;

  @Attr({ persist: false }) public readonly uniqueId: string;
  @BelongsTo() public customField: CustomField;
}

@Model()
export class CustomFieldOptionGroup extends ApplicationRecord {
  public static jsonapiType = 'custom_field_option_groups';

  @Attr() public position: number;

  @HasMany() public customFieldOptions: CustomFieldOption[];
  @BelongsTo() public customField: CustomField;
  @HasOne() public scopeCondition: ScopeCondition;
}

@Model()
export class Department extends MobilityApplicationRecord {
  public static jsonapiType = 'departments';
  public static MAX_PER = 10000;

  public static NEW() {
    const attrs: Partial<Department> = {
      name: '',
    };
    return new this(attrs);
  }

  @Attr() public name: string;
  @Attr() public activate: boolean;
  @Attr() public deactivate: boolean;
  @Attr({ persist: false }) public readonly inactive: boolean;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly inUse: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public company: Company;
  @HasOne() public themesThemeable: ThemesThemeable;
  @HasOne() public dataRetentionPolicyRetentionable: DataRetentionPolicyRetentionable;
}

@Model()
export class DelayedJob extends ApplicationRecord {
  public static jsonapiType = 'delayed_jobs';
}

@Model()
export class Division extends MobilityApplicationRecord {
  public static jsonapiType = 'divisions';
  public static MAX_PER = 10000;

  public static NEW() {
    const attrs: Partial<Division> = {
      name: '',
    };
    return new this(attrs);
  }

  @Attr() public name: string;
  @Attr() public activate: boolean;
  @Attr() public deactivate: boolean;
  @Attr({ persist: false }) public readonly inactive: boolean;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly inUse: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public company: Company;
  @HasOne() public themesThemeable: ThemesThemeable;
  @HasOne() public dataRetentionPolicyRetentionable: DataRetentionPolicyRetentionable;
}

@Model()
export class DocuSignEnvelope extends ApplicationRecord {
  public static jsonapiType = 'docu_sign_envelopes';
  @Attr({ persist: false }) public readonly templateName: string;
  @Attr({ persist: false }) public readonly envelopeId: string;
  @Attr({ persist: false }) public readonly appLink: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly requestType: string;
  @Attr({ persist: false }) public readonly lastSyncedAtShortWithTime: string;
  @Attr({ persist: false }) public readonly applicationId: number;
  @Attr({ persist: false }) public readonly updatedAt: string;
}

export type EqualityMonitoringCategoryType =
  | 'gender'
  | 'gender_identity'
  | 'ethnicity'
  | 'sexual_orientation'
  | 'religion'
  | 'marital_status'
  | 'age_bracket'
  | 'disability'
  | 'veteran_status'
  | 'preferred_pronouns';

@Model()
export class EqualityMonitoringCategory extends MobilityApplicationRecord {
  public static jsonapiType = 'equality_monitoring_categories';

  public static DEFAULT_CATEGORIES: { value: EqualityMonitoringCategoryType; label: string }[] = [
    { value: 'age_bracket', label: 'Age Bracket' },
    { value: 'disability', label: 'Disability' },
    { value: 'ethnicity', label: 'Ethnicity' },
    { value: 'gender_identity', label: 'Gender Identity' },
    { value: 'gender', label: 'Gender' },
    { value: 'marital_status', label: 'Marital Status' },
    { value: 'religion', label: 'Religion' },
    { value: 'sexual_orientation', label: 'Sexual Orientation' },
    { value: 'veteran_status', label: 'Veteran Status' },
    { value: 'preferred_pronouns', label: 'Preferred Pronouns' },
  ];
  get isPreferredPronouns(): boolean {
    return this.basicType === 'preferred_pronouns';
  }

  public static validationSchema = Yup.object().shape({
    basicType: Yup.mixed()
      .oneOf(
        EqualityMonitoringCategory.DEFAULT_CATEGORIES.map(({ value }) => value),
        `Basic type must be one of: ${EqualityMonitoringCategory.DEFAULT_CATEGORIES.map(({ label }) => label).join(
          ', ',
        )}`,
      )
      .required("Can't be blank"),
    enabled: Yup.mixed().oneOf(['0', '1'], "Can't be blank").required("Can't be blank"),
    name: Yup.string().required("Can't be blank"),
  });

  @Attr() public companyId: string;
  @Attr() public name: string;
  @Attr() public basicType: EqualityMonitoringCategoryType;
  @Attr() public enabled: boolean;
  @Attr() public equalityMonitoringTemplateId: string;
  @Attr({ persist: false }) public readonly nameEn: string;

  @BelongsTo() public equalityMonitoringTemplate: EqualityMonitoringTemplate;

  @HasMany() public equalityMonitoringOptions: EqualityMonitoringOption[];
}

@Model()
export class EqualityMonitoringOption extends ApplicationRecord {
  public static jsonapiType = 'equality_monitoring_options';

  public static NEW() {
    const attrs: Partial<EqualityMonitoringOption> = {
      companyId: '',
      enabled: true,
      equalityMonitoringCategoryId: '',
      name: '',
      optionType: 'custom',
    };
    return new EqualityMonitoringOption(attrs);
  }

  @Attr() public companyId: string;
  @Attr() public enabled: boolean;
  @Attr() public equalityMonitoringCategoryId: string;
  @Attr() public name: string;
  @Attr() public optionType: string;
  @Attr() public position: number;
  @Attr({ persist: false }) public readonly nameEn: string;
  @Attr({ persist: false }) public uniqueId: string;

  @BelongsTo() public company: Company;
  @BelongsTo() public equalityMonitoringCategory: EqualityMonitoringCategory;
}

@Model()
export class EqualityMonitoringTemplate extends MobilityApplicationRecord {
  public static jsonapiType = 'equality_monitoring_templates';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank").min(2, 'Must be 2 characters minimum'),
  });

  @Attr() public duplicate: boolean;
  @Attr() public name: string;
  @Attr() public companyId: string;
  @Attr() public default: boolean;
  @Attr() public explanation: string;
  @Attr() public required: boolean;
  @Attr() public updatedAt: string;

  @BelongsTo() public company: Company;

  @HasMany() public locations: Location[];
  @HasMany() public equalityMonitoringCategories: EqualityMonitoringCategory[];
}

@Model()
export class EqualityMonitoringTrait extends ApplicationRecord {
  public static jsonapiType = 'equality_monitoring_traits';
  @Attr() public createdAt: string;

  @HasOne() public equalityMonitoringOption: EqualityMonitoringOption;
}

export namespace EqualityMonitoringTrait {
  export namespace Stats {
    export type total_count = number;
    export type by_grouped_categories_count = { [categoryName: string]: { [sourceName: string]: number } };
  }
}

@Model()
export class DataRetentionPolicy extends ApplicationRecord {
  public static jsonapiType = 'data_retention_policies';

  public static NEW() {
    const defaultAttrs: Partial<DataRetentionPolicy> = {
      name: '',
      dataRetentionMonths: 1000,
      hiredDataRetentionMonths: 1000,
      recentActivityMonths: 6,
      default: false,
    };
    return new DataRetentionPolicy({ ...defaultAttrs });
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
    dataRetentionMonths: Yup.number().required("Can't be blank"),
    hiredDataRetentionMonths: Yup.number().required("Can't be blank"),
    recentActivityMonths: Yup.number().required("Can't be blank"),
  });

  @Attr() public dataRetentionMonths: number;
  @Attr() public default: boolean;
  @Attr() public hiredDataRetentionMonths: number;
  @Attr() public recentActivityMonths: number;
  @Attr() public name: string;
}

@Model()
export class DataRetentionPolicyRetentionable extends ApplicationRecord {
  public static jsonapiType = 'data_retention_policy_retentionables';

  @Attr({ persist: false }) public dataRetentionPolicyId: string;
  @BelongsTo() public dataRetentionPolicy: DataRetentionPolicy;
}

@Model()
export class DocumentRequest extends ApplicationRecord {
  public static jsonapiType = 'document_requests';

  @Attr() public createdAt: string;
  @Attr() public documentType: DocumentRequest_document_type_key;

  @Attr() public applicationId: string;
  @Attr() public comment: string;
  @Attr() public companyId: string;
  @Attr() public document: string;
  @Attr() public documentTemplateId: string;
  @Attr() public docuSignEnvelopeId: string;
  @Attr() public status: DocumentRequest_status_key;
  @Attr({ persist: false }) public readonly documentFile: IActiveStorageFile;

  @HasOne() public helloSignSignatureRequest: HelloSignSignatureRequest;
  @HasOne() public docuSealSubmission: DocuSealSubmission;
  @HasOne() public docuSignEnvelope: DocuSignEnvelope;
  @BelongsTo() public documentTemplate: DocumentTemplate;
}

@Model()
export class DocumentTemplate extends ApplicationRecord {
  public static jsonapiType = 'document_templates';

  public static uploadableDocumentTypes = ['uploaded', 'hello_sign'] as const;

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
  });

  get isFillable() {
    if (this.documentType === 'docu_seal') return true;

    return false;
  }

  get acceptanceType() {
    switch (this.documentType) {
      case 'docu_seal':
        return 'Accept, Fill and Sign';
      case 'docu_sign':
      case 'hello_sign':
        return 'Accept and Sign';
      case 'uploaded':
        return 'Accept Only';
      default:
        return 'None';
    }
  }

  @Attr() public createdAt: string;
  @Attr() public documentType: DocumentTemplate_document_type_key;

  @Attr() public name: string;
  @Attr({ persist: false }) public companyId: string;
  @Attr() public document: string;
  @Attr() public docuSignTemplateId: string;
  @Attr({ persist: false }) public documentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<DocumentTemplate> = {
      name: '',
      document: null,
      documentType: null,
      docuSignTemplateId: null,
    };
    return new DocumentTemplate({ ...defaultAttrs, ...attrs });
  }
  @BelongsTo() public stage: Stage;
  @HasOne() public scopeCondition: ScopeCondition;
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
}

@Model()
export class EmailTemplate extends MobilityApplicationRecord {
  public static jsonapiType = 'email_templates';
  public static MAX_PER = 1000;

  @Attr() public bcc: string[];
  @Attr() public cc: string[];
  @Attr() public emailType: string;
  @Attr({ persist: false }) public parserResources: string[];
  @Attr({ persist: false }) public previewPath?: string;
  @Attr({ persist: false }) public custom: boolean;
  @Attr() public name: string;
  @Attr() public subject: string;
  @Attr() public duplicate: boolean;
  @Attr() public salutation: string;
  @Attr() public body: string;
  @Attr() public signature: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];

  /* extra attributes */
  @Attr() public tagList: string[];
  @Attr({ persist: false }) public deletable: boolean;
  @Attr({ persist: false }) public tagsWithContext: TagWithContext[];
  @Attr() public setTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public customFieldParserResources: { text: string; value: string }[];

  @HasOne() public scopeCondition: ScopeCondition;

  get isServerTemplate(): boolean {
    return this.id === '-1';
  }

  get isCreating(): boolean {
    if (this.isServerTemplate) return true;

    return !this.isPersisted;
  }
}

@Model()
export class EducationHistory extends ApplicationRecord {
  public static jsonapiType = 'education_histories';
  @Attr({ persist: false }) public readonly resumeId: number;
  @Attr({ persist: false }) public readonly schoolName: string;
  @Attr({ persist: false }) public readonly comments: string;
  @Attr({ persist: false }) public readonly period: string;
  @Attr({ persist: false }) public readonly location: string;
  @Attr({ persist: false }) public readonly listContent: Record<string, string>;
  @BelongsTo() public resume: Resume;
}

@Model()
export class EmailDigestSubscription extends ApplicationRecord {
  public static jsonapiType = 'email_digest_subscriptions';
  @Attr() public userId: string;
  @Attr() public companyId: string;
  @Attr() public dailyConsent: boolean;
  @Attr() public weeklyConsent: boolean;
  @Attr() public monthlyConsent: boolean;
  @BelongsTo() public user: User;
  @BelongsTo() public company: Company;
}

@Model()
export class EmploymentHistory extends ApplicationRecord {
  public static jsonapiType = 'employment_histories';
  @Attr({ persist: false }) public readonly resumeId: number;
  @Attr({ persist: false }) public readonly employerName: string;
  @Attr({ persist: false }) public readonly description: string;
  @Attr({ persist: false }) public readonly jobLevel: string;
  @Attr({ persist: false }) public readonly skillsComments: string;
  @Attr({ persist: false }) public readonly skillsCategory: string;
  @Attr({ persist: false }) public readonly position: string;
  @Attr({ persist: false }) public readonly period: string;
  @Attr({ persist: false }) public readonly location: string;
  @Attr({ persist: false }) public readonly listContent: Record<string, string>;
  @BelongsTo() public resume: Resume;
}

@Model()
export class EnterpriseCronofyAccessToken extends ApplicationRecord {
  public static jsonapiType = 'enterprise_cronofy_access_tokens';

  @Attr({ persist: false }) public readonly createdAtLongOrdinal: string;
  @Attr({ persist: false }) public readonly serviceAccountId: string;

  @HasMany() public audits: Audit[];
}

@Model()
export class ExternalMessage extends ApplicationRecord {
  public static jsonapiType = 'external_messages';

  get isInbound(): boolean {
    return this.direction === 'inbound';
  }

  public staticType = 'ExternalMessage' as const;

  @Attr() public createdAt: string;
  @Attr() public createdAtLongOrdinal: string;
  @Attr() public contactableId: string;
  @Attr() public contactableType: string;
  @Attr() public direction: string;
  @Attr() public body: string;
  @Attr({ persist: false }) public readonly subject: string;
  @Attr({ persist: false }) public readonly source: string;
  @Attr() public status: string;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr() public updatedAt: string;
  @BelongsTo() public sender: User;
  @BelongsTo({ persist: false }) public readonly contactable: Application | JobSeeker;
}

@Model()
export class FilterCollection extends ApplicationRecord {
  public static jsonapiType = 'filter_collections';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
  });

  get isPublic(): boolean {
    return this.visibility === 'global';
  }

  @Attr() public recordType:
    | 'candidate'
    | 'talent_pipeline'
    | 'job'
    | 'requisition'
    | 'job_posting'
    | 'application'
    | 'custom_field';
  @Attr() public name: string;
  @Attr() public visibility: 'personal' | 'global';
  @Attr() public filters: Record<string, any>;
  @Attr({ persist: false }) public readonly humanizedFilters: string[];
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly canDelete: boolean;
}

@Model()
export class Folder extends ApplicationRecord {
  public static jsonapiType = 'folders';

  public static NEW(attrs = {}) {
    return new Folder({
      name: '',
      private: false,
      ...attrs,
    });
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public name: string;
  @Attr() public private: boolean;
  @Attr() public documents: string[];
  // Extra attributes
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly editable: boolean;

  @BelongsTo() public company: Company;
  @BelongsTo() public user: User;
  @BelongsTo() public folderable: Application | JobSeeker;
}

@Model()
export class GeocodedAddress extends ApplicationRecord {
  public static jsonapiType = 'geocoded_addresses';

  @Attr() public formattedAddress: string;
  @Attr() public latitude: number;
  @Attr() public longitude: number;

  @BelongsTo() public addressable: Application | JobSeeker | Candidate | Location;
}

@Model()
export class GroupAssessment extends MobilityApplicationRecord {
  public static jsonapiType = 'group_assessments';

  public static NEW(attrs = {}) {
    return new GroupAssessment({
      name: '',
      description: '',
      candidateInstructions: '',
      allowRescheduling: true,
      ...attrs,
    });
  }

  @Attr() public allowRescheduling: boolean;
  @Attr() public candidateInstructions: string;
  @Attr() public description: string;
  @Attr() public name: string;
  @Attr() public updatedAt: string;

  @Attr() public inviteEmailSubject: string;
  @Attr() public inviteEmailSalutation: string;
  @Attr() public inviteEmailBody: string;
  @Attr() public inviteEmailSignature: string;

  @Attr() public confirmEmailSubject: string;
  @Attr() public confirmEmailSalutation: string;
  @Attr() public confirmEmailBody: string;
  @Attr() public confirmEmailSignature: string;

  @Attr({ persist: false }) public readonly hasUpcomingSessions: boolean;

  @HasMany() public groupAssessmentManagers: GroupAssessmentManager[];
  @HasMany() public groupAssessmentInvites: GroupAssessmentInvite[];
  @HasMany() public groupAssessmentSessions: GroupAssessmentSession[];
  @HasMany() public scorecardableScorecards: ScorecardableScorecard[];
}

@Model()
export class GroupAssessmentAttendee extends ApplicationRecord {
  public static jsonapiType = 'group_assessment_attendees';

  get isCancelled(): boolean {
    return this.status === 'cancelled';
  }

  get isNoShow(): boolean {
    return this.status === 'no_show';
  }

  get isConfirmed(): boolean {
    return this.status === 'confirmed';
  }

  @Attr() public applicationId: string;
  @Attr() public applicationFullName: string;
  @Attr() public ensureSave: boolean;
  @Attr() public stageId: string;
  @Attr() public status: string;
  @Attr() public timezone: string;

  // Extra attributes
  @Attr({ persist: false }) public readonly sessionStartTime: string;
  @Attr({ persist: false }) public readonly sessionStartAt: string;
  @Attr({ persist: false }) public readonly jobTitle: string;

  @BelongsTo() public groupAssessmentSession: GroupAssessmentSession;
}

@Model()
export class GroupAssessmentInvite extends ApplicationRecord {
  public static jsonapiType = 'group_assessment_invites';

  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly applicationId: string;

  @BelongsTo() public application: Application;
  @BelongsTo() public groupAssessment: GroupAssessment;
  @HasOne() public groupAssessmentAttendee: GroupAssessmentAttendee;
}

@Model()
export class GroupAssessmentSession extends ApplicationRecord {
  public static jsonapiType = 'group_assessment_sessions';

  public static NEW(attrs = {}) {
    return new GroupAssessmentSession({
      name: '',
      maxAttendees: 10,
      duration: 240,
      startTimeString: '09:00',
      startOn: null,
      timezone: '',
      userIds: [],
      ...attrs,
    });
  }

  @Attr({ persist: false }) public attendees: string[];
  @Attr() public name: string;
  @Attr() public maxAttendees: number;
  @Attr() public duration: number;
  @Attr({ persist: false }) public readonly endAt: number;
  @Attr({ persist: false }) interviewerFullNames: string;
  @Attr({ persist: false }) startAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) startAtHourAndMinuteTime: string;
  @Attr({ persist: false }) startAtLongOrdinalWithTimeZoneTime: string;
  @Attr({ persist: false }) startAt: string;
  @Attr() public startOn: string;
  @Attr() public startTime: string;
  @Attr() public startTimeString: string;
  @Attr() public timezone: string;
  @Attr() public updatedAt: string;

  @Attr({ persist: false }) public userIds: string[];

  @BelongsTo() public groupAssessment: GroupAssessment;
  @HasMany() public groupAssessmentAttendees: GroupAssessmentAttendee[];
  @HasMany() public groupAssessmentSessionsUsers: GroupAssessmentSessionsUser[];
  @HasMany() public groupAssessmentManager: GroupAssessmentManager[];
}

@Model()
export class GroupAssessmentManager extends ApplicationRecord {
  public static jsonapiType = 'group_assessment_managers';

  @Attr() public ensureSave: boolean;
  @Attr() public userId: string;

  @BelongsTo() public user: User;
  @BelongsTo() public groupAssessment: GroupAssessment;
}

@Model()
export class GroupAssessmentSessionsUser extends ApplicationRecord {
  public static jsonapiType = 'group_assessment_sessions_users';
  @Attr() public userId: string;
  @Attr() public updatedAt: string;

  @BelongsTo() public user: User;
  @BelongsTo() public groupAssessmentSession: GroupAssessmentSession;
}

@Model()
export class GroupInterview extends MobilityApplicationRecord {
  public static jsonapiType = 'group_interviews';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
    candidateInstructions: Yup.string().required("Can't be blank"),
  });

  @Attr() public companyId: string;
  @Attr() public interviewableId: string;
  @Attr() public interviewableType: string;
  @Attr() public candidateInstructions: string;
  @Attr() public name: string;

  @BelongsTo() public interviewable: Job;
  @HasMany() public groupInterviewInvites: GroupInterviewInvite[];
  @HasMany() public groupInterviewSessions: GroupInterviewSession[];
}

@Model()
export class GroupInterviewAttendee extends ApplicationRecord {
  public static jsonapiType = 'group_interview_attendees';

  @Attr() public attendableId: string;
  @Attr() public attendableType: string;
  @Attr() public groupInterviewSessionId: string;

  @BelongsTo() public attendable: Application;
}

@Model()
export class GroupInterviewInvite extends ApplicationRecord {
  public static jsonapiType = 'group_interview_invites';

  @Attr() public invitableId: string;
  @Attr() public invitableType: string;
  @Attr() public groupInterviewId: string;

  /** @attrAccessors */
  @Attr() public body: string;
  @Attr() public salutation: string;
  @Attr() public signature: string;
  @Attr() public subject: string;

  @BelongsTo() public invitable: Application;
  @BelongsTo() public groupInterview: GroupInterview;
}

@Model()
export class GroupInterviewSession extends ApplicationRecord {
  public static jsonapiType = 'group_interview_sessions';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
    maxAttendeeCount: Yup.number().required("Can't be blank").typeError('Must be a number.'),
    duration: Yup.number().required("Can't be blank").typeError('Must be a number.'),
    startAt: Yup.string().required("Can't be blank"),
    timeZone: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public companyId: string;
  @Attr() public groupInterviewId: string;
  @Attr() public maxAttendeeCount: number;
  @Attr() public duration: number;
  @Attr() public name: string;
  @Attr() public startAt: string;
  @Attr() public endAt: string;
  @Attr() public timeZone: string;
  @Attr({ persist: false }) public readonly startAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public uniqueId: string;

  @HasMany() public groupInterviewTeamMembers: GroupInterviewTeamMember[];
  @HasMany() public groupInterviewAttendees: GroupInterviewAttendee[];
  @BelongsTo() public groupInterview: GroupInterview;
}

@Model()
export class GroupInterviewTeamMember extends ApplicationRecord {
  public static jsonapiType = 'group_interview_team_members';

  @Attr() public companyId: string;
  @Attr() public userId: string;
  @Attr() public groupInterviewSessionId: string;
  @BelongsTo() public user: User;
}

@Model()
export class HelloSignSignatureRequest extends ApplicationRecord {
  public static jsonapiType = 'hello_sign_signature_requests';

  @Attr() public readonly signableId: string;
  @Attr() public readonly signableType: 'OnboardingAnswer' | 'DocumentRequest';
  @Attr() public readonly status: string;
  @Attr() public documentFile: IActiveStorageFile;
  @BelongsTo() public signable: OnboardingAnswer | DocumentRequest;
}

@Model()
export class DocuSealSubmission extends ApplicationRecord {
  public static jsonapiType = 'docu_seal_submissions';

  @Attr({ persist: false }) public readonly createdAtShortWithYearTime: string;
  @Attr({ persist: false }) public readonly signedAtShortWithYearTime: string;
  @Attr({ persist: false }) public readonly documentFile: IActiveStorageFile;
  @BelongsTo() public signable: DocumentRequest;
  @HasMany() public answers: Answer[];
}

@Model()
export class DocuSealSignatureRequest extends ApplicationRecord {
  public static jsonapiType = 'docu_seal_signature_requests';

  @Attr() public readonly signableId: string;
  @Attr() public readonly signableType: 'OnboardingAnswer' | 'DocumentRequest';
  @Attr() public readonly status: string;
  @Attr() public documentFile: IActiveStorageFile;
  @BelongsTo() public signable: OnboardingAnswer | DocumentRequest;
}

@Model()
export class InsightsPage extends ApplicationRecord {
  public static jsonapiType = 'insights_pages';

  public static NEW(attrs = {}) {
    return new InsightsPage({ name: '', description: '', visibility: 'personal' });
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
    description: Yup.string().required("Can't be blank"),
    visibility: Yup.string().required("Can't be blank"),
  });

  public static visibilityOptions = [
    { value: 'shared', label: 'Shared' },
    { value: 'personal', label: 'Private' },
  ];

  @Attr() public name: string;
  @Attr() public description: string;
  @Attr() public visibility: 'personal' | 'shared';
  @Attr() public userId: string;
  @Attr() public layout: {
    lg: IInsightsPageBreakpointLayout[];
    md: IInsightsPageBreakpointLayout[];
    sm: IInsightsPageBreakpointLayout[];
  };
  @Attr() public duplicate: boolean;
  @Attr({ persist: false }) public readonly userFullName: string;
  @Attr({ persist: false }) public readonly duplicatedId: string;

  @HasOne() public user: User;
  @HasMany() public insightsPageWidgets: InsightsPageWidget[];
}

@Model()
export class InsightsPageWidget extends ApplicationRecord {
  public static jsonapiType = 'insights_page_widgets';

  get isCustomDateRange(): boolean {
    return this.timePeriod === 'custom_date_to_now' || this.timePeriod === 'custom_fixed_date_range';
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank."),
    subject: Yup.string().nullable().required("Can't be blank."),
    timePeriod: Yup.string().nullable().required("Can't be blank."),
    visual: Yup.string().nullable().required("Can't be blank."),
    property: Yup.string().nullable().required("Can't be blank."),
    endDate: Yup.string()
      .nullable()
      .when('timePeriod', {
        is: (value) => value && value === 'custom_fixed_date_range',
        otherwise: Yup.string().nullable(),
        then: Yup.string().nullable().required("Can't be blank."),
      }),
  });

  public static NEW(attrs = {}) {
    return new InsightsPageWidget({
      insightsPage: null,
      name: '',
      description: '',
      subject: null,
      timePeriod: null,
      property: null,
      grouping: null,
      visual: null,
      showPercentage: null,
      filters: [],
      startDate: null,
      endDate: null,
      subGroupingId: null,
    });
  }

  @Attr() public name: string;
  @Attr() public description: string;
  @Attr() public endDate: string;
  @Attr({ persist: false }) public readonly endDateShortWithYearDate: string;
  @Attr() public subject: string;
  @Attr() public type: string;
  @Attr() public property: string;
  @Attr() public grouping: string;
  @Attr() public timePeriod: string;
  @Attr() public visual: string;
  @Attr() public showPercentage: string;
  @Attr() public startDate: string;
  @Attr({ persist: false }) public readonly startDateShortWithYearDate: string;
  @Attr() public filters: {
    property: string;
    operator: 'include' | 'exclude';
    value: ISelectOption[];
  }[];
  @Attr() public subGroupingId: string;
  @Attr() public subGroupingType: string;

  @Attr({ persist: false }) public readonly payload: InsightWidgetPayload;
  @Attr() public duplicate: boolean;

  @BelongsTo() public insightsPage: InsightsPage;
}

/**
 * @note: IntegrationDescription has to exist in the same file as the {@link Integration} model
 * below. This is because of our tooling setup and decorators setup (not sure exactly how yet)
 * and the fact that if we use `IntegrationDescription` as a type only below without the full class
 * being available to Spraypaint when `Integration` is instantiated it will not deserialize the associated
 * `integrationDescription` included attribute correctly.
 *
 * There was a suggestion on how to fix this in Graphiti's Slack,
 * {@link https://graphiti-api.slack.com/archives/CTGRQALH0/p1616165530012500}
 * but depending on how many messages ago that was from when you're reading this message it could be hidden by
 * the 10k messages history limit in free slack groups.
 *
 * The suggested fix in the message thread was to have each model in an individual file and then export all models
 * from a single `index.ts` file that re-exports them, because of how that is handled by build tools.
 *
 * - Leo Melo 2021-03-23
 */
@Model()
export class IntegrationDescription extends ApplicationRecord {
  public static jsonapiType = 'integration_descriptions';

  @Attr({ persist: false }) readonly backgroundFile: IActiveStorageFile;
  @Attr() public readonly category: (typeof INTEGRATION_CATEGORIES)[number]['value'];
  @Attr() public readonly summary: string;
  @Attr() public readonly description: string;
  @Attr() public readonly integrationDetails: string;
  @Attr() public readonly integrationType: keyof typeof INTEGRATION_TYPES;
  @Attr({ persist: false }) readonly logoFile: IActiveStorageFile;
}

export class IntegrationAccount extends ApplicationRecord {
  public static jsonapiType = 'integration_accounts';

  public instanceClasses: IntegrationAccountsSeekAccount | IntegrationAccountsReedAccount;

  @Attr() public type: string;
  @Attr() public name: string;
  @Attr() public validated: boolean;

  /** @relationships */
  @BelongsTo() public integration: Integration;
  @BelongsTo() public company: Company;
}

@Model()
export class IntegrationAccountsSeekAccount extends IntegrationAccount {
  public static jsonapiType = 'integration_accounts_seek_accounts';
  public static endpoint = '/integration_accounts/seek_accounts';
  public staticType = 'SeekAccount' as const;

  @Attr() public hirerId: string;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly hasPostings: boolean;
  @Attr({ persist: false }) public readonly browserToken: string;
}

@Model()
export class IntegrationAccountsReedAccount extends IntegrationAccount {
  public static jsonapiType = 'integration_accounts_reed_accounts';
  public static endpoint = '/integration_accounts/reed_accounts';
  public staticType = 'ReedAccount' as const;

  @Attr() public username: string;
  @Attr({ persist: false }) public maskedPostingKey: string;
  @Attr() public postingKey: string;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly hasPostings: boolean;
}

@Model()
export class Integration extends ApplicationRecord {
  public static jsonapiType = 'integrations';
  public static endpoint = '/integrations';
  public staticType: string;

  public instanceClasses:
    | IntegrationsAdonisHr
    | IntegrationsAdpWorkforceNow
    | IntegrationsAlvaLabs
    | IntegrationsAppcast
    | IntegrationsAzureActiveDirectory
    | IntegrationsAzureOAuth
    | IntegrationsBambooHR
    | IntegrationsBizimply
    | IntegrationsBob
    | IntegrationsBreathe
    | IntegrationsBryq
    | IntegrationsCanditech
    | IntegrationsCappfinity
    | IntegrationsCarePlanner
    | IntegrationsCascade
    | IntegrationsCertn
    | IntegrationsCezanne
    | IntegrationsCheckr
    | IntegrationsCintra
    | IntegrationsCodility
    | IntegrationsCompas
    | IntegrationsCreativeServicesInc
    | IntegrationsCriteria
    | IntegrationsCvLibrary
    | IntegrationsDeel
    | IntegrationsDocuSign
    | IntegrationsFirstAdvantage
    | IntegrationsFirstAdvantageKyp
    | IntegrationsGusto
    | IntegrationsGoogleOAuth
    | IntegrationsHackerRank
    | IntegrationsHarver
    | IntegrationsHireflix
    | IntegrationsHireRight
    | IntegrationsHumaans
    | IntegrationsIndeed
    | IntegrationsIntellihr
    | IntegrationsInternsProgramme
    | IntegrationsIrishJobsBoard
    | IntegrationsItrent
    | IntegrationsJotForm
    | IntegrationsLinkedIn
    | IntegrationsMevitae
    | IntegrationsMyInterview
    | IntegrationsNaukri
    | IntegrationsNhsJobs
    | IntegrationsOpenAi
    | IntegrationsOpenHr
    | IntegrationsOidc
    | IntegrationsOracle
    | IntegrationsPeoplehr
    | IntegrationsPinpointApi
    | IntegrationsPredictiveIndex
    | IntegrationsReed
    | IntegrationsRefNow
    | IntegrationsRippling
    | IntegrationsSagePeople
    | IntegrationsSaml
    | IntegrationsSapling
    | IntegrationsScim
    | IntegrationsSeek
    | IntegrationsSftpCsvTransfer
    | IntegrationsSharePoint
    | IntegrationsSlack
    | IntegrationsShl
    | IntegrationsSonru
    | IntegrationsSterling
    | IntegrationsTestGorilla
    | IntegrationsTotalJobs
    | IntegrationsTrustId
    | IntegrationsTwilio
    | IntegrationsUcheck
    | IntegrationsYoti
    | IntegrationsZapier
    | IntegrationsZipRecruiter
    | IntegrationsZinc;

  @Attr() public type: keyof typeof INTEGRATION_TYPES;
  @Attr() public title: string;
  @Attr() public configured: boolean;
  @Attr() public enabled: boolean;

  @Attr({ persist: false }) public readonly companyId: string;

  /** @extraAttributes */
  // loaded via the integration description, but without sideloading, as it causes an error
  @Attr({ persist: false }) public readonly logo?: string;
  @Attr({ persist: false }) public readonly background?: string;
  @Attr({ persist: false }) public readonly summary?: string;
  @Attr({ persist: false }) public readonly category?: (typeof INTEGRATION_CATEGORIES)[number]['value'];
  @Attr({ persist: false }) public readonly canEdit?: boolean;

  /** @relationships */
  @BelongsTo() public company: Company;
}

@Model()
export class IntegrationPlugin extends ApplicationRecord {
  public static jsonapiType = 'integration_plugins';

  @Attr() public configurationFormFieldValues: ConfigurationFormFieldValuesSchema['values'];
  @Attr({ persist: false }) public readonly enabled: boolean;
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly logoBase64: string;
  @Attr({ persist: false }) public readonly configurationFormFields: FrontendConfigurationFormFieldItemArray['values'];
}

@Model()
export class IntegrationsAdonisHr extends Integration {
  public static jsonapiType = 'integrations_adonis_hr';
  public staticType = 'AdonisHr' as const;

  @Attr() public login: string;
  @Attr() public maskedLogin: string;
  @Attr() public password: string;
  @Attr() public maskedPassword: string;
  @Attr() public aiwsUrl: string;
  @Attr() public cpwsUrl: string;
  @Attr() public selectedDataMappingConfiguration: string;
}

@Model()
export class IntegrationsAdpWorkforceNow extends Integration {
  public static jsonapiType = 'integrations_adp_workforce_now';
  public staticType = 'AdpWorkforceNow' as const;

  @Attr() public hideHireDateField: boolean;
  @Attr() public hideJobField: boolean;
  @Attr() public hideMobilePhoneNumberField: boolean;
  @Attr() public hideAddressFields: boolean;
  @Attr() public hideDateOfBirthField: boolean;
  @Attr() public hideTaxFields: boolean;
  @Attr() public hideGenderField: boolean;
  @Attr() public hideRaceField: boolean;
  @Attr() public hideEthnicityField: boolean;
  @Attr() public showSpecificTemplateCountryUs: boolean;
  @Attr() public showSpecificTemplateCountryCa: boolean;
  @Attr() public showSpecificTemplateCountryInt: boolean;

  @Attr({ persist: false }) public readonly configurationToken: string;
  @Attr({ persist: false }) public readonly ooid: string;
}

@Model()
export class IntegrationsAlvaLabs extends Integration {
  public static jsonapiType = 'integrations_alva_labs';
  public staticType = 'AlvaLabs' as const;

  @Attr() public apiKey: string;
  @Attr() public webhookSignature: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
  @Attr({ persist: false }) public readonly maskedWebhookSignature: string;
}

@Model()
export class IntegrationsAppcast extends Integration {
  public static jsonapiType = 'integrations_appcast';
  public staticType = 'Appcast' as const;

  @Attr() public employerId: string;
  @Attr() public entId: string;
}

@Model()
export class IntegrationsAzureActiveDirectory extends Integration {
  public static jsonapiType = 'integrations_azure_active_directory';
  public staticType = 'AzureActiveDirectory' as const;

  @Attr() public applicationId: string;
  @Attr({ persist: false }) public readonly allowConferencing: boolean;
  @Attr() public secretValue: string;
  @Attr() public secretValueProvided: boolean;
  @Attr() public tenantId: string;
  @Attr() public useTeamsForConferencing: boolean;
}

@Model()
export class IntegrationsAzureOAuth extends Integration {
  public static jsonapiType = 'integrations_azure_o_auth';
  public staticType = 'AzureOAuth' as const;

  @Attr() public authEnforced: boolean;
  @Attr() public authEnforcedExternalRecruiter: boolean;
  @Attr() public enableRedirect: boolean;
}

@Model()
export class IntegrationsBambooHR extends Integration {
  public static jsonapiType = 'integrations_bamboo_hr';
  public staticType = 'BambooHR' as const;

  @Attr() public apiKey: string;
  @Attr() public subdomain: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsBizimply extends Integration {
  public static jsonapiType = 'integrations_bizimply';
  public staticType = 'Bizimply' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly signInLink: string;
}

@Model()
export class IntegrationsBob extends Integration {
  public static jsonapiType = 'integrations_bob';
  public staticType = 'Bob' as const;

  @Attr({ persist: false }) public testUserEmail: string;
  @Attr({ persist: false }) public integrationMethod: string;
  @Attr() public tokenizedUrl: string;
  @Attr() public secretKey: string;

  @Attr({ persist: false }) public readonly maskedSecretKey: string;
}

@Model()
export class IntegrationsBreathe extends Integration {
  public static jsonapiType = 'integrations_breathe';
  public staticType = 'Breathe' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsBryq extends Integration {
  public static jsonapiType = 'integrations_bryq';
  public staticType = 'Bryq' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsCanditech extends Integration {
  public static jsonapiType = 'integrations_canditech';
  public staticType = 'Canditech' as const;

  @Attr() public clientKey: string;

  @Attr({ persist: false }) public readonly maskedClientKey: string;
}

@Model()
export class IntegrationsCappfinity extends Integration {
  public static jsonapiType = 'integrations_cappfinity';
  public staticType = 'Cappfinity' as const;

  @Attr() public username: string;
  @Attr() public password: string;

  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsCezanne extends Integration {
  public static jsonapiType = 'integrations_cezanne';
  public staticType = 'Cezanne' as const;

  @Attr() public clientId: string;
  @Attr() public clientSecret: string;

  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsCodility extends Integration {
  public static jsonapiType = 'integrations_codility';
  public staticType = 'Codility' as const;

  @Attr() public apiKey: string;
  @Attr() public clientSecret: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsCompas extends Integration {
  public static jsonapiType = 'integrations_compas';
  public staticType = 'Compas' as const;

  @Attr() public attributeMapping: any;
  @Attr() public endpoint: string;
  @Attr() public token: string;
  @Attr({ persist: false }) public readonly maskedToken: string;
}

@Model()
export class IntegrationsCarePlanner extends Integration {
  public static jsonapiType = 'integrations_care_planner';
  public staticType = 'CarePlanner' as const;

  @Attr() public allowDocumentUploads: boolean;
  @Attr() public checklistFields: string;
  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr() public organisationNameProvided: boolean;
  @Attr() public organisationName: string;

  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsCascade extends Integration {
  public static jsonapiType = 'integrations_cascade';
  public staticType = 'Cascade' as const;

  @Attr() public password: string;
  @Attr() public url: string;
  @Attr() public username: string;
  @Attr() public filePath: string;
  @Attr() public fileName: string;
  @Attr() public port: string;
  @Attr() public maritalStatusOptions: string[];
  @Attr() public contractTypeOptions: string[];
  @Attr() public classificationOptions: string[];
  @Attr() public holidayLocationOptions: string[];
  @Attr() public payFrequencyOptions: string[];

  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsCertn extends Integration {
  public static jsonapiType = 'integrations_certn';
  public staticType = 'Certn' as const;

  @Attr() public tagFieldName: string;
  @Attr() public token: string;

  @Attr({ persist: false }) public readonly maskedToken: string;
  @Attr({ persist: false }) public readonly availablePackages: ICertnPackage[];
}

@Model()
export class IntegrationsCheckr extends Integration {
  public static jsonapiType = 'integrations_checkr';
  public staticType = 'Checkr' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly checkrAccountId: string;
  @Attr({ persist: false }) public readonly signinLink: string;
  @Attr({ persist: false }) public readonly signupLink: string;
}

@Model()
export class IntegrationsCintra extends Integration {
  public static jsonapiType = 'integrations_cintra';
  public staticType = 'Cintra' as const;

  @Attr() public integrationId: string;
  @Attr() public showEmploymentId: boolean;
}

@Model()
export class IntegrationsCreativeServicesInc extends Integration {
  public static jsonapiType = 'integrations_creative_services_inc';
  public staticType = 'CreativeServicesInc' as const;

  @Attr() public parentAccount: string;
  @Attr() public parentUsername: string;
  @Attr() public parentPassword: string;
  @Attr() public childAccount: string;
  @Attr() public childUsername: string;
  @Attr() public childPassword: boolean;
  @Attr() public billingReferenceList: string;
  @Attr() public accountNameOnBehalfOf: string;
  @Attr() public usernameOnBehalfOf: string;

  @Attr({ persist: false }) public maskedParentPassword: string;
  @Attr({ persist: false }) public maskedChildPassword: string;
}

@Model()
export class IntegrationsCriteria extends Integration {
  public static jsonapiType = 'integrations_criteria';
  public staticType = 'Criteria' as const;

  @Attr() public apiKey: string;
  @Attr() public regionUrl: string;
  @Attr({ persist: false }) public readonly regionUrlOptions: ISelectOption[];
  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsCvLibrary extends Integration {
  public static jsonapiType = 'integrations_cv_library';
  public staticType = 'CvLibrary' as const;

  @Attr() public password: string;
  @Attr() public posterEmail: string;
  @Attr() public username: string;
  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsDeel extends Integration {
  public static jsonapiType = 'integrations_deel';
  public staticType = 'Deel' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly signinLink: string;
}

@Model()
export class IntegrationsDocuSign extends Integration {
  public static jsonapiType = 'integrations_docu_sign';
  public staticType = 'DocuSign' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly signinLink: string;
  @Attr({ persist: false }) public readonly signupLink: string;
  @Attr() public selectedDocuSignCompany: string;
  @Attr() public useForOffers: boolean;
  @Attr({ persist: false }) public readonly docuSignCompanies: {
    account_id: string;
    account_name: string;
  }[];
}

@Model()
export class IntegrationsFirstAdvantage extends Integration {
  public static jsonapiType = 'integrations_first_advantage';
  public staticType = 'FirstAdvantage' as const;

  @Attr() public accountId: string;
  @Attr() public apiKey: string;
  @Attr() public clientRefVariable1: string;
  @Attr() public clientRefVariable2: string;
  @Attr() public targetEnvironment: string;
  @Attr() public userDefinedFieldVariable1: string;
  @Attr() public userDefinedFieldVariable2: string;
  @Attr() public userDefinedFieldVariable3: string;
  @Attr() public userDefinedFieldVariable4: string;
  @Attr() public userDefinedFieldVariable5: string;
  @Attr() public userDefinedFieldVariable6: string;
  @Attr() public userDefinedFieldVariable7: string;
  @Attr() public userDefinedFieldVariable8: string;
  @Attr() public userDefinedFieldVariable9: string;
  @Attr() public userDefinedFieldVariable10: string;
  @Attr() public userDefinedFieldVariable11: string;
  @Attr() public userDefinedFieldVariable12: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsFirstAdvantageKyp extends Integration {
  public static jsonapiType = 'integrations_first_advantage_kyp';
  public staticType = 'FirstAdvantageKyp' as const;

  @Attr() public customerId: string;
  @Attr() public clientId: string;
  @Attr() public clientSecret: string;

  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsGusto extends Integration {
  public static jsonapiType = 'integrations_gusto';
  public staticType = 'Gusto' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly signinLink: string;
  @Attr() public selectedGustoCompany: string;
  @Attr({ persist: false }) public readonly gustoCompanies: {
    name: string;
    uuid: string;
  }[];
}

@Model()
export class IntegrationsGoogleOAuth extends Integration {
  public static jsonapiType = 'integrations_google_o_auth';
  public staticType = 'GoogleOAuth' as const;

  @Attr() public authEnforced: boolean;
  @Attr() public authEnforcedExternalRecruiter: boolean;
  @Attr() public enableRedirect: boolean;
}

@Model()
export class IntegrationsHackerRank extends Integration {
  public static jsonapiType = 'integrations_hacker_rank';
  public staticType = 'HackerRank' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
  @Attr({ persist: false }) public readonly availableAssessments: {
    name: string;
    id: string;
  }[];
}

@Model()
export class IntegrationsHarver extends Integration {
  public static jsonapiType = 'integrations_harver';
  public staticType = 'Harver' as const;

  @Attr() public userId: string;
  @Attr() public password: string;

  @Attr({ persist: false }) public maskedPassword: string;
}

@Model()
export class IntegrationsHireflix extends Integration {
  public static jsonapiType = 'integrations_hireflix';
  public staticType = 'Hireflix' as const;

  @Attr() public apiKey: string;
  @Attr() public webhookSecretKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
  @Attr({ persist: false }) public readonly maskedWebhookSecretKey: string;
}

@Model()
export class IntegrationsHireRight extends Integration {
  public static jsonapiType = 'integrations_hire_right';
  public staticType = 'HireRight' as const;

  @Attr() public companyLogin: string;
  @Attr() public i9Enabled: boolean;
  @Attr() public i9CompanyLogin: string;
}

@Model()
export class IntegrationsHumaans extends Integration {
  public static jsonapiType = 'integrations_humaans';
  public staticType = 'Humaans' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public maskedApiKey: string;
}

@Model()
export class IntegrationsIndeed extends Integration {
  public static jsonapiType = 'integrations_indeed';
  public staticType = 'Indeed' as const;

  @Attr() public email: string;
  @Attr() public dispositionSyncEnabled: boolean;
  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr() public readonly xmlFeedUrl: string;

  @Attr({ persist: false }) public maskedClientId: string;
  @Attr({ persist: false }) public maskedClientSecret: string;
}

@Model()
export class IntegrationsIntellihr extends Integration {
  public static jsonapiType = 'integrations_intellihr';
  public staticType = 'Intellihr' as const;

  @Attr() public apiKey: string;
  @Attr() public subdomain: string;

  @Attr({ persist: false }) public maskedApiKey: string;
}

@Model()
export class IntegrationsInternsProgramme extends Integration {
  public static jsonapiType = 'integrations_interns_programme';
  public staticType = 'InternsProgramme' as const;

  @Attr() public additionalPoolingOptions: string;
  @Attr() public allowedCompanies: string;
  @Attr() public allowPooling: boolean;
  @Attr() public enabled: boolean;
  @Attr() public programmeName: string;
  @Attr() public poolingCustomFieldId: string;
  @Attr() public newUserAccessGroupId: string;
  @Attr() public apiKey: string;

  @Attr({ persist: false }) public maskedApiKey: string;
}

@Model()
export class IntegrationsIrishJobsBoard extends Integration {
  public static jsonapiType = 'integrations_irish_jobs_board';
  public staticType = 'IrishJobsBoard' as const;

  @Attr() public email: string;
  @Attr() public password: string;
  @Attr() public contactEmail: string;

  @Attr({ persist: false }) public maskedPassword: string;
}

@Model()
export class IntegrationsItrent extends Integration {
  public static jsonapiType = 'integrations_itrent';
  public staticType = 'iTrent' as const;

  @Attr() public endpointUrl: string;
  @Attr() public username: string;
  @Attr() public password: string;

  @Attr({ persist: false }) public readonly maskedPassword: string;
  @Attr({ persist: false }) public readonly webhookUrl: string;
}

@Model()
export class IntegrationsJotForm extends Integration {
  public static jsonapiType = 'integrations_jot_form';
  public staticType = 'JotForm' as const;

  @Attr() public apiKey: string;
  @Attr() public emailFieldName: string;

  @Attr({ persist: false }) public maskedApiKey: string;
}

@Model()
export class IntegrationsMevitae extends Integration {
  public static jsonapiType = 'integrations_mevitae';
  public staticType = 'Mevitae' as const;

  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr() public mevitaePrefix: string;

  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsNaukri extends Integration {
  public static jsonapiType = 'integrations_naukri';
  public staticType = 'Naukri' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsNhsJobs extends Integration {
  public static jsonapiType = 'integrations_nhs_jobs';
  public staticType = 'NhsJobs' as const;

  @Attr() public basicAuthPassword: string;

  @Attr({ persist: false }) public readonly maskedBasicAuthPassword: string;
}

@Model()
export class IntegrationsOpenAi extends Integration {
  public static jsonapiType = 'integrations_open_ai';
  public staticType = 'OpenAI' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsOpenHr extends Integration {
  public static jsonapiType = 'integrations_open_hr';
  public staticType = 'OpenHr' as const;

  @Attr() public lastPayrollNumber: number;
}

@Model()
export class IntegrationsOracle extends Integration {
  public static jsonapiType = 'integrations_oracle';

  public static formValidationSchema = Yup.object().shape({
    first_name: Yup.string().nullable().required("Can't be blank"),
    last_name: Yup.string().nullable().required("Can't be blank"),
    email: Yup.string().nullable().required("Can't be blank"),
    home_phone_number: Yup.string().nullable().required("Can't be blank"),
    date_of_birth: Yup.string().nullable().required("Can't be blank"),
    marital_status: Yup.string().nullable().required("Can't be blank"),
    national_identifier_type: Yup.string().nullable().required("Can't be blank"),
    national_identifier_id: Yup.string().nullable().required("Can't be blank"),
    contract_type: Yup.string().nullable().required("Can't be blank"),
    contract_end_date: Yup.string().nullable().required("Can't be blank"),
    preferred_name: Yup.string().nullable().required("Can't be blank"),
    effective_start_date: Yup.string().nullable().required("Can't be blank"),
    position_code: Yup.string().nullable().required("Can't be blank"),
    legal_entity: Yup.string().nullable().required("Can't be blank"),
    assignment_category: Yup.string().nullable().required("Can't be blank"),
    assignment_status: Yup.string().nullable().required("Can't be blank"),
    business_unit: Yup.string().nullable().required("Can't be blank"),
    salary_basis: Yup.string().nullable().required("Can't be blank"),
    currency: Yup.string().nullable().required("Can't be blank"),
    salary_amount: Yup.string().nullable().required("Can't be blank"),
    salary_frequency_code: Yup.string().nullable().required("Can't be blank"),
    hourly_salaried_code: Yup.string().nullable().required("Can't be blank"),
    payroll: Yup.string().nullable().required("Can't be blank"),
    // Note (LiamBorner): I've made all of the address fields required. Whilst in theory you could supply
    // no address detail at all and submit the form correctly in Oracle, I couldn't get these fields to conditionally
    // render (see models.tsx comment from Luce about +noSortEdges+)
    address_type: Yup.string().required("Can't be blank").nullable(),
    address_line_1: Yup.string().nullable().required("Can't be blank"),
    postal_code: Yup.string().nullable().required("Can't be blank"),
    city: Yup.string().nullable().required("Can't be blank"),
    // state: Yup.string().nullable().required("Can't be blank"),
    country: Yup.string().nullable().required("Can't be blank"),
    emergency_contact_first_name: Yup.string().nullable().required("Can't be blank"),
    emergency_contact_last_name: Yup.string().nullable().required("Can't be blank"),
    emergency_contact_phone_number: Yup.string().nullable().required("Can't be blank"),
    emergency_contact_relationship: Yup.string().nullable().required("Can't be blank"),
  });

  public static updateWorkerFormValidationSchema = Yup.object().shape({
    // address_type: Yup.string().required("Can't be blank").nullable(),
    // address_line_1: Yup.string().nullable().required("Can't be blank"),
    // postal_code: Yup.string().nullable().required("Can't be blank"),
    // city: Yup.string().nullable().required("Can't be blank"),
    // country: Yup.string().nullable().required("Can't be blank"),
    effective_start_date: Yup.string().nullable().required("Can't be blank"),
    position_code: Yup.string().nullable().required("Can't be blank"),
    legal_entity: Yup.string().nullable().required("Can't be blank"),
    assignment_category: Yup.string().nullable().required("Can't be blank"),
    assignment_status: Yup.string().nullable().required("Can't be blank"),
    business_unit: Yup.string().nullable().required("Can't be blank"),
    salary_basis: Yup.string().nullable().required("Can't be blank"),
    currency: Yup.string().nullable().required("Can't be blank"),
    salary_amount: Yup.string().nullable().required("Can't be blank"),
    salary_frequency_code: Yup.string().nullable().required("Can't be blank"),
    hourly_salaried_code: Yup.string().nullable().required("Can't be blank"),
    payroll: Yup.string().nullable().required("Can't be blank"),
    contract_type: Yup.string().nullable().required("Can't be blank"),
    contract_end_date: Yup.string().nullable().required("Can't be blank"),
  });

  public staticType = 'Oracle' as const;

  @Attr() public username: string;
  @Attr({ persist: false }) public maskedPassword: string;
  @Attr() public password: string;
  @Attr() public serverName: string;
}

@Model()
export class IntegrationsPeoplehr extends Integration {
  public static jsonapiType = 'integrations_peoplehr';
  public staticType = 'Peoplehr' as const;

  @Attr() public apiKey: string;
  @Attr() public apiKeyProvided: boolean;
  @Attr() public authorized: boolean;
  @Attr() onboardingLogbookDataMapping: string;
  @Attr() public onboardingDataMappingOptions: { label: string; value: string }[];
  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsPinpointApi extends Integration {
  public static jsonapiType = 'integrations_pinpoint_api';
  public staticType = 'PinpointApi' as const;

  @Attr({ persist: false }) public readonly apiKey: string;

  @HasMany() public pinpointApiKeys: PinpointApiKey[];
}

@Model()
export class IntegrationsPredictiveIndex extends Integration {
  public static jsonapiType = 'integrations_predictive_index';
  public staticType = 'PredictiveIndex' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsReed extends Integration {
  public static jsonapiType = 'integrations_reed';
  public staticType = 'Reed' as const;

  @HasMany() public reedAccounts: IntegrationAccountsReedAccount[];

  @Attr() public postingKey: string;
  @Attr() public username: string;

  @Attr({ persist: false }) public readonly maskedPostingKey: string;
}

@Model()
export class IntegrationsRefNow extends Integration {
  public static jsonapiType = 'integrations_ref_now';
  public staticType = 'RefNow' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsRippling extends Integration {
  public static jsonapiType = 'integrations_rippling';
  public staticType = 'Rippling' as const;

  @Attr({ persist: false }) public readonly authorized: boolean;
  @Attr({ persist: false }) public readonly signinLink: string;
}

@Model()
export class IntegrationsLinkedIn extends Integration {
  public static jsonapiType = 'integrations_linked_in';
  public staticType = 'LinkedIn' as const;

  @Attr() public rscEnabled: boolean;
  @HasMany() public linkedInCompanies: LinkedInCompany[];
}

@Model()
export class LinkedInCompany extends ApplicationRecord {
  public static jsonapiType = 'linked_in_companies';

  public static validationSchema = Yup.object().shape<Shape<LinkedInCompany>>({
    linkedInCompanyId: Yup.string().required("Can't be blank."),
    linkedInCompanyName: Yup.string().required("Can't be blank."),
  });

  @Attr() public linkedInAppId: string;
  @Attr({ persist: false }) public readonly linkedInClientId: string;
  @Attr() public linkedInClientSecret: string;
  @Attr() public linkedInCompanyId: string;
  @Attr() public linkedInCompanyName: string;
  @Attr({ persist: false }) public readonly premiumPostingsEnabled: boolean;
  @Attr({ persist: false }) public readonly rscEnabled: boolean;
  @Attr({ persist: false }) public readonly error: boolean;
  @Attr({ persist: false }) public readonly errorMessage: string;

  @BelongsTo() public company: Company;
}

@Model()
export class IntegrationsSagePeople extends Integration {
  public static jsonapiType = 'integrations_sage_people';
  public staticType = 'SagePeople' as const;

  @Attr() public password: string;
  @Attr() public url: string;
  @Attr() public username: string;
  @Attr() public port: string;
  @Attr() public workLocations: string[];
  @Attr() public currencyOptions: string[];
  @Attr() public basisOptions: string[];
  @Attr() public hrDepartments: string[];
  @Attr() public divisions: string[];
  @Attr() public departments: string[];
  @Attr() public periodOptions: string[];
  @Attr() public teamOptions: string[];

  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsSapling extends Integration {
  public static jsonapiType = 'integrations_sapling';
  public staticType = 'Sapling' as const;

  @Attr() public apiKey: string;
  @Attr() public subdomain: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsSaml extends Integration {
  public static jsonapiType = 'integrations_saml';
  public staticType = 'Saml' as const;

  @Attr() public metadata: string;
  @Attr() public authEnforced: boolean;
  @Attr() public authEnforcedExternalRecruiter: boolean;
  @Attr() public enableRedirect: boolean;
  @Attr() public providerName: string;

  @Attr() public authnRequestsSigned: boolean;
  @Attr() public logoutRequestsSigned: boolean;
  @Attr() public logoutResponsesSigned: boolean;
  @Attr() public wantAssertionsSigned: boolean;
  @Attr() public metadataSigned: boolean;
  @Attr() public checkIdpCertExpiration: boolean;
  @Attr() public checkSpCertExpiration: boolean;

  @Attr({ persist: false }) public readonly entityId: string;
  @Attr({ persist: false }) public readonly acsUrl: string;
  @Attr({ persist: false }) public readonly loginUrl: string;
  @Attr({ persist: false }) public readonly certificate: string;
}

export namespace IntegrationsSeek {
  export namespace Stats {
    export type SalaryIntervalCode = 'Hour' | 'Month' | 'Year';
    export type WorkType = 'Casual' | 'ContractTemp' | 'FullTime' | 'PartTime';
    export type BasisCode = 'Hourly' | 'Salaried' | 'SalariedPlusCommission';
    export type mappings = {
      work_types?: Record<string, WorkType>;
    };
  }
}

@Model()
export class IntegrationsScim extends Integration {
  public static jsonapiType = 'integrations_scim';
  public staticType = 'Scim' as const;

  @Attr() public scimGroupNamesForSync: string[];
  @Attr() public disableAutomaticSync: boolean;

  @Attr({ persist: false }) public readonly bearerToken: string;
  @Attr({ persist: false }) public readonly scimBaseUrl: string;
  @Attr({ persist: false }) public readonly availableScimGroupNames: { value: string; label: string }[];
}

@Model()
export class IntegrationsSeek extends Integration {
  public static jsonapiType = 'integrations_seek';
  public staticType = 'Seek' as const;

  @Attr() public hirerId: string;

  @HasMany() public seekAccounts: IntegrationAccountsSeekAccount[];

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly browserToken: string;
}

@Model()
export class IntegrationsSftpCsvTransfer extends Integration {
  public static jsonapiType = 'integrations_sftp_csv_transfer';
  public staticType = 'SftpCsvTransfer' as const;

  @Attr() public password: string;
  @Attr() public url: string;
  @Attr() public username: string;
  @Attr() public filePath: string;
  @Attr() public fileName: string;
  @Attr() public port: string;

  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsSharePoint extends Integration {
  public static jsonapiType = 'integrations_share_point';
  public staticType = 'SharePoint' as const;

  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr() public documentsSyncDriveId: string;
  @Attr() public documentsSyncEnabled: boolean;
  @Attr() public documentsSyncFolderId: string;
  @Attr() public documentsSyncSiteId: string;
  @Attr() public tenantId: string;

  @Attr({ persist: false }) public readonly maskedClientSecret: string;
}

@Model()
export class IntegrationsOidc extends Integration {
  public static jsonapiType = 'integrations_oidc';
  public staticType = 'Oidc' as const;

  @Attr() public host: string;
  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr({ persist: false }) public readonly clientSecretProvided: boolean;
  @Attr({ persist: false }) public readonly loginUrl: string;
  @Attr({ persist: false }) public readonly redirectUrl: string;
}

@Model()
export class IntegrationsSlack extends Integration {
  public static jsonapiType = 'integrations_slack';
  public staticType = 'Slack' as const;

  @Attr() public notifications: boolean;
  @Attr() public accessToken: string;
}

@Model()
export class IntegrationsShl extends Integration {
  public static jsonapiType = 'integrations_shl';
  public staticType = 'Shl' as const;

  @Attr() public username: string;
  @Attr() public password: string;

  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsTestGorilla extends Integration {
  public static jsonapiType = 'integrations_test_gorilla';
  public staticType = 'TestGorilla' as const;

  @Attr() public username: string;
  @Attr() public password: string;

  @Attr({ persist: false }) public readonly maskedPassword: string;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly availableAssessments: {
    name: string;
    id: string;
  }[];
}

@Model()
export class IntegrationsYoti extends Integration {
  public static jsonapiType = 'integrations_yoti';
  public staticType = 'Yoti' as const;

  @Attr() public sdkId: string;
  @Attr() public serviceId: string;
  @Attr() public pemPrivateKey: string;

  @Attr({ persist: false }) public readonly maskedPemPrivateKey: string;
}

@Model()
export class IntegrationsUcheck extends Integration {
  public static jsonapiType = 'integrations_ucheck';
  public staticType = 'Ucheck' as const;

  @Attr() public apiKey: string;
  @Attr() public useUatEnvironment: boolean;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsMyInterview extends Integration {
  public static jsonapiType = 'integrations_my_interview';
  public staticType = 'MyInterview' as const;

  @Attr() public apiKey: string;
  @Attr() public secretKey: string;
  @Attr() public backendClientUid: string;
  @Attr() public backendApiKey: string;
  @Attr() public backendSecretKey: string;

  @Attr({ persist: false }) public readonly maskedSecretKey: string;
  @Attr({ persist: false }) public readonly maskedBackendSecretKey: string;
}

@Model()
export class IntegrationsSonru extends Integration {
  public static jsonapiType = 'integrations_sonru';
  public staticType = 'Sonru' as const;

  @Attr() public apiKey: string;
  @Attr() public defaultCompanyDescription: string;
  @Attr() public defaultInterviewDescription: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsSterling extends Integration {
  public static jsonapiType = 'integrations_sterling';

  public staticType = 'Sterling' as const;

  @Attr() public billCodeVariable: string;
  @Attr() public clientId: string;
  @Attr() public clientSecret: string;
  @Attr() public overrideBillCodeVariable: boolean;
  @Attr() public region: string;
  @Attr() public selectedAccounts: string[];
  @Attr() public targetEnvironment: string;
  @Attr({ persist: false }) public readonly maskedClientSecret: string;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly accounts: {
    accountName: string;
    accountId: string;
  }[];
}

@Model()
export class IntegrationsTalent extends Integration {
  public static jsonapiType = 'integrations_talent';
  public staticType = 'Talent' as const;

  @Attr() public readonly xmlFeedUrl: string;
}

@Model()
export class IntegrationsTotalJobs extends Integration {
  public static jsonapiType = 'integrations_total_jobs';
  public staticType = 'TotalJobs' as const;

  @Attr() public email: string;
  @Attr() public firstName: string;
  @Attr() public liveDays: number;
  @Attr() public password: string;
  @Attr() public username: string;
  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsTrustId extends Integration {
  public static jsonapiType = 'integrations_trust_id';
  public staticType = 'TrustId' as const;

  @Attr() public username: string;
  @Attr() public password: string;
  @Attr({ persist: false }) public readonly maskedPassword: string;
}

@Model()
export class IntegrationsTwilio extends Integration {
  public static jsonapiType = 'integrations_twilio';
  public staticType = 'Twilio' as const;

  public static validationSchema = Yup.object().shape({
    businessName: Yup.string().required("Can't be blank.").nullable(),
    businessType: Yup.string().required("Can't be blank.").nullable(),
    businessCompanyType: Yup.string().required("Can't be blank.").nullable(),
    businessStockExchange: Yup.string().when('businessCompanyType', {
      is: (value: string) => value && value === 'public',
      otherwise: Yup.string().nullable(),
      then: Yup.string().nullable().required('Stock exchange is required for publicly traded companies'),
    }),
    businessStockTicker: Yup.string().when('businessCompanyType', {
      is: (value: string) => value && value === 'public',
      otherwise: Yup.string().nullable(),
      then: Yup.string()
        .nullable()
        .required('Stock ticker code is required for publicly traded companies')
        .max(4, 'Max length is 4 characters.'),
    }),
    businessRegistrationNumber: Yup.string().when('businessRegistrationIdentifier', {
      is: (value: string) => value && value === 'EIN',
      otherwise: Yup.string().required("Can't be blank.").nullable(),
      then: Yup.string()
        .matches(/^\d{2}-\d{7}$/, 'Please enter a valid EIN in the format XX-XXXXXXX')
        .required('EIN is required')
        .nullable(),
    }),
    businessRegistrationIdentifier: Yup.string().required("Can't be blank").nullable(),
    businessRegionsOfOperation: Yup.array()
      .of(Yup.string())
      .required('At least one region must be selected')
      .nullable(),
    businessAddressOne: Yup.string().required("Can't be blank.").nullable(),
    businessCity: Yup.string().required("Can't be blank.").nullable(),
    businessState: Yup.string().required("Can't be blank.").nullable(),
    businessCountry: Yup.string().required("Can't be blank.").nullable(),
    businessPostcode: Yup.string().required("Can't be blank.").nullable(),
    businessIndustry: Yup.string().required("Can't be blank.").nullable(),
    businessWebsite: Yup.string().required("Can't be blank.").nullable(),
    contactName: Yup.string().required("Can't be blank.").nullable(),
    contactEmail: Yup.string().required("Can't be blank.").nullable(),
    contactPhoneNumber: Yup.number().required("Can't be blank.").nullable().typeError('Must be a number'),
    contactTitle: Yup.string().required("Can't be blank.").nullable(),
    contactPosition: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public accountSid: string;
  @Attr() public accountType: 'managed' | 'provided';
  @Attr() public authToken: string;
  @Attr() public authToken_Hidden: boolean;
  @Attr({ persist: false }) public readonly availableCountryCodes: string[];
  @Attr({ persist: false }) public readonly phoneNumbers: {
    GB?: { number: string };
    US?: { number: string };
  };
  @Attr() public requiresBrandRegistration: boolean;
  @Attr() public subaccountName: string;
  @Attr() public businessName: string;
  @Attr() public businessType:
    | 'partnership'
    | 'limited liability corporation'
    | 'co-operative'
    | 'non-profit corporation'
    | 'corporation';
  @Attr() public businessCompanyType: 'public' | 'private' | 'non-profit' | 'government';
  @Attr() public businessRegistrationNumber: string;
  @Attr() public businessStockExchange: string;
  @Attr() public businessStockTicker: string;
  @Attr() public businessRegistrationIdentifier:
    | 'EIN'
    | 'DUNS'
    | 'CCN'
    | 'CN'
    | 'ACN'
    | 'CIN'
    | 'VAT'
    | 'VATRN'
    | 'RN'
    | 'OTHER';
  @Attr() public businessRegionsOfOperation: string[];
  @Attr() public businessAddressOne: string;
  @Attr() public businessAddressTwo: string;
  @Attr() public businessCity: string;
  @Attr() public businessState: string;
  @Attr() public businessCountry: string;
  @Attr() public businessPostcode: string;
  @Attr() public businessIndustry:
    | 'AUTOMOTIVE'
    | 'AGRICULTURE'
    | 'BANKING'
    | 'CONSUMER'
    | 'EDUCATION'
    | 'ENGINEERING'
    | 'ENERGY'
    | 'OIL_AND_GAS'
    | 'FAST_MOVING_CONSUMER_GOODS'
    | 'FINANCIAL'
    | 'FINTECH'
    | 'FOOD_AND_BEVERAGE'
    | 'GOVERNMENT'
    | 'HEALTHCARE'
    | 'HOSPITALITY'
    | 'INSURANCE'
    | 'LEGAL'
    | 'MANUFACTURING'
    | 'MEDIA'
    | 'ONLINE'
    | 'RAW_MATERIALS'
    | 'REAL_ESTATE'
    | 'RELIGION'
    | 'RETAIL'
    | 'JEWELRY'
    | 'TECHNOLOGY'
    | 'TELECOMMUNICATIONS'
    | 'TRANSPORTATION'
    | 'TRAVEL'
    | 'ELECTRONICS'
    | 'NOT_FOR_PROFIT';
  @Attr() public businessWebsite: string;
  @Attr() public linkedinUrl: string;
  @Attr() public twitterUrl: string;
  @Attr() public facebookUrl: string;
  @Attr() public instagramUrl: string;
  @Attr() public contactName: string;
  @Attr() public contactEmail: string;
  @Attr() public contactPhoneNumber: string;
  @Attr() public contactPhoneNumberIso2: string;
  @Attr() public contactTitle: string;
  @Attr() public contactPosition: string;
}

@Model()
export class IntegrationsZapier extends Integration {
  public static jsonapiType = 'integrations_zapier';
  public staticType = 'Zapier' as const;

  @Attr() public apiKey: string;
  @Attr() public subdomain: string;
}

@Model()
export class IntegrationsZinc extends Integration {
  public static jsonapiType = 'integrations_zinc';
  public staticType = 'Zinc' as const;

  @Attr() public apiKey: string;
  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class IntegrationsZipRecruiter extends Integration {
  public static jsonapiType = 'integrations_zip_recruiter';
  public staticType = 'ZipRecruiter' as const;

  @Attr() public apiKey: string;

  @Attr({ persist: false }) public readonly maskedApiKey: string;
}

@Model()
export class Interview extends ApplicationRecord {
  public static jsonapiType = 'interviews';

  public static createReschedulingInterview = (interview: Interview, newAttributes: Partial<Interview>) => {
    const newInterview = new Interview({
      ...interview,
      ...newAttributes,
      id: null,
      reschedulingFromId: interview.id,
      manualCreation: true,
    });

    newInterview.interviewParts = interview.interviewParts.map((interviewPart) => {
      const newInterviewPart = new InterviewPart({
        ...interviewPart,
        id: null,
        interviewId: null,
      });

      if (interviewPart.cronofyHost) {
        newInterviewPart.cronofyHost = new CronofyHost({
          ...interviewPart.cronofyHost,
          id: null,
          interviewId: null,
        });
      }

      newInterviewPart.usersInterviewPartables = interviewPart.usersInterviewPartables.map((usersInterviewPartable) => {
        const newUsersInterviewPartable = new UsersInterviewPartable({
          ...usersInterviewPartable,
          id: null,
          interviewPartId: null,
        });
        return newUsersInterviewPartable;
      });
      return newInterviewPart;
    });

    if (interview.cronofyHost) {
      newInterview.cronofyHost = new CronofyHost({
        ...interview.cronofyHost,
        id: null,
        interviewId: null,
      });
    }

    newInterview.interviewType = interview.interviewType;
    newInterview.interviewable = interview.interviewable;
    return newInterview;
  };

  get noShow(): boolean {
    return this.presence === 'no_show';
  }

  @Attr() public emailTemplate: {
    bcc: string[];
    cc: string[];
    body: string;
    salutation: string;
    subject: string;
    signature: string;
  };
  @Attr() public interviewableId: string;
  @Attr() public interviewableType: string;
  @Attr() public manualCreation: boolean;
  @Attr() public retroactiveManualCreation: boolean;
  @Attr() public duration: number;
  @Attr() public presence: string;
  @Attr() public stageId: string;
  @Attr() public startAt: string;
  @Attr() public timeZone: string;
  @Attr() public customDuration: number;
  @Attr({ persist: false }) public readonly humanisedCustomDurationLong: string;
  @Attr({ persist: false }) public readonly humanisedCustomDurationShort: string;

  @Attr({ persist: false }) public readonly allDay: boolean;
  @Attr({ persist: false }) public attendees: { id: string; firstName: string; lastName: string }[];
  @Attr({ persist: false }) public readonly endAt: string;
  @Attr({ persist: false }) public readonly endAtUtc: string;
  @Attr({ persist: false }) public readonly startAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly startAtHourAndMinute: string;
  @Attr({ persist: false }) public readonly startAtShortWithTime: string;
  @Attr({ persist: false }) public readonly startAtUtc: string;
  @Attr({ persist: false }) public readonly summary: string;
  @Attr({ persist: false }) public readonly addedRetroactively: boolean;
  // Used to determine which interview to reschedule from
  @Attr() public reschedulingFromId: string;
  @Attr() public resendConfirmation: boolean;

  @Attr() public documents: string[];
  @Attr() public tagsWithContext: TagWithContext[];

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly candidateName: string;
  @Attr({ persist: false }) public readonly hasPassed: boolean;
  @Attr({ persist: false }) public readonly jobId: string;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr({ persist: false }) public readonly showPath: string;
  @Attr({ persist: false }) public readonly stageName: string;
  @Attr({ persist: false }) public readonly startAtIsInPast: boolean;
  @Attr({ persist: false }) public readonly endAtIsInPast: boolean;
  @Attr({ persist: false }) public readonly daysUntilStartAt: number;
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;

  /**
   * @deprecated (TomLuce) This application association should be removed as the table is now polymorphic
   */
  @BelongsTo() public application: Application;
  @BelongsTo() public interviewable: Application | JobSeeker;
  @BelongsTo() public interviewType: InterviewType;
  @BelongsTo() public interviewInvite: InterviewInvite;
  @BelongsTo() public stage: Stage;
  @HasOne() public cronofyHost: CronofyHost;
  @HasMany() public interviewParts: InterviewPart[];
  @HasMany() public interviewConfirmations: InterviewConfirmation;
}

export namespace Interview {
  export namespace Stats {
    export type total_count = number;
    export type average_interview_team_members = number;
    export type percentage_hired = number;
    export type by_interview_type_interview_type_count = { key: string; value: number; label: string }[];
    export type by_interview_type_name_count = { [sourceName: string]: number };
    export type by_time_period_count = {
      allTime: number;
      thisYear: number;
      thisMonth: number;
      thisWeek: number;
    };
    export type options = {
      interview_types: { value: string; label: string }[];
    };
  }
}

@Model()
export class InterviewConfirmation extends ApplicationRecord {
  public static jsonapiType = 'interview_confirmations';

  @Attr() public status: string;
  @Attr() public updatedAtShortWithYearTimeWithZone: string;
}

@Model()
export class InterviewInvite extends ApplicationRecord {
  public static jsonapiType = 'interview_invites';

  public static selfSchedulingTypes = [
    { value: 'availability', label: "Interviewing team members' calendar availability" },
    { value: 'defined', label: 'Manually defined interview slots' },
  ];

  public static schedulingUpdateValidationSchema = Yup.object().shape({
    schedulePeriodEndOn: Yup.date().min(new Date(), 'Deadline must be in the future').nullable(),
  });

  @Attr({ persist: false }) public readonly attendees: { id: string; firstName: string; lastName: string }[];
  @Attr() public createdAt: string;
  @Attr() public createdAtUtc: string;
  @Attr() public createdAtShortWithYearTimeWithZone: string;
  @Attr() public customDuration: number;
  @Attr() public invitableId: number;
  @Attr() public interviewStatus: 'invited' | 'scheduled' | 'in_progress' | 'completed';
  @Attr() public invitableType: string;
  @Attr() public stageId: string;
  @Attr() public schedulePeriodEndOn: string;
  @Attr() public schedulePeriodStartOn: string;
  @Attr() public schedulePeriodTimeZone: string;
  @Attr() public schedulingType: string;
  @Attr({ persist: false }) public readonly humanisedCustomDurationLong: string;
  @Attr({ persist: false }) public readonly humanisedCustomDurationShort: string;

  /** @attrAccessors */
  @Attr() public rescheduling: boolean;
  @Attr() public resending: boolean;
  @Attr() public cancelAndNotify: boolean;
  @Attr() public reschedulingInterviewId: string;

  /** @attrAccessors */
  @Attr() public bcc: string[];
  @Attr() public cc: string[];
  @Attr() public body: string;
  @Attr() public salutation: string;
  @Attr() public signature: string;
  @Attr() public subject: string;
  @Attr() public tagsWithContext: TagWithContext[];
  @Attr() public documents: string[];

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly inviteLink: string;
  @Attr({ persist: false }) public readonly outboundEmailEvents: { data: IOutboundEmailEvent[] };
  @Attr({ persist: false }) public readonly outboundEmailStatus: string;

  @BelongsTo() public interviewType: InterviewType;
  @BelongsTo() public stage: Stage;
  @BelongsTo() public invitable: Application | JobSeeker;
  @HasOne() public cronofyHost: CronofyHost;
  @HasOne() public interview: Interview;
  @HasMany() public interviewInviteParts: InterviewInvitePart[];
}

@Model()
export class UsersInterviewPartable extends ApplicationRecord {
  public static jsonapiType = 'users_interview_partables';
  @Attr() public companyId: string;
  @Attr() public createCronofy: boolean;
  @Attr() public restrictedCronofyScope: boolean;
  @Attr() public userId: string;
  @Attr() public partableId: string;
  @Attr() public partableType: string;
  @BelongsTo() public user: User;
  @HasOne() public cronofyHost: CronofyHost;
}

@Model()
export class InterviewPart extends ApplicationRecord {
  public static jsonapiType = 'interview_parts';
  @Attr() public conferencing: boolean;
  @Attr() public interviewTypePartId: string;
  @Attr() public position: number;
  @Attr() public removingUserIds: string[];
  @Attr() public startAt: string;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly interviewId: string;

  @BelongsTo() public interview: Interview;
  @BelongsTo() public interviewTypePart: InterviewTypePart;
  @HasOne() public cronofyHost: CronofyHost;
  @HasOne(ConferencingHost) public conferencingHost: ConferencingHost;
  @HasMany() public usersInterviewPartables: UsersInterviewPartable[];
  @HasOne() public cronofyResourcesInterviewPartable: CronofyResourcesInterviewPartable;
}

@Model()
export class InterviewInvitePart extends ApplicationRecord {
  public static jsonapiType = 'interview_invite_parts';

  public static requiredParticipantOptions = [
    { label: 'All members', value: 'all' },
    { label: 'Select the number of interviewers required', value: 'defined' },
  ];

  @Attr() public readonly interviewInviteId: string;
  @Attr() public readonly interviewTypePartId: string;
  @Attr() public readonly interviewerCount: number;
  @Attr() public readonly requiredParticipants: string;

  @BelongsTo() public interviewInvite: InterviewInvite;
  @HasOne() public cronofyHost: CronofyHost;
  @HasOne() public conferencingHost: ConferencingHost;
  @BelongsTo() public interviewTypePart: InterviewTypePart;
  @HasMany() public usersInterviewPartables: UsersInterviewPartable[];
  @HasMany() public cronofyResourcesInterviewPartables: CronofyResourcesInterviewPartable[];
}

@Model()
export class InterviewOpening extends ApplicationRecord {
  public static jsonapiType = 'interview_openings';

  @Attr() public startAt: string;
  @Attr() public endAt: string;
  @Attr() public startAtUtc: string;
  @Attr() public endAtUtc: string;
  @Attr() public interviewableType: string;
  @Attr() public interviewableId: string;
  @Attr() public timeZone: string;
  @Attr() public allDay: boolean;
}

@Model()
export class InterviewTeamMember extends ApplicationRecord {
  public static jsonapiType = 'interview_team_members';

  @Attr() public userId: string;
  @Attr() public interviewableId: string;
  @Attr() public interviewableType: string;
  @Attr() public interviewTypePartId: string;
  @BelongsTo() public stage: Stage;
  @BelongsTo() public user: BypassUser;
}

@Model()
export class ScorecardableScorecard extends ApplicationRecord {
  public static jsonapiType = 'scorecardable_scorecards';

  @Attr() public scorecardId: string;
  @Attr() public scorecardableId: string;
  @Attr() public scorecardableType: string;
  @Attr() public updatedAt: string;
  @BelongsTo() public scorecard: Scorecard;
  @BelongsTo() public scorecardable: Stage | TalentPipeline;
}

@Model()
export class InterviewTextConfiguration extends ApplicationRecord {
  public static jsonapiType = 'interview_text_configurations';

  public static reminderOptions = [
    { value: 'twoDaysBefore', name: 'twoDaysBefore', label: 'Two days before' },
    { value: 'oneDayBefore', name: 'oneDayBefore', label: 'One day before' },
    { value: 'fourHoursBefore', name: 'fourHoursBefore', label: 'Four hours before' },
    { value: 'oneHourBefore', name: 'oneHourBefore', label: 'One hour before' },
  ];

  @Attr() public stageId: string;
  @Attr() public sendConfirmation: string;
  @Attr() public twoDaysBefore: boolean;
  @Attr() public oneDayBefore: boolean;
  @Attr() public fourHoursBefore: boolean;
  @Attr() public oneHourBefore: boolean;
  @Attr() public inactive: boolean;
}

@Model()
export class InterviewType extends MobilityApplicationRecord {
  public static jsonapiType = 'interview_types';

  public static schedulingOptions = [
    { value: 'selfScheduled', label: 'Candidate picks a time' },
    { value: 'prescribed', label: 'You pick a time for the candidate' },
  ] as const;

  public static basicValidationSchema = Yup.object()
    .nullable()
    .shape({
      name: Yup.string().nullable().required("Can't be blank."),
    });

  public static validationSchema = Yup.object()
    .nullable()
    .shape({
      allDay: Yup.boolean().required("Can't be blank."),
      candidateInstructions: Yup.string()
        .nullable()
        .required("Can't be blank")
        .max(3500, 'Max length is 3500 characters.'),
      hiringManagerInstructions: Yup.string().nullable().max(3500, 'Max length is 3500 characters.'),
      interviewType: Yup.string().nullable().required("Can't be blank"),
      name: Yup.string().nullable().required("Can't be blank."),
    });

  public static interviewTypeOptions = [
    { value: 'face_to_face', label: 'Face to face' },
    { value: 'phone', label: 'Phone' },
    { value: 'video', label: 'Video' },
  ];

  public static newInterviewTypeOptions = [
    { value: 'face_to_face', label: 'Face to face' },
    { value: 'phone', label: 'Phone' },
    { value: 'video', label: 'Video (external)' },
    { value: 'conferencing', label: 'Video Conferencing (Pinpoint)' },
  ];

  get isFaceToFace(): boolean {
    return this.interviewType === 'face_to_face';
  }

  @Attr() public allDay: boolean;
  @Attr() public companyId: string;
  @Attr() public name: string;
  @Attr() public interviewType: string;
  @Attr() public duration: number;
  @Attr() public location: string;
  @Attr() public candidateInstructions: string;
  @Attr() public hiringManagerInstructions: string;
  @Attr() public candidateDocuments: string[];
  @Attr() public hiringManagerDocuments: string[];
  @Attr() public numOfInterviewers: number;
  /** @extraAttributes */
  @Attr() public candidateInstructionsI18n: string;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly humanizedType: string;
  @Attr({ persist: false }) public readonly candidateDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly hiringManagerDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly humanisedDurationLong: string;
  @Attr({ persist: false }) public readonly humanisedDurationShort: string;
  // @unpersistedAttributes
  @Attr({ persist: false }) public splitInterview: boolean;

  @HasMany() public interviewTypeParts: InterviewTypePart[];
  @BelongsTo() public interviewInviteTemplate: EmailTemplate;
  @BelongsTo() public interviewConfirmationTemplate: EmailTemplate;
  @BelongsTo() public recruiterInterviewInviteTemplate: EmailTemplate;
  @BelongsTo() public recruiterInterviewConfirmationTemplate: EmailTemplate;
  @HasOne() public scopeCondition: ScopeCondition;
}

@Model()
export class InterviewTypePart extends ApplicationRecord {
  public static jsonapiType = 'interview_type_parts';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });

  @Attr() public companyId: string;
  @Attr() public name: string;
  @Attr() public numOfInterviewers: number;
  @Attr() public interviewTypeId: string;
  @Attr() public position: number;
  @Attr() public duration: number;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public readonly humanisedDurationShort: string;
  @Attr({ persist: false }) public readonly humanisedDurationLong: string;

  @BelongsTo() public interviewType: InterviewType;
  @HasMany() public interviewTeamMembers: InterviewTeamMember[];
}

@Model()
export class InvitesJobSeeker extends ApplicationRecord {
  public static jsonapiType = 'invites_job_seekers';
  @Attr({ persist: false }) public createdAtShortWithYearTimeWithZone: string;

  @BelongsTo() public jobSeeker: JobSeeker;
}

@Model()
export class JobHiringWorkflowReassignment extends ApplicationRecord {
  public static jsonapiType = 'job_hiring_workflow_reassignments';

  @Attr() public jobId: string;
  @Attr() public hiringWorkflowId: string;
  @Attr() public stagesMap: Record<'from' | 'to', string>[];
}

export interface IJobFilter {
  name: string;
  type: 'boolean' | 'multiple_choice' | 'tags_list' | 'countries' | 'skills' | 'channels' | 'agencies' | 'states';
  question_id: number;
  options: ISelectOption[];
}

@Model()
export class Job extends MobilityApplicationRecord {
  public static MAX_PER = 10000;
  get isInternal(): boolean {
    return this.visibility === 'internal';
  }

  get isOpen(): boolean {
    return this.status === 'open';
  }

  get isExternal(): boolean {
    return this.visibility === 'external';
  }

  get isDraft(): boolean {
    return this.status === 'draft';
  }

  get isFilled(): boolean {
    return this.status === 'filled';
  }

  get isPublished(): boolean {
    return this.isPersisted && this.status !== 'draft';
  }

  public static resumeOptions: ISelectOption[] = [
    { value: 'optional', label: 'Shown and optional' },
    { value: 'required', label: 'Shown and required' },
    { value: 'not_required', label: 'Hidden' },
  ];

  public static jsonapiType = 'jobs';
  public static DEFAULT_HIRING_WORKFLOW_ID = '-1' as const;
  public static DUPLICATE_HIRING_WORKFLOW_ID = '0' as const;

  /**
   * NOTE: (TomLuce) If you're wondering what the +noSortEdges+ array is at the end of the validation schema:
   *
   * It's a ugly escape hatch for dealing with cyclical dependencies.
   * In our case we need it for the compensation validations.
   *
   * Generally fields are topographically sorted so they can be resolved in the correct order
   * i.e. field A resolves before field B if field B depends on it.
   *
   * In some cases though the order doesn't matter, because the dependency is purely for the sake of validation,
   * which all happen after all casting is done. In those cases it's fine if there are cycles between fields,
   * and you can manually opt out of the ordering, if you are sure that it's not needed.
   *
   * The reason it's not documented is that its a bad API and really easy to shoot one's self in the foot with it.
   */
  public static compensationValidations = {
    compensationCurrency: Yup.string().when(['compensationFrequency', 'compensationMaximum', 'compensationMinimum'], {
      is: (compensationFrequency: unknown, compensationMaximum: unknown, compensationMinimum: unknown) =>
        !!compensationFrequency || !!compensationMaximum || !!compensationMinimum,
      otherwise: Yup.string().nullable(),
      then: Yup.string().required("Can't be blank").nullable(),
    }),
    compensationFrequency: Yup.string().when(['compensationCurrency', 'compensationMaximum', 'compensationMinimum'], {
      is: (compensationCurrency: unknown, compensationMaximum: unknown, compensationMinimum: unknown) =>
        !!compensationCurrency || !!compensationMaximum || !!compensationMinimum,
      otherwise: Yup.string().nullable(),
      then: Yup.string().required("Can't be blank").nullable(),
    }),
    compensationMaximum: Yup.number()
      .when(['compensationCurrency', 'compensationFrequency', 'compensationMinimum'], {
        is: (compensationCurrency: unknown, compensationFrequency: unknown, compensationMinimum: unknown) =>
          !!compensationCurrency || !!compensationFrequency || !!compensationMinimum,
        otherwise: Yup.number().nullable(),
        then: Yup.number().required("Can't be blank").min(0, "Can't be negative").nullable(),
      })
      .min(Yup.ref('compensationMinimum'), 'Must be greater than or equal to the compensation minimum'),
    compensationMinimum: Yup.number().when(['compensationCurrency', 'compensationFrequency', 'compensationMaximum'], {
      is: (compensationCurrency: unknown, compensationFrequency: unknown, compensationMaximum: unknown) =>
        !!compensationCurrency || !!compensationFrequency || !!compensationMaximum,
      otherwise: Yup.number().nullable(),
      then: Yup.number().required("Can't be blank").min(0, "Can't be negative").nullable(),
    }),
  };

  /**
   * @see +compensationValidations+ for information on the +noSortEdges+ array argument
   */
  public static draftValidationSchema = Yup.object().shape(
    {
      ...Job.compensationValidations,
      structureLinkId: Yup.string().required("Can't be blank.").nullable(),
      title: Yup.string().required("Can't be blank.").nullable(),
      referralAmount: Yup.number().typeError('Must be a number.').nullable(),
    },
    [
      ['compensationCurrency', 'compensationFrequency'],
      ['compensationCurrency', 'compensationMaximum'],
      ['compensationCurrency', 'compensationMinimum'],
      ['compensationFrequency', 'compensationMinimum'],
      ['compensationFrequency', 'compensationMaximum'],
      ['compensationMaximum', 'compensationMinimum'],
    ],
  );

  /**
   * @see +compensationValidations+ for information on the +noSortEdges+ argument
   */
  public static validationSchema({ requireHeadcount }: { requireHeadcount: boolean }) {
    return Yup.object().shape(
      {
        ...Job.compensationValidations,
        description: Yup.string().required("Can't be blank.").nullable(),
        employmentType: Yup.string().required("Can't be blank").nullable(),
        externalKeyResponsibilitiesHeader: Yup.string().required("Can't be blank").nullable(),
        headcountMaximumEnabled: requireHeadcount
          ? Yup.boolean().nullable().isTrue("Can't be disabled")
          : Yup.boolean().nullable(),
        keyResponsibilities: Yup.string().required("Can't be blank").nullable(),
        resumeRequirement: Yup.string().required("Can't be blank.").nullable(),
        selectedHiringWorkflowId: Yup.string().required("Can't be blank.").nullable(),
        structureLinkId: Yup.string().required("Can't be blank."),
        title: Yup.string().required("Can't be blank.").nullable(),
        visibility: Yup.string().required("Can't be blank.").nullable(),
        referralAmount: Yup.number().typeError('Must be a number.').nullable(),
      },
      [
        ['compensationCurrency', 'compensationFrequency'],
        ['compensationCurrency', 'compensationMaximum'],
        ['compensationCurrency', 'compensationMinimum'],
        ['compensationFrequency', 'compensationMinimum'],
        ['compensationFrequency', 'compensationMaximum'],
        ['compensationMaximum', 'compensationMinimum'],
      ],
    );
  }

  public static booleanFields = [
    'accessThroughVisibility',
    'askForAddress',
    'askForDateOfBirth',
    'askForLinkedinProfile',
    'askForMiddleName',
    'askForPreferredName',
    'blindScreening',
    'diversityMonitoring',
    'isCompensationVisible',
    'isRemoteWithLocationRestrictions',
    'notify',
    'requirePhone',
  ];

  public static duplicatedJobFields = [
    'accessThroughVisibility',
    'appcast',
    'askForAddress',
    'askForDateOfBirth',
    'askForLinkedinProfile',
    'askForMiddleName',
    'askForPreferredName',
    'benefits',
    'blindScreening',
    'compensation',
    'compensationCurrency',
    'compensationFrequency',
    'compensationMaximum',
    'compensationMinimum',
    'coverLetterRequirement',
    'customCountry',
    'customCounty',
    'customApplicationReceivedTemplateId',
    'customTown',
    'deadlineOn',
    'description',
    'documents',
    'documentsFiles',
    'employmentType',
    'experienceLevel',
    'externalBenefitsHeader',
    'externalKeyResponsibilitiesHeader',
    'externalSkillsKnowledgeExpertiseHeader',
    'facebookjobs',
    'headcountMaximum',
    'headcountMaximumEnabled',
    'hiringUserId',
    'hiringManagerName',
    'hiringManagerEmail',
    'includeInIndeed',
    'includeInLinkedinRss',
    'isCompensationVisible',
    'keyResponsibilities',
    'linkedin',
    'linkedinCompanyId',
    'monster',
    'openGraphImage',
    'personalSummaryRequirement',
    'recruitmentManagerUserId',
    'recruitmentManagerName',
    'recruitmentManagerEmail',
    'referralAmount',
    'referralCurrency',
    'referralDetails',
    'referralsEnabled',
    'reportingTo',
    'requirePhone',
    'requisitionId',
    'resumeRequirement',
    'selectedHiringWorkflowId',
    'skillsKnowledgeExpertise',
    'structureLinkId',
    'talentdotcom',
    'title',
    'visibility',
    'workplaceType',
    'ziprecruiter',
  ];

  public static jobDetailsRequiredAttributes = ['title', 'employmentType', 'locationId', 'departmentId'];

  public static jobSettingsRequiredAttributes = [
    'askForAddress',
    'askForDateOfBirth',
    'askForMiddleName',
    'askForPreferredName',
    'blindScreening',
    'coverLetterRequirement',
    'diversityMonitoring',
    'requirePhone',
    'resumeRequirement',
    'visibility',
  ];

  public static hiringWorkflowRequiredAttributes = [];
  public static jobDescriptionRequiredAttributes = [
    'description',
    'externalKeyResponsibilitiesHeader',
    'keyResponsibilities',
  ];
  public static jobApplicationFormRequiredAttributes = [];

  public static defaultAttrs: Partial<Job> = {
    accessThroughVisibility: true,
    appcast: false,
    askForLinkedinProfile: false,
    askForMiddleName: false,
    askForPreferredName: false,
    benefits: '',
    companyNameOverride: null,
    compensation: null,
    compensationCurrency: null,
    compensationFrequency: null,
    compensationMaximum: null,
    compensationMinimum: null,
    coverLetterRequirement: 'not_required',
    customCountry: '',
    customCounty: '',
    customTown: '',
    customApplicationReceivedTemplateId: null,
    deadlineOn: null,
    description: '',
    diversityMonitoring: true,
    draftJobAdditionalInformation: '',
    documents: [],
    employmentType: '',
    experienceLevel: null,
    externalBenefitsHeader: 'Benefits',
    externalKeyResponsibilitiesHeader: 'Key Responsibilities',
    externalSkillsKnowledgeExpertiseHeader: 'Skills, Knowledge and Expertise',
    facebookjobs: false,
    headcountMaximum: null,
    headcountMaximumEnabled: false,
    hiringUserId: '',
    isCompensationVisible: true,
    isRemoteWithLocationRestrictions: false,
    keyResponsibilities: '',
    linkedin: false,
    linkedinCompanyId: '',
    linkedInPremiumPosting: false,
    monster: false,
    notify: true,
    personalSummaryRequirement: 'optional',
    publishing: true,
    recruitmentManagerUserId: '',
    referralAmount: null,
    referralCurrency: '',
    referralDetails: '',
    referralsEnabled: false,
    reportingTo: '',
    requisitionId: '',
    resumeRequirement: '',
    selectedHiringWorkflowId: '',
    skillsKnowledgeExpertise: '',
    structureLinkId: '',
    talentdotcom: false,
    title: '',
    visibility: '',
    workplaceType: 'onsite',
    ziprecruiter: false,
  };

  public static NEW(attrs = {}) {
    return new Job({ ...this.defaultAttrs, ...attrs });
  }

  get isConfidential(): boolean {
    return this.visibility === 'confidential';
  }

  get isPrivate(): boolean {
    return this.visibility === 'private_job';
  }

  @Attr() public accessThroughVisibility: boolean;
  @Attr() public applicationCountByStage: [{ key: string; value: number; label: string; position: number }];
  @Attr() public askForAddress: boolean;
  @Attr() public askForDateOfBirth: boolean;
  @Attr() public askForLinkedinProfile: boolean;
  @Attr() public askForMiddleName: boolean;
  @Attr() public askForPreferredName: boolean;
  @Attr() public location: Location;
  @Attr() public benefits: string;
  @Attr() public blindScreening: boolean;
  @Attr() public companyId: string;
  @Attr() public compensation: string;
  @Attr({ persist: false }) public compensationDeprecated: string;
  @Attr() public compensationCurrency: string;
  @Attr() public compensationFrequency: string;
  @Attr() public compensationMinimum: number;
  @Attr() public compensationMaximum: number;
  @Attr() public coverLetterRequirement: string;
  @Attr() public isCompensationVisible: boolean;
  @Attr() public customTown: string;
  @Attr() public customCounty: string;
  @Attr() public customCountry: string;
  @Attr() public customApplicationReceivedTemplateId: string;
  @Attr() public description: string;
  @Attr() public discardedAt: string;
  @Attr() public diversityMonitoring: boolean;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public draftJobAdditionalInformation: string;
  // Used to store the id of the job this job is being copied from
  @Attr() public duplicateJobId: string;
  @Attr() public experienceLevel: string;
  @Attr() public externalBenefitsHeader: string;
  @Attr() public externalKeyResponsibilitiesHeader: string;
  @Attr() public externalSkillsKnowledgeExpertiseHeader: string;
  @Attr() public favouriteUserIds: string[];
  @Attr() public isRemoteWithLocationRestrictions: boolean;
  @Attr() public jobId: string;
  @Attr() public jobTemplateId: string;
  @Attr() public keyResponsibilities: string;
  @Attr() public companyNameOverride: boolean;
  @Attr() public linkedinCompanyId: string;
  @Attr() public linkedInPremiumPosting: boolean;
  @Attr() public notify: boolean;
  @Attr() public skillsKnowledgeExpertise: string;
  @Attr() public deadlineOn: string;
  @Attr() public skipDeadlineValidations: boolean;
  @Attr() public employmentType: string;
  @Attr() public referralAmount: number;
  @Attr() public referralCurrency: string;
  @Attr() public referralDetails: string;
  @Attr() public referralsEnabled: boolean;
  @Attr() public requisitionId: string;
  @Attr() public headcountMaximumEnabled: boolean;
  @Attr() public headcountMaximum: number;
  @Attr() public hiringManagerId: string;
  @Attr() public departmentId: string;
  @Attr() public personalSummaryRequirement: string;
  // Used to determine if we are trying to publish the job or not
  @Attr() public publishing: boolean;
  @Attr() public saveFromRequisition: boolean;
  @Attr() public reportingTo: string;
  @Attr() public locationId: string;
  @Attr() public status: string;
  @Attr() public daysToHire: number;
  @Attr() public daysToFill: number;

  @Attr() public title: string;
  @Attr() public hiringUserId: string;
  @Attr() public irishJobBoardShortDescription: string;
  @Attr() public irishJobBoardDetailedDescription: string;
  @Attr() public irishJobBoardJobType: string;
  @Attr() public requirePhone: boolean;
  @Attr() public resumeRequirement: string;
  @Attr() public structureLinkId: string;
  @Attr() public visibility: string;
  @Attr() public openGraphImage: string;
  @Attr() public includeInIndeed: boolean;
  @Attr() public includeInLinkedinRss: boolean;
  @Attr() public recruitmentManagerUserId: string;
  @Attr() public selectedHiringWorkflowId: string;
  @Attr() public workplaceType: string;

  @Attr() public appcast: boolean;
  @Attr() public facebookjobs: boolean;
  @Attr() public monster: boolean;
  @Attr() public linkedin: boolean;
  @Attr() public ziprecruiter: boolean;
  @Attr() public talentdotcom: boolean;

  @Attr({ persist: false }) public readonly structureLinkNames: string[];
  @Attr({ persist: false }) public readonly closedAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly filledAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly createRedirectPath: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly openedAt: string;
  @Attr({ persist: false }) public readonly openedAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly updatedAt: string;
  @Attr({ persist: false }) public readonly daysOpen: number;
  @Attr({ persist: false }) public readonly referralAmountFormatted?: string;
  @Attr({ persist: false }) public readonly referralAmountPaid?: string;
  @Attr({ persist: false }) public readonly statusType:
    | 'archived'
    | 'cancelled'
    | 'closed'
    | 'draft'
    | 'filled'
    | 'hold'
    | 'open'
    | 'pending_approval';

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly applicableOrganicJobBoards: string[];
  @Attr({ persist: false }) public readonly applicationCount: number;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly canFavourite: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly creatorName: string;
  @Attr({ persist: false }) public readonly detailedTitle: string;
  @Attr({ persist: false }) public disabledFieldsFromRequisition: string[];
  @Attr({ persist: false }) public disabledCustomFieldsFromRequisition: string[];
  @Attr({ persist: false }) public readonly deadlineOnLongDate: string;
  @Attr({ persist: false }) public readonly extendedVisibility: string;
  @Attr({ persist: false }) public readonly formattedBaseSalary: string;
  @Attr({ persist: false }) public readonly formattedReferralAmount: string;
  @Attr({ persist: false }) public readonly hiringManager: string;
  @Attr({ persist: false }) public readonly hiringManagerEmail: string;
  @Attr({ persist: false }) public readonly hiringManagerName: string;
  @Attr({ persist: false }) public readonly jobCreator: string;
  @Attr({ persist: false }) public readonly recruitmentManager: string;
  @Attr({ persist: false }) public readonly recruitmentManagerEmail: string;
  @Attr({ persist: false }) public readonly recruitmentManagerName: string;
  @Attr({ persist: false }) public readonly draftRejectionsPresent: boolean;
  @Attr({ persist: false }) public readonly events: string[];
  @Attr({ persist: false }) public readonly signOffApproved: boolean;
  @Attr({ persist: false }) public readonly tags: string[];
  @Attr({ persist: false }) public readonly organicJobBoards: {
    facebook?: boolean;
    linkedin?: boolean;
    monster?: boolean;
    talentdotcom?: boolean;
    ziprecruiter?: boolean;
  };
  @Attr({ persist: false }) public readonly preferredUrl: string;
  @Attr({ persist: false }) public readonly structureLinkInactive: boolean;
  @Attr({ persist: false }) public readonly filters: IJobFilter[];

  @BelongsTo() public structureLink: StructureLink;

  @HasOne() public activeSeekPosting: SeekPosting;
  @HasOne() public activeReedPosting: ReedPosting;
  @HasOne() public naukriPosting: JobBoardPostingsNaukri;
  @HasOne() public linkedInPosting: JobBoardPostingsLinkedIn;
  @HasOne() public totalJobsPosting: TotalJobsPosting;
  @HasOne() public cvLibraryPosting: CvLibraryPosting;
  @HasOne() public jobsUser: JobsUser;
  @HasOne() public signableSignOffStage: SignableSignOffStage;

  @HasMany() public applications: Application[];
  @HasMany() public audits: Audit[];
  @HasMany() public associatedAudits: Audit[];
  @HasMany() public automationStepsRequirables: AutomationStepsRequirable[];
  @HasMany() public comments: JobRecordComment[];
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public jobGroupsJobs: JobGroupsJob[];
  @HasMany() public jobPostings: JobPosting[];
  @HasMany() public questions: Question[];
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @HasMany() public jobStructuredSections: JobStructuredSection[];
  @HasMany() public scorecards: Scorecard[];
  @HasMany() public stages: Stage[];
  @HasMany() public usersVisibilities: UserVisibility[];
  @HasMany() public signOffStageApprovals: SignOffStageApproval[];
  @HasMany() public skippedSignOffStages: SkippedSignOffStage[];
  @HasMany() public sharingLinks: SharingLink[];
  @HasMany() public vonqCampaigns: VonqCampaign[];
  @HasMany() public jobsNotificationPresets: JobsNotificationPreset[];
  @HasMany() public requisitions: Requisition[];
  @HasMany() public reedPostings: ReedPosting[];
  @HasMany() public seekPostings: SeekPosting[];
  @HasMany() public positions: Position[];
  @HasMany() public positionOpenings: PositionOpening[];
  @HasMany() public jobsAgencies: JobsAgency[];
}

export namespace Job {
  export namespace Stats {
    export type options = {
      compensation_frequencies: ISelectOption[];
      employment_types: ISelectOption[];
      experience_levels: ISelectOption[];
      statuses: ISelectOption[];
      visibilities: ISelectOption[];
      enum_statuses: ISelectOption[];
      enum_visibilities: ISelectOption[];
      enum_compensation_frequencies: ISelectOption[];
      workplace_types: ISelectOption[];
    };

    export namespace timings {
      export type days_to_hire = number;
      export type days_to_fill = number;
    }
    export type total_count = number;
    export type number_of_applications_count_by_job = { [jobId: string]: number };
    export type number_of_applications_hired = { [jobId: string]: number };
    export type average_daily_applicants_last_week = number;
    export type number_of_applications_count = number;
    export type by_status_count = { key: string; value: number; label: string }[];
  }
}

@Model()
export class JobsAgency extends ApplicationRecord {
  public static jsonapiType = 'jobs_agencies';

  @Attr() public updatedAt: string;

  @BelongsTo() public agency: Agency;
  @BelongsTo() public job: Job;
}

@Model()
export class JobPosting extends MobilityApplicationRecord {
  public static jsonapiType = 'job_postings';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    title: Yup.string().required("Can't be blank.").nullable(),
    description: Yup.string().required("Can't be blank.").nullable(),
    keyResponsibilitiesHeader: Yup.string().required("Can't be blank").nullable(),
    keyResponsibilities: Yup.string().required("Can't be blank").nullable(),
    locationId: Yup.string().required("Can't be blank.").nullable(),
    visibility: Yup.string().required("Can't be blank.").nullable(),
    locationName: Yup.string().when('customLocation', {
      is: (value: string) => value && value === '1',
      then: Yup.string().nullable().required("Can't be blank."),
    }),
    city: Yup.string().when('customLocation', {
      is: (value: string) => value && value === '1',
      then: Yup.string().nullable().required("Can't be blank."),
    }),
    state: Yup.string().when('customLocation', {
      is: (value: string) => value && value === '1',
      then: Yup.string().nullable().required("Can't be blank."),
    }),
    country: Yup.string().when('customLocation', {
      is: (value: string) => value && value === '1',
      then: Yup.string().nullable().required("Can't be blank."),
    }),
  });

  public static booleanFields = [
    'compensationVisible',
    'askForMiddleName',
    'askForAddress',
    'askForDateOfBirth',
    'requirePhone',
    'askForLinkedinProfile',
  ];

  public static defaultAttrs: Partial<JobPosting> = {
    askForMiddleName: false,
    askForAddress: false,
    askForDateOfBirth: false,
    askForLinkedinProfile: false,
    benefits: '',
    benefitsHeader: 'Benefits',
    companyNameOverride: '',
    compensationCurrency: null,
    compensationFrequency: null,
    compensationMaximum: null,
    compensationMinimum: null,
    compensationVisible: false,
    description: '',
    documents: [],
    experienceLevel: null,
    linkedinCompanyId: '',
    linkedInPremiumPosting: false,
    keyResponsibilitiesHeader: 'Key Responsibilities',
    keyResponsibilities: '',
    name: '',
    skillsKnowledgeExpertiseHeader: 'Skills, Knowledge and Expertise',
    skillsKnowledgeExpertise: '',
    resumeRequirement: 'optional',
    personalSummaryRequirement: 'optional',
    status: 'inactive',
    title: '',
    visibility: '',
    workplaceType: 'onsite',
    facebookjobs: false,
    monster: false,
    linkedin: false,
    locationId: null,
    ziprecruiter: false,
    talentdotcom: false,
  };

  public static NEW(attrs = {}) {
    return new JobPosting({ ...JobPosting.defaultAttrs, ...attrs });
  }

  @Attr() public askForMiddleName: boolean;
  @Attr() public askForAddress: boolean;
  @Attr() public askForDateOfBirth: boolean;
  @Attr() public askForLinkedinProfile: boolean;
  @Attr() public benefits: string;
  @Attr() public benefitsHeader: string;
  @Attr() public companyNameOverride: string;
  @Attr() public compensation: string;
  @Attr() public compensationCurrency: string;
  @Attr() public compensationFrequency: string;
  @Attr() public compensationMinimum: number;
  @Attr() public compensationMaximum: number;
  @Attr() public compensationVisible: boolean;
  @Attr() public coverLetterRequirement: string;
  @Attr() public deadlineOn: string;
  @Attr({ persist: false }) public readonly defaultForJob: boolean;
  @Attr() public description: string;
  @Attr() public discardedAt: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public employmentType: string;
  @Attr() public experienceLevel: string;
  @Attr() public linkedinCompanyId: string;
  @Attr() public linkedInPremiumPosting: boolean;
  @Attr({ persist: false }) public locationId: string;
  @Attr({ persist: false }) public jobId: string;
  @Attr() public keyResponsibilitiesHeader: string;
  @Attr() public keyResponsibilities: string;
  @Attr() public name: string;
  @Attr() public personalSummaryRequirement: string;
  @Attr() public resumeRequirement: string;
  @Attr() public reportingTo: string;
  @Attr() public requirePhone: boolean;
  @Attr() public skillsKnowledgeExpertiseHeader: string;
  @Attr() public skillsKnowledgeExpertise: string;
  @Attr() public status: string;
  @Attr() public title: string;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr() public workplaceType: string;
  @Attr() public visibility: string;

  // Each option is one of the organic job boards
  //
  @Attr() public facebookjobs: boolean;
  @Attr() public monster: boolean;
  @Attr() public linkedin: boolean;
  @Attr() public ziprecruiter: boolean;
  @Attr() public talentdotcom: boolean;

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly canDelete: string;
  @Attr({ persist: false }) public readonly canDuplicate: string;
  @Attr({ persist: false }) public readonly canEdit: string;
  @Attr({ persist: false }) public readonly canUpdateStatus: string;
  @Attr({ persist: false }) public readonly preferredUrl: string;

  @BelongsTo() public location: Location;
  @BelongsTo() public job: Job;
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @HasOne() public linkedInPosting: JobBoardPostingsLinkedIn;
}

@Model()
export class JobsUser extends ApplicationRecord {
  public static jsonapiType = 'jobs_users';

  @BelongsTo() public job: Job;
  @BelongsTo() public user: User;
}

@Model()
export class JobGroupsJob extends ApplicationRecord {
  public static jsonapiType = 'job_groups_jobs';

  @Attr({ persist: false }) public jobId: string;
  @Attr({ persist: false }) public jobGroupId: string;

  @HasOne() job: Job;
  @BelongsTo() jobGroup: JobGroup;
}

@Model()
export class JobGroup extends ApplicationRecord {
  public static jsonapiType = 'job_groups';
  public static MAX_PER = 1000;

  @Attr() public name: string;
  /** @extraAttribute */
  @Attr() public candidateCount: number;

  @HasMany() jobGroupsJobs: JobGroupsJob[];
}

@Model()
export class JobBoardBasket extends ApplicationRecord {
  public static jsonapiType = 'job_board_baskets';

  @Attr({ persist: false }) public basketCurrency: string;
  @Attr({ persist: false }) public basketTotal: number;
  @Attr({ persist: false }) public formattedBasketTotal: string;
  @Attr({ persist: false }) public companyId: string;
  @Attr() public jobId: string;
  @Attr({ persist: false }) public paymentIntentId: string;
  @Attr() public status: string;
  @Attr({ persist: false }) public userId: string;
  @Attr() public jobBoardProductIds: string[];
  @Attr() public vonqJobBoards: VonqJobBoard[];
  @HasMany() public jobBoardProducts: JobBoardProduct[];
}

@Model()
export class JobBoardCollection extends ApplicationRecord {
  public static jsonapiType = 'job_board_collections';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });

  @Attr() public name: string;
  @Attr() public jobBoardProviderIds: string[];
  @Attr() public jobBoardCollectionItemIds: string[];
  @HasMany() public jobBoardProviders: JobBoardProvider[];
  @HasMany() public jobBoardCollectionItems: JobBoardCollectionItem[];
}

@Model()
export class JobBoardCollectionItem extends ApplicationRecord {
  public static jsonapiType = 'job_board_collection_items';

  @Attr() public jobBoardCollectionId: string;
  @Attr() public jobBoardProviderId: string;
  @Attr() public jobBoardProductId: string;
  @BelongsTo() public jobBoardProvider: JobBoardProvider;
  @BelongsTo() public jobBoardProduct: JobBoardProduct;
}

@Model()
export class JobBoardProvider extends ApplicationRecord {
  public static jsonapiType = 'job_board_providers';

  @Attr({ persist: false }) public readonly background: string;
  @Attr({ persist: false }) public readonly description: string;
  @Attr({ persist: false }) public readonly live: boolean;
  @Attr({ persist: false }) public readonly logo: string;
  @Attr({ persist: false }) public readonly recommendation: string;
  @Attr({ persist: false }) public readonly industries: string[];
  @Attr({ persist: false }) public readonly regions: string[];
  @Attr({ persist: false }) public readonly title: string;
  @Attr({ persist: false }) public readonly url: string;
  @HasMany() public jobBoardPostingTypes: JobBoardPostingType[];
  @HasMany() public jobBoardProducts: JobBoardProduct[];
  @HasMany() public jobBoardCollectionItems: JobBoardCollectionItem[];
}

@Model()
export class JobBoardProduct extends ApplicationRecord {
  public static jsonapiType = 'job_board_products';

  @Attr() public name: string;
  @Attr() public priceInCompanyCurrency: number;
  @Attr() public formattedPriceInCompanyCurrency: string;
  @Attr() public providerReference: string;
  @BelongsTo() public jobBoardPostingType: JobBoardPostingType;
}

@Model()
export class JobBoardPostingType extends ApplicationRecord {
  public static jsonapiType = 'job_board_posting_types';

  @Attr() public description: string;
  @Attr() public title: string;
  @Attr({ persist: false }) public readonly jobBoardProviderName: string;
  @BelongsTo() public jobBoardProvider: JobBoardProvider;
}

@Model()
export class JobBoardTaxonomy extends ApplicationRecord {
  public static jsonapiType = 'job_board_taxonomies';
  public static validationSchema = Yup.object().shape({
    address1: Yup.string().required("Can't be blank.").nullable(),
    campaignName: Yup.string().required("Can't be blank.").nullable(),
    city: Yup.string().required("Can't be blank.").nullable(),
    country: Yup.string().required("Can't be blank.").nullable(),
    description: Yup.string().required("Can't be blank.").nullable(),
    educationLevel: Yup.string().required("Can't be blank.").nullable(),
    experienceYears: Yup.number().required("Can't be blank.").nullable().typeError('Must be a number'),
    industry: Yup.string().required("Can't be blank.").nullable(),
    jobCategory: Yup.string().required("Can't be blank.").nullable(),
    organisationName: Yup.string().required("Can't be blank.").nullable(),
    postcode: Yup.string().required("Can't be blank.").nullable(),
    salaryFrom: Yup.number().nullable().typeError('Must be a number'),
    salaryPeriod: Yup.string().required("Can't be blank.").nullable().typeError('Must be a number'),
    salaryTo: Yup.number().required("Can't be blank.").nullable().typeError('Must be a number'),
    salaryCurrency: Yup.string().nullable().required("Can't be blank."),
    seniority: Yup.string().required("Can't be blank.").nullable(),
    title: Yup.string().required("Can't be blank.").nullable(),
    weeklyWorkingHoursFrom: Yup.number().nullable().typeError('Must be a number'),
    weeklyWorkingHoursTo: Yup.number()
      .required("Can't be blank.")
      .nullable()
      .typeError('Must be a number')
      .min(1, "Can't be less than 1 hour"),
  });

  @Attr() public address1: string;
  @Attr() public address2: string;
  @Attr() public campaignName: string;
  @Attr() public city: string;
  @Attr() public contactEmail: string;
  @Attr() public contactName: string;
  @Attr() public contactPhone: string;
  @Attr() public contactPhoneIso2: string;
  @Attr() public country: string;
  @Attr() public description: string;
  @Attr() public educationLevel: string;
  @Attr() public experienceYears: number;
  @Attr() public companyId: string;
  @Attr() public industry: string;
  @Attr() public jobCategory: string;
  @Attr() public jobId: string;
  @Attr() public logo: string;
  @Attr() public organisationName: string;
  @Attr() public postcode: string;
  @Attr() public region: string;
  @Attr() public salaryPeriod: string;
  @Attr() public salaryFrom: number;
  @Attr() public salaryTo: number;
  @Attr() public salaryCurrency: string;
  @Attr() public seniority: string;
  @Attr() public title: string;
  @Attr() public weeklyWorkingHoursFrom: number;
  @Attr() public weeklyWorkingHoursTo: number;
}

@Model()
export class JobBoardPosting extends ApplicationRecord {
  public static jsonapiType = 'job_board_postings';
  public static endpoint = '/job_board_postings';
  public instanceClasses: JobBoardPostingsNaukri;

  @Attr() public status: 'created' | 'posted' | 'deleted' | 'errored' | 'posting_requested' | 'closed';
  @BelongsTo() public job: Job;
}

@Model()
export class JobBoardPostingsNaukri extends JobBoardPosting {
  public static jsonapiType = 'job_board_postings_naukri';
  public staticType = 'Naukri Job Posting' as const;

  @Attr() public functionalArea: string;
  @Attr() public industry: string;
  @Attr() public internationalLocations: string[];
  @Attr() public keySkills: string;
  @Attr() public jobType: string;
  @Attr() public maxSalary: string;
  @Attr() public maxWorkExperience: string;
  @Attr() public minSalary: string;
  @Attr() public minWorkExperience: string;
  @Attr() public nationalLocations: string[];
  @Attr() public salaryCurrency: string;
  @Attr() public showSalary: boolean;
  @Attr() public workmode: string;

  @Attr() public postingUrl: string;
}

@Model()
export class JobBoardPostingsLinkedIn extends JobBoardPosting {
  public static jsonapiType = 'job_board_postings_linked_in';
  public staticType = 'LinkedIn Job Posting' as const;

  @Attr() public postingErrors: string;
  @Attr() public postingId: string;
  @Attr() public postingLink: string;
}

@Model()
export class JobsNotificationPreset extends ApplicationRecord {
  public static jsonapiType = 'jobs_notification_presets';
  @Attr() public jobId: string;
  @Attr() public userId: string;
  @Attr() public companyId: string;
  @Attr() public notificationPresetId: string;

  @BelongsTo() public job: Job;
  @BelongsTo() public company: Company;
  @BelongsTo() public notificationPreset: NotificationPreset;
}

@Model()
export class JobSeeker extends ApplicationRecord {
  public static jsonapiType = 'job_seekers';
  public static validationSchema = Yup.object().shape({
    email: Yup.string().required("Can't be blank."),
    firstName: Yup.string().required("Can't be blank."),
    lastName: Yup.string().required("Can't be blank."),
  });

  @Attr() public addTagWithContext: TagWithContext;
  @Attr() public addTagsWithContext: TagWithContext[];
  @Attr({ persist: false }) public readonly averageRating: number;
  @Attr() public candidateId: string;
  @Attr() public channel: string;
  @Attr() public channelSource: string;
  @Attr({ persist: false }) public createdAt: string;
  @Attr({ persist: false }) public consentedAt: string;
  @Attr({ persist: false }) public readonly createdAtShortDate: string;
  @Attr() public createdFromCandidate: boolean;
  @Attr({ persist: false }) public readonly currentUserRating: number;
  @Attr() public cv: string;
  @Attr({ persist: false }) public readonly cvFile: IActiveStorageFile;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public email: string;
  @Attr() public firstName: string;
  @Attr() public preferredName: string;
  @Attr() public fullName: string;
  @Attr() public middleName: string;
  @Attr() public lastName: string;
  @Attr() public dateOfBirth: string;
  @Attr() public linkedinUrl: string;
  @Attr() public locale?: TLocaleKey;
  @Attr() public phone: string;
  @Attr() public phoneIso2: string;
  @Attr() public address1: string;
  @Attr() public address2: string;
  @Attr() public country: string;
  @Attr({ persist: false }) public readonly redactedCvFile: IActiveStorageFile;
  @Attr() public town: string;
  @Attr() public postcode: string;
  @Attr() public pipelineSource: string;
  @Attr({ persist: false }) public readonly preferredPronouns: string;
  @Attr() public removeTagWithContext: TagWithContext;
  @Attr() public resumeDownloadLink: string;
  @Attr() public sendSmsMessage: string;
  @Attr({ persist: false }) public readonly showPath: string;
  @Attr() public smsPreview: string;
  @Attr() public summary: string;
  @Attr() public structureLinkIds: string[];
  @Attr() public updatedAt: string;

  /** Used to determine if we should wait when parsing the resume and if the resume is currently being parsed */
  @Attr() public isParsing: boolean;
  @Attr() public waitForParse: boolean;

  /** localised dates and times */
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly consentedAtShortWithYearDate: string;

  // Extra attributes
  @Attr({ persist: false }) public readonly avatarSvg: string;
  @Attr({ persist: false }) public readonly canAddToJob: boolean;
  @Attr({ persist: false }) public readonly canInviteToApply: boolean;
  @Attr({ persist: false }) public readonly canSendEqualityMonitoringSurvey: boolean;
  @Attr({ persist: false }) public readonly emailConsent: boolean;
  @Attr({ persist: false }) public readonly humanizedCountryName: string;
  @Attr({ persist: false }) public readonly matchingSkillsCount: number;
  @Attr({ persist: false }) public readonly pdfCvFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly scorecardRated: boolean;
  @Attr({ persist: false }) public readonly smsConsent: boolean;
  @Attr({ persist: false }) public readonly structuresNames: string[];
  @Attr({ persist: false }) public readonly tags: ITag[];
  @Attr({ persist: false }) public readonly talentPipelineId: string;

  // False attributes
  @Attr({ persist: false }) public readonly concealed: boolean;
  @Attr({ persist: false }) public readonly hideInterpretedResume: boolean;
  // Scorecard attributes calculated on the frontend
  @Attr({ persist: false }) public scorecardPercentageScore: number;
  @Attr({ persist: false }) public scorecardUserWeightedScoreTotal: number;
  @Attr({ persist: false }) public scorecardMaxWeightedScoreTotal: number;
  @Attr({ persist: false }) public meanByScorecardOptionIds: { [key: string]: number };

  // Accessors
  @Attr() public createApplication: boolean;
  @Attr() public jobId: string;
  @Attr() public jobStageId: string;
  @Attr() public talentPoolToRemoveId: string;

  // Associations
  @BelongsTo() public candidate: Candidate;

  @HasOne() public resume: Resume;
  @HasOne() public geocodedAddress: GeocodedAddress;

  @HasMany() public answers: Answer[];
  @HasMany() public comments: Comment[];
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public folders: Folder[];
  @HasMany() public messages: Message[];
  @HasMany() public scorecardAnswers: ScorecardAnswer[];
  @HasMany() public scorecardNotes: ScorecardNote[];
  @HasMany() public scorecardRatings: ScorecardRating[];
  @HasMany() public jobSeekersInterests: JobSeekersInterest[];
  @HasMany() public jobSeekerStructureLinks: JobSeekerStructureLink[];
  @HasMany() public userScorecards: UserScorecard[];
  @HasMany() public structuredSectionResponses: StructuredSectionResponse[];

  // Interviewable Concern
  @HasMany() public interviews: Interview[];
  @HasMany() public interviewInvites: InterviewInvite[];
  @HasMany() public invitesJobSeekers: InvitesJobSeeker[];
  @HasOne() public currentInterview: Interview;
  @HasOne() public currentInterviewInvite: InterviewInvite;

  // Scheduled Messages Concern
  @Attr({ persist: false }) public readonly scheduledMessages: IScheduledMessage[];
}

export namespace JobSeeker {
  export namespace Stats {
    export type total_count = number;
    export type by_date_created_cumulative_count = { [date: string]: number };
    export type by_tag_category_count_by_tag = { [tagCategory: string]: { [tag: string]: number } };
    export type by_channel_source_count = {
      key: string;
      value: number;
      label: string;
      detail: { label: string; value: number }[];
    }[];
  }
}

@Model()
export class JobSeekersInterest extends ApplicationRecord {
  public static jsonapiType = 'job_seekers_interests';
  @Attr({ persist: false }) public readonly interestableType: string;
  @Attr({ persist: false }) public readonly interestableId: string;

  @BelongsTo() public jobSeeker: JobSeeker;
  @BelongsTo() public interestable: BypassDepartment | BypassLocation | BypassDivision | BypassStructureCustomGroupOne;
}

@Model()
export class JobSeekerStructureLink extends MobilityApplicationRecord {
  public static jsonapiType = 'job_seeker_structure_links';

  @Attr() public structureLinkId: string;
}

@Model()
export class JobTemplate extends MobilityApplicationRecord {
  public static jsonapiType = 'job_templates';

  public static validationSchema = Yup.object().shape({
    title: Yup.string().required("Can't be blank."),
  });

  public static attributesToDuplicate = [
    'accessThroughVisibility',
    'title',
    'employmentType',
    'reportingTo',
    'compensationCurrency',
    'compensationFrequency',
    'compensationMaximum',
    'compensationMinimum',
    'compensation',
    'customApplicationReceivedTemplateId',
    'remote',
    'structureLinkId',
    'askForAddress',
    'askForDateOfBirth',
    'askForMiddleName',
    'askForPreferredName',
    'requirePhone',
    'askForLinkedinProfile',
    'blindScreening',
    'coverLetterRequirement',
    'resumeRequirement',
    'personalSummaryRequirement',
    'visibility',
    'diversityMonitoringRequirement',
    'requisitionId',
    'hiringWorkflowId',
    'description',
    'externalKeyResponsibilitiesHeader',
    'keyResponsibilities',
    'externalSkillsKnowledgeExpertiseHeader',
    'skillsKnowledgeExpertise',
    'externalBenefitsHeader',
    'benefits',
    'referralsEnabled',
    'referralDetails',
    'referralAmount',
    'referralCurrency',
    'companyNameOverride',
    'customCountry',
    'customCounty',
    'customTown',
    'notify',
    'selectedHiringWorkflowId',
    'diversityMonitoring',
    'isCompensationVisible',
    'linkedinCompanyId',
    'workplaceType',
    'linkedin',
    'monster',
    'talentdotcom',
    'ziprecruiter',
    'experienceLevel',
  ];

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<JobTemplate> = {
      accessThroughVisibility: true,
      askForAddress: false,
      askForDateOfBirth: false,
      askForLinkedinProfile: false,
      askForMiddleName: false,
      askForPreferredName: false,
      benefits: '',
      blindScreening: false,
      companyNameOverride: null,
      compensationCurrency: null,
      compensationFrequency: null,
      compensationMaximum: null,
      compensationMinimum: null,
      coverLetterRequirement: 'not_required',
      customCountry: '',
      customCounty: '',
      customTown: '',
      customApplicationReceivedTemplateId: null,
      description: '',
      diversityMonitoring: false,
      employmentType: '',
      experienceLevel: null,
      externalBenefitsHeader: 'Benefits',
      externalKeyResponsibilitiesHeader: 'Key Responsibilities',
      externalSkillsKnowledgeExpertiseHeader: 'Skills, Knowledge and Expertise',
      facebookjobs: false,
      isCompensationVisible: true,
      keyResponsibilities: '',
      linkedin: false,
      linkedinCompanyId: '',
      linkedInPremiumPosting: false,
      monster: false,
      notify: true,
      personalSummaryRequirement: 'optional',
      referralAmount: null,
      referralCurrency: '',
      referralDetails: '',
      referralsEnabled: false,
      reportingTo: '',
      requirePhone: false,
      requisitionId: '',
      resumeRequirement: '',
      selectedHiringWorkflowId: Job.DEFAULT_HIRING_WORKFLOW_ID,
      skillsKnowledgeExpertise: '',
      title: '',
      visibility: '',
      workplaceType: 'onsite',
      talentdotcom: false,
      ziprecruiter: false,
    };
    return new JobTemplate({ ...defaultAttrs, ...attrs });
  }

  @Attr() public accessThroughVisibility: boolean;
  @Attr() public askForAddress: boolean;
  @Attr() public askForDateOfBirth: boolean;
  @Attr() public askForLinkedinProfile: boolean;
  @Attr() public askForMiddleName: boolean;
  @Attr() public askForPreferredName: boolean;
  @Attr() public benefits: string;
  @Attr() public blindScreening: boolean;
  @Attr() public companyNameOverride: string;
  @Attr() public compensation: string;
  @Attr() public compensationCurrency: string;
  @Attr() public compensationFrequency: string;
  @Attr() public compensationMaximum: number;
  @Attr() public compensationMinimum: number;
  @Attr() public coverLetterRequirement: string;
  @Attr() public customCountry: string;
  @Attr() public customCounty: string;
  @Attr() public customTown: string;
  @Attr() public customApplicationReceivedTemplateId: string;
  @Attr() public description: string;
  @Attr() public diversityMonitoring: boolean | string;
  @Attr() public employmentType: string;
  @Attr() public experienceLevel: string;
  @Attr() public externalBenefitsHeader: string;
  @Attr() public externalKeyResponsibilitiesHeader: string;
  @Attr() public externalSkillsKnowledgeExpertiseHeader: string;
  @Attr() public isCompensationVisible: boolean;
  @Attr() public keyResponsibilities: string;
  @Attr() public linkedinCompanyId: string;
  @Attr() public linkedInPremiumPosting: boolean;
  @Attr() public notify: boolean;
  @Attr() public personalSummaryRequirement: string;
  @Attr() public referralAmount: number;
  @Attr() public referralCurrency: string;
  @Attr() public referralDetails: string;
  @Attr() public referralsEnabled: boolean;
  @Attr() public reportingTo: string;
  @Attr() public requirePhone: boolean;
  @Attr() public requisitionId: string;
  @Attr() public resumeRequirement: string;
  @Attr() public selectedHiringWorkflowId: string;
  @Attr() public skillsKnowledgeExpertise: string;
  @Attr() public title: string;
  @Attr() public visibility: string;
  @Attr() public workplaceType: string;

  // Organic job posting
  @Attr() public facebookjobs: boolean;
  @Attr() public monster: boolean;
  @Attr() public linkedin: boolean;
  @Attr() public ziprecruiter: boolean;
  @Attr() public talentdotcom: boolean;

  // Extra attributes
  @Attr({ persist: false }) public readonly multiLingual: boolean;

  // Meta
  @Attr({ persist: false }) public canDestroy: boolean;

  // Associations
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @HasMany() public jobTemplatesAutomations: JobTemplatesAutomation[];
  @HasMany() public jobTemplatePostings: JobTemplatePosting[];
}

@Model()
export class JobTemplatePosting extends MobilityApplicationRecord {
  public static jsonapiType = 'job_template_postings';

  public static defaultAttrs: Partial<JobTemplatePosting> = {
    askForMiddleName: false,
    askForAddress: false,
    askForDateOfBirth: false,
    askForLinkedinProfile: false,
    benefits: '',
    benefitsHeader: 'Benefits',
    compensationCurrency: null,
    compensationFrequency: null,
    compensationMaximum: null,
    compensationMinimum: null,
    compensationVisible: false,
    description: '',
    documents: [],
    experienceLevel: null,
    linkedinCompanyId: '',
    linkedInPremiumPosting: false,
    keyResponsibilitiesHeader: 'Key Responsibilities',
    keyResponsibilities: '',
    name: '',
    skillsKnowledgeExpertiseHeader: 'Skills, Knowledge and Expertise',
    skillsKnowledgeExpertise: '',
    resumeRequirement: 'optional',
    personalSummaryRequirement: 'optional',
    title: '',
    visibility: '',
    workplaceType: 'onsite',
    facebookjobs: false,
    monster: false,
    linkedin: false,
    ziprecruiter: false,
    talentdotcom: false,
  };

  public static NEW(attrs = {}) {
    return new JobTemplatePosting({ ...JobTemplatePosting.defaultAttrs, ...attrs });
  }

  public static booleanFields = [
    'compensationVisible',
    'askForMiddleName',
    'askForAddress',
    'askForDateOfBirth',
    'requirePhone',
    'askForLinkedinProfile',
  ];

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    title: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public askForMiddleName: boolean;
  @Attr() public askForAddress: boolean;
  @Attr() public askForDateOfBirth: boolean;
  @Attr() public askForLinkedinProfile: boolean;
  @Attr() public benefits: string;
  @Attr() public benefitsHeader: string;
  @Attr() public compensation: string;
  @Attr() public compensationCurrency: string;
  @Attr() public compensationFrequency: string;
  @Attr() public compensationMinimum: number;
  @Attr() public compensationMaximum: number;
  @Attr() public compensationVisible: boolean;
  @Attr() public coverLetterRequirement: string;
  @Attr() public deadlineOn: string;
  @Attr() public description: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public discardedAt: string;
  @Attr() public employmentType: string;
  @Attr() public experienceLevel: string;
  @Attr() public linkedinCompanyId: string;
  @Attr() public linkedInPremiumPosting: boolean;
  @Attr() public keyResponsibilitiesHeader: string;
  @Attr() public keyResponsibilities: string;
  @Attr() public name: string;
  @Attr() public personalSummaryRequirement: string;
  @Attr() public resumeRequirement: string;
  @Attr() public reportingTo: string;
  @Attr() public requirePhone: boolean;
  @Attr() public skillsKnowledgeExpertiseHeader: string;
  @Attr() public skillsKnowledgeExpertise: string;
  @Attr() public title: string;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr() public workplaceType: string;
  @Attr() public visibility: string;

  // Each option is one of the organic job boards
  //
  @Attr() public facebookjobs: boolean;
  @Attr() public monster: boolean;
  @Attr() public linkedin: boolean;
  @Attr() public ziprecruiter: boolean;
  @Attr() public talentdotcom: boolean;

  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
}

@Model()
export class JobTemplatesAutomation extends ApplicationRecord {
  public static jsonapiType = 'job_templates_automations';
  @Attr() public jobTemplateId: string;
  @Attr() public automationId: string;
  @Attr({ persist: false }) public companyId: string;

  @BelongsTo() public jobTemplate: JobTemplate;
  @BelongsTo() public automation: Automation;
}

@Model()
export class Message extends ApplicationRecord {
  public static jsonapiType = 'messages';
  public staticType = 'Message' as const;

  @Attr({ persist: false }) public bcc: string[];
  @Attr({ persist: false }) public cc: string[];
  @Attr() public contactableId: string;
  @Attr() public contactableType: string;
  @Attr({ persist: false }) public createdAt: string;
  @Attr({ persist: false }) public createdAtLongOrdinal: string;
  @Attr({ persist: false }) public subject: string;
  @Attr({ persist: false }) public body: string;
  @Attr({ persist: false }) public direction: string;
  @Attr({ persist: false }) public fromName: string;
  @Attr({ persist: false }) public fromEmail: string;
  @Attr({ persist: false }) public messageId: string;
  @Attr() public sensitive: boolean;
  @Attr() public attachments: string[];
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr({ persist: false }) public readonly attachmentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly outboundEmailStatus: string;
  @Attr({ persist: false }) public readonly outboundEmailEvents: { data: IOutboundEmailEvent[] };
  @BelongsTo({ persist: false }) public readonly contactable: Application | JobSeeker;
  @HasOne() public usersMessage: UsersMessage;
}

export namespace Message {
  export namespace Stats {
    export type total_count = number;
    export type by_application_average_seconds_between_first_two_outbound_emails = number;
    export type by_application_average_seconds_between_outbound_emails = number;
    export type by_application_outbound_email_count_distribution = { [count: string]: number };
  }
}

@Model()
export class Notification extends ApplicationRecord {
  public static jsonapiType = 'notifications';
  @Attr() public body: string;
  @Attr() public createdAt: string;
  @Attr() public linkPath: string;
  @Attr({ persist: false }) public readonly notifiableId: string;
  @Attr() public notifyOn: string;
  @Attr({ persist: false }) public readonly notifiableType: string;
  @Attr() public notificationType:
    | 'default'
    | 'application'
    | 'automation'
    | 'message'
    | 'interview'
    | 'talent_pipeline'
    | 'expiring'
    | 'hire'
    | 'advert'
    | 'job'
    | 'video_interview'
    | 'offer'
    | 'onboarding_application'
    | 'comment'
    | 'report'
    | 'scorecard'
    | 'requisition'
    | 'task';
  @Attr() public usersNotificationsId: string;
  @Attr() public readAt: string;

  @Attr({ persist: false }) public readonly canCreateTask: boolean;
}

export namespace Notification {
  export namespace Stats {
    export type total = { unviewed_count: number };
  }
}

@Model()
export class NotificationPreset extends ApplicationRecord {
  public static jsonapiType = 'notification_presets';
  @Attr() public companyId: string;
  @Attr() public presetType: string;
  @Attr() public name: string;

  @BelongsTo() public presetable: User | AccessGroup;
  @HasOne() public jobsNotificationPreset: JobsNotificationPreset;
  @HasMany() public notificationPreferences: NotificationPreference[];
  @HasMany() public usersStageNotifications: UsersStageNotification[];
}

@Model()
export class NotificationPreference extends ApplicationRecord {
  public static jsonapiType = 'notification_preferences';
  @Attr() public companyId: string;
  @Attr() public userId: string;
  @Attr() public notificationType: string;
  @Attr() public notificationPresetId: string;
  @Attr() public slack: boolean;
  @Attr() public email: boolean;
  @Attr() public inApp: boolean;

  @BelongsTo() public notificationPreset: NotificationPreset;
}

@Model()
export class Location extends MobilityApplicationRecord {
  public static jsonapiType = 'locations';
  public static MAX_PER = 10000;

  public static NEW(attrs: Partial<Location> = {}) {
    const defaultAttrs: Partial<Location> = {
      city: '',
      countryCode: '',
      name: '',
      postalCode: '',
      province: '',
      streetAddress: '',
      structureLinkable: true,
    };
    return new Location({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape<Shape<Location>>({
    city: Yup.string().nullable().required("Can't be blank."),
    countryCode: Yup.string().nullable().required("Can't be blank."),
    name: Yup.string().nullable().required("Can't be blank."),
    province: Yup.string().nullable().required("Can't be blank."),
  });

  @Attr() public city: string;
  @Attr() public countryCode: string;
  @Attr() public name: string;
  @Attr() public postalCode: string;
  @Attr() public province: string;
  @Attr() public streetAddress: string;
  @Attr() public equalityMonitoringTemplateId: number;
  @Attr() public activate: boolean;
  @Attr() public deactivate: boolean;
  @Attr() public structureLinkable: boolean;
  @Attr({ persist: false }) public readonly inactive: boolean;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly inUse: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public equalityMonitoringTemplate: EqualityMonitoringTemplate;
  @BelongsTo() public company: Company;
  @HasOne() public themesThemeable: ThemesThemeable;
  @HasOne() public dataRetentionPolicyRetentionable: DataRetentionPolicyRetentionable;
  @HasOne() public geocodedAddress: GeocodedAddress;
  @HasMany() public applications: Application[];
}

@Model()
export class OfferTemplate extends ApplicationRecord {
  public static jsonapiType = 'offer_templates';
  public static validationSchema = Yup.object().shape<Shape<OfferTemplate>>({
    emailBody: Yup.string().required("Can't be blank."),
    emailSalutation: Yup.string().required("Can't be blank."),
    emailSignature: Yup.string().required("Can't be blank."),
    emailSubject: Yup.string().required("Can't be blank."),
    letterBody: Yup.string().required("Can't be blank."),
    letterSignature: Yup.string().required("Can't be blank."),
    letterSubject: Yup.string().required("Can't be blank."),
  });
  public static tagOptions = ['Offer'];

  public static NEW() {
    const attrs: Partial<OfferTemplate> = {
      address: '',
      companySignature: '',
      companySignatureDescription: '',
      emailBody: '',
      emailSalutation: '',
      emailSignature: '',
      emailSubject: '',
      jobId: '',
      letterBody: '',
      letterSignature: '',
      letterSubject: '',
      logo: '',
      name: '',
      signatureDescription: '',
      isCustomLetterTemplate: false,
    };
    return new this(attrs);
  }

  @Attr() public jobId: string | number;

  @Attr() public address: string;
  @Attr() public companySignature: string;
  @Attr() public companySignatureDescription: string;
  @Attr() public name: string;
  @Attr() public logo: string;
  @Attr() public emailSubject: string;
  @Attr() public emailSalutation: string;
  @Attr() public emailBody: string;
  @Attr() public emailSignature: string;
  @Attr() public letterSubject: string;
  @Attr() public letterBody: string;
  @Attr() public letterSignature: string;
  @Attr() public signatureDescription: string;
  @Attr() public isCustomLetterTemplate: boolean;
  @Attr() public templateDocx: string;
  @Attr() public docxTemplateDocuments: string[];
  @Attr() public pdfTemplateDocx: string;
  @Attr() public emailDocuments: string[];
  @Attr({ persist: false }) public readonly companySignatureFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly templateDocxFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly docxTemplateDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly emailDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly logoFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly updatedAt: string;

  @BelongsTo() public job: Job;
  @BelongsTo() public company: Company;
  @HasOne() public scopeCondition: ScopeCondition;
  @HasMany() public offerDocumentTemplates: OfferDocumentTemplate[];
}

@Model()
export class OfferDocumentTemplate extends ApplicationRecord {
  public static jsonapiType = 'offer_document_templates';

  @Attr() public documentType: string;
  @Attr() public name: string;
  @Attr({ persist: false }) public readonly offerTemplateId: string;
  @Attr() public position: string;
  @Attr() public templateDocx: string;
  @Attr({ persist: false }) public readonly templateDocxFile: IActiveStorageFile;
}

@Model()
export class Offer extends ApplicationRecord {
  public static MAX_DATE = addYears(new Date(), 5);
  public static MIN_DATE = subYears(new Date(), 1);

  public static jsonapiType = 'offers';

  public static signatureTypeOptions = [
    { value: 'wet', label: 'Wet Signature' },
    { value: 'electronic', label: 'E-Signature' },
    { value: 'not_required', label: 'No Signature' },
  ];

  public static salaryValidations = {
    salaryCurrency: Yup.string().when(['salaryAmount'], {
      is: (salaryAmount: unknown) => !!salaryAmount,
      then: Yup.string().required("Can't be blank").nullable(),
      otherwise: Yup.string().nullable(),
    }),
    salaryAmount: Yup.string().when(['salaryCurrency'], {
      is: (salaryCurrency: unknown) => !!salaryCurrency,
      then: Yup.string()
        .required("Can't be blank")
        .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
      otherwise: Yup.string().nullable(),
    }),
  };

  public static sharedValidations = {
    expiresAt: Yup.string().required("Can't be blank."),
    jobTitle: Yup.string().required("Can't be blank."),
    offerTemplateId: Yup.string().when(['skipOfferTemplate'], {
      is: (skipOfferTemplate) => !skipOfferTemplate,
      then: Yup.string().required("Can't be blank."),
      otherwise: Yup.string().nullable(),
    }),
    signatureType: Yup.string().required('Must be selected'),
  };

  public static positionValidations = Yup.object().shape<Shape<Offer>>(
    {
      ...Offer.salaryValidations,
      ...Offer.sharedValidations,
      startOn: Yup.string().required("Can't be blank.").nullable(),
      positionOpeningId: Yup.string().nullable().required("Can't be blank."),
    },
    [
      ['salaryAmount', 'salaryCurrency'],
      ['salaryCurrency', 'salaryCurrency'],
    ],
  );

  public static validationSchema = Yup.object().shape<Shape<Offer>>(
    {
      ...Offer.salaryValidations,
      ...Offer.sharedValidations,
    },
    [
      ['salaryAmount', 'salaryCurrency'],
      ['salaryCurrency', 'salaryCurrency'],
    ],
  );

  public static salaryValidationSchema = Yup.object().shape<Shape<Offer>>({
    ...Offer.sharedValidations,
    salaryCurrency: Yup.string().required("Can't be blank").nullable(),
    salaryAmount: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
    salaryDuration: Yup.string().required("Can't be blank").nullable(),
  });

  public static strictValidationSchema = Yup.object().shape<Shape<Offer>>({
    ...Offer.sharedValidations,
    startOn: Yup.string().required("Can't be blank.").nullable(),
    salaryCurrency: Yup.string().required("Can't be blank").nullable(),
    salaryAmount: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
    salaryDuration: Yup.string().required("Can't be blank").nullable(),
    managerId: Yup.string().required("Can't be blank."),
    paidDaysOff: Yup.string()
      .required("Can't be blank")
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' }),
    workAddress: Yup.string().required('Must be selected'),
  });

  public static NEW(attrs?: Partial<Offer>) {
    const defaultAttrs: Partial<Offer> = {
      additionalLetterText: '',
      bcc: [],
      cc: [],
      docuSignEnvelopeId: null,
      emailBody: '',
      emailSalutation: '',
      emailSignature: '',
      emailSubject: '',
      expiresAt: addDays(new Date(), 7).toISOString(),
      jobTitle: '',
      letterBody: '',
      letterSignature: '',
      letterSubject: '',
      managerId: null,
      offerDocuments: [],
      openAndSend: false,
      paidDaysOff: '',
      positionOpeningId: null,
      salaryAmount: null,
      salaryCurrency: null,
      salaryDuration: null,
      signatureType: 'electronic',
      startOn: null,
      workAddress: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  get isDraft(): boolean {
    return this.status === 'draft';
  }

  get isApproved(): boolean {
    return this.status === 'approved';
  }

  get isReadyToSend(): boolean {
    return this.status === 'ready_to_send';
  }

  get isOpen(): boolean {
    return this.status === 'open';
  }

  get isDraftOrApproved(): boolean {
    return this.isDraft || this.isApproved;
  }

  get isAccepted(): boolean {
    return this.result === 'accepted';
  }

  get isPending(): boolean {
    return this.result === 'pending';
  }

  @Attr() public stageId: string;
  @Attr() public applicationId: string;
  @Attr() public docuSignEnvelopeId: string;
  @Attr() public managerId: string;
  @Attr({ persist: false }) public readonly creatorId: string;
  @Attr() public offerTemplateId: string;
  @Attr({ persist: false }) public readonly canEdit: boolean;
  @Attr({ persist: false }) public readonly canSend: boolean;

  @Attr() public cc: string[];
  @Attr() public bcc: string[];
  @Attr() public createOfferDocuments: boolean;
  @Attr() public jobTitle: string;
  @Attr() public expiresAt: string;
  @Attr() public startOn: string;
  @Attr() public paidDaysOff: string;
  @Attr() public emailSubject: string;
  @Attr() public emailSalutation: string;
  @Attr() public emailBody: string;
  @Attr() public emailSignature: string;
  @Attr() public letterSubject: string;
  @Attr() public letterBody: string;
  @Attr() public letterSignature: string;
  @Attr() public salaryAmount: string;
  @Attr() public salaryCurrency: string;
  @Attr() public salaryDuration: string;
  @Attr() public signatureType: Offer_signature_type_key;
  @Attr() public additionalLetterText: string;
  @Attr() public workAddress: string;
  @Attr() public openAndSend: boolean;
  @Attr() public bulkOfferUuid: string;
  @Attr() public positionOpeningId: string;
  @Attr() public result: Offer_result_key;

  @Attr({ persist: false }) public readonly uninterpolatedEmailSubject: string;
  @Attr({ persist: false }) public readonly uninterpolatedEmailBody: string;
  @Attr({ persist: false }) public readonly uninterpolatedEmailSalutation: string;
  @Attr({ persist: false }) public readonly uninterpolatedEmailSignature: string;
  @Attr({ persist: false }) public readonly uninterpolatedLetterSubject: string;
  @Attr({ persist: false }) public readonly uninterpolatedLetterBody: string;
  @Attr({ persist: false }) public readonly uninterpolatedLetterSignature: string;

  @Attr({ persist: false }) public readonly status: Offer_status_key;
  @Attr({ persist: false }) public readonly comment: string;
  // I18n formatted dates
  @Attr({ persist: false }) public readonly createdAtShortWithTime: string;
  @Attr({ persist: false }) public readonly expiresAtShortWithTime: string;
  @Attr({ persist: false }) public readonly resultAtShortWithTime: string;
  @Attr({ persist: false }) public readonly sentAtShortWithTime: string;
  @Attr({ persist: false }) public readonly updatedAtShortWithTime: string;

  @Attr({ persist: false }) public readonly retractable: boolean;
  @Attr({ persist: false }) public readonly rejectable: boolean;
  @Attr({ persist: false }) public readonly expired: boolean;
  @Attr({ persist: false }) public readonly resultAt: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly salaryFormatted: string;
  @Attr({ persist: false }) public readonly salaryFormattedPer: string;
  @Attr({ persist: false }) public skipOfferTemplate: boolean;
  @Attr({ persist: false }) public readonly pdfFile: { path: string; name: string };
  @Attr({ persist: false }) public readonly userStageSignatory: { id: string; user_id: string };
  @Attr({ persist: false }) public readonly positionName: string;

  @Attr() public overwriteTemplateDocx: string;
  @Attr() public offerDocuments: string[];
  @Attr({ persist: false }) public readonly offerDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly responseDocuments: string[];
  @Attr({ persist: false }) public readonly responseDocumentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly signableSignOffStageId: string;
  @Attr({ persist: false }) public readonly draftRejectionsPresent: boolean;

  @BelongsTo() public docuSignEnvelope: DocuSignEnvelope;
  @BelongsTo() public offerTemplate: OfferTemplate;
  @BelongsTo() public bulkOffer: BulkOffer;
  @BelongsTo() public application: Application;
  @BelongsTo() public manager: User;
  @HasOne() public currentApprovalWorkflow: ApprovalWorkflow;
  @HasMany() public audits: Audit[];
  @HasMany() public associatedAudits: Audit[];
  @HasMany() public customAttributes: CustomAttribute[];
  @HasMany() public signOffStageApprovals: SignOffStageApproval[];
  @HasMany() public offerDocumentObjects: OfferDocument[];
}

export namespace Offer {
  export namespace Stats {
    export type PropertiesParserResources = { value: string; text: string }[];
    export type options = {
      salary_durations: { label: string; value: string }[];
      enum_results: { label: string; value: string }[];
    };
  }
}

@Model()
export class OfferDocument extends ApplicationRecord {
  public static jsonapiType = 'offer_documents';

  @Attr() public name: string;
  @Attr() public position: number;
  @Attr({ persist: false }) public readonly docxDocumentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly pdfDocumentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly docxDocumentUrl: string;
  @Attr({ persist: false }) public readonly documentCallbackUrl: string;
  @Attr({ persist: false }) public readonly signedDocumentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly docxLastGeneratedAt: string;
  @Attr({ persist: false }) public readonly pdfLastGeneratedAt: string;
}

@Model()
export class PinpointApiKey extends ApplicationRecord {
  public static jsonapiType = 'pinpoint_api_keys';

  public static validationSchema = Yup.object().shape<Shape<PinpointApiKey>>({
    name: Yup.string().required("Can't be blank."),
  });

  public static NEW = (attributes: Partial<PinpointApiKey> = {}) =>
    new PinpointApiKey({
      name: '',
      ...attributes,
    });

  @Attr() public name: string;
  @Attr() public readonly token: string;
  @Attr() public readonly lastUsedOnShortWithYearDate: string;
  @Attr() public scope: Record<string, ('read' | 'write' | 'delete')[]>;

  @BelongsTo() public company: Company;
  @BelongsTo() public integration: IntegrationsPinpointApi;
}

@Model()
export class Profile extends MobilityApplicationRecord {
  public static jsonapiType = 'profiles';

  public static validationSchema = Yup.object().shape({
    avatar: Yup.string().required("Can't be blank.").nullable(),
    roleDescription: Yup.string().required("Can't be blank.").nullable(),
  });

  public static NEW() {
    const attrs: Partial<Profile> = {
      adviceDescription: '',
      backgroundDescription: '',
      facebookHandle: '',
      jobTitle: '',
      linkedinHandle: '',
      public: false,
      recommendationDescription: '',
      roleDescription: '',
      telephoneNumber: '',
      telephoneNumberIso2: '',
      twitterHandle: '',
      whatsappNumber: '',
      whatsappNumberIso2: '',
    };
    return new this(attrs);
  }

  @Attr() public jobTitle: string;
  @Attr() public userId: string;
  @Attr() public avatar: string;

  @Attr() public roleDescription: string;
  @Attr() public backgroundDescription: string;
  @Attr() public recommendationDescription: string;
  @Attr() public adviceDescription: string;

  @Attr() public whatsappNumber: string;
  @Attr() public whatsappNumberIso2: string;
  @Attr() public telephoneNumber: string;
  @Attr() public telephoneNumberIso2: string;
  @Attr() public public: boolean;
  @Attr() public linkedinHandle: string;
  @Attr() public facebookHandle: string;
  @Attr() public twitterHandle: string;
  @Attr() public externalShowPath: string;

  @Attr({ persist: false }) public readonly publicizable: boolean;
  @Attr({ persist: false }) public readonly isPubliclyVisible: boolean;

  @BelongsTo() public user: User;
}

export namespace Question {
  export namespace Stats {
    export type options = {
      answer_regex_options: ISelectOption[];
    };
  }
}

@Model()
export class Question extends MobilityApplicationRecord {
  public static MAX_PER = 1000;

  public static jsonapiType = 'questions';
  public static validationSchema = Yup.object().shape({
    title: Yup.string().nullable().required("Can't be blank."),
  });

  public static booleanAttributeOptions: ISelectOption[] = [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ];

  public static questionTypeOptions: ISelectOption[] = [
    { label: 'Long Text', value: 'long_text' },
    { label: 'Short Text', value: 'short_text' },
    { label: 'Yes / No', value: 'boolean' },
    { label: 'Multiple Choice', value: 'multiple_choice' },
    { label: 'Document', value: 'document' },
    { label: 'Date', value: 'date' },
    { label: 'URL', value: 'url' },
    { label: 'Date range', value: 'date_range' },
  ];

  public static NEW(attrs: Partial<Question> = {}) {
    const defaultAttrs: Partial<Question> = {
      additionalInformation: '',
      questionType: 'long_text',
      enforced: false,
      required: false,
      sensitive: false,
      title: '',
      numRequiredAnswerOptions: null,
      maxNumRequiredAnswerOptions: null,
      answerRegex: null,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public additionalInformation: string;
  @Attr() public answerRegex: string;
  @Attr({ persist: false }) public readonly answerRegexDetails: Record<string, string>;
  @Attr({ persist: false }) public collapsed: boolean;
  @Attr() public title: string;
  @Attr({ persist: false }) public readonly titleEn: string;
  @Attr() public position: number;
  @Attr() public questionType: Question_question_type_key;
  @Attr() public questionContext: Question_question_context_key;
  @Attr() public required: boolean;
  @Attr() public numRequiredAnswerOptions: string;
  @Attr() public maxNumRequiredAnswerOptions: string;
  @Attr() public sensitive: boolean;
  @Attr() public banked: boolean;
  @Attr() public enforced: boolean;
  @Attr() public duplicate: boolean;
  @Attr({ persist: false }) public uniqueId: number;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly numberOfUses: number;

  // @extraAttributes
  @Attr({ persist: false }) public readonly multipleChoiceHelperText: string;

  @HasMany() public scorecardAnswers: ScorecardAnswer[];
  @HasMany() public questionOptions: QuestionOption[];
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @HasMany() public questionCollectionsQuestions: QuestionCollectionsQuestion[];
  @HasMany() public conditionalQuestions: ConditionalQuestion[];
}

@Model()
export class QuestionCollection extends ApplicationRecord {
  public static jsonapiType = 'question_collections';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank."),
  });

  public static NEW(attrs: Partial<QuestionCollection> = {}) {
    const defaultAttrs: Partial<QuestionCollection> = {
      name: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public name: string;
  @Attr({ persist: false }) public readonly questionCount: number;

  @BelongsTo() public company: Company;
  @HasMany() public questionCollectionsQuestions: QuestionCollectionsQuestion[];
}

@Model()
export class QuestionCollectionsQuestion extends ApplicationRecord {
  public static jsonapiType = 'question_collections_questions';

  @Attr() public position: number;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly questionCollectionId: string;
  @Attr({ persist: false }) public readonly questionId: string;

  @BelongsTo() public question: Question;
  @BelongsTo() public questionCollection: QuestionCollection;
  @BelongsTo() public company: Company;
}

@Model()
export class QuestionsQuestionable extends ApplicationRecord {
  public static jsonapiType = 'questions_questionables';

  public static NEW(attrs: Partial<QuestionsQuestionable> = {}) {
    return new this({ ...attrs });
  }

  @Attr() public position: number;
  @Attr() public questionId: string;
  @Attr({ persist: false }) public readonly questionableId: string;
  @Attr({ persist: false }) public readonly questionableType:
    | 'Job'
    | 'TalentPipeline'
    | 'ScorecardSection'
    | 'DocumentTemplate';

  @HasMany() public audits: Audit[];
  @BelongsTo() public company: Company;
  @BelongsTo() public question: Question;
  @BelongsTo() public questionable: Job | TalentPipeline | ScorecardSection | DocumentTemplate;
}

@Model()
export class QuestionOption extends ApplicationRecord {
  public static jsonapiType = 'question_options';
  @Attr() public questionId: string;
  @Attr() public text: string;
  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class Rating extends ApplicationRecord {
  public static jsonapiType = 'ratings';
  @Attr() public score: number;
  @Attr() public createdAt: string;
  @BelongsTo() public user: User[];
}

@Model()
export class ReferenceCheck extends ApplicationRecord {
  public static jsonapiType = 'reference_checks';
  @Attr({ persist: false }) public readonly applicationId: number;
  @Attr({ persist: false }) public readonly pdfUrl: string;
  @Attr({ persist: false }) public readonly complete: boolean;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly numberOfReferencesRequested: number;
  @Attr({ persist: false }) public readonly numberOfReferencesCompleted: number;
}

@Model()
export class Referral extends ApplicationRecord {
  public static jsonapiType = 'referrals';

  @Attr() public status: 'pending' | 'accepted' | 'rejected';
  @Attr({ persist: false }) public readonly amountFormatted: string;
  @Attr() public amountPaid: string;
  @Attr() public currency: string;
  @Attr() public amount: string;
  @Attr() public referrableId: string;
  @Attr() public referralsUserId: string;
  @Attr() public referrableType: string;
  @Attr() public companyId: string;
  @Attr() public updatedAt: string;

  @Attr({ persist: false }) public readonly referralsUserFullName: string;
  @Attr({ persist: false }) public readonly referralsUserEmail: string;
  @Attr({ persist: false }) public readonly referralsUserDeleted: boolean;
  @Attr({ persist: false }) public readonly details?: string;
  @Attr({ persist: false }) public readonly statusAt: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  // @extraAttributes
  @Attr({ persist: false }) public readonly jobTitleAndStatus: string;
  @Attr({ persist: false }) public readonly applicationFullNameAndStatus: string;

  get workflowTagVariant() {
    switch (this.status) {
      case 'pending':
        return 'review';
      case 'accepted':
        return 'hired';
      case 'rejected':
        return 'rejected';
      default:
        const never: never = this.status;
    }
  }

  get workflowTagText() {
    switch (this.status) {
      case 'pending':
        return 'Pending';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      default:
        const never: never = this.status;
    }
  }

  @BelongsTo() public referrable: Application;
  @BelongsTo() public referralsUser: ReferralsUser;
}

@Model()
export class ReferralsUser extends ApplicationRecord {
  public static jsonapiType = 'referrals_users';
  public static validationSchema = Yup.object().shape({
    email: Yup.string().required("Can't be blank."),
    firstName: Yup.string().required("Can't be blank."),
    lastName: Yup.string().required("Can't be blank."),
  });

  public static NEW(attrs: Partial<ReferralsUser> = {}) {
    const defaultAttrs: Partial<ReferralsUser> = {
      email: '',
      firstName: '',
      lastName: '',
      locale: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public userId: string;
  @Attr() public firstName: string;
  @Attr() public lastName: string;
  @Attr() public locale: string;
  @Attr({ persist: false }) public readonly fullName: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr() public email: string;
  @Attr() public shouldInvite: boolean;

  @Attr({ persist: false }) public readonly invitationCreatedAt?: string;
  @Attr({ persist: false }) public readonly invitationAcceptedAt?: string;
  @Attr({ persist: false }) public readonly canDestroy?: boolean;

  @HasMany() public companies: Company[];
  @BelongsTo() public user: User;
}

@Model()
export class Requisition extends ApplicationRecord {
  public static jsonapiType = 'requisitions';
  public static MAX_PER = 1000;

  get isApproved(): boolean {
    return this.status === 'approved';
  }

  get isPending(): boolean {
    return this.status === 'pending_approval';
  }

  get isArchived(): boolean {
    return !!this.archivedAt;
  }

  @Attr() public archivedAt: string;
  @Attr() public identifier: string;
  @Attr() public requisitionTemplateId: string;
  @Attr() public name: string;
  @Attr() public jobId: string;
  @Attr() public archive?: boolean;
  @Attr() public unarchive?: boolean;
  @Attr() public jobUpdate?: boolean;
  @Attr({ persist: false }) public structureLinkId: string;
  @Attr({ persist: false }) public status: string;
  @Attr({ persist: false }) public creatorId: string;
  @Attr({ persist: false }) public companyId: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly createdAtLongDate: string;
  @Attr({ persist: false }) public readonly archivedAtLongDate: string;
  @Attr({ persist: false }) public readonly approvedAtLongDate: string;
  @Attr({ persist: false }) public readonly headcount: number;
  @Attr({ persist: false }) public readonly enforcedMaximumHeadcount: number;

  /* Policy checks, also extra attributes */
  @Attr({ persist: false }) public readonly canCreateJob: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr({ persist: false }) public readonly requisitionTemplateName: string;
  @Attr({ persist: false }) public readonly jobDetailedTitle: string;
  @Attr({ persist: false }) public readonly isSignOffRejected: boolean;
  @Attr({ persist: false }) public readonly mappedTitleField: string;

  @BelongsTo() public requisitionTemplate: RequisitionTemplate;
  @BelongsTo() public job: Job;
  @BelongsTo() public creator: User;
  @BelongsTo() public structureLink: StructureLink;
  @HasMany() public audits: Audit[];
  @HasMany() public associatedAudits: Audit[];
  @HasMany() public requisitionDetails: RequisitionDetail[];
  @HasMany() public signOffStages: SignOffStage[];
  @HasMany() public signOffStageApprovals: SignOffStageApproval[];
  @HasMany() public skippedSignOffStages: SkippedSignOffStage[];
  @HasOne() public currentApprovalWorkflow: ApprovalWorkflow;
  @HasMany() public positions: Position[];
  @HasMany() public positionOpenings: PositionOpening[];
}

export namespace Requisition {
  export namespace Stats {
    export type options = {
      enum_statuses: { value: number; label: string }[];
    };
    export type total_count = number;
  }
}

@Model()
export class RequisitionDetail extends ApplicationRecord {
  public static jsonapiType = 'requisition_details';

  @Attr() public requisitionId: string;
  @Attr() public requisitionTemplateFieldId: string;
  @Attr() public varcharValue: string;
  @Attr() public textValue: string;
  @Attr() public richTextValue: string;
  @Attr() public booleanValue: boolean;
  @Attr() public dateValue: string;
  @Attr() public decimalValue: string;
  @Attr() public integerValue: string;
  @Attr() public document: string;
  @Attr() public singleSelectValue: string;
  @Attr() public multiSelectValue: string[];
  @Attr({ persist: false }) public readonly selectedMultiSelectOptions: ISelectOption[];

  @Attr({ persist: false }) public fieldType: string;
  @Attr({ persist: false }) public label: string;
  @Attr({ persist: false }) public customFieldType: string;
  @Attr({ persist: false }) public readonly customFieldId: string;
  @Attr({ persist: false }) public jobFormField: string;
  @Attr({ persist: false }) public sensitiveCustomFieldDetail: string;
  @Attr({ persist: false }) public humanizedCustomFieldSingleSelectValue: string;
  @Attr({ persist: false }) public humanizedCustomFieldMultiSelectValue: string;
  @Attr({ persist: false }) public readonly documentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly requisitionTemplateName: string;

  @BelongsTo() requisition: Requisition;
  @BelongsTo() requisitionTemplateField: RequisitionTemplateField;
}

@Model()
export class RequisitionTemplate extends ApplicationRecord {
  public static jsonapiType = 'requisition_templates';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
    enforcedMaximumHeadcount: Yup.number()
      .nullable()
      .when('hasEnforcedMaximumHeadcount', {
        is: true,
        then: Yup.number().required("Can't be blank").min(1, "Can't be less than 1").integer("Can't be a decimal"),
      }),
  });

  @Attr() public companyId: string;
  @Attr() public duplicateId: string;
  @Attr() public name: string;
  @Attr() public enforcedMaximumHeadcount: number;
  // @extraAttributes
  @Attr({ persist: false }) public readonly hasEnforcedMaximumHeadcount: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @HasMany() public requisitionTemplateFields: RequisitionTemplateField[];
}

@Model()
export class RequisitionTemplateField extends ApplicationRecord {
  public static jsonapiType = 'requisition_template_fields';

  get isCustomField(): boolean {
    return this.fieldType === 'custom_field';
  }

  @Attr() public requisitionId: string;
  @Attr() public description: string;
  @Attr() public label: string;
  @Attr() public fieldType: string;
  @Attr() public mappedJobFormField: string;
  @Attr() public options: string[];
  @Attr() public position: number;
  @Attr() public deletable: boolean;
  @Attr() public customFieldUniqueReference: string;
  @Attr() public requirementLevel: string;

  @Attr({ persist: false }) public uniqueId: string;

  @HasOne() public requisitionTemplateFieldsCustomField: RequisitionTemplateFieldsCustomField;
  @BelongsTo() public requisitionTemplate: RequisitionTemplate;
}

@Model()
export class RequisitionTemplateFieldsCustomField extends ApplicationRecord {
  public static jsonapiType = 'requisition_template_fields_custom_fields';

  @Attr() public companyId: string;
  @Attr() public requisitionTemplateFieldId: string;
  @Attr() public customFieldId: string;

  @BelongsTo() public requisitionTemplateField: RequisitionTemplateField;
  @BelongsTo() public customField: CustomField;
}

@Model()
export class Resume extends ApplicationRecord {
  public static jsonapiType = 'resumes';

  @Attr() public resumeableId: string;
  @Attr() public resumeableType: string;
  @Attr() public executiveSummary: string;
  @Attr() public sovrenDescription: string;
  @Attr() public formattedName: string;
  @Attr() public address: string;
  @Attr() public telephone: string;
  @Attr() public mobile: string;
  @Attr() public email: string;
  @Attr() public website: string;
  @Attr() public certifications: string[];
  @Attr() public achievements: string[];
  @Attr() public isProcessing: boolean;
  @HasMany() public employmentHistories: EmploymentHistory[];
  @HasMany() public educationHistories: EducationHistory[];
}

@Model()
export class Role extends ApplicationRecord {
  public static jsonapiType = 'roles';

  public static requisitionSlugs = ['requisition_creator', 'requisition_manager', 'requisition_viewer'];

  @Attr({ persist: false }) public readonly description: string;
  @Attr({ persist: false }) public readonly slug: string;
  @Attr({ persist: false }) public readonly visible: string;
}

export namespace Role {
  export namespace Stats {
    export type options = {
      role_categories: {
        name: string;
        subjects: {
          name: string;
          visibility_dependant?: boolean;
          roles: {
            slug: string;
            description: string;
            additional_information?: string;
            uneditable?: boolean;
            tooltip_content?: string;
          }[];
        }[];
      }[];
    };
    export type total_count = number;
  }
}

@Model()
export class ScheduledBackgroundCheck extends ApplicationRecord {
  public static jsonapiType = 'scheduled_background_checks';

  get isPending(): boolean {
    return this.status === 'pending';
  }

  get isCancelled(): boolean {
    return this.status === 'cancelled';
  }

  get isSterling(): boolean {
    return this.provider === 'sterling';
  }

  @Attr({ persist: false }) public readonly humanizedIdentifier: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly provider: string;
  @Attr({ persist: false }) public readonly applicationId: number;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly runAt: string;
  @Attr({ persist: false }) public readonly logo: string;
}

@Model()
export class ScheduledTask extends ApplicationRecord {
  public static jsonapiType = 'scheduled_tasks';

  public static validationSchema = Yup.object().shape({
    delayOperator: Yup.string().required("Can't be blank.").nullable(),
    delayValue: Yup.number().required("Can't be blank.").nullable(),
    dateType: Yup.string().required("Can't be blank.").nullable(),
    title: Yup.string().required("Can't be blank").nullable(),
    description: Yup.string().required("Can't be blank").nullable(),
  });

  public static recipientBooleanFields = ['assignToHiringManager', 'assignToRecruitmentManager'];

  @Attr() public companyId: string;
  @Attr() public onboardingWorkflowId: string;
  @Attr() public dateType: string;
  @Attr() public delayValue: number;
  @Attr() public delayOperator: string;
  @Attr() public assignToHiringManager: boolean;
  @Attr() public assignToRecruitmentManager: boolean;
  @Attr() public title: string;
  @Attr() public description: string;
  @Attr() public triggerType: string;
  @Attr() public userIds: string[];

  @BelongsTo() public scheduledTaskable: OnboardingWorkflow;
  @BelongsTo() public company: Company;
}

@Model()
export class ScopeCondition extends ApplicationRecord {
  public static jsonapiType = 'scope_conditions';

  @Attr() public scopeableId: string;
  @Attr() public scopeableType: string;
  @Attr() public conditions: ScopeConditionConditions;

  @BelongsTo() public company: Company;
  @BelongsTo() public scopeable: OfferTemplate | EmailTemplate | SmsTemplate | DocumentTemplate | InterviewType;
}

@Model()
export class Scorecard extends ApplicationRecord {
  public static jsonapiType = 'scorecards';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });
  @Attr() public name: string;
  @Attr() public interviewGuidance: string;
  @Attr({ persist: false }) public isActive: boolean;
  @Attr() public scorecardableId: string;
  @Attr() public scorecardableType: string;
  @Attr({ persist: false }) public assignedStagesCount: string;
  @HasMany() public scorecardSections: ScorecardSection[];
  @HasMany() public scorecardableScorecards: ScorecardableScorecard[];
  @HasMany() public scorecardNotes: ScorecardNote[];
  @HasMany() public userScorecards: UserScorecard[];
}

@Model()
export class ScorecardNote extends ApplicationRecord {
  public static jsonapiType = 'scorecard_notes';
  @Attr() public scorecardId: string;
  @Attr() public body: string;
  @Attr() public userId: string;
  @BelongsTo() public user: User;
}

@Model()
export class ScorecardAnswer extends ApplicationRecord {
  public static jsonapiType = 'scorecard_answers';
  @Attr() public booleanAnswer: boolean;
  @Attr() public textAnswer: string;
  @Attr() public userId: number;
  @Attr() public questionId: string;

  @BelongsTo() public question: Question;
  @BelongsTo() public scoreable: Application | JobSeeker;
  @BelongsTo() public assignable: Stage | TalentPipeline;
  @BelongsTo() public user: User;
}

@Model()
export class ScorecardOption extends ApplicationRecord {
  public static jsonapiType = 'scorecard_options';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").max(255, 'Max length is 255 characters.'),
    weightedValue: Yup.number().required("Can't be blank").nullable().typeError('Must be a number'),
  });
  @Attr() public name: string;
  @Attr() public position: number;
  @Attr() public weightedValue: number;
  @Attr({ persist: false }) public uniqueId: string;

  @HasMany() public scorecardRatings: ScorecardRating;
}

@Model()
export class ScorecardRating extends ApplicationRecord {
  public static jsonapiType = 'scorecard_ratings';
  @Attr() public scorecardOptionId: string;
  @Attr() public score: number;
  @Attr() public userId: string;
  @BelongsTo() public user: User;
  @BelongsTo() public scorecardOption: ScorecardOption;
  @BelongsTo() public userScorecard: UserScorecard;
}

@Model()
export class ScorecardSection extends ApplicationRecord {
  public static jsonapiType = 'scorecard_sections';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").max(255, 'Max length is 255 characters.'),
  });
  @Attr() public additionalDetails: string;
  @Attr() public name: string;
  @Attr() public position: number;
  // @extraAttributes
  @Attr({ persist: false }) public readonly optionCount: number;
  @Attr({ persist: false }) public readonly questionCount: number;
  @Attr({ persist: false }) public uniqueId: string;
  // @unpersistedAttributes
  @Attr({ persist: false }) public collapsed: boolean;
  @HasMany() public scorecardOptions: ScorecardOption[];
  @HasMany() public questions: Question[];
}

@Model()
export class Skill extends ApplicationRecord {
  public static jsonapiType = 'skills';
  public static MAX_PER = 10000;

  @Attr() public name: string;
}

@Model()
export class SkillablesSkill extends ApplicationRecord {
  public static jsonapiType = 'skillables_skills';
  @Attr() public skillId: string;
  @Attr() public skillableId: string;
  @Attr() public skillableType: string;
  @Attr() public context: string;

  @BelongsTo() public skill: Skill;
  @BelongsTo() public skillable: Application | Job | JobSeeker;
}

@Model()
export class StageGroup extends ApplicationRecord {
  public static jsonapiType = 'stage_groups';

  public static validationSchema = Yup.object().shape({
    color: Yup.string().nullable().required("Can't be blank."),
    name: Yup.string().nullable().required("Can't be blank."),
  });

  public static deletionValidationSchema = Yup.object().shape({
    reassignStageGroupId: Yup.string().nullable().required("Can't be blank."),
  });

  @Attr() public color: string;
  @Attr() public name: string;
  @Attr() public position: number;
  @Attr() public reassignStageGroupId: string;

  // Extra attributes
  @Attr() public groupedStageCount: { hiring_workflow: number; job: number };

  @HasMany() public stages: Stage[];
}

@Model()
export class StructureCustomGroupOne extends MobilityApplicationRecord {
  public static jsonapiType = 'structure_custom_group_ones';
  public static MAX_PER = 10000;

  public static NEW() {
    const attrs: Partial<StructureCustomGroupOne> = {
      name: '',
    };
    return new this(attrs);
  }

  @Attr() public name: string;
  @Attr() public activate: boolean;
  @Attr() public deactivate: boolean;
  @Attr({ persist: false }) public readonly inactive: boolean;

  /** @extraAttribute */
  @Attr({ persist: false }) public readonly inUse: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @HasOne() public themesThemeable: ThemesThemeable;
  @HasOne() public dataRetentionPolicyRetentionable: DataRetentionPolicyRetentionable;
  @BelongsTo() public company: Company;
}

@Model()
export class Source extends ApplicationRecord {
  public static jsonapiType = 'sources';

  @Attr({ persist: false }) public readonly userId: string;
  @Attr() public updatedAt: string;

  @BelongsTo() user: User;
}

@Model()
export class BulkStructureLink extends ApplicationRecord {
  public static jsonapiType = 'bulk_structure_links';

  public static NEW() {
    const attrs: Partial<BulkStructureLink> = {
      acceptsSpeculativeApplications: true,
      departmentIds: [],
      divisionIds: [],
      locationIds: [],
      structureCustomGroupOneIds: [],
    };
    return new this(attrs);
  }

  @Attr() public departmentIds: string[];
  @Attr() public divisionIds: string[];
  @Attr() public locationIds: string[];
  @Attr() public structureCustomGroupOneIds: string[];
  @Attr() public acceptsSpeculativeApplications: boolean;
}

@Model()
export class StructureLink extends ApplicationRecord {
  public static jsonapiType = 'structure_links';
  public static MAX_PER = 16000;
  public static NEW() {
    const attrs: Partial<StructureLink> = {
      acceptsSpeculativeApplications: true,
      departmentId: '',
      divisionId: '',
      locationId: '',
      structureCustomGroupOneId: '',
    };
    return new this(attrs);
  }

  @Attr() public divisionId: string;
  @Attr() public locationId: string;
  @Attr() public departmentId: string;
  @Attr() public structureCustomGroupOneId: string;
  @Attr() public acceptsSpeculativeApplications: boolean;

  @Attr({ persist: false }) public readonly displayName: string;
  @Attr({ persist: false }) public readonly locationName: string;
  @Attr({ persist: false }) public readonly departmentName: string;
  @Attr({ persist: false }) public readonly divisionName: string;
  @Attr({ persist: false }) public readonly structureCustomGroupOneName: string;
  @Attr({ persist: false }) public readonly usersWithVisibility: string[];
  @Attr({ persist: false }) public readonly inactive: boolean;

  @Attr({ persist: false }) public readonly inUse: boolean;
  @Attr({ persist: false }) public readonly canUpdate: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public location: Location;
}

@Model()
export class TagCategory extends ApplicationRecord {
  public static jsonapiType = 'tag_categories';
  public static MAX_PER = 1000;

  @Attr({ persist: false }) public readonly categoryType: string;
  @Attr() public companyId: string;
  @Attr() public color: string;
  @Attr() public name: string;
  @Attr() public parameterizedName: string;
  @Attr() public tagList: string[];
  @Attr() public tagToRemove: string;
}

@Model()
export class UsersRole extends ApplicationRecord {
  public static jsonapiType = 'users_roles';
  @Attr() public companyId: string;
  @Attr() public canDestroy: string;
  @Attr() public directAssignment: boolean;
  @Attr() public userId: string;
  @Attr() public roleId: string;
  @BelongsTo() public user: User;
  @BelongsTo() public role: Role;
}

@Model()
export class VideoInterviewQuestion extends ApplicationRecord {
  public static jsonapiType = 'video_interview_questions';
  public static endpoint = '/video_interview_questions';
  public instanceClasses: VideoInterviewQuestionSonru | VideoInterviewQuestionMyInterview;

  @Attr() public content: string;

  @Attr() public editable: boolean;
  @Attr() public deletable: boolean;

  @Attr() public videoInterviewId: string;
}

@Model()
export class VideoInterviewQuestionSonru extends VideoInterviewQuestion {
  public static jsonapiType = 'video_interview_questions_sonru';

  public static validationSchema = Yup.object().shape<Shape<VideoInterviewQuestionSonru>>({
    content: Yup.string().required("Can't be blank."),
    timeToAnswer: Yup.string()
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' })
      .required("Can't be blank."),
    timeToRead: Yup.string()
      .matches(/^[0-9\.]+$/, { message: 'Must contain only numeric characters.' })
      .required("Can't be blank."),
  });

  public static NEW() {
    const attrs: Partial<VideoInterviewQuestionSonru> = {
      content: '',
      timeToAnswer: '2',
      timeToRead: '2',
    };
    return new this(attrs);
  }

  public staticType = 'Sonru' as const;

  @Attr() public timeToAnswer: number | string;
  @Attr() public timeToRead: number | string;

  @BelongsTo() public videoInterview: VideoInterviewSonru;
}

@Model()
export class VideoInterviewQuestionMyInterview extends VideoInterviewQuestion {
  public static jsonapiType = 'video_interview_questions_my_interview';

  public static validationSchema = Yup.object().shape<Shape<VideoInterviewQuestionMyInterview>>({
    content: Yup.string().required("Can't be blank."),
    numOfRetakes: Yup.string()
      .matches(/^[0-9]+$/, { message: 'Must be a whole number.' })
      .required("Can't be blank."),
    partDuration: Yup.string()
      .matches(/^[0-9]+$/, { message: 'Must be a whole number.' })
      .required("Can't be blank."),
  });

  public static NEW() {
    const attrs: Partial<VideoInterviewQuestionMyInterview> = {
      conclusion: '',
      content: '',
      description: '',
      explanation: '',
      numOfRetakes: '1',
      partDuration: '30',
      points: [],
    };
    return new this(attrs);
  }

  public staticType = 'MyInterview' as const;

  @Attr() public partDuration: number | string;
  @Attr() public numOfRetakes: number | string;
  @Attr() public description: string;
  @Attr() public explanation: string;
  @Attr() public points: string[];
  @Attr() public conclusion: string;

  @BelongsTo() public videoInterview: VideoInterviewMyInterview;
}

@Model()
export class VideoInterviewCandidate extends ApplicationRecord {
  public static jsonapiType = 'video_interview_candidates';
  public static endpoint = '/video_interview_candidates';
  public instanceClasses:
    | VideoInterviewCandidateHireflix
    | VideoInterviewCandidateSonru
    | VideoInterviewCandidateMyInterview;

  @Attr() public status: string;

  @Attr() public applicationId: string;
  @Attr() public videoInterviewId: string;

  @Attr() public responded: boolean;

  @Attr() public sentAt: string;

  @Attr({ persist: false }) public readonly sentAtShortWithYearTimeWithZone: string;

  @BelongsTo() public application: Application;
}

@Model()
export class VideoInterviewCandidateHireflix extends VideoInterviewCandidate {
  public static jsonapiType = 'video_interview_candidates/hireflix';
  public staticType = 'Hireflix' as const;

  @Attr({ persist: false }) public readonly canDestroy: boolean;
  @Attr() public readonly resultUrl: string;
  @Attr() public readonly status: string;

  @BelongsTo() public videoInterview: VideoInterviewHireflix;
}

@Model()
export class VideoInterviewCandidateSonru extends VideoInterviewCandidate {
  public static jsonapiType = 'video_interview_candidates/sonru';
  public staticType = 'Sonru' as const;

  @Attr() public iframeUrl: string;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public videoInterview: VideoInterviewSonru;
}

@Model()
export class VideoInterviewCandidateMyInterview extends VideoInterviewCandidate {
  public static jsonapiType = 'video_interview_candidates/my_interview';
  public staticType = 'MyInterview' as const;

  @Attr() public iframeUrl: string;
  @Attr() public resend?: boolean;
  @Attr({ persist: false }) public readonly canInvite: boolean;
  @Attr({ persist: false }) public readonly canResend: boolean;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public videoInterview: VideoInterviewMyInterview;
}

@Model()
export class VideoInterview extends ApplicationRecord {
  public static jsonapiType = 'video_interviews';
  public static endpoint = '/video_interviews';
  public instanceClasses: VideoInterviewSonru | VideoInterviewMyInterview;

  @Attr() public uid: string;
  @Attr() public title: string;

  @Attr() public sentAt: string;
  @Attr() public stageId: string;
  @Attr() public integrationId: string;

  @Attr() public editable: boolean;
  @Attr() public publishable: boolean;
  @Attr() public published: boolean;

  @BelongsTo() public stage: Stage;
  @HasMany() public videoInterviewQuestions: VideoInterviewQuestion[];
  @HasMany() public videoInterviewCandidates: VideoInterviewCandidate[];
}
// Note (Michal Sikora) - we don't extend the VideoInterview class here
@Model()
export class VideoInterviewHireflix extends ApplicationRecord {
  public static jsonapiType = 'video_interviews_hireflix';

  public staticType = 'Hireflix' as const;

  @Attr() public title: string;
  @Attr() public positionId: string;

  @Attr() public sentAt: string;
  @Attr() public stageId: string;
  @Attr() public integrationId: string;

  @Attr() public published: boolean;
}

@Model()
export class VideoInterviewSonru extends VideoInterview {
  public static jsonapiType = 'video_interviews_sonru';

  public static NEW() {
    const attrs: Partial<VideoInterviewSonru> = {
      companyDescription: '',
      dueAt: addDays(new Date(), 7).toISOString(),
      interviewDescription: '',
      title: '',
    };

    return new this(attrs);
  }

  public staticType = 'Sonru' as const;

  @Attr() public dueAt: string;
  @Attr() public notifyUnresponded: boolean;
  @Attr() public companyDescription: string;
  @Attr() public interviewDescription: string;
  @Attr() public locale: string;

  @Attr({ persist: false }) public readonly companyDescriptionLocked: boolean;
  @Attr({ persist: false }) public readonly interviewDescriptionLocked: boolean;

  @HasMany() public videoInterviewQuestions: VideoInterviewQuestionSonru[];

  get questionClass() {
    return VideoInterviewQuestionSonru;
  }

  get candidateClass() {
    return VideoInterviewCandidateSonru;
  }
}

@Model()
export class VideoInterviewMyInterview extends VideoInterview {
  public static jsonapiType = 'video_interviews_my_interview';

  public static NEW() {
    const attrs: Partial<VideoInterviewMyInterview> = {
      countdownTime: '30',
      firstScreenSubtitleOne: '',
      firstScreenSubtitleTwo: '',
      firstScreenTitle: '',
      hideQuestions: false,
      introVideo: '',
      introVideoTitle: '',
      practice: false,
      title: '',
    };
    return new this(attrs);
  }

  public staticType = 'MyInterview' as const;

  @Attr() public practice: boolean;
  @Attr() public hideQuestions: boolean;
  @Attr() public countdownTime: number | string;
  @Attr() public firstScreenTitle: string;
  @Attr() public firstScreenSubtitleOne: string;
  @Attr() public firstScreenSubtitleTwo: string;
  @Attr() public language: 'en' | 'es' | 'pt' | 'he' | 'de' | null;
  @Attr() public introVideo: string;
  @Attr() public introVideoTitle: string;
  @Attr() public region: 'eu' | 'us' | 'au' | null;

  @HasMany() public videoInterviewQuestions: VideoInterviewQuestionMyInterview[];

  get questionClass() {
    return VideoInterviewQuestionMyInterview;
  }

  get candidateClass() {
    return VideoInterviewCandidateMyInterview;
  }
}

@Model()
export class OnboardingApplication extends ApplicationRecord {
  public static jsonapiType = 'onboarding_applications';

  public static validationSchema = Yup.object().shape({
    onboardingProcessId: Yup.string().nullable().required("Can't be blank."),
    onboardingPortalBigintId: Yup.string().nullable().required("Can't be blank."),
    ownerId: Yup.string().nullable().required("Can't be blank."),
    email: Yup.string().nullable().required("Can't be blank."),
  });

  get isArchived(): boolean {
    return this.status === 'archived';
  }

  public static NO_START_DATE_DATE_UTC = '3000-01-01T12:00Z';
  public static NO_START_DATE_DATE = '3000-01-01';

  @Attr() public applicationId: string;
  @Attr() public onboardingWorkflowId: string;
  @Attr() public startAt: string;
  @Attr() public email: string;
  @Attr() public discardedAt: string;
  @Attr() public status: string;
  @Attr() public companyId: string;
  @Attr() public restart: boolean;
  @Attr() public restartProcess: boolean;
  @Attr() public bulkAssignment: boolean;
  @Attr() public updatedAt: string;
  @Attr() public onboardingProcessId: string;
  @Attr() public onboardingPortalBigintId: string;
  @Attr() public processAssignedAt: string;
  @Attr() public ownerId: string;
  @Attr() public recruitmentManagerId: string;
  @Attr() public hiringManagerId: string;
  /** @extraAttributes */
  @Attr({ persist: false }) public readonly cachedInferredStatus: string;
  @Attr({ persist: false }) public readonly onboardingStagesViewState: StagePayload;
  @Attr({ persist: false }) public readonly condensedCurrentViewState: ProcessPayload;
  @Attr({ persist: false }) public readonly cachedUnapprovedRequiredAnswersCount: number;
  @Attr() public progress: number;
  @Attr({ persist: false }) public currentViewState: ProcessPayload;
  @Attr({ persist: false }) public managedByCurrentUser: boolean;
  @Attr({ persist: false }) public docuSignUrl: string;
  @Attr({ persist: false }) public inferredStatus: string;
  @Attr({ persist: false }) public unapprovedRequiredAnswersCount: number;
  @Attr({ persist: false }) public canSendToAdonisHr: boolean;
  @Attr({ persist: false }) public canSendToBizimply: boolean;
  @Attr({ persist: false }) public canSendToBob: boolean;
  @Attr({ persist: false }) public canSendToBobV2: boolean;
  @Attr({ persist: false }) public canSendToPeoplehr: boolean;
  @Attr({ persist: false }) public canSendToItrent: boolean;
  @Attr({ persist: false }) public canSendToUcheck: boolean;
  @Attr({ persist: false }) public canSendEmail: boolean;
  @Attr({ persist: false }) public formattedStartAt: string;
  @Attr({ persist: false }) public fullName: string;
  @Attr({ persist: false }) public currentUserIsCandidateOwner: boolean;
  @Attr({ persist: false }) public lastInvitationToken: string;
  @Attr({ persist: false }) public candidateOwnerUserSelectOptions: ISelectOption[];
  @Attr({ persist: false }) public daysInOnboardingProcess: number;
  @BelongsTo() public application: Application;
  @BelongsTo() public company: Company;
  @BelongsTo() public onboardingWorkflow: OnboardingWorkflow;
  @BelongsTo() public onboardingPortal: OnboardingPortal;
  @BelongsTo() public onboardingProcess: OnboardingProcess;
  @BelongsTo() public owner: User;
  @BelongsTo() public recruitmentManager: User;
  @BelongsTo() public hiringManager: User;
  @HasMany() public onboardingProcessStepResponses: OnboardingProcessStepResponse[];
  @HasMany() public onboardingAnswers: OnboardingAnswer[];
  @HasMany() public onboardingApplicationUniqueAttachments: OnboardingApplicationUniqueAttachment[];
  @HasMany() public onboardingApplicationDocumentTemplates: OnboardingApplicationDocumentTemplate[];
  @HasMany() public onboardingApplicationScheduledEmails: OnboardingApplicationScheduledEmail[];
  @HasMany() public onboardingApplicationSteps: OnboardingApplicationStep[];
  @HasMany() public onboardingInvitationTokens: OnboardingInvitationToken[];
  @HasMany() public tasks: Task[];
}

@Model()
export class BulkOnboardingApplication extends OnboardingApplication {
  public static jsonapiType = 'bulk_onboarding_applications';

  public static getValidationSchema(onboardingApplicationsCount: number) {
    return Yup.object().shape({
      ...OnboardingApplication.validationSchema.fields,
      email: Yup.string().when([], {
        is: () => onboardingApplicationsCount === 1,
        then: Yup.string().nullable().required("Can't be blank"),
        otherwise: Yup.string().nullable(),
      }),
    });
  }

  @HasMany() public onboardingApplications: OnboardingApplication[];

  public static NEW(attrs?: Partial<BulkOnboardingApplication>) {
    const defaultAttrs: Partial<BulkOnboardingApplication> = {
      onboardingProcessId: '',
      onboardingPortalBigintId: '',
      startAt: '',
      ownerId: '',
      processAssignedAt: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class OnboardingWorkflow extends ApplicationRecord {
  public static jsonapiType = 'onboarding_workflows';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
  });

  @Attr() public status: string;
  @Attr() public name: string;
  @Attr() public duplicate: boolean;
  @Attr() public companyId: string;
  @Attr() public discardedAt: string;
  @Attr() public convertToWorkflow: boolean;
  @Attr({ persist: false }) public assignable: boolean;
  @Attr({ persist: false }) public emailsCount: number;
  @Attr({ persist: false }) public tasksCount: number;
  @Attr({ persist: false }) public onboardingApplicationsCount: number;
  @HasMany() public onboardingStages: OnboardingStage[];
  @HasMany() public onboardingApplications: OnboardingApplication[];
  @HasMany() public onboardingWorkflowScheduledEmails: OnboardingWorkflowScheduledEmail[];
  @HasMany() public scheduledTasks: ScheduledTask[];
}

@Model()
export class OnboardingPortal extends ApplicationRecord {
  public static jsonapiType = 'onboarding_portals';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public name: string;
  @Attr() public companyId: string;
  @Attr({ persist: false }) token: string;
  // Extra attributes
  @Attr({ persist: false }) public activeCandidatesCount: number;
  @Attr({ persist: false }) public canDestroy: boolean;
  @HasMany() public onboardingApplications: OnboardingApplication[];
  @HasMany() public careersPages: CareersPage[];
  @HasMany() public careersNavigationMenuItems: CareersNavigationMenuItem[];
  @HasMany() public onboardingProcesses: OnboardingProcess[];
}

@Model()
export class OnboardingProcess extends ApplicationRecord {
  public static jsonapiType = 'onboarding_processes';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank"),
    status: Yup.string().nullable().required("Can't be blank"),
    onboardingPortalId: Yup.string()
      .nullable()
      .when('status', {
        is: 'published',
        then: Yup.string().nullable().required("Can't be blank"),
        otherwise: Yup.string().nullable(),
      }),
  });

  @Attr() public name: string;
  @Attr() public id: string;
  @Attr() public status: string;
  @Attr() public companyId: string;
  @Attr() public onboardingPortalId: string;
  @Attr() public compliantStageId: string;
  @Attr() public duplicate: boolean;

  @Attr() public readonly stagesCount: number;
  @Attr() public readonly candidateCount: number;
  @Attr() public readonly activeCandidateCount: number;

  @Attr({ persist: false }) public readonly uniqueAttachmentQuestions: {
    question: OnboardingQuestion;
    stepName: string;
  }[];

  @Attr({ persist: false }) public readonly helloSignUniqueAttachmentQuestions: {
    question: OnboardingQuestion;
    stepName: string;
  }[];

  @Attr({ persist: false }) public readonly esignatureDocumentTemplateQuestions: {
    question: OnboardingQuestion;
    stepName: string;
    stepId: string;
  }[];

  @Attr({ persist: false }) public readonly documentTemplateQuestions: {
    question: OnboardingQuestion;
    stepName: string;
    stepId: string;
  }[];

  @Attr({ persist: false }) public readonly docuSignQuestions: {
    question: OnboardingQuestion;
    stepName: string;
  }[];

  @HasMany() public onboardingApplications: OnboardingApplication[];
  @HasMany() public onboardingProcessStages: OnboardingProcessStage[];
  @HasMany() public onboardingProcessStageGroups: OnboardingProcessStageGroup[];
  @BelongsTo() public onboardingPortal: OnboardingPortal;
}

export type OnboardingDateOffset = { offsetReference: OffsetReference; timeUnit: TimeUnit; numberOfUnit: number };
export type TimeUnit = 'Day' | 'Month' | 'Week';
export type OffsetReference = 'BeforeStart' | 'AfterStart' | 'OnStart' | 'AfterAssigned' | 'BeforeDeadline';

@Model()
export class OnboardingProcessStageGroup extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_stage_groups';

  @Attr() public onboardingProcessId: string;
  @Attr() public position: number;
  @Attr({ persist: false }) public uniqueId: string;
  @HasMany() public onboardingProcessStages: OnboardingProcessStage[];
  @BelongsTo() public onboardingProcessTrigger: OnboardingProcessTrigger;
}

export interface OwnerInfo {
  label: string;
  value: string;
  target: string;
  userId: number;
}

export const RECIPIENT_OPTIONS: OwnerInfo[] = [
  { label: 'Hiring Manager', value: 'hiring_manager', target: 'hiring_manager', userId: null },
  {
    label: 'Recruitment Manager',
    value: 'recruitment_manager',
    target: 'recruitment_manager',
    userId: null,
  },
  { label: 'Candidate Owner', value: 'candidate_owner', target: 'candidate_owner', userId: null },
  { label: 'Other', value: 'other', target: 'other', userId: null },
];

@Model()
export class OnboardingProcessStage extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_stages';

  public static targetOptions: OwnerInfo[] = [
    { label: 'Candidate', value: 'candidate', target: 'candidate', userId: null },
    { label: 'Hiring Manager', value: 'hiring_manager', target: 'hiring_manager', userId: null },
    {
      label: 'Recruitment Manager',
      value: 'recruitment_manager',
      target: 'recruitment_manager',
      userId: null,
    },
    { label: 'Candidate Owner', value: 'candidate_owner', target: 'candidate_owner', userId: null },
    { label: 'Other', value: 'other', target: 'other', userId: null },
  ];

  // TODO LB: This could do with a refactoring, but I'm keen to move on to the next feature
  public static ownerOptions: OwnerInfo[] = [
    { label: 'Hiring Manager', value: 'hiring_manager', target: 'hiring_manager', userId: null },
    {
      label: 'Recruitment Manager',
      value: 'recruitment_manager',
      target: 'recruitment_manager',
      userId: null,
    },
    { label: 'Candidate Owner', value: 'candidate_owner', target: 'candidate_owner', userId: null },
  ];

  public static ownerCategories = [
    { name: 'User types', types: ['candidate', 'hiring_manager', 'recruitment_manager', 'candidate_owner', 'other'] },
    { name: 'Users', types: ['company_user'] },
  ];

  public static timeUnits = [
    { label: 'Days', value: 'Day' },
    { label: 'Weeks', value: 'Week' },
    { label: 'Months', value: 'Month' },
  ];

  public static offsetReferencePoints = [
    { label: 'Before start date', value: 'BeforeStart' },
    { label: 'After start date', value: 'AfterStart' },
    { label: 'On start date', value: 'OnStart' },
    { label: 'After assigned to process', value: 'AfterAssigned' },
  ];

  public static validationSchema = Yup.object().shape({
    name: Yup.string().nullable().required("Can't be blank"),
    notifyOnCompletion: Yup.string().nullable().required("Can't be blank"),
    reviewRequired: Yup.string().nullable().required("Can't be blank"),
    sendReminder: Yup.string().nullable().required("Can't be blank"),
    target: Yup.string().nullable().required("Can't be blank"),
    dueDateOffset: Yup.object().when('showDeadline', {
      is: (value: boolean) => value === true,
      then: Yup.object().shape({
        offsetReference: Yup.string().nullable().required("Can't be blank"),
        timeUnit: Yup.string()
          .nullable()
          .when('offsetReference', {
            is: (value: string) => value !== 'OnStart',
            then: Yup.string().nullable().required("Can't be blank"),
            otherwise: Yup.string().nullable(),
          }),
        numberOfUnit: Yup.number()
          .nullable()
          .when('offsetReference', {
            is: (value: string) => value !== 'OnStart',
            then: Yup.number().nullable().required("Can't be blank"),
            otherwise: Yup.number().nullable(),
          }),
      }),
      otherwise: Yup.object().nullable().shape({}),
    }),
  });

  public static booleanFields = ['displayContactDetails', 'notifyOnCompletion', 'reviewRequired', 'sendReminder'];

  @Attr() public name: string;
  @Attr() public externalName: string;
  @Attr() public dueDateOffset: OnboardingDateOffset;
  @Attr() public reminderDateOffset: OnboardingDateOffset;
  @Attr() public atRiskDateOffset: OnboardingDateOffset;
  @Attr() public contactDetails: string;
  /**
   * @deprecated This attribute is no longer used as we always show 'contact details' (help information) for candidates
   */
  @Attr() public displayContactDetails: boolean | string;
  @Attr() public notifyOnCompletion: boolean | string;
  @Attr() public reviewRequired: boolean | string;
  @Attr() public sendReminder: boolean | string;
  @Attr() public position: number;
  @Attr() public requiresAllPriorStageCompletion: boolean;
  @Attr() public owner: string;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public showDeadline: boolean;
  @Attr() public target: string;
  @Attr() public targetUserId: number;
  @Attr() public ownerUserId: number;
  @Attr() public customRecipientName: string;
  @Attr() public customRecipientEmail: string;
  @Attr() public onboardingProcessStageGroupId: string;
  @Attr() public accessGroupId: string;

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly accessGroupName: string;

  // Extra attributes
  @Attr({ persist: false }) public readonly approvableByUser: boolean;

  @BelongsTo() public company: Company;
  @BelongsTo() public onboardingProcessStageGroup: OnboardingProcessStageGroup;
  @HasMany() public onboardingProcessSteps: OnboardingProcessStep[];

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingProcessStage> = {
      name: '',
      externalName: null,
      dueDateOffset: { offsetReference: null, timeUnit: null, numberOfUnit: null },
      // we currently only remind before the deadline, hence the default value
      reminderDateOffset: { offsetReference: null, timeUnit: null, numberOfUnit: null },
      atRiskDateOffset: { offsetReference: null, timeUnit: null, numberOfUnit: null },
      notifyOnCompletion: false,
      reviewRequired: false,
      sendReminder: false,
      owner: null,
      target: null,
      targetUserId: null,
      ownerUserId: null,
      contactDetails: null,
      displayContactDetails: false,
      customRecipientEmail: null,
      showDeadline: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class OnboardingProcessStageApprovalChange extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_stage_approval_changes';

  @Attr() public status: string;
  @Attr() public onboardingProcessStageId: string;
  @Attr() public userId: string;
  @Attr() public onboardingApplicationId: string;

  @BelongsTo() public onboardingProcessStage: OnboardingProcessStage;
  @BelongsTo() public user: User;
  @BelongsTo() public company: Company;
}

@Model()
export class OnboardingProcessStep extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_steps';

  @Attr() public updatedAt: string;
  @Attr() public position: number;
  @Attr() public conditions: { property: string; operator: string; values: ISelectOption[] }[];
  @Attr() public accessGroupId: string;
  @Attr({ persist: false }) public previewId: number;
  /** @extraAttributes */
  @Attr({ persist: false }) public readonly canManageStep: boolean;
  @Attr({ persist: false }) public readonly accessGroupName: string;

  @BelongsTo() public onboardingProcessStepTemplate: OnboardingProcessStepTemplate;
  @BelongsTo() public onboardingProcessStage: OnboardingProcessStage;
}

export const STEP_TEMPLATE_TYPES = {
  'OnboardingSteps::Assessment': 'Assessment',
  'OnboardingSteps::StageInvite': 'Stage Invite',
  'OnboardingSteps::Content': 'Content',
  'OnboardingSteps::Email': 'Email',
  'OnboardingSteps::Form': 'Form',
  'OnboardingSteps::BackgroundCheck': 'Background Check',
  'OnboardingSteps::Signable': 'Signable',
  'OnboardingSteps::Sms': 'SMS',
  'OnboardingSteps::TeamMemberAction': 'Team Member Action',
  'OnboardingSteps::TeamMemberEmail': 'Team Member Email',
  'OnboardingSteps::TeamMemberForm': 'Team Member Form',
  'OnboardingSteps::TeamMemberReferenceCheck': 'Team Member Reference Check',
};

@Model()
export class OnboardingProcessStepStatusOverride extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_step_status_overrides';

  @Attr() public status: string;
  @Attr() public reason: string;
  @Attr() public onboardingProcessStepId: string;
  @Attr() public onboardingApplicationId: string;
  @Attr() public rerunFromFailed: boolean;
  @Attr() public createdAt: string;
  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingProcessStep: OnboardingProcessStep;
}

@Model()
export class OnboardingProcessStepTemplate extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_step_templates';
  public static endpoint = '/onboarding_process_step_templates';
  public staticType: string;

  @Attr() public type: keyof typeof STEP_TEMPLATE_TYPES;
  @Attr() public name: string;
  @Attr() public externalName: string;
  @Attr() public description: string;
  @Attr() public isHidden: boolean;
  @Attr() public updatedAt: string;
  @Attr() public createdAt: string;
  @Attr({ persist: false }) public readonly assessmentProviders: string[];
  @Attr({ persist: false }) public readonly backgroundCheckProviders: string[];
  @Attr({ persist: false }) public customFieldParserResources: { text: string; value: string }[];
  @Attr({ persist: false }) public customFieldConditionalResources: {
    label: string;
    value: string;
    response: 'string' | 'number' | 'date' | 'boolean' | 'select';
  }[];
  @HasMany() public onboardingQuestions: OnboardingQuestion[];
  @HasMany() public onboardingProcessStepQuestions: OnboardingProcessStepQuestion[];

  @HasMany() public onboardingContentBlocks: OnboardingContentBlock[];

  @Attr() public configuration: {
    payload: {
      email_address?: { reference?: string; value?: string };
      email?: { reference?: string; value?: string };
      phone?: { reference?: string; value?: string };
      first_name?: { reference?: string; value?: string };
      firstName?: { reference?: string; value?: string };
      lastName?: { reference?: string; value?: string };
      last_name?: { reference?: string; value?: string };
      // First Advantage KYP
      position?: { reference?: string; value?: string };
      organisationShortCode?: { reference?: string; value?: string };
      profile?: { reference?: string; value?: string };
      // Sterling
      account_id?: { reference?: string; value?: string };
      package_name?: { reference?: string; value?: string };
      package_id?: { reference?: string; value?: string };
      bill_code?: { reference?: string; value?: string };
      // CreativeServicesInc
      billing_code?: { reference?: string; value?: string };
      // Checkr
      node?: { reference?: string; value?: string };
      package?: { reference?: string; value?: string };
      country?: { reference?: string; value?: string };
      state?: { reference?: string; value?: string };
      city?: { reference?: string; value?: string };
      // Certn
      selected_package?: { reference?: string; value?: string };
      selected_checks?: { reference?: string; value?: string };
      tag?: { reference?: string; value?: string };
      // Yoti
      package_code?: { reference?: string; value?: string };
    };
    provider: string;
    forcibly_allow_wism_progress: boolean;
  };

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly onboardingProcessNames: string[];
  @Attr({ persist: false }) public readonly hasActiveCandidates: boolean;

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
    externalName: Yup.string().required("Can't be blank."),
  });

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingProcessStepTemplate> = {
      name: '',
      externalName: '',
      description: '',
      isHidden: false,
      configuration: { payload: {}, provider: null, forcibly_allow_wism_progress: false },
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class OnboardingProcessStepQuestion extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_step_questions';

  // Extra attributes
  @Attr() public sanitizedName: string;
  @Attr() public onboardingProcessStepTemplateId: string;
  @Attr() public onboardingQuestionId: string;
  @Attr() public position: number;

  @BelongsTo() public onboardingProcessStepTemplate: OnboardingProcessStepTemplate;
  @BelongsTo() public onboardingQuestion: OnboardingQuestion;
}

@Model()
export class OnboardingStepsAssessment extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/assessments';
  public staticType = 'Assessment' as const;
}

@Model()
export class OnboardingStepsContent extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/contents';
  public staticType = 'Content' as const;
}

@Model()
export class OnboardingStepsForm extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/forms';
  public staticType = 'Form' as const;
}

@Model()
export class OnboardingStepsStageInvite extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/stage_invites';
  public staticType = 'Stage Invite' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsStageInvite> = {
      name: '',
      externalName: '',
      description: '',
      subject: '',
      salutation: '',
      body: '',
      signature: '',
      isScheduled: false,
      scheduleTimezone: '',
      scheduleTime: '',
      isHidden: false,
      buttonText: 'My Onboarding',
      buttonDestination: 'onboarding_portal',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    smsBody: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public subject: string;
  @Attr() public salutation: string;
  @Attr() public body: string;
  @Attr() public signature: string;
  @Attr() public isScheduled: boolean;
  @Attr() public scheduleTimezone: string;
  @Attr() public scheduleTime: string;
  @Attr() public buttonText: string;
  @Attr() public buttonDestination: 'onboarding_hub' | 'onboarding_portal';
}

@Model()
export class OnboardingStepsEmail extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/emails';
  public staticType = 'Email' as const;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public emailBody: string;
  @Attr() public emailSubject: string;
  @Attr() public emailSalutation: string;
  @Attr() public emailSignature: string;
  @Attr() public cc: string[];
  @Attr() public bcc: string[];

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsEmail> = {
      name: '',
      externalName: '',
      description: '',
      emailBody: '',
      emailSubject: '',
      emailSalutation: '',
      emailSignature: '',
      cc: [],
      bcc: [],
      documents: [],
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    emailBody: Yup.string().required("Can't be blank.").nullable(),
    emailSubject: Yup.string().required("Can't be blank.").nullable(),
    emailSalutation: Yup.string().required("Can't be blank.").nullable(),
  });
}

@Model()
export class OnboardingStepsBackgroundCheck extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/background_checks';
  public staticType = 'Background Check' as const;
}

@Model()
export class OnboardingStepsSignable extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/signables';
  public staticType = 'Signable' as const;
}

@Model()
export class OnboardingStepsSms extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/smses';
  public staticType = 'SMS' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsSms> = {
      name: '',
      externalName: '',
      description: '',
      smsBody: '',
      isHidden: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    smsBody: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public smsBody: string;
}

@Model()
export class OnboardingStepsTeamMemberAction extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/team_member_actions';
  public staticType = 'Team Member Action' as const;

  public static recipientOptions: OwnerInfo[] = RECIPIENT_OPTIONS;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsTeamMemberAction> = {
      name: '',
      externalName: '',
      description: '',
      actionInstructions: '',
      recipient: '',
      customRecipientName: '',
      customRecipientEmail: '',
      isHidden: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    actionInstructions: Yup.string().required("Can't be blank.").nullable(),
    recipient: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public actionInstructions: string;
  @Attr() public recipient: string;
  @Attr() public customRecipientName: string;
  @Attr() public customRecipientEmail: string;
}

@Model()
export class OnboardingStepsTeamMemberReferenceCheck extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/team_member_reference_checks';
  public staticType = 'Team Member Reference Check' as const;

  public static recipientOptions: OwnerInfo[] = RECIPIENT_OPTIONS;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsTeamMemberReferenceCheck> = {
      name: '',
      externalName: '',
      description: '',
      stepInstructions: '',
      recipient: '',
      customRecipientName: '',
      customRecipientEmail: '',
      isHidden: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    stepInstructions: Yup.string().required("Can't be blank.").nullable(),
    recipient: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public stepInstructions: string;
  @Attr() public recipient: string;
  @Attr() public customRecipientName: string;
  @Attr() public customRecipientEmail: string;
}
@Model()
export class OnboardingStepsTeamMemberEmail extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/team_member_emails';
  public staticType = 'Team Member Email' as const;
  @Attr() public recipient: string;
  @Attr() public customRecipientName: string;
  @Attr() public customRecipientEmail: string;
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public emailBody: string;
  @Attr() public emailSubject: string;
  @Attr() public emailSalutation: string;
  @Attr() public emailSignature: string;
  @Attr() public cc: string[];
  @Attr() public bcc: string[];

  public static recipientOptions: OwnerInfo[] = RECIPIENT_OPTIONS;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsTeamMemberEmail> = {
      name: '',
      externalName: '',
      description: '',
      emailBody: '',
      emailSubject: '',
      emailSalutation: '',
      emailSignature: '',
      recipient: '',
      customRecipientName: '',
      customRecipientEmail: '',
      cc: [],
      bcc: [],
      documents: [],
      isHidden: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static subClassValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank.").nullable(),
    emailBody: Yup.string().required("Can't be blank.").nullable(),
    emailSubject: Yup.string().required("Can't be blank.").nullable(),
    emailSalutation: Yup.string().required("Can't be blank.").nullable(),
    recipient: Yup.string().required("Can't be blank.").nullable(),
  });
}

@Model()
export class OnboardingStepsTeamMemberForm extends OnboardingProcessStepTemplate {
  public static jsonapiType = 'onboarding_process_step_templates/team_member_forms';
  public staticType = 'Team Member Form' as const;

  @Attr() public recipient: string;
  @Attr() public customRecipientName: string;
  @Attr() public customRecipientEmail: string;
  @Attr() public userSpecifiedName: string;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<OnboardingStepsTeamMemberForm> = {
      name: '',
      externalName: '',
      description: '',
      recipient: '',
      customRecipientName: '',
      customRecipientEmail: '',
      userSpecifiedName: '',
      isHidden: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class OnboardingProcessStepResponse extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_step_responses';

  @Attr() public onboardingProcessStageId: string;
  @Attr() public onboardingProcessStepId: string;
  @Attr() public onboardingApplicationId: string;
  @Attr() public response: string;
  @Attr() public status: string;
  @Attr() public skipReason: string;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingProcessStep: OnboardingProcessStep;
  @BelongsTo() public onboardingProcessStage: OnboardingProcessStage;
}

@Model()
export class OnboardingWorkflowScheduledEmail extends ApplicationRecord {
  public static jsonapiType = 'onboarding_workflow_scheduled_emails';

  public static validationSchema = Yup.object().shape({
    delayOperator: Yup.string().required("Can't be blank.").nullable(),
    delayValue: Yup.string().required("Can't be blank.").nullable(),
    dateType: Yup.string().required("Can't be blank.").nullable(),
    emailTemplateId: Yup.string().required("Can't be blank."),
    scheduleTime: Yup.string().required("Can't be blank."),
    scheduleTimezone: Yup.string().required("Can't be blank."),
  });

  public static recipientBooleanFields = ['sendToCandidate', 'sendToHiringManager', 'sendToRecruitmentManager'];

  @Attr() public companyId: string;
  @Attr() public onboardingWorkflowId: string;
  @Attr() public emailTemplateId: string;

  @Attr() public scheduleTime: string;
  @Attr() public scheduleTimezone: string;
  @Attr() public dateType: string;
  @Attr() public delayValue: number;
  @Attr() public delayOperator: string;
  @Attr() public sendToCandidate: boolean;
  @Attr() public sendToHiringManager: boolean;
  @Attr() public sendToRecruitmentManager: boolean;
  @Attr() public recipients: string[];
  @Attr() public sendOnStartDate: boolean;

  @BelongsTo() public onboardingWorkflow: OnboardingWorkflow;
  @BelongsTo() public emailTemplate: EmailTemplate;
  @BelongsTo() public company: Company;
}

export type OnboardingProcessTriggerConditions = {
  offsetReference: OffsetReference;
  timeUnit: TimeUnit;
  numberOfUnit: number;
};

@Model()
export class OnboardingProcessTrigger extends ApplicationRecord {
  public static jsonapiType = 'onboarding_process_triggers';

  @Attr() public onboardingProcessId: string;
  @Attr() public companyId: string;
  @Attr() public position: number;
  @Attr() public requiresPreviousStagesCompletion: boolean;
  @Attr() public conditions: OnboardingProcessTriggerConditions;

  @BelongsTo() public onboardingProcess: OnboardingProcess;
  @BelongsTo() public company: Company;
  @HasMany() public onboardingProcessStageGroups: OnboardingProcessStageGroup[];
  @Attr({ persist: false }) public readonly convertedChronologicalTime: {};
}

@Model()
export class OnboardingConfiguration extends ApplicationRecord {
  public static jsonapiType = 'onboarding_configurations';
  public static stageCompletionCriteriaOptions = [
    { value: 'all', label: 'All' },
    { value: 'required', label: 'Required only' },
  ];

  @Attr() public companyId: string;
  @Attr() public showDaysToStart: boolean;
  @Attr() public high: number;
  @Attr() public medium: number;
  @Attr() public autoArchive: boolean;
  @Attr() public autoArchiveDelay: number;
  @Attr() public jobVisibilitiesEnforced: boolean;
  @Attr() public stageCompletionCriteria: string;
}

@Model()
export class OnboardingStage extends ApplicationRecord {
  public static jsonapiType = 'onboarding_stages';

  @Attr() public onboardingStageableId: string;
  @Attr() public onboardingStageableType: string;
  @Attr({ persist: false }) public color: COLORS;
  @Attr({ persist: false }) public icon: string;
  @Attr() public ensureSave: boolean;
  @Attr() public name: string;
  @Attr() public position: number;
  @Attr() public duplicate: boolean;
  @Attr({ persist: false }) public readonly editable: boolean;
  @Attr({ persist: false }) public readonly deletable: boolean;
  @Attr({ persist: false }) public readonly basicType: 'hired' | 'candidate_information' | 'onboarded';
  @BelongsTo() public onboardingStageable: OnboardingWorkflow;
  @HasMany() public onboardingApplications: OnboardingApplication[];
  @HasMany() public onboardingQuestions: OnboardingQuestion[];
  @HasMany() public onboardingDetails: OnboardingDetail[];
  @HasMany() public onboardingAnswers: OnboardingAnswer[];
  @HasMany() public onboardingContentBlocks: OnboardingContentBlock[];
  @HasMany() public onboardingStagesOnboardingQuestions: OnboardingStagesOnboardingQuestion[];
}

@Model()
export class OnboardingStagesOnboardingQuestion extends ApplicationRecord {
  public static jsonapiType = 'onboarding_stages_onboarding_questions';
  @Attr() public position: number;
  @BelongsTo() public onboardingStage: OnboardingStage;
  @BelongsTo() public onboardingQuestion: OnboardingQuestion;
}

@Model()
export class AiAssistantConversation extends ApplicationRecord {
  public static jsonapiType = 'ai_assistant_conversations';
}

@Model()
export class AiAssistantRating extends ApplicationRecord {
  public static jsonapiType = 'ai_assistant_ratings';

  @Attr() public comment: string;
  @Attr() public rating: string;
  @BelongsTo() public aiAssistantConversation: AiAssistantConversation;
}

@Model()
export class SignOffStage extends ApplicationRecord {
  public static jsonapiType = 'sign_off_stages';

  public static basicValidationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
    userIds: Yup.array().when('assignment', {
      is: (value: string) => value && value === 'fixed',
      then: Yup.array().min(
        Yup.ref('minimumSignatories'),
        'selection must be greater than or equal to minimum signatories',
      ),
    }),
  });

  @Attr({ persist: false }) public approverType: string;
  @Attr() public assignment: string;
  @Attr() public dynamicApproverType: string;
  @Attr() public fallbackApprover: string;
  @Attr() public name: string;
  @Attr() public minimumSignatories: number;
  @Attr() public position: number;
  @Attr() public signOffStageVariantId: string | number;
  @Attr() public signOffWorkflowId: string | number;
  @Attr({ persist: false }) public finalStage: boolean;
  @Attr() public approvalListIds: string[];
  @Attr() public positionIds: string[];
  @Attr() public userIds: string[];
  @Attr() public conditions: { property: string; operator: string; value: ISelectOption }[];

  @BelongsTo() public signOffWorkflow: SignOffWorkflow;
  @BelongsTo() public signOffStageVariant: SignOffStageVariant;
  @HasMany() public signOffStageSignatories: SignOffStageSignatory[];
}

@Model()
export class SignOffStageVariant extends ApplicationRecord {
  public static jsonapiType = 'sign_off_stage_variants';

  @Attr() public color: COLORS;
  @Attr() public icon: string;
  @Attr() public name: string;
  @Attr({ persist: false }) public readonly editable: boolean;
  @Attr({ persist: false }) public readonly deletable: boolean;
  @Attr({ persist: false }) public readonly basicType: 'approval';
}

@Model()
export class SignOffStageSignatory extends ApplicationRecord {
  public static jsonapiType = 'sign_off_stage_signatories';

  @Attr() public discardedAt: string;
  @Attr() public userId: string;
  @Attr() public signOffStageId: string | number;

  @BelongsTo() public user: User;
  @BelongsTo() public signOffStage: SignOffStage;
  @HasMany() public signOffStageApprovals: SignOffStageApproval[];
}

@Model()
export class SignOffStageApproval extends ApplicationRecord {
  public static jsonapiType = 'sign_off_stage_approvals';

  @Attr() public userId: string;
  @Attr() public signOffStageSignatoryId: string;
  @Attr() public jobIdOld: string;
  @Attr() public signableId: string;
  @Attr() public signableType: string;
  @Attr() public consent: boolean;
  @Attr() public comment: string;
  // Extra attributes
  @Attr({ persist: false }) public readonly minimumStageSignatoriesFulfilled: boolean;

  @BelongsTo() public user: User;
  @BelongsTo() public signOffStageSignatory: SignOffStageSignatory;
  @BelongsTo() public signable: Job | Offer | Requisition;
  @HasMany() public audits: Audit[];
}

@Model()
export class SignableSignOffStage extends ApplicationRecord {
  public static jsonapiType = 'signable_sign_off_stages';

  @Attr() public signableId: string;
  @Attr() public signableType: string;
  @Attr() public signOffStageId: string;

  @BelongsTo() public signOffStage: SignOffStage;
  @BelongsTo() public signable: Job | Offer | Requisition;
}

@Model()
export class SkippedSignOffStage extends ApplicationRecord {
  public static jsonapiType = 'skipped_sign_off_stages';

  @Attr({ persist: false }) public readonly signableId: string;
  @Attr({ persist: false }) public readonly signableType: string;
  @Attr({ persist: false }) public readonly signOffStageId: string;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly companyId: string;

  @BelongsTo() public signOffStage: SignOffStage;
  @BelongsTo() public signable: Job | Offer | Requisition;
  @BelongsTo() public company: Company;
  @BelongsTo() public user: User;
  @HasMany() public audits: Audit[];
}

@Model()
export class SmsTemplate extends MobilityApplicationRecord {
  public static jsonapiType = 'sms_templates';
  public static validationSchema = Yup.object().shape({
    body: Yup.string().required("Can't be blank").max(1600, 'Max length is 1600 characters.'),
  });

  @Attr({ persist: false }) public smsType: string;
  @Attr({ persist: false }) public parserResources: string[];
  @Attr({ persist: false }) public custom: boolean;
  @Attr({ persist: false }) public previewPath?: string;
  @Attr() public name: string;
  @Attr() public body: string;
  @Attr() public duplicate: boolean;

  // @Attr() public tagList: string[];

  @HasOne() public scopeCondition: ScopeCondition;

  get isServerTemplate(): boolean {
    return this.id === '-1';
  }

  get isCreating(): boolean {
    if (this.isServerTemplate) return true;

    return !this.isPersisted;
  }
}

@Model()
export class Stage extends MobilityApplicationRecord {
  public static jsonapiType = 'stages';

  get isApplied(): boolean {
    return this.basicType === 'applied';
  }

  get isOffer(): boolean {
    return this.basicType === 'offer';
  }

  get isHired(): boolean {
    return this.basicType === 'hired';
  }

  get intent(): Intent {
    switch (this.basicType) {
      case 'hired':
        return Intent.SUCCESS;
      case 'offer':
        return Intent.SUCCESS;
      case 'interview':
        return Intent.PRIMARY;
      case 'rejected':
        return Intent.DANGER;
      case 'review':
        return Intent.WARNING;
      default:
        return Intent.NONE;
    }
  }

  get thumbtackIcon(): IconNames16 {
    switch (this.basicType) {
      case 'hired':
        return IconNames16.HIRED;
      case 'offer':
        return IconNames16.EMAIL;
      case 'interview':
        return IconNames16.INTERVIEW;
      case 'review':
        return IconNames16.REVIEW;
      case 'rejected':
        return IconNames16.REJECTED;
      default:
        return IconNames16.APPLIED;
    }
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
    stageVariantId: Yup.string().required("Can't be blank."),
  });

  @Attr({ persist: false }) public color: COLORS;
  @Attr() public name: string;
  @Attr() public ensureSave: boolean;
  @Attr() public externalName: string;
  @Attr() public description: string;
  @Attr() public interviewAdditionalDescription: string;
  @Attr() public interviewSync: string;
  @Attr() public location: string;
  @Attr() public position: number;
  @Attr() public scorecardLocked: boolean;
  @Attr() public stageVisible: boolean;
  @Attr() public stageVariantId: string;
  @Attr() public stageableId: string;
  @Attr({ persist: false }) public readonly stageGroupId: string;
  @Attr() public stageableType: string;
  @Attr() public scorecardIds: string[];
  @Attr() public documentTemplateIds: string[];
  @Attr() public questionCollectionId: string;
  @Attr() public questionnaireEmailTemplateId: string;
  @Attr() public candidateEngagementCheckDelayDays: number;
  @Attr() public alternativeInterviewTypeIds: string[];
  @Attr({ persist: false }) public readonly documentTemplateFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public readonly basicType: StageVariant['basicType'];

  /** @extraAttribute */
  @Attr() public autoSendDocumentTemplates: boolean;
  @Attr({ persist: false }) public readonly editable: boolean;
  @Attr({ persist: false }) public readonly extendedType: string;
  @Attr({ persist: false }) public readonly deletable: boolean;
  @Attr({ persist: false }) public readonly interviewTypeId: string;
  @Attr({ persist: false }) public readonly groupAssessmentId: string;
  @Attr({ persist: false }) public readonly stageDeletable: boolean;
  @Attr({ persist: false }) public readonly applicationCount: number;

  @BelongsTo() public stageable: Job | HiringWorkflow | SignOffWorkflow;
  @BelongsTo() public stageGroup: StageGroup;
  @BelongsTo() public stageVariant: StageVariant;
  @BelongsTo() public questionCollection: QuestionCollection;
  @BelongsTo() public emailTemplate: EmailTemplate;

  @HasOne() public interviewTextConfiguration: InterviewTextConfiguration;
  @HasOne() public stageInterviewType: StageInterviewType;
  @HasOne() public stageGroupAssessment: StageGroupAssessment;
  @HasOne() public videoInterview: VideoInterview;

  @HasMany() public applications: Application[];
  @HasMany() public interviewTeamMembers: InterviewTeamMember[];
  @HasMany() public usersVisibilities: UserVisibility[];
  @HasMany() public stageActions: StageAction['instanceClasses'][];
  @HasMany() public stagesDocumentTemplates: StagesDocumentTemplate[];
  @HasMany() public scorecardableScorecards: ScorecardableScorecard[];
}

@Model()
export class StageAction extends ApplicationRecord {
  public static jsonapiType = 'stage_actions';

  public instanceClasses:
    | StageActionsInviteToHireflixVideoInterview
    | StageActionsSendAlvaLabsAssessment
    | StageActionsSendCriteriaAssessment
    | StageActionsSendDocumentTemplate
    | StageActionsSendEngagementAlert
    | StageActionsSendFirstAdvantageCheck
    | StageActionsSendHireRightI9
    | StageActionsSendHireRightOrder
    | StageActionsSendInterviewInvite
    | StageActionsSendOutmatchAssessment
    | StageActionsSendQuestionSet
    | StageActionsSendReferenceCheckRequest
    | StageActionsSendInterviewInvite
    | StageActionsSendShlAssessment;

  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public readonly humanizedActionType: string;
  @Attr({ persist: false }) public readonly type: string;
}

@Model()
export class StageActionsSendReferenceCheckRequest extends StageAction {
  public static jsonapiType = 'stage_actions/send_reference_check_request';
  public staticType = 'sendReferenceCheckRequest' as const;
  public actionName = 'Send Reference Request' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendReferenceCheckRequest> = {
      referenceCheckRequestTemplateId: null,
      emailTemplateId: null,
    };
    return new StageActionsSendReferenceCheckRequest({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    referenceCheckRequestTemplateId: Yup.string().nullable().required("Can't be blank"),
    emailTemplateId: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public referenceCheckRequestTemplateId: string;
  @Attr() public emailTemplateId: string;
}

@Model()
export class StageActionsSendEngagementAlert extends StageAction {
  public static jsonapiType = 'stage_actions/send_engagement_alert';
  public staticType = 'sendEngagementAlert' as const;
  public actionName = 'Send Engagement Alert' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendEngagementAlert> = {
      delayDays: 7,
      notificationUser: 'recruitment_manager',
      uniqueId: Math.random().toString(),
    };
    // biome-ignore lint/complexity/noThisInStatic: <explanation>
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    delayDays: Yup.number().required("Can't be blank").min(1, "Can't be less than 1").integer("Can't be a decimal"),
    notificationUser: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public delayDays: number;
  @Attr() public notificationUser: string;
}

@Model()
export class StageActionsSendInterviewInvite extends StageAction {
  public static jsonapiType = 'stage_actions/send_interview_invite';
  public staticType = 'sendInterviewInvite' as const;
  public actionName = 'Send Interview Invite' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendInterviewInvite> = {
      hiringTeams: [],
      interviewTypeId: null,
      customDuration: null,
      schedulingWindowPeriod: 2,
      schedulingWindowUnit: 'week',
      restrictWorkingHours: false,
      restrictWorkingWeek: false,
      restrictionTimezone: null,
    };

    return new StageActionsSendInterviewInvite({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    interviewTypeId: Yup.string().nullable().required("Can't be blank"),
    schedulingWindowPeriod: Yup.string().nullable().required("Can't be blank"),
    schedulingWindowUnit: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public hiringTeams: IHiringTeam[];
  @Attr() public interviewTypeId: string;
  @Attr() public customDuration: number;
  @Attr() public schedulingWindowPeriod: number;
  @Attr() public schedulingWindowUnit: string;
  @Attr() public restrictWorkingHours: boolean;
  @Attr() public restrictWorkingWeek: boolean;
  @Attr() public restrictionTimezone: string;
}

@Model()
export class StageActionsInviteToHireflixVideoInterview extends StageAction {
  public static jsonapiType = 'stage_actions/invite_to_hireflix_video_interview';
  public staticType = 'inviteToHireflixVideoInterview' as const;
  public actionName = 'Invite to Hireflix Video Interview' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsInviteToHireflixVideoInterview> = {
      hireflixPositionId: null,
    };

    return new StageActionsInviteToHireflixVideoInterview({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    hireflixPositionId: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public hireflixPositionId: string;
}

@Model()
export class StageActionsSendAlvaLabsAssessment extends StageAction {
  public static jsonapiType = 'stage_actions/send_alva_labs_assessment';
  public staticType = 'sendAlvaLabsAssessment' as const;
  public actionName = 'Send Alva Labs Assessment' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendAlvaLabsAssessment> = {
      requireLogicTest: false,
      requirePersonalityTest: false,
      testProfileId: null,
      testProfileName: null,
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendAlvaLabsAssessment({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    requireLogicTest: Yup.boolean().required("Can't be blank"),
    requirePersonalityTest: Yup.boolean().required("Can't be blank"),
    testProfileId: Yup.string().nullable().required("Can't be blank"),
    testProfileName: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public requireLogicTest: boolean;
  @Attr() public requirePersonalityTest: boolean;
  @Attr() public testProfileId: string;
  @Attr() public testProfileName: string;
}

@Model()
export class StageActionsSendCriteriaAssessment extends StageAction {
  public static jsonapiType = 'stage_actions/send_criteria_assessment';
  public staticType = 'sendCriteriaAssessment' as const;
  public actionName = 'Send Criteria Assessment' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendCriteriaAssessment> = {
      assessmentName: null,
      packageId: null,
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendCriteriaAssessment({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    assessmentName: Yup.string().nullable().required("Can't be blank"),
    packageId: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public assessmentName: string;
  @Attr() public packageId: string;
}

@Model()
export class StageActionsSendDocumentTemplate extends StageAction {
  public static jsonapiType = 'stage_actions/send_document_template';
  public staticType = 'sendDocumentTemplate' as const;
  public actionName = 'Send Document Template' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendDocumentTemplate> = {
      documentTemplateId: null,
      documentTemplateName: null,
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendDocumentTemplate({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    documentTemplateId: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public documentTemplateId: string;
  @Attr() public documentTemplateName: string;
}

@Model()
export class StageActionsSendFirstAdvantageCheck extends StageAction {
  public static jsonapiType = 'stage_actions/send_first_advantage_check';
  public staticType = 'sendFirstAdvantageCheck' as const;
  public actionName = 'Send First Advantage Check' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendFirstAdvantageCheck> = {
      firstAdvantageAccountId: null,
      firstAdvantageAccountName: null,
      firstAdvantagePackageId: null,
      firstAdvantagePackageName: null,
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendFirstAdvantageCheck({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    firstAdvantagePackageId: Yup.string().nullable().required("Can't be blank"),
    firstAdvantageAccountId: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public firstAdvantagePackageId: string;
  @Attr() public firstAdvantagePackageName: string;
  @Attr() public firstAdvantageAccountId: string;
  @Attr() public firstAdvantageAccountName: string;
}

@Model()
export class StageActionsSendHireRightI9 extends StageAction {
  public static jsonapiType = 'stage_actions/send_hire_right_i9';
  public staticType = 'sendHireRightI9' as const;
  public actionName = 'Send HireRight I9' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendHireRightI9> = {
      accountId: null,
      accountName: null,
      defaultRequestorEmail: null,
      defaultRequestorId: null,
      defaultRequestorName: null,
    };

    return new StageActionsSendHireRightI9({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    accountId: Yup.string().nullable().required("Can't be blank"),
    defaultRequestorEmail: Yup.string().nullable().email('Must be a valid email').required("Can't be blank"),
  });

  @Attr() public accountId: string;
  @Attr() public accountName: string;
  @Attr() public defaultRequestorEmail: string;
  @Attr() public defaultRequestorId: string;
  @Attr() public defaultRequestorName: string;
}

@Model()
export class StageActionsSendHireRightOrder extends StageAction {
  public static jsonapiType = 'stage_actions/send_hire_right_order';
  public staticType = 'sendHireRightOrder' as const;
  public actionName = 'Send HireRight Background Check' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendHireRightOrder> = {
      accountId: null,
      accountName: null,
      defaultRequestorEmail: null,
      defaultRequestorId: null,
      defaultRequestorName: null,
      packageId: null,
      packageName: null,
      jobType: 'onsite',
      jobLocationCountryCode: null,
      jobLocationCity: null,
      jobLocationRegion: null,
      jobLocationPostalCode: null,
      legalRequirementsAccepted: false,
    };

    return new StageActionsSendHireRightOrder({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    accountId: Yup.string().nullable().required("Can't be blank"),
    packageId: Yup.string().nullable().required("Can't be blank"),
    defaultRequestorEmail: Yup.string().nullable().email('Must be a valid email').required("Can't be blank"),
    legalRequirementsAccepted: Yup.boolean().nullable().oneOf([true], 'Legal Requirements must be accepted.'),
  });

  @Attr() public accountId: string;
  @Attr() public accountName: string;
  @Attr() public packageId: string;
  @Attr() public packageName: string;
  @Attr() public defaultRequestorEmail: string;
  @Attr() public defaultRequestorId: string;
  @Attr() public defaultRequestorName: string;
  @Attr() public jobType: string;
  @Attr() public jobLocationCountryCode: string;
  @Attr() public jobLocationCity: string;
  @Attr() public jobLocationRegion: string;
  @Attr() public jobLocationPostalCode: string;
  @Attr() public legalRequirementsAccepted: boolean;
}

@Model()
export class StageActionsSendOutmatchAssessment extends StageAction {
  public static jsonapiType = 'stage_actions/send_outmatch_assessment';
  public staticType = 'sendOutmatchAssessment' as const;
  public actionName = 'Send Outmatch Assessment' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendOutmatchAssessment> = {
      jobCodeName: null,
      jobCode: null,
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendOutmatchAssessment({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    jobCode: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public jobCode: string;
  @Attr() public jobCodeName: string;
}

@Model()
export class StageActionsSendQuestionSet extends StageAction {
  public static jsonapiType = 'stage_actions/send_question_set';
  public staticType = 'sendQuestionSet' as const;
  public actionName = 'Send Question Set' as const;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<StageActionsSendQuestionSet> = {
      emailTemplateId: null,
      emailTemplateName: null,
      requestAdditionalQuestions: false,
      questionCollectionId: null,
      questionCollectionName: null,
      structuredSectionIds: [],
      requestStructuredSections: false,

      uniqueId: Math.random().toString(),
    };
    // biome-ignore lint/complexity/noThisInStatic: <explanation>
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    questionCollectionId: Yup.string().when(['requestStructuredSections', 'requestAdditionalQuestions'], {
      is: (requestStructuredSections: unknown, requestAdditionalQuestions: unknown) =>
        requestAdditionalQuestions || !requestStructuredSections,
      then: Yup.string().required("Can't be blank").nullable(),
      otherwise: Yup.string().nullable(),
    }),
    emailTemplateId: Yup.string().nullable().required("Can't be blank"),
    structuredSectionIds: Yup.array().when(['requestStructuredSections', 'requestAdditionalQuestions'], {
      is: (requestStructuredSections: unknown, requestAdditionalQuestions: unknown) =>
        requestStructuredSections || !requestAdditionalQuestions,
      then: Yup.array().min(1, 'At least one structured section must be selected'),
    }),
  });

  @Attr() public requestAdditionalQuestions: boolean;
  @Attr() public questionCollectionId: string;
  @Attr() public questionCollectionName: string;
  @Attr() public emailTemplateId: string;
  @Attr() public emailTemplateName: string;
  @Attr() public structuredSectionIds: string[];
  @Attr() public requestStructuredSections: boolean;
}

@Model()
export class StageActionsSendShlAssessment extends StageAction {
  public static jsonapiType = 'stage_actions/send_shl_assessment';
  public staticType = 'sendShlAssessment' as const;
  public actionName = 'Send SHL Assessment' as const;

  public static NEW(attrs: Partial<StageActionsSendShlAssessment> = {}) {
    const defaultAttrs: Partial<StageActionsSendShlAssessment> = {
      packagesNames: [],
      timerAdjustment: '',
      deadlineDays: attrs.deadlineDays || '',
      uniqueId: Math.random().toString(),
    };

    return new StageActionsSendShlAssessment({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    packagesNames: Yup.array(Yup.string()).min(1, 'At least one package must be selected'),
    deadlineDays: Yup.string().nullable(),
    timerAdjustment: Yup.string().nullable(),
  });

  @Attr() public packagesNames: string[];
  @Attr() public timerAdjustment: string;
  @Attr() public deadlineDays: string;
}

@Model()
export class StageGroupAssessment extends ApplicationRecord {
  public static jsonapiType = 'stage_group_assessments';

  @Attr() public ensureSave: boolean;
  @Attr() public groupAssessmentId: string;
  @BelongsTo() public stage: Stage;
  @BelongsTo() public groupAssessment: GroupAssessment;
}

@Model()
export class StagesDocumentTemplate extends ApplicationRecord {
  public static jsonapiType = 'stages_document_templates';

  @Attr() public documentTemplateId: string;
  @Attr() public stageId: string;

  @BelongsTo() public documentTemplate: DocumentTemplate;
  @BelongsTo() public stage: Stage;
}

@Model()
export class State extends ApplicationRecord {
  public static jsonapiType = 'states';

  @Attr() public alpha2: string;
  @Attr() public name: string;
}

@Model()
export class OnboardingApplicationScheduledEmail extends ApplicationRecord {
  public static jsonapiType = 'onboarding_application_scheduled_emails';

  @Attr() public onboardingApplicationId: string;
  @Attr() public onboardingApplicationStepId: string;
  @Attr() public onboardingWorkflowScheduledEmailId: string;
  @Attr() public companyId: string;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingApplicationStep: OnboardingApplicationStep;
  @BelongsTo() public onboardingWorkflowScheduledEmail: OnboardingWorkflowScheduledEmail;
  @BelongsTo() public company: Company;
}

@Model()
export class OnboardingApplicationStep extends ApplicationRecord {
  public static jsonapiType = 'onboarding_application_steps';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank"),
  });

  public static completedAnswersVisibilityOptions = [
    { value: 'revealed', label: 'Shown by default' },
    { value: 'hidden_when_valid', label: 'Hidden when submitted' },
    { value: 'hidden_when_approved', label: 'Hidden when approved' },
  ];

  get completed(): boolean {
    return this.status === 'complete';
  }

  @Attr() public onboardingApplicationId: string;
  @Attr() public name: string;
  @Attr() public position: number;
  @Attr({ persist: false }) public status: string;
  @Attr() public onboardingWorkflowId: string;
  @Attr() public onboardingPortalId: string;
  @Attr() public stageAdvancementFixed: boolean;
  @Attr() public completedAnswersVisibility: string;
  @Attr() public scheduledEmailsToCreate?: string[];
  @Attr() public scheduledEmailsToDestroy?: string[];
  @Attr() public invite?: boolean;
  @Attr() public reinvite?: boolean;
  @Attr() public complete?: boolean;
  @Attr() public progress: number;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingWorkflow: OnboardingWorkflow;
  @BelongsTo() public onboardingPortal: OnboardingPortal;
  @HasMany() public onboardingApplicationUniqueAttachments: OnboardingApplicationUniqueAttachment[];
  @HasMany() public onboardingApplicationDocumentTemplates: OnboardingApplicationDocumentTemplate[];
  @HasMany() public onboardingApplicationScheduledEmails: OnboardingApplicationScheduledEmail[];
  @HasMany() public onboardingAnswers: OnboardingAnswer[];
  @HasMany() public onboardingScheduledInvitations: OnboardingScheduledInvitation[];
  @HasMany() public onboardingApplicationStepScheduledTasks: OnboardingApplicationStepScheduledTask[];
  @HasOne() public onboardingInvitationToken: OnboardingInvitationToken;
}

@Model()
export class OnboardingApplicationStepScheduledTask extends ApplicationRecord {
  public static jsonapiType = 'onboarding_application_step_scheduled_tasks';

  @Attr() public onboardingApplicationStepId: string;
  @HasMany() public tasks: Task[];
}

@Model()
export class OnboardingScheduledInvitation extends ApplicationRecord {
  public static jsonapiType = 'onboarding_scheduled_invitations';

  get isCustomDate(): boolean {
    return this.triggerType === 'custom_date';
  }

  get isTriggeredByStep(): boolean {
    return this.triggerType === 'step_completion';
  }

  public static validationSchema = Yup.object().shape({
    triggerOnboardingApplicationStepId: Yup.string()
      .nullable()
      .when('triggerType', {
        is: 'step_completion',
        then: Yup.string().required("Can't be blank").nullable(),
      }),
    triggerType: Yup.string().required("Can't be blank").nullable(),
    delayValue: Yup.string()
      .nullable()
      .when('triggerType', {
        is: 'custom_date',
        then: Yup.string().required("Can't be blank"),
      }),
    delayOperator: Yup.string()
      .nullable()
      .when('triggerType', {
        is: 'custom_date',
        then: Yup.string().required("Can't be blank"),
      }),
    dateType: Yup.string()
      .nullable()
      .when('triggerType', {
        is: 'custom_date',
        then: Yup.string().required("Can't be blank"),
      }),
  });

  public static triggerTypeOptions: ISelectOption[] = [
    { label: 'Custom Date', value: 'custom_date' },
    { label: 'Onboarding Step Completed', value: 'step_completion' },
  ];

  public static delayOperatorOptions: ISelectOption[] = [
    { label: 'before', value: 'before' },
    { label: 'after', value: 'after' },
  ];

  public static dateTypeOptions: ISelectOption[] = [{ label: 'Candidate Start Date', value: 'start_at' }];

  @Attr() public onboardingApplicationStepId: string;
  @Attr() public triggerOnboardingApplicationStepId: string;
  @Attr() public companyId: string;
  @Attr() public triggerType: string;
  @Attr() public scheduleTime: string;
  @Attr() public scheduleTimezone: string;
  @Attr() public dateType: string;
  @Attr() public delayOperator: string;
  @Attr() public delayValue: number;
  @Attr() public scheduleDetails: string;

  @BelongsTo() public onboardingApplicationStep: OnboardingApplicationStep;
  @BelongsTo() public triggerOnboardingApplicationStep: OnboardingApplicationStep;
}

@Model()
export class OnboardingApplicationUniqueAttachment extends ApplicationRecord {
  public static jsonapiType = 'onboarding_application_unique_attachments';

  @Attr() public onboardingApplicationId: string;
  @Attr() public onboardingApplicationStepId: string;
  @Attr() public onboardingQuestionId: string;
  @Attr() public companyId: string;
  @Attr() public document: string;
  @Attr({ persist: false }) public documentObject: { path: string; id: string; name: string };
  @Attr({ persist: false }) public readonly documentFile: IActiveStorageFile;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingApplicationStep: OnboardingApplicationStep;
  @BelongsTo() public onboardingQuestion: OnboardingQuestion;
}

@Model()
export class OnboardingApplicationDocumentTemplate extends ApplicationRecord {
  public static jsonapiType = 'onboarding_application_document_templates';

  @Attr() public onboardingApplicationId: string;
  @Attr() public documentTemplateId: string;
  @Attr() public onboardingQuestionId: string;
  @Attr() public onboardingProcessStepId: string;
  @Attr() public companyId: string;
  @Attr() public updatedAt: string;
  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingProcessStep: OnboardingProcessStep;
  @BelongsTo() public documentTemplate: DocumentTemplate;
  @BelongsTo() public onboardingQuestion: OnboardingQuestion;
}

@Model()
export class OnboardingInvitationToken extends ApplicationRecord {
  public static jsonapiType = 'onboarding_invitation_tokens';

  @Attr() public onboardingApplicationId: string;
  @Attr() public onboardingApplicationStepId: string;
  @Attr({ persist: false }) public tokenExpired: boolean;

  @Attr({ persist: false }) public readonly tokenExpiresAt: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTime: string;
  @Attr({ persist: false }) public readonly tokenExpiresAtShortWithYearTime: string;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingApplicationStep: OnboardingApplicationStep;
}

export namespace OnboardingQuestion {
  export namespace Stats {
    export type options = {
      answer_regex_options: ISelectOption[];
    };
  }
}

@Model()
export class OnboardingQuestion extends ApplicationRecord {
  public static jsonapiType = 'onboarding_questions';

  public static requiredOptions = [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ];

  get isDocuSign(): boolean {
    return this.questionType === 'docu_sign';
  }

  get isHelloSignDocument(): boolean {
    return this.questionType === 'hello_sign_document';
  }

  get isHelloSignUniqueAttachment(): boolean {
    return this.questionType === 'hello_sign_unique_attachment';
  }

  get isUniqueAttachment(): boolean {
    return this.questionType === 'unique_attachment';
  }

  @Attr() public answerRegex: string;
  @Attr() public banked: boolean;
  @Attr() public title: string;
  @Attr() public additionalInformation: string;
  @Attr() public questionType: string;
  @Attr() public document: string;
  @Attr() public reportingName: string;
  @Attr() public maxNumRequiredAnswerOptions: number;
  @Attr() public numRequiredAnswerOptions: number;
  @Attr() public docuSignId: string;
  @Attr() public options: string[];
  // Making this writable to allow for them to be deleted
  @Attr({ persist: false }) public documentFile: IActiveStorageFile;
  @Attr() public position: number;
  @Attr() public required: boolean;
  @Attr() public sensitive: boolean;
  @Attr() public showUpload: boolean;
  @Attr() public target: 'candidate' | 'team_member';
  @Attr() public documentTemplateId: string;
  @Attr({ persist: false }) public updatedAt: string;
  @Attr() public allowDocumentTemplateSelection: boolean;

  @Attr({ persist: false }) public documentObject: { path: string; id: string; name: string };
  @Attr({ persist: false }) public readonly multipleChoiceHelperText: string;
  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public readonly timesUsed: string;

  /**
   * Note (DS):
   * As part of the onboarding workflow builder I needed to be able to
   * provide icons and names to onboarding question templates as part
   * of the drag and drop. I don't however want these attributes to be
   * saved, but just passed to another component
   */
  @Attr({ persist: false }) public icon: string;
  @Attr({ persist: false }) public name: string;

  @BelongsTo() public onboardingStage: OnboardingStage;
  @BelongsTo() public onboardingProcessStepQuestion: OnboardingProcessStepQuestion;
  @HasMany() public onboardingApplicationUniqueAttachments: OnboardingApplicationUniqueAttachment[];
  @HasMany() public onboardingAnswers: OnboardingAnswer[];
  @HasOne() public documentTemplate: DocumentTemplate;

  public static NEW(attrs: Partial<OnboardingQuestion>) {
    const defaultAttrs: Partial<OnboardingQuestion> = {
      required: null,
      title: null,
      answerRegex: '',
      banked: null,
      additionalInformation: '',
      questionType: '',
      document: null,
      reportingName: '',
      maxNumRequiredAnswerOptions: null,
      numRequiredAnswerOptions: null,
      docuSignId: '',
      options: [],
      position: null,
      showUpload: null,
      uniqueId: null,
      allowDocumentTemplateSelection: null,
      sensitive: false,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class OnboardingContentBlock extends ApplicationRecord {
  public static jsonapiType = 'onboarding_content_blocks';

  @Attr() public onboardingStageId: string;
  @Attr() public onboardingProcessStepTemplateId: string;
  @Attr() public title: string;
  @Attr() public content: string;
  @Attr() public contentType: string;
  @Attr() public position: number;
  // Cloudinary Image
  @Attr() public testimonialImage: string;
  // Attachments
  @Attr() public documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr() public images: string[];
  @Attr({ persist: false }) public readonly imagesFiles: ICloudinaryFile[];
  // Configuration
  @Attr() public provider: string;
  @Attr() public contentAlign: 'left' | 'center' | 'right';
  @Attr() public attachmentText: string;
  @BelongsTo() public onboardingProcessStepTemplate: OnboardingProcessStepTemplate;
  @Attr({ persist: false }) public uniqueId: string;

  // NOTE (DS): Maybe Add back in if requested
  // @Attr() public textAlign: string;
  // @Attr() public variant: string;
  // @Attr() public backgroundColor: string;

  @Attr() public configuration: {
    provider: string;
    contentAlign: string;
    attachmentText: string;
    // textAlign: string;
    // variant: string;
    // backgroundColor: string;
  };

  /**
   * Note (DS):
   * As part of the onboarding workflow builder I needed to be able to
   * provide icons and names to onboarding content block templates as part
   * of the drag and drop. I don't however want these attributes to be
   * saved, but just passed to another component
   */
  @Attr({ persist: false }) public icon: string;
  @Attr({ persist: false }) public name: string;
}

@Model()
export class OnboardingAnswer extends ApplicationRecord {
  public static jsonapiType = 'onboarding_answers';

  get isDocuSign(): boolean {
    return this.responseType === 'docu_sign';
  }

  @Attr() public onboardingQuestionId: string;
  @Attr() public onboardingApplicationId: string;
  @Attr() public target: 'candidate' | 'team_member';
  @Attr() public onboardingApplicationStepId: string;
  @Attr() public onboardingStageId: string;
  @Attr() public onboardingStageName: string;
  @Attr() public title: string;
  @Attr() public additionalInformation: string;
  @Attr() public textAnswer: string;
  @Attr() public booleanAnswer: boolean;
  @Attr() public dateAnswer: string;
  @Attr() public document: string;
  @Attr() public image: string;
  @Attr() public multipleChoiceAnswer: string[];
  @Attr() public urlAnswer: string;
  @Attr() public checkboxAnswer: boolean;
  @Attr() public docuSignAnswer: { envelope_id: string; status: string };
  @Attr() public docuSignTemplate: {
    recipients: IDocuSignRecipientProps[];
    id: string;
    tabs: IDocuSignTabsProps[];
  };
  @Attr() public isPremadeHelloSignResponse: boolean;
  @Attr() public isPremadeDocuSignResponse: boolean;

  @Attr({ persist: false }) public uniqueId: string;
  @Attr({ persist: false }) public isValid: boolean;
  @Attr() public responseType: string;
  @Attr() public approved: boolean;
  @Attr() public hidden: boolean;
  @Attr({ persist: false }) public readonly documentFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly imageFile: IActiveStorageFile;

  @BelongsTo() public onboardingApplication: OnboardingApplication;
  @BelongsTo() public onboardingApplicationStep: OnboardingApplicationStep;
  @BelongsTo() public onboardingQuestion: OnboardingQuestion;
  @BelongsTo() public onboardingStage: OnboardingStage;
  @HasOne() public helloSignSignatureRequest: HelloSignSignatureRequest;

  get docuSignEnvelopeStatus() {
    switch (this.docuSignAnswer?.status) {
      case 'sent':
      case 'delivered':
      case 'created':
        return Intent.PRIMARY;
      case 'completed':
        return Intent.SUCCESS;
      case 'declined':
      case 'voided':
        return Intent.DANGER;
      default:
        return Intent.NONE;
    }
  }

  get docuSignEnvelopeStatusColorMap() {
    switch (this.docuSignAnswer?.status) {
      case 'sent':
      case 'delivered':
      case 'created':
        return Colors.MARINE_50;
      case 'completed':
        return Colors.GREEN_50;
      case 'declined':
      case 'voided':
        return Colors.RED_50;
      default:
        return Colors.GREY_80;
    }
  }
}

@Model()
export class OnboardingDetail extends ApplicationRecord {
  public static jsonapiType = 'onboarding_details';

  @Attr() public text: string;
  @Attr() public onboardingStageId: string;
  @Attr() public documents: { name: string; path: string; id: string; signedId: string }[];
  @Attr({ persist: false }) public documentObject: { path: string; id: string; name: string };

  @BelongsTo() public onboardingStage: OnboardingStage;
}

@Model()
export class QrCode extends ApplicationRecord {
  get isServerTemplate(): boolean {
    return this.id === '-1';
  }

  get isCreating(): boolean {
    if (this.isServerTemplate) return true;

    return !this.isPersisted;
  }

  public static jsonapiType = 'qr_codes';

  public static channels = [
    { value: 'in_store', label: 'In-store' },
    { value: 'event', label: 'Event' },
    { value: 'other', label: 'Other' },
  ];

  public static NEW(attrs: Partial<QrCode>) {
    const defaultAttrs: Partial<QrCode> = {
      channel: 'in_store',
      destination: 'job_description_page',
      id: '-1',
      source: '',
      title: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public companyId: string;
  @Attr() public jobId: string;
  @Attr() public title: string;
  @Attr() public url: string;
  @Attr() public destination: string;
  @Attr() public channel: string;
  @Attr() public source: string;
  @Attr() public createdAt: string;

  @BelongsTo() public job: Job;
  @BelongsTo() public jobPosting: JobPosting;
  @BelongsTo() public company: Company;
}

@Model()
export class RecruitmentManager extends ApplicationRecord {
  public static jsonapiType = 'recruitment_managers';

  @Attr() public userId: string;
  @Attr() public jobId: string;
  @Attr() public companyId: string;
}

@Model()
export class SharingLink extends ApplicationRecord {
  public static jsonapiType = 'sharing_links';

  public static validationSchema = Yup.object().shape({
    channel: Yup.string().nullable().required("Can't be blank."),
    channelSource: Yup.string().nullable().required("Can't be blank."),
    name: Yup.string().nullable().required("Can't be blank."),
  });

  @Attr({ persist: false }) public readonly jobId: string;
  @Attr({ persist: false }) public readonly jobPostingId: string;
  @Attr() public name: string;
  @Attr() public channel: string;
  @Attr() public channelSource: string;

  @Attr({ persist: false }) public readonly linkUrl: string;

  @BelongsTo() public jobPosting: JobPosting;
}

@Model()
export class StageInterviewType extends ApplicationRecord {
  public static jsonapiType = 'stage_interview_types';
  @Attr() public stageId: string;
  @Attr() public interviewTypeId: string;

  @Attr() public ensureSave: boolean;

  @BelongsTo() public stage: Stage;
  @BelongsTo() public interviewType: InterviewType;
}

@Model()
export class StageVariant extends ApplicationRecord {
  public static jsonapiType = 'stage_variants';

  @Attr({ persist: false }) public readonly color: COLORS;
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly extendedType: string;
  @Attr({ persist: false }) public readonly editable: boolean;
  @Attr({ persist: false }) public readonly deletable: boolean;
  @Attr({ persist: false }) public readonly basicType:
    | 'applied'
    | 'rejected'
    | 'review'
    | 'interview'
    | 'hired'
    | 'offer';

  get isInterview(): boolean {
    return this.basicType === 'interview';
  }

  get isStandardInterview(): boolean {
    return this.basicType === 'interview' && this.extendedType === 'no_extended_type';
  }

  get isGroupAssessment(): boolean {
    return this.basicType === 'interview' && this.extendedType === 'group_assessment';
  }

  get isVideoInterview(): boolean {
    return this.basicType === 'interview' && this.extendedType === 'video_interview';
  }
}

@Model()
export class StructuredSection extends MobilityApplicationRecord {
  public static jsonapiType = 'structured_sections';

  public static sensitiveOptions: ISelectOption[] = [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ];

  public static enforcedOptions: ISelectOption[] = [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ];

  public static sectionTypeOptions: ISelectOption[] = [
    { label: 'Employment History', value: 'employment_history' },
    { label: 'Education History', value: 'education_history' },
    { label: 'Qualifications', value: 'qualifications' },
    { label: 'Custom', value: 'custom' },
  ];

  public static gapUnitOptions: ISelectOption[] = [
    { label: 'Weeks', value: 'weeks' },
    { label: 'Months', value: 'months' },
    { label: 'Years', value: 'years' },
  ];

  public static booleanFields = ['sensitive', 'identifyGaps', 'enforced'];

  @Attr() public title: string;
  @Attr() public description: string;
  @Attr() public internalName: string;
  @Attr() public minimumResponses: number;
  @Attr() public maximumResponses: number;
  @Attr() public sensitive: boolean | string;
  @Attr() public enforced: boolean | string;
  @Attr() public identifyGaps: boolean | string;
  @Attr() public gapValue: number;
  @Attr() public gapUnit: 'weeks' | 'months' | 'years';
  @Attr() public position: number;
  @Attr() public sectionType: 'employment_history' | 'education_history' | 'qualifications' | 'custom';
  /** @extraAttributes */
  @Attr({ persist: false }) public readonly jobsCount: number;

  @BelongsTo() public company: Company;
  @HasMany() public structuredSectionQuestions: StructuredSectionQuestion[];

  public static NEW(attrs: Partial<StructuredSection>) {
    const defaultAttrs: Partial<StructuredSection> = {
      title: '',
      description: '',
      internalName: '',
      minimumResponses: null,
      maximumResponses: null,
      sensitive: false,
      enforced: false,
      identifyGaps: false,
      gapValue: null,
      gapUnit: null,
      position: null,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    title: Yup.string().required("Can't be blank.").nullable(),
    internalName: Yup.string().required("Can't be blank.").nullable(),
    sensitive: Yup.boolean().required("Can't be blank.").nullable(),
  });
}

@Model()
export class StructuredSectionQuestion extends ApplicationRecord {
  public static jsonapiType = 'structured_section_questions';

  @Attr() public fieldType:
    | 'job_title'
    | 'company'
    | 'description'
    | 'location'
    | 'employment_start_end_date'
    | 'education_institute'
    | 'education_level'
    | 'course'
    | 'grade'
    | 'education_start_end_date'
    | 'qualification_name'
    | 'qualification_achievement_date'
    | 'custom';
  @Attr() public enabled: boolean;
  @Attr() public questionId: string;
  @Attr() public structuredSectionId: string;
  @Attr({ persist: false }) public readonly uniqueId: string;

  @BelongsTo() public question: Question;
  @BelongsTo() public structuredSection: StructuredSection;

  public static NEW(attrs: Partial<StructuredSectionQuestion>) {
    const defaultAttrs: Partial<StructuredSectionQuestion> = {
      fieldType: 'custom',
      enabled: true,
    };
    return new this({ ...defaultAttrs, ...attrs });
  }
}

@Model()
export class StructuredSectionResponse extends ApplicationRecord {
  public static jsonapiType = 'structured_section_responses';

  @Attr() public structuredSectionId: string;
  @Attr() public applicationId: string;
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr() public dateFromAnswer: string;
  @Attr() public dateToAnswer: string;
  @Attr() public employmentHistoryGap: {
    isOverMaxEmploymentGap: boolean;
    dateGapFrom: string;
    dateGapTo: string;
    humanizedGap: string;
    isCurrentOverMaxEmploymentGap: boolean;
    currentDateGapFrom: string;
    currentDateGapTo: string;
    currentHumanizedGap: string;
  };
  @BelongsTo() public structuredSection: StructuredSection;
  @HasMany() public structuredSectionResponseAnswers: StructuredSectionResponseAnswer[];
}

@Model()
export class StructuredSectionResponseAnswer extends ApplicationRecord {
  public static jsonapiType = 'structured_section_response_answers';

  @Attr() public answerId: string;
  @BelongsTo() public answer: Answer;
}

@Model()
export class JobStructuredSection extends ApplicationRecord {
  public static jsonapiType = 'job_structured_sections';

  @Attr() public structuredSectionId: number;
  @Attr() public companyId: string;
  @Attr() public identifyGaps: boolean;
  @Attr() public gapValue: number;
  @Attr() public gapUnit: 'weeks' | 'months' | 'years';
  @Attr() public position: number;

  @BelongsTo() public company: Company;
  @BelongsTo() public structuredSection: StructuredSection;
  @BelongsTo() public job: Job;
}

@Model()
export class TalentPipelineStructuredSection extends ApplicationRecord {
  public static jsonapiType = 'talent_pipeline_structured_sections';

  @Attr() public structuredSectionId: number;
  @Attr() public companyId: string;
  @Attr() public identifyGaps: boolean;
  @Attr() public gapValue: number;
  @Attr() public gapUnit: 'weeks' | 'months' | 'years';
  @Attr() public position: number;

  @BelongsTo() public company: Company;
  @BelongsTo() public structuredSection: StructuredSection;
  @BelongsTo() public talentPipeline: TalentPipeline;
}

@Model()
export class TalentPipelineInterviewType extends ApplicationRecord {
  public static jsonapiType = 'talent_pipeline_interview_types';
  @Attr() public talentPipelineId: string;
  @Attr() public interviewTypeId: string;
}

@Model()
export class Task extends ApplicationRecord {
  public static jsonapiType = 'tasks';

  public static NEW(attrs = {}) {
    const newThis = new this();
    const defaultAttrs: Partial<typeof newThis> = {
      description: '',
      dueOn: new Date().toString(),
      title: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    title: Yup.string().required("Can't be blank.").nullable(),
    dueOn: Yup.string().required("Can't be blank.").nullable(),
  });

  get isCompleted(): boolean {
    return this.status === 'completed';
  }

  get isOutstanding(): boolean {
    return this.status === 'outstanding';
  }

  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public creatorId: string;
  @Attr() public description: string;
  @Attr() public dueOn: string;
  @Attr({ persist: false }) public readonly dueOnShortWithYearDate: string;
  @Attr() public status: string;
  @Attr({ persist: false }) public ownerId: string;
  @Attr() public title: string;
  @Attr({ persist: false }) public readonly taskableType: string;
  @Attr({ persist: false }) public readonly taskableId: string;
  @Attr({ persist: false }) public readonly taskableName: string;
  /** @extraAttributes */
  @Attr({ persist: false }) public readonly linkedRecordName: string;
  @Attr({ persist: false }) public readonly linkedRecordPath: string;

  @HasMany() public comments: BypassComment[];
  @BelongsTo() public readonly creator: User;
  @BelongsTo() public owner: User;
  @BelongsTo() public taskable:
    | Job
    | Application
    | OnboardingApplication
    | JobSeeker
    | OnboardingApplicationStepScheduledTask
    | Candidate;
}

@Model()
export class UsersAccessGroup extends ApplicationRecord {
  public static jsonapiType = 'users_access_groups';

  @Attr({ persist: false }) public readonly accessGroupId: string;
  @Attr({ persist: false }) public readonly userId: boolean;
  @Attr() public updateNotificationPreferences: boolean;
  @Attr() public updatePermissions: boolean;
  @Attr() public updateVisibilities: boolean;
  @Attr() public updateWizards: boolean;
  @BelongsTo(AccessGroup) public accessGroup: AccessGroup;
}

@Model()
export class User extends ApplicationRecord {
  public static jsonapiType = 'users';
  public static MAX_PER = 10000;

  public static validationSchema = Yup.object().shape({
    email: Yup.string().required("Can't be blank."),
    firstName: Yup.string().required("Can't be blank."),
    lastName: Yup.string().required("Can't be blank."),
    timeZone: Yup.string().required("Can't be blank."),
  });

  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly lastSignInAt: string;
  @Attr({ persist: false }) public readonly currentSignInAt: string;
  @Attr() public firstName: string;
  @Attr() public lastName: string;
  @Attr() public email: string;
  @Attr() public otpRequiredForLogin: boolean;
  @Attr() public otpConfigured: boolean;
  @Attr() public disallowedApplicationEmails: string[];
  @Attr() public lastEmailBouncedAt: string;
  @Attr() public notifiable: boolean;
  @Attr() public timeZone: string;
  @Attr() public away: boolean;
  @Attr() public scheduledAwayStartAt: string;
  @Attr() public scheduledAwayEndAt: string;
  // Used to determine if we want to send an invitation to the user
  @Attr() public sendInvitation: boolean;
  // Used to indicate that we simple want to validate the users (not actually create it)
  @Attr() public validate: boolean;
  // Used when building the company user join when creating new users
  @Attr() public userType: string;
  // Used to signify we are scheduling away status
  @Attr() public scheduleAwayStatus: boolean;
  // Used to signify we are clearing any away status
  @Attr() public clearAwayStatus: boolean;
  // Used to scheduled what date you'll be away from
  @Attr() public awayStartScheduledAt: string;
  // Used to scheduled what date you'll be away until
  @Attr() public awayEndScheduledAt: string;
  // Used when inviting an employee not user to become a full user
  @Attr() public employeeUserId: string;
  // Used when inviting user as an agency user
  @Attr() public addToAgencyId: string;

  @Attr({ persist: false }) public readonly fullName: string;
  @Attr({ persist: false }) public readonly signInCount: number;
  @Attr({ persist: false }) public readonly unconfirmedEmail: string;
  @Attr({ persist: false }) public uniqueId: string;

  /** localised dates and times */
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly currentSignInAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly invitationSentAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly invitationAcceptedAtShortWithYearTimeWithZone: string;

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly accessGroupName: string;
  @Attr({ persist: false }) public readonly assignedToPosition: boolean;
  @Attr({ persist: false }) public readonly canDelete: boolean;
  @Attr({ persist: false }) public readonly canInvite: boolean;
  @Attr({ persist: false }) public readonly canInviteToInterview: boolean;
  @Attr({ persist: false }) public readonly canManageAccessGroup: boolean;
  @Attr({ persist: false }) public readonly canManageNotifications: boolean;
  @Attr({ persist: false }) public readonly canManageProfile: boolean;
  @Attr({ persist: false }) public readonly canManageStatus: boolean;
  @Attr({ persist: false }) public readonly canManageTags: boolean;
  @Attr({ persist: false }) public readonly canManageDisallowedApplicationEmails: boolean;
  @Attr({ persist: false }) public readonly canReinvite: boolean;
  @Attr({ persist: false }) public readonly canManageSecuritySettings: boolean;
  @Attr({ persist: false }) public readonly canManageCompany: boolean;
  @Attr({ persist: false }) public readonly canManageOffers: boolean;
  @Attr({ persist: false }) public readonly canManageComments: boolean;
  @Attr({ persist: false }) public readonly canSeeSensitiveAnswers: boolean;
  @Attr({ persist: false }) public readonly companyRoleIds: string[];
  @Attr({ persist: false }) public readonly favouritePresetId: string;
  @Attr({ persist: false }) public readonly globalPresetId: string;
  @Attr({ persist: false }) public readonly recruitmentManagerPresetId: string;
  @Attr({ persist: false }) public readonly hasCronofyAccessTokens: boolean;
  @Attr({ persist: false }) public readonly hasJobPresets: boolean;
  @Attr({ persist: false }) public readonly isCurrentUser: boolean;
  @Attr({ persist: false }) public readonly infuseEmployee: boolean;
  @Attr({ persist: false }) public readonly jobManager: boolean;
  @Attr({ persist: false }) public readonly twoFactorQrCode: string;
  @Attr({ persist: false }) public readonly recruiter: boolean;
  @Attr({ persist: false }) public readonly hasUserSlackProfileForCompany: boolean;
  @Attr({ persist: false }) public readonly visibilitiesCount: number;
  @Attr({ persist: false }) public readonly currentCompanyUserOnly: boolean;
  @Attr({ persist: false }) public readonly userTypeForCompany: string;

  @HasOne() public delegatedUser: UsersDelegate;
  @HasOne() public currentPositionHolder: PositionHolder;
  @HasMany() public comments: Comment[];
  @HasMany() public companies: Company[];
  @HasMany() public companyTeams: CompanyTeam[];
  @HasMany(CronofyAccessToken) public cronofyAccessTokens: CronofyAccessToken[];
  @HasMany() public scorecardRatings: ScorecardRating[];
  @HasMany() public scorecardNotes: ScorecardNote[];
  @HasMany() public signOffStageSignatories: SignOffStageSignatory[];
  @HasMany() public usersMessages: UsersMessage[];
  @HasMany() public notificationPresets: NotificationPreset[];
  @HasMany() public usersStageNotifications: UsersStageNotification[];
  @HasMany() public usersFavouriteJobs: UsersFavouriteJob[];
  @HasMany() public emailDigestSubscriptions: EmailDigestSubscription[];
  @HasMany() public profiles: Profile[];
  @HasMany() public usersRoles: UsersRole[];
  @HasMany() public usersAccessGroups: UsersAccessGroup[];
  @HasMany() public visibilities: Visibility[];
  @HasMany() public wizards: Wizard[];
}

@Model()
export class UsersDelegate extends ApplicationRecord {
  public static jsonapiType = 'users_delegates';

  @Attr() public principalUserId: string;
  @Attr() public delegateUserId: string;

  @BelongsTo() public delegateUser: User;
}

@Model()
export class UsersFavouriteJob extends ApplicationRecord {
  public static jsonapiType = 'users_favourite_jobs';
  @BelongsTo({ persist: false }) public readonly user: User;
  @BelongsTo({ persist: false }) public readonly company: Company;
  @BelongsTo({ persist: false }) public readonly job: Job;
}

@Model()
export class UsersMessage extends ApplicationRecord {
  public static jsonapiType = 'users_messages';
  @BelongsTo({ persist: false }) public readonly message: Message;
  @BelongsTo({ persist: false }) public readonly user: User;
}

export namespace UsersMessage {
  export namespace Stats {
    export namespace by {
      export type user = { key: string; value: number; label: string }[];
    }
  }
}

@Model()
export class VisibilitiesLayerable extends ApplicationRecord {
  public static jsonapiType = 'visibilities_layerables';

  @Attr() public readonly companyId: string;
  @Attr() public readonly visibilityId: string;
  @Attr() public readonly layerableId: string;
  @Attr() public readonly layerableType: string;
  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class Visibility extends ApplicationRecord {
  public static jsonapiType = 'visibilities';

  @Attr() public visibleId: string;
  @Attr() public visibleType: string;
  @Attr() public visibilityType: string;
  @Attr() public layer: string;
  @Attr({ persist: false }) public editable: boolean;
  @HasMany() public visibilitiesLayerables: VisibilitiesLayerable[];
}

@Model()
export class TalentPipeline extends ApplicationRecord {
  public static jsonapiType = 'talent_pipelines';

  public static booleanFields = [
    'askForAddress',
    'askForDateOfBirth',
    'askForLinkedinProfile',
    'askForMiddleName',
    'askForPreferredName',
    'requirePhone',
  ];

  @Attr() public companyId: string;
  @Attr() public enabled: boolean;
  @Attr() public interviewSync: string;
  @Attr() public askForAddress: boolean;
  @Attr() public askForDateOfBirth: boolean;
  @Attr() public askForLinkedinProfile: boolean;
  @Attr() public askForMiddleName: boolean;
  @Attr() public interviewAdditionalDescription: string;
  @Attr() public requirePhone: boolean;
  @Attr() public resumeRequirement: string;
  @Attr() public personalSummaryRequirement: string;
  @Attr() public structureValidationType: string;

  // Talent pipeline V2 attributes
  @Attr() public middleNameVisible: boolean;
  @Attr() public preferredNameVisible: boolean;
  @Attr() public dateOfBirthVisible: boolean;
  @Attr() public addressVisible: boolean;
  @Attr() public addressRequired: boolean;
  @Attr() public phoneVisible: boolean;
  @Attr() public phoneRequired: boolean;
  @Attr() public linkedInProfileVisible: boolean;
  @Attr() public resumeVisible: boolean;
  @Attr() public resumeRequired: boolean;
  @Attr() public personalSummaryVisible: boolean;
  @Attr() public personalSummaryRequired: boolean;

  @Attr() public interestedLocationsVisible: boolean;
  @Attr() public minimumLocationsRequired: number;
  @Attr() public maximumLocationsAllowed: number;
  @Attr() public interestedDepartmentsVisible: boolean;
  @Attr() public minimumDepartmentsRequired: number;
  @Attr() public maximumDepartmentsAllowed: number;
  @Attr() public interestedDivisionsVisible: boolean;
  @Attr() public minimumDivisionsRequired: number;
  @Attr() public maximumDivisionsAllowed: number;
  @Attr() public interestedStructureCustomGroupOnesVisible: boolean;
  @Attr() public minimumStructureCustomGroupOnesRequired: number;
  @Attr() public maximumStructureCustomGroupOnesAllowed: number;
  @Attr() public excludedLocationIds: string[];
  @Attr() public excludedDepartmentIds: string[];
  @Attr() public excludedDivisionIds: string[];
  @Attr() public excludedStructureCustomGroupOneIds: string[];

  @BelongsTo() public company: Company;
  @HasMany() public scorecards: Scorecard[];
  @HasMany() public questions: Question[];
  @HasMany() public talentPipelineScorecards: TalentPipelineScorecard[];
  @HasMany() public questionsQuestionables: QuestionsQuestionable[];
  @HasMany() public talentPipelineStructuredSections: TalentPipelineStructuredSection[];
}

@Model()
export class TalentPipelineScorecard extends ApplicationRecord {
  public static jsonapiType = 'talent_pipeline_scorecards';
  @Attr() public talentPipelineId: string;
  @Attr() public scorecardId: string;
  @Attr() public name: string;
  @Attr() public detailsString: string;
  @BelongsTo() public company: Scorecard;
  @BelongsTo() public talentPipeline: TalentPipeline;
}

@Model()
export class Theme extends MobilityApplicationRecord {
  public static jsonapiType = 'themes';

  public static NEW = (params: Partial<Theme> = {}) => {
    const defaultParams: Partial<Theme> = {
      highlightColor: '#FFFFFF',
      name: 'New Theme',
      primary: false,
      primaryColor: '#000000',
      secondaryColor: '#888888',
      logo: null,
      logoLight: null,
      description: '',
    };

    const theme = new Theme({
      ...defaultParams,
      ...params,
    });
    return theme;
  };
  @Attr() public description: string;
  @Attr() public highlightColor: string;
  @Attr() public logo: string;
  @Attr({ persist: false }) public readonly logoFile: IActiveStorageFile;
  @Attr() public logoLight: string;
  @Attr({ persist: false }) public readonly logoLightFile: IActiveStorageFile;
  @Attr() public name: string;
  @Attr() public primary: boolean;
  @Attr() public primaryColor: string;
  @Attr() public privacyPolicyText: string;
  @Attr() public secondaryColor: string;
  @Attr() public readonly companyId: string;

  @BelongsTo() public company: Company;
}

@Model()
export class Tag extends ApplicationRecord {
  public static jsonapiType = 'tags';
  public static MAX_PER = 1000;

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class TalentPool extends ApplicationRecord {
  public static jsonapiType = 'talent_pools';

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<TalentPool> = {
      assignJobSeekersToPool: true,
      name: '',
      description: '',
      visibility: 'personal',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Can't be blank.").nullable(),
    visibility: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public description: string;
  @Attr() public name: string;
  @Attr() public visibility: string;

  // Attr accessors
  @Attr() public assignJobSeekersToPool: boolean;
  @Attr() public createUserSubscription: boolean;
  @Attr() public duplicate: boolean;
  @Attr({ persist: false }) public readonly duplicatedId: string;

  // Extra attributes
  @Attr({ persist: false }) public isCreator: boolean;

  @HasMany() public talentPoolConditions: TalentPoolCondition[];
  @HasMany() public talentPoolSubscriptions: TalentPoolSubscription[];
}

@Model()
export class TalentPoolCondition extends ApplicationRecord {
  public static jsonapiType = 'talent_pool_conditions';

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<TalentPoolCondition> = {
      and_or: 'and',
      booleanQuestionValue: true,
      field: null,
      operator: 'is_one_of',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr() public and_or: string;
  @Attr() public field: string;
  @Attr() public operator: string;
  @Attr() public questionId: string;
  @Attr() public booleanQuestionValue: boolean;
  @Attr({ persist: false }) public booleanQuestion: boolean;

  @BelongsTo() public talentPool: TalentPool;
  @HasMany() public talentPoolConditionValues: TalentPoolConditionValue[];
}

@Model()
export class TalentPoolConditionValue extends ApplicationRecord {
  public static jsonapiType = 'talent_pool_condition_values';

  @Attr({ persist: false }) public readonly conditionableId: string;
  @Attr({ persist: false }) public readonly conditionableType: string;
  @Attr({ persist: false }) public readonly conditionableName: string;

  @BelongsTo() public talentPoolCondition: TalentPoolCondition;
  @BelongsTo() public conditionable: Location | Department | Division | StructureCustomGroupOne;
}

@Model()
export class TalentPoolsJobSeeker extends ApplicationRecord {
  public static jsonapiType = 'talent_pools_job_seekers';

  @BelongsTo() public jobSeeker: JobSeeker;
  @BelongsTo() public talentPool: TalentPool;
}

@Model()
export class TalentPoolSubscription extends ApplicationRecord {
  public static jsonapiType = 'talent_pool_subscriptions';

  @Attr({ persist: false }) public readonly talentPoolId: string;
  @Attr({ persist: false }) public readonly userId: string;
}

@Model()
export class ThemesThemeable extends ApplicationRecord {
  public static jsonapiType = 'themes_themeables';

  @Attr({ persist: false }) public themeId: string;
  @BelongsTo() public theme: Theme;
}

@Model()
export class UsersNotification extends ApplicationRecord {
  public static jsonapiType = 'users_notifications';
  @Attr({ persist: false }) public readonly createdAt: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly viewed: boolean;
  @Attr() public read: boolean;
  @Attr() public readAt: string;

  @BelongsTo() public notification: Notification;
}

export namespace UsersNotification {
  export namespace Stats {
    export type total = { unviewed_count: number; unread_count: number; unread_onboarding_count: number };
  }
}

@Model()
export class UserScorecard extends ApplicationRecord {
  public static jsonapiType = 'user_scorecards';
  @Attr() public aiAssistantConversationId: string;
  @Attr() public assignableType: string;
  @Attr() public assignableId: string;
  @Attr() public completedAt: Date;
  @Attr() public generateSummary: boolean;
  @Attr() public locked: boolean;
  @Attr() public name: string;
  @Attr() public scorecardId: string;
  @Attr() public scoreableType: string;
  @Attr() public scoreableId: string;
  @Attr() public sentiment: string;
  @Attr() public status: string;
  @Attr() public summary: string;

  @Attr({ persist: false }) public readonly completedAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly stageBasicType: StageVariantBasicType;
  @Attr({ persist: false }) public readonly stageName: string;
  @Attr({ persist: false }) public readonly userId: string;
  @Attr({ persist: false }) public readonly userName: string;

  @BelongsTo() public user: User;
  @BelongsTo() public scorecard: Scorecard;
  @BelongsTo() public scoreable: Application | JobSeeker;
  @HasOne() public scorecardNote: ScorecardNote;
  @HasMany() public scorecardRatings: ScorecardRating[];
  @HasMany() public scorecardAnswers: ScorecardAnswer[];
}

@Model()
export class UsersStageNotification extends ApplicationRecord {
  public static jsonapiType = 'users_stage_notifications';
  @Attr() public stageName: string;
  @Attr() public notificationPresetId: string;
  @Attr() public email: boolean;
  @Attr() public inApp: boolean;
  @Attr() public slack: boolean;

  @BelongsTo() public notificationPreset: NotificationPreset;
}

export type WithUserVisibility = Company | Location | Job | Stage;

@Model()
export class UsersSlackProfile extends ApplicationRecord {
  public static jsonapiType = 'users_slack_profiles';
  public static validationSchema = Yup.object().shape({
    profileId: Yup.string().required("Can't be blank.").nullable(),
  });

  @Attr() public userId: string;
  @Attr() public companyId: string;
  @Attr() public profileId: string;
  @Attr() public notifications: boolean;
}

@Model()
export class UserVisibility extends ApplicationRecord {
  public static jsonapiType = 'users_visibilities';

  @Attr({ persist: false }) public directAssignment: string;
  @Attr() public userId: string;
  @Attr() public visibleId: string;
  @Attr() public visibleType: string;

  /** @extraAttribute `visible_name` */
  @Attr({ persist: false }) public readonly visibleName: string;
  @Attr({ persist: false }) public readonly canDestroy: boolean;

  @BelongsTo() public user: User;
  @BelongsTo() public visible: Job;
  @BelongsTo() public company: Company | CurrentCompany;
}

@Model()
export class HiringWorkflow extends ApplicationRecord {
  public static jsonapiType = 'hiring_workflows';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });

  @Attr() public name: string;
  @Attr() public duplicate: boolean;
  @Attr() public duplicateId: string;
  @HasMany() public stages: Stage[];
}

@Model()
export class HiringManager extends ApplicationRecord {
  public static jsonapiType = 'hiring_managers';

  @Attr() public userId: string;
  @Attr() public jobId: string;
}

@Model()
export class SignOffWorkflow extends ApplicationRecord {
  public static jsonapiType = 'sign_off_workflows';

  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });

  @Attr() public companyId: number | string;
  @Attr() public name: string;
  @Attr() public duplicate: boolean;
  @Attr() public duplicateId: string;

  @Attr() public structureLinkIdsForJobs: string[];
  @Attr() public structureLinkIdsForOffers: string[];

  @Attr({ persist: false }) public readonly createdAt: string;

  @Attr({ persist: false }) public readonly structureLinkNamesForJobs: string[];
  @Attr({ persist: false }) public readonly structureLinkNamesForOffers: string[];
  @Attr({ persist: false }) public readonly inUseSignOffStageIds: string[];

  @Attr() public conditions: { property: string; operator: string; value: ISelectOption }[];
  @Attr() public appliesToJob: boolean;
  @Attr() public appliesToOffer: boolean;
  @Attr() public appliesToRequisition: boolean;

  @HasMany() public stages: Stage[];
  @HasMany() public signOffStages: SignOffStage[];
}

export namespace SignOffWorkflow {
  export namespace Stats {
    export namespace properties {
      export type conditions = {
        value: string;
        label: string;
        response: string;
        options?: string[];
      }[];
    }
  }
}

type TSignOffWorkflowPreferences = { email_sign_off: boolean; email_sign_off_fields: string[] };

@Model()
export class SignOffWorkflowConfiguration extends ApplicationRecord {
  public static jsonapiType = 'sign_off_workflow_configurations';

  @Attr() public companyId: number;
  @Attr() public jobs: TSignOffWorkflowPreferences;
  @Attr() public offers: TSignOffWorkflowPreferences;
  @Attr() public requisitions: TSignOffWorkflowPreferences;
  @Attr() public approvalRequestReminderDelayDays: number;

  @BelongsTo() public company: Company;
}

export namespace SignOffWorkflowConfiguration {
  export namespace Stats {
    export namespace email_sign_off_visible_fields {
      export type jobs = { attribute_name: string; humanized_attribute_name: string; attribute_group: string }[];
      export type offers = { attribute_name: string; humanized_attribute_name: string; attribute_group: string }[];
      export type requisitions = {
        attribute_name: string;
        humanized_attribute_name: string;
        attribute_group: string;
      }[];
    }
  }
}

@Model()
export class VonqCampaign extends ApplicationRecord {
  public static jsonapiType = 'vonq_campaigns';

  @Attr({ persist: false }) public campaignId: string;
  @Attr() public cancelledAt: string;
  @Attr({ persist: false }) public companyId: string;
  @Attr({ persist: false }) public createdAt: string;
  @Attr() public jobBoardBasketId: string;
  @Attr() public jobBoardProductId: string;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @Attr({ persist: false }) public vonqAttributes: {
    createdOn: string;
    orderedProductsSpecs: {
      productId: 'string';
      status: 'string';
      deliveredOn: 'string';
      duration: 'string';
      jobBoardLink: 'string';
    }[];
    postings: { clicks: number }[];
    status: string;
  };
  @Attr() public vonqJobBoard: VonqJobBoard[];
  @BelongsTo(JobBoardBasket) public jobBoardBasket: JobBoardBasket;
  @BelongsTo(JobBoardProduct) public jobBoardProduct: JobBoardProduct;
}

@Model()
export class VonqJobBoard extends ApplicationRecord {
  public static jsonapiType = 'vonq_job_boards';

  @Attr({ persist: false }) public readonly description: string;
  @Attr({ persist: false }) public readonly logoUrl: { url: string };
  @Attr({ persist: false }) public readonly logoSquareUrl: { url: string };
  @Attr({ persist: false }) public readonly logoRectangleUrl: { url: string };
  @Attr({ persist: false }) public readonly locations: { id: number; canonical_name: string }[];
  @Attr({ persist: false }) public readonly jobFunctions: string[];
  @Attr({ persist: false }) public readonly industries: { id: number; name: string }[];
  @Attr({ persist: false }) public readonly duration: { range: string; period: number };
  @Attr({ persist: false }) public readonly timeToProcess: { range: string; period: number };
  @Attr({ persist: false }) public readonly timeToSetup: { range: string; period: number };
  @Attr({ persist: false }) public readonly vonqPrice: number;
  @Attr({ persist: false }) public readonly ratecardPrice: string;
  @Attr({ persist: false }) public readonly crossPostings: string[];
  @Attr({ persist: false }) public readonly homepage: string;
  @Attr({ persist: false }) public readonly type: string;
  @Attr({ persist: false }) public readonly title: string;
  @Attr({ persist: false }) public readonly channel: string;
  @Attr({ persist: false }) public readonly productId: string;
  @Attr({ persist: false }) public readonly audienceGroup: string;
  @Attr({ persist: false }) public readonly mcEnabled: string;
  @Attr({ persist: false }) public readonly allowOrders: boolean;
  @Attr({ persist: false }) public readonly formattedPriceInCompanyCurrency: string;
  @Attr({ persist: false }) public readonly recommended: boolean;
}

@Model()
export class Position extends ApplicationRecord {
  public static jsonapiType = 'positions';
  public static MAX_PER = 250;

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<Position> = {
      identifier: '',
      name: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  public static validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Can't be blank.").nullable(),
    numberOfPositions: Yup.number().required("Can't be blank").min(1, 'Must be at least 1').nullable(),
  });

  @Attr({ persist: false }) public readonly duplicateIds: string[];
  @Attr() public identifier: string;
  @Attr() public name: string;
  @Attr() public archivedAt: string;
  @Attr() public parentPositionId: string;
  // Virtual attributes
  @Attr() public numberOfPositions: number;
  @Attr() public archive: boolean;
  @Attr() public restore: boolean;
  @Attr() public reopen: boolean;
  @Attr() public duplicate: boolean;
  @Attr({ persist: false }) public readonly createdAtLongDate: string;

  @HasMany() public childPositions: Position[];
  @HasMany() public positionOpenings: PositionOpening[];
  @HasOne() public currentPositionOpening: PositionOpening;
  @BelongsTo() public parentPosition: Position;
  @HasMany() public customAttributes: CustomAttribute[];
}

@Model()
export class PositionOpening extends ApplicationRecord {
  public static jsonapiType = 'position_openings';

  @Attr() public closeOn: string;
  @Attr() public current: boolean;
  @Attr() public filledAt: string;
  @Attr() public jobId: string;
  @Attr() public openOn: string;
  @Attr() public positionId: string;
  @Attr() public status: string;
  @Attr() public targetFillDate: string;
  @Attr() public targetOpenDate: string;
  @Attr() public targetStartDate: string;
  @Attr({ persist: false }) public readonly openOnLongDate: string;
  @Attr({ persist: false }) public readonly closeOnLongDate: string;
  @Attr({ persist: false }) public readonly filledAtLongDate: string;

  @BelongsTo() public position: Position;
  @BelongsTo() public job: Job;
  @HasOne() public positionHolder: PositionHolder;
}

@Model()
export class PositionHolder extends ApplicationRecord {
  public static jsonapiType = 'position_holders';

  public static validationSchema = Yup.object().shape({
    startOn: Yup.string().nullable().required("Can't be blank"),
  });

  @Attr() public positionOpeningId: string;
  @Attr() public userId: string;
  @Attr() public startOn: string;
  @Attr() public endOn: string;
  @Attr() public companyId: string;

  @Attr({ persist: false }) public readonly startOnShortWithYearDate: string;
  @Attr({ persist: false }) public readonly endOnShortWithYearDate: string;
  @Attr({ persist: false }) public readonly startOnLongDate: string;
  @Attr({ persist: false }) public readonly endOnLongDate: string;

  @BelongsTo() public positionOpening: PositionOpening;
  @BelongsTo() public user: User;
}

/**
 * These models inherit from ones defined above so can only be defined after
 */

@Model()
export class CurrentCompany extends Company {
  public static jsonapiType = 'current_companies';
}

export namespace CurrentCompany {
  export namespace Stats {
    export namespace options {
      export type organic_job_boards = { name: string; key: string; logo: string }[];
      export type requisition_id_requirements = Record<string, { id: number; label: string }>;
      export type max_applications_time_periods = Record<string, { id: number; label: string; datetime: string }>;
      export type redaction_options = Record<string, { id: number; label: string; description: string }>;
    }
  }
}

@Model()
export class CurrentUser extends User {
  public static jsonapiType = 'current_users';
}

@Model()
export class CurrentProfile extends Profile {
  public static jsonapiType = 'current_profiles';
}

/**
 * Bypass models - those that have less restrictive scopes, but are not as extensive
 */

@Model()
export class BypassApplication extends ApplicationRecord {
  public static jsonapiType = 'bypass/applications';

  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr({ persist: false }) public readonly createdAtShortWithYearTimeWithZone: string;
  @Attr({ persist: false }) public readonly fullName: string;
  @Attr({ persist: false }) public readonly jobId: string;
  @Attr({ persist: false }) public readonly visible: boolean;
  @Attr({ persist: false }) public readonly jobTitle: string;
  @BelongsTo() public job: BypassJob;
  @BelongsTo() public stage: BypassStage;
}

@Model()
export class BypassComment extends Comment {
  public static jsonapiType = 'bypass/comments';
}

@Model()
export class BypassGroupAssessment extends GroupAssessment {
  public static jsonapiType = 'bypass/group_assessments';
}

@Model()
export class BypassInterviewType extends GroupAssessment {
  public static jsonapiType = 'bypass/interview_types';

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class BypassQuestionCollection extends ApplicationRecord {
  public static jsonapiType = 'bypass/question_collections';

  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly questionCount: number;

  @HasMany() public questionCollectionsQuestions: BypassQuestionCollectionsQuestion[];
}

@Model()
export class BypassQuestionCollectionsQuestion extends ApplicationRecord {
  public static jsonapiType = 'bypass/question_collections_questions';

  @Attr() public position: number;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly questionCollectionId: string;
  @Attr({ persist: false }) public readonly questionId: string;

  @BelongsTo() public question: BypassQuestion;
}

@Model()
export class BypassQuestion extends ApplicationRecord {
  public static jsonapiType = 'bypass/questions';

  @Attr({ persist: false }) public readonly title: string;

  @HasMany() public conditionalQuestions: BypassConditionalQuestion[];
}

@Model()
export class BypassConditionalQuestion extends ApplicationRecord {
  public static jsonapiType = 'bypass/conditional_questions';

  @Attr() public position: number;
  @Attr() public conditions: { operator: string; value: string }[];

  @BelongsTo() public parentQuestion: BypassQuestion;
  @BelongsTo() public childQuestion: BypassQuestion;
}

@Model()
export class BypassRecruiterApplication extends ApplicationRecord {
  public static jsonapiType = 'bypass/recruiter/applications';

  @Attr() public cv: string;
  @Attr() public coverLetter: string;
  @Attr({ persist: false }) public readonly coverLetterFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly coverLetterText: string;
  @Attr({ persist: false }) public readonly cvFile: IActiveStorageFile;

  /** Used to determine if we should wait when parsing the resume and if the resume is currently being parsed */
  @Attr() public isParsing: boolean;
  @Attr() public waitForParse: boolean;

  // Extra attributes
  @Attr({ persist: false }) public readonly pdfCoverLetterFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly pdfCvFile: IActiveStorageFile;
  @Attr({ persist: false }) public readonly redactedCvFile: IActiveStorageFile;

  // False attributes
  @Attr({ persist: false }) public readonly concealed: boolean;
  @Attr({ persist: false }) public readonly hideInterpretedResume: boolean;

  @HasOne() public resume: BypassRecruiterResume;
}

@Model()
export class BypassRecruiterResume extends ApplicationRecord {
  public static jsonapiType = 'bypass/recruiter/resumes';

  @Attr() public resumeableId: string;
  @Attr() public resumeableType: string;
  @Attr() public executiveSummary: string;
  @Attr() public sovrenDescription: string;
  @Attr() public formattedName: string;
  @Attr() public address: string;
  @Attr() public telephone: string;
  @Attr() public mobile: string;
  @Attr() public email: string;
  @Attr() public website: string;
  @Attr() public certifications: string[];
  @Attr() public achievements: string[];
  @Attr() public isProcessing: boolean;
  @HasMany() public employmentHistories: EmploymentHistory[];
  @HasMany() public educationHistories: EducationHistory[];
}

@Model()
export class BypassJob extends ApplicationRecord {
  public static jsonapiType = 'bypass/jobs';

  get isOpen(): boolean {
    return this.status === 'open';
  }

  get isClosed(): boolean {
    return this.status === 'closed';
  }

  @Attr({ persist: false }) public readonly detailedTitle: string;
  @Attr({ persist: false }) public readonly locationName: string;
  @Attr({ persist: false }) public readonly requisitionId: string;
  @Attr({ persist: false }) public readonly stageIds: string[];
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly structureLinkNames: string[];
  @Attr({ persist: false }) public readonly title: string;
  @Attr({ persist: false }) public readonly visibility: string;
  @HasMany() public stages: BypassStage[];
}

@Model()
export class BypassStage extends ApplicationRecord {
  public static jsonapiType = 'bypass/stages';

  get isHired(): boolean {
    return this.basicType === 'hired';
  }

  @Attr({ persist: false }) public readonly basicType: StageVariant['basicType'];
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly questionCollectionId: string;
  @Attr({ persist: false }) public readonly scorecardLocked: boolean;

  @HasMany() public interviewTeamMembers: BypassInterviewTeamMember[];
}

@Model()
export class BypassInterviewTeamMember extends ApplicationRecord {
  public static jsonapiType = 'bypass/interview_team_members';
  @Attr() public interviewTypePartId: string;

  @BelongsTo() public user: BypassUser;
}

@Model()
export class BypassLocation extends ApplicationRecord {
  public static jsonapiType = 'bypass/locations';

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class BypassDepartment extends ApplicationRecord {
  public static jsonapiType = 'bypass/departments';

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class BypassDivision extends ApplicationRecord {
  public static jsonapiType = 'bypass/divisions';

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class BypassStructureCustomGroupOne extends ApplicationRecord {
  public static jsonapiType = 'bypass/structure_custom_group_ones';

  @Attr({ persist: false }) public readonly name: string;
}

@Model()
export class BypassStructureLink extends ApplicationRecord {
  public static jsonapiType = 'bypass/structure_links';
  public static MAX_PER = 16000;

  @Attr({ persist: false }) public readonly acceptsSpeculativeApplications: string;
  @Attr({ persist: false }) public readonly divisionId: string;
  @Attr({ persist: false }) public readonly locationId: string;
  @Attr({ persist: false }) public readonly departmentId: string;
  @Attr({ persist: false }) public readonly structureCustomGroupOneId: string;
  @Attr({ persist: false }) public readonly inactive: boolean;

  // Extra attributes
  @Attr({ persist: false }) public readonly displayName: string;
  @Attr({ persist: false }) public readonly locationName: string;
  @Attr({ persist: false }) public readonly departmentName: string;
  @Attr({ persist: false }) public readonly divisionName: string;
  @Attr({ persist: false }) public readonly structureCustomGroupOneName: string;
}

/**
 * These models are not backed by ActiveRecord
 */
export class CustomEmail extends ApplicationRecord {
  public static validationSchema = Yup.object().shape({
    body: Yup.string().nullable().required("Can't be blank"),
    subject: Yup.string().nullable().required("Can't be blank"),
  });

  public static NEW() {
    const attrs: Partial<CustomEmail> = {
      body: '',
      documents: [],
      salutation: 'Hi {{first_name}},',
      signature: 'Thanks, \r\n{{company}}',
      subject: '',
      cc: [],
      bcc: [],
    };
    return new this(attrs);
  }

  @Attr({ persist: false }) public readonly documents: string[];
  @Attr({ persist: false }) public readonly documentsFiles: IActiveStorageFile[];
  @Attr({ persist: false }) public subject: string;
  @Attr({ persist: false }) public salutation: string;
  @Attr({ persist: false }) public body: string;
  @Attr({ persist: false }) public signature: string;
  @Attr({ persist: false }) public tagsWithContext: any;
  @Attr({ persist: false }) public cc: string[];
  @Attr({ persist: false }) public bcc: string[];
}

export class CustomEmailReply extends ApplicationRecord {
  public static validationSchema = Yup.object().shape({
    body: Yup.string().required("Can't be blank"),
  });

  public static NEW(attrs = {}) {
    const defaultAttrs: Partial<CustomEmailReply> = {
      cc: [],
      bcc: [],
      body: '',
      subject: '',
    };
    return new this({ ...defaultAttrs, ...attrs });
  }

  @Attr({ persist: false }) public cc: string[];
  @Attr({ persist: false }) public bcc: string[];
  @Attr({ persist: false }) public subject: string;
  @Attr({ persist: false }) public body: string;
}

export class CustomSms extends ApplicationRecord {
  public static validationSchema = Yup.object().shape({
    body: Yup.string().required("Can't be blank").max(1600, 'Max length is 1600 characters.'),
  });

  public static NEW() {
    const attrs: Partial<CustomSms> = {
      body: '',
    };
    return new this(attrs);
  }

  @Attr({ persist: false }) public body: string;
}

@Model()
export class ReportTemplate extends ApplicationRecord {
  public static jsonapiType = 'report_templates';
  public static operatorOptions = [
    { label: 'equals', value: 'equals' },
    { label: 'does not equal', value: 'not_equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'less than', value: 'less_than' },
  ];
  public static repeatPeriods = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ];
  // The numbers correspond to the weekday method in rails which has sunday as 0 and then moves up from there
  public static weeklyOptions = [
    { label: 'M', value: '1' },
    { label: 'T', value: '2' },
    { label: 'W', value: '3' },
    { label: 'T', value: '4' },
    { label: 'F', value: '5' },
    { label: 'S', value: '6' },
    { label: 'S', value: '0' },
  ];
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });

  public static dynamicDateOptions = [
    { label: 'Today', value: 'today' },
    { label: '1 day ago', value: '1_day_ago' },
    { label: '2 days ago', value: '2_day_ago' },
    { label: '3 days ago', value: '3_day_ago' },
    { label: '4 days ago', value: '4_day_ago' },
    { label: '5 days ago', value: '5_day_ago' },
    { label: '6 days ago', value: '6_day_ago' },
    { label: '1 week ago', value: '1_week_ago' },
    { label: '2 weeks ago', value: '2_week_ago' },
    { label: '3 weeks ago', value: '3_week_ago' },
    { label: '4 weeks ago', value: '4_week_ago' },
    { label: '1 month ago', value: '1_month_ago' },
    { label: '2 months ago', value: '2_month_ago' },
    { label: '3 months ago', value: '3_month_ago' },
    { label: '4 months ago', value: '4_month_ago' },
    { label: '5 months ago', value: '5_month_ago' },
    { label: '6 months ago', value: '6_month_ago' },
    { label: '9 months ago', value: '9_month_ago' },
    { label: '1 year ago', value: '1_year_ago' },
    { label: '2 years ago', value: '2_year_ago' },
    { label: '3 years ago', value: '3_year_ago' },
    { label: '4 years ago', value: '4_year_ago' },
    { label: '5 years ago', value: '5_year_ago' },
    { label: '1 day from now', value: '1_day_in_future' },
    { label: '2 days from now', value: '2_day_in_future' },
    { label: '3 days from now', value: '3_day_in_future' },
    { label: '4 days from now', value: '4_day_in_future' },
    { label: '5 days from now', value: '5_day_in_future' },
    { label: '6 days from now', value: '6_day_in_future' },
    { label: '1 week from now', value: '1_week_in_future' },
    { label: '2 weeks from now', value: '2_week_in_future' },
    { label: '3 weeks from now', value: '3_week_in_future' },
    { label: '4 weeks from now', value: '4_week_in_future' },
    { label: '1 month from now', value: '1_month_in_future' },
    { label: '2 months from now', value: '2_month_in_future' },
    { label: '3 months from now', value: '3_month_in_future' },
    { label: '4 months from now', value: '4_month_in_future' },
    { label: '5 months from now', value: '5_month_in_future' },
    { label: '6 months from now', value: '6_month_in_future' },
    { label: '9 months from now', value: '9_month_in_future' },
    { label: '1 year from now', value: '1_year_in_future' },
    { label: '2 years from now', value: '2_year_in_future' },
    { label: '3 years from now', value: '3_year_in_future' },
    { label: '4 years from now', value: '4_year_in_future' },
    { label: '5 years from now', value: '5_year_in_future' },
  ];

  public static accessValidationSchema = Yup.object().shape({
    repeatPeriod: Yup.string()
      .nullable()
      .when('scheduled', {
        is: true,
        then: Yup.string().required("Can't be blank").nullable(),
      }),
    scheduleTimezone: Yup.string()
      .nullable()
      .when('scheduled', {
        is: true,
        then: Yup.string().required("Can't be blank").nullable(),
      }),
    scheduled: Yup.boolean(),
  });

  @Attr() public baseReport: boolean;
  @Attr() public columns: { value: string; label: string; type: string; custom_label: string }[];
  @Attr({ persist: false }) public readonly createdAtShortWithYearDate: string;
  @Attr() public filters: { property: string; operator: string; value: Record<string, unknown> }[];
  @Attr({ persist: false }) public readonly accessRecipientUserIds: string[];
  @Attr({ persist: false }) public readonly copyAccess: boolean;
  @Attr({ persist: false }) public readonly copySchedule: boolean;
  @Attr({ persist: false }) public readonly createdAtLongWithDay: string;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly description: string;
  @Attr() public emailBody: string;
  @Attr() public emailSubject: string;
  @Attr() public fileExtension: string;
  @Attr() public name: string;
  @Attr() public notifyOwner: boolean;
  @Attr() public repeatInterval: string;
  @Attr() public repeatPeriod: string;
  @Attr() public reportType: string;
  @Attr() public scheduled: boolean;
  @Attr() public scheduledType: string;
  @Attr() public sftpFolderName: string;
  @Attr() public sftpUrl: string;
  @Attr() public sftpPassword: string;
  @Attr() public sftpUsername: string;
  @Attr() public sftpPort: string;
  @Attr() public lineEnding: 'LF' | 'CRLF';
  @Attr({ persist: false }) public readonly scheduleDescription: string;
  @Attr() public scheduleTimeString: string;
  @Attr({ persist: false }) public scheduleTimeAsDate: string;
  @Attr() public scheduleTimezone: string;
  @Attr({ persist: false }) public readonly updatedAtLongWithDay: string;

  /** @extraAttributes */
  @Attr({ persist: false }) public readonly canEdit: boolean;
  @Attr({ persist: false }) public readonly canDelete: boolean;

  @BelongsTo() public lastModifier: User;
  @HasOne() public accessOwner: ReportTemplatesAccess;
  @HasMany() public accessViewers: ReportTemplatesAccess[];
  @HasMany() public accessEditors: ReportTemplatesAccess[];
  @HasMany() public accessRecipients: ReportTemplatesAccess[];
}

@Model()
export class ReportTemplatesAccess extends ApplicationRecord {
  public static jsonapiType = 'report_templates_accesses';

  @Attr() public accessType: string;
  @Attr() public companyId: string;
  @Attr() public reportTemplateId: string;
  @Attr() public userId: string;
  @BelongsTo(User) public user: User;
}

@Model()
export class Report extends ApplicationRecord {
  public static jsonapiType = 'reports';
  public static validationSchema = Yup.object().shape({
    name: Yup.string().required("Can't be blank."),
  });
  public static reportTypeOptions = [
    { label: 'Job', value: 'job' },
    { label: 'Candidate', value: 'candidate' },
    { label: 'Location', value: 'location' },
    { label: 'Department', value: 'department' },
    { label: 'Other', value: 'other' },
  ];

  @Attr() public columns: { value: string; label: string; type: string; custom_label: string }[];
  @Attr() public filters: {
    property: string;
    property_label?: string;
    operator: string;
    value: Record<string, unknown>;
  }[];
  @Attr() public name: string;
  @Attr() public reportTemplateId: string;
  @Attr() public reportType: string;
  @Attr({ persist: false }) public readonly companyId: string;
  @Attr({ persist: false }) public readonly status: string;
  @Attr({ persist: false }) public readonly createdAtShortWithTime: string;
  // Extra Attributes
  @Attr({ persist: false }) public readonly canEdit: boolean;
  @Attr({ persist: false }) public readonly csvReportFile: IActiveStorageFile;
  // Any type used because this is an array of arrays with each array being a row
  @Attr({ persist: false }) public readonly parsedReport: any;
}

@Model()
export class AiAssistantResponse extends ApplicationRecord {
  public static jsonapiType = 'ai_assistant_responses';

  @Attr() public previousResponseId: string;
  @Attr() public input: string;
  @Attr() public responseId: string;
  @Attr() public responseContent: string;
  @Attr() public inputContent: string;
  @Attr() public responsibleType: string;
  @Attr() public responsibleId: string;
}

@Model()
export class CvLibraryPosting extends ApplicationRecord {
  public static jsonapiType = 'cv_library_postings';

  public static validationSchema = Yup.object().shape<Shape<CvLibraryPosting>>({
    title: Yup.string().required("Can't be blank").max(70, 'Title is limited to 70 characters'),
    salaryPeriod: Yup.string().required("Can't be blank"),
    salaryMin: Yup.string().required("Can't be blank").nullable(),
    preciseLocation: Yup.string().required("Can't be blank"),
    displayLocation: Yup.string().required("Can't be blank"),
    jobType: Yup.string().required("Can't be blank"),
    startOn: Yup.string().nullable().max(15, 'Start is limited to 15 characters'),
    duration: Yup.string().nullable().max(20, 'Duration is limited to 20 characters'),
    industry: Yup.string().required("Can't be blank"),
  });

  public static NEW(attributes: Partial<CvLibraryPosting> = {}) {
    return new CvLibraryPosting({
      relocateFromEu: false,
      remote: false,
      title: '',
      preciseLocation: '',
      displayLocation: '',
      jobType: null,
      hideSalary: false,
      salaryPeriod: '',
      salaryMax: '',
      salaryMin: '',
      industry: '',
      quickApply: false,
      startOn: '',
      duration: '',
      ...attributes,
    });
  }

  @Attr public readonly uuid: string;
  @Attr public relocateFromEu: boolean;
  @Attr public title: string;
  @Attr public reference: string;
  @Attr public preciseLocation: string;
  @Attr public displayLocation: string;
  @Attr public jobType: string;
  @Attr public salaryPeriod: string;
  @Attr public salaryMax: string;
  @Attr public salaryMin: string;
  @Attr public industry: string;

  @Attr public hideSalary: boolean;
  @Attr public startOn: string;
  @Attr public duration: string;
  @Attr public remote: boolean;
  @Attr public quickApply: boolean;
  @Attr({ persist: false }) public readonly userId: string;

  @Attr({ persist: false }) public readonly status: string;

  @BelongsTo(Company) public company: Company;
  @BelongsTo(Integration) public integration: Integration;
  @BelongsTo(Job) public job: Job;
  @BelongsTo(Job) public user: User;
}

@Model()
export class TotalJobsPosting extends ApplicationRecord {
  public static jsonapiType = 'total_jobs_postings';

  public static validationSchema = Yup.object().shape<Shape<TotalJobsPosting>>({
    euOnly: Yup.boolean().required("Can't be blank"),
    rateUnit: Yup.string().required("Can't be blank"),
    region: Yup.string().required("Can't be blank"),
    salaryMax: Yup.number().required("Can't be blank"),
    salaryMin: Yup.number().required("Can't be blank"),
  });

  public static NEW(attributes: Partial<TotalJobsPosting> = {}) {
    return new TotalJobsPosting({
      euOnly: true,
      hideFullPostcode: true,
      jobTemplateName: '',
      notificationEmail: '',
      postcode: '',
      rateUnit: '',
      region: '',
      salaryDesc: '',
      salaryMax: 0,
      salaryMin: 0,
      town: '',
      ...attributes,
    });
  }

  @Attr public readonly uuid: string;
  @Attr public euOnly: boolean;
  @Attr public hideFullPostcode: boolean;
  @Attr public jobTemplateName: string;
  @Attr public notificationEmail: string;
  @Attr public postcode: string;
  @Attr public rateUnit: string;
  @Attr public region: string;
  @Attr public salaryDesc: string;
  @Attr public salaryMax: number;
  @Attr public salaryMin: number;
  @Attr({ persist: false }) public readonly status: string;
  @Attr public town: string;

  @BelongsTo(Company) public user: Company;
  @BelongsTo(Integration) public integration: Integration;
  @BelongsTo(Job) public job: Job;
}

@Model()
export class ReedPosting extends ApplicationRecord {
  public static jsonapiType = 'reed_postings';

  get isPosted(): boolean {
    return this.status === 'posted';
  }

  public static validationSchema = Yup.object().shape<Shape<ReedPosting>>({
    expireIn: Yup.string().required("Can't be blank").nullable(),
  });

  public static NEW(attributes: Partial<SeekPosting> = {}) {
    return new ReedPosting({
      productId: null,
      expireIn: 42,
      isPublic: null,
      isGraduate: null,
      sendApplicationDigest: null,
      ownerRef: null,
      parentSectorId: null,
      jobSectorId: null,
      onTargetEarnings: null,
      benefits: null,
      proRata: null,
      negotiable: null,
      showSalary: null,
      hiddenSalaryDescription: null,
      emailForApplications: null,
      eligibleUkOnly: null,
      skills: null,
      locationBaseType: null,
      ...attributes,
    });
  }

  @Attr public jobType: number;
  @Attr public jobAdUid: string;
  @Attr public workingHours: number;
  @Attr public description: string;
  @Attr public title: string;
  @Attr public townName: string;
  @Attr public productId: number;
  @Attr public expireIn: string;
  @Attr public isPublic: boolean;
  @Attr public isGraduate: boolean;
  @Attr public sendApplicationDigest: boolean;
  @Attr public ownerRef: string;
  @Attr public countyName: string;
  @Attr public countryName: string;
  @Attr public parentSectorId: string;
  @Attr public jobSectorId: string;
  @Attr public minSalary: number;
  @Attr public maxSalary: number;
  @Attr public currency: string;
  @Attr public salaryType: number;
  @Attr public onTargetEarnings: boolean;
  @Attr public benefits: boolean;
  @Attr public proRata: boolean;
  @Attr public negotiable: boolean;
  @Attr public showSalary: boolean;
  @Attr public hiddenSalaryDescription: number;
  @Attr public emailForApplications: string;
  @Attr public eligibleUkOnly: boolean;
  @Attr public coverLetterPreference: number;
  @Attr public skills: string;
  @Attr public locationBaseType: number;
  @Attr public lastError: string;
  @Attr public endedAt: string;
  @Attr public hasEndedOrExpired: boolean;
  @Attr public status: string;
  @Attr public url: string;
  @Attr public isRelist: boolean;

  @BelongsTo(Company) public user: Company;
  @BelongsTo(Integration) public integration: Integration;
  @BelongsTo(Job) public job: Job;
  @BelongsTo(IntegrationAccountsReedAccount) public integrationAccount: IntegrationAccountsReedAccount;
}

@Model()
export class SeekPosting extends ApplicationRecord {
  public static jsonapiType = 'seek_postings';

  public static validationSchema = Yup.object().shape<Shape<SeekPosting>>({
    title: Yup.string().required("Can't be blank").nullable(),
    basisCode: Yup.string().required("Can't be blank").nullable(),
    currency: Yup.string().required("Can't be blank").nullable(),
    hiringManagerName: Yup.string().nullable().required("Can't be blank"),
    hiringManagerEmail: Yup.string().nullable().required("Can't be blank"),
    salaryMinAmount: Yup.number().required("Can't be blank").nullable(),
    salaryMaxAmount: Yup.number().required("Can't be blank").nullable(),
    searchResultsSummary: Yup.string().required("Can't be blank").nullable().max(150, 'Max length is 150 characters.'),
    searchBulletPoints: Yup.array().of(Yup.string().nullable().max(80, 'Max length is 80 characters')).nullable(),
    jobParentCategories: Yup.string().nullable().required("Can't be blank"),
    jobCategories: Yup.string().nullable().required("Can't be blank"),
    positionLocation: Yup.string().nullable().required("Can't be blank"),
    seekAdvertisementProductId: Yup.string().nullable().required("Can't be blank"),
  });

  public static NEW(attributes: Partial<SeekPosting> = {}) {
    return new SeekPosting({
      title: null,
      basisCode: null,
      currency: null,
      salaryMinAmount: null,
      salaryMaxAmount: null,
      salaryDescription: null,
      intervalCode: null,
      jobParentCategories: null,
      jobCategories: null,
      positionLocation: null,
      positionLocationReadableName: null,
      seekAdvertisementProductId: null,
      searchResultsSummary: null,
      searchBulletPoints: [],
      hiringManagerName: null,
      hiringManagerEmail: null,
      hiringManagerPhone: null,
      seekAnzWorkTypeCode: null,
      includePrivacyPolicy: false,
      includeJobQuestions: false,
      brandingId: null,
      videoUrl: null,
      videoPosition: null,
      billingReference: null,
      ...attributes,
    });
  }

  @Attr public readonly uuid: string;
  @Attr public title: string;
  @Attr public hiringManagerName: string;
  @Attr public hiringManagerEmail: string;
  @Attr public hiringManagerPhone: string;
  @Attr public basisCode: IntegrationsSeek.Stats.BasisCode;
  @Attr public currency: string;
  @Attr public status: string;
  @Attr public salaryMinAmount: number;
  @Attr public salaryMaxAmount: number;
  @Attr public salaryDescription: string;
  @Attr public intervalCode: IntegrationsSeek.Stats.SalaryIntervalCode;
  @Attr public jobParentCategories: string;
  @Attr public jobCategories: string;
  @Attr public positionLocation: string;
  @Attr public positionLocationReadableName: string;
  @Attr public seekAdvertisementProductId: string;
  @Attr public readonly seekAnzWorkTypeCode: string;
  @Attr public searchResultsSummary: string;
  @Attr public searchBulletPoints: string[];
  @Attr public jobAdUid: string;
  @Attr public includePrivacyPolicy: boolean;
  @Attr public includeJobQuestions: boolean;
  @Attr public jobAdUrl: string;
  @Attr public brandingId: string;
  @Attr public videoUrl: string;
  @Attr public videoPosition: string;
  @Attr public billingReference: string;
  @Attr public lastError: string;
  @Attr public expiredAt: string;

  @BelongsTo(Company) public user: Company;
  @BelongsTo(Integration) public integration: Integration;
  @BelongsTo(Job) public job: Job;
  @BelongsTo(IntegrationAccountsSeekAccount) public integrationAccount: IntegrationAccountsSeekAccount;
}

@Model()
export class TotalJobsRegion extends ApplicationRecord {
  public static jsonapiType = 'total_jobs_regions';

  @Attr public readonly location: string;
  @Attr public readonly town: string;
  @Attr public readonly county: string;
  @Attr public readonly region: string;
  @Attr public readonly country: string;
  @Attr public readonly continent: string;
}

@Model()
export class Wizard extends ApplicationRecord {
  public static jsonapiType = 'wizards';

  @Attr({ persist: false }) public readonly currentStep: string;
  @Attr public redirectUrl: string;
  @Attr public steps: { name: string; complete: boolean }[];
  @Attr() public status: string;
  @Attr({ persist: false }) public readonly userId: string;
}

@Model()
export class VisibilityLevel extends ApplicationRecord {
  public static jsonapiType = 'visibility_levels';

  @Attr({ persist: false }) public readonly levelName: string;
  @Attr({ persist: false }) public readonly name: string;
  @Attr({ persist: false }) public readonly description?: string;
  @Attr({ persist: false }) public readonly visibleId: string;
  @Attr({ persist: false }) public readonly visibleType: string;
  @Attr({ persist: false }) public readonly levelChildrenContainJobs?: boolean;
  @Attr({ persist: false }) public readonly levelChildren: {
    levelName: string;
    visibleId: string;
    visibleType: string;
  }[];
  @Attr({ persist: false }) public readonly inheritedVisibilities?: {
    userId: string;
    inheritedFromLevels: string;
  }[];
}

/**
 * Job record models - these have scopes that are only relevant to job
 */

@Model()
export class JobRecordComment extends Comment {
  public static jsonapiType = 'job_records/comments';

  @BelongsTo() public user: JobRecordUser;
  @HasMany() public mentionedUsers: JobRecordUser[];
}

@Model()
export class JobRecordUser extends User {
  public static jsonapiType = 'job_records/users';
}
